import classnames from 'classnames';
import React, { useState, useEffect, useRef, useCallback, useContext, createContext, forwardRef, cloneElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import dayjs from 'dayjs';
import ReactModal from 'react-modal';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import flattenChildren from 'react-keyed-flatten-children';

const tokenSpacingSmall3X = [4, "0.25rem"];
const tokenSpacingLarge1X = [24, "1.5rem"];
const tokenSpacingLarge2X = [32, "2rem"];
const tokenSizingBreakpointSmallTo = [767.98, "47.99875em"];
const tokenSizingBreakpointMediumFrom = [768, "48em"];
const tokenSizingBreakpointMediumTo = [1023.98, "63.99875em"];
const tokenSizingBreakpointLarge1XFrom = [1024, "64em"];
const tokenSizingBreakpointLarge1XTo = [1279.98, "79.99875em"];
const tokenSizingElementIconSmall = [18, "1.125rem"];
const tokenSizingElementIconMedium = [24, "1.5rem"];
const tokenSizingElementIconLarge = [32, "2rem"];
const tokenFontFamiliesDefault = "SofiaPro";
const tokenFontFamiliesHeading = "Recoleta";
const tokenFontFamiliesFallback = "system-ui, -apple-system, \"Segoe UI\", Roboto, Ubuntu, Cantarell, \"Noto Sans\", sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"";
const tokenFontWeightsBold = 700;
const tokenFontWeightsSemiBold = 600;
const tokenFontWeightsMedium = 500;
const tokenFontWeightsRegular = 400;
const tokenFontSizesDefault = [16, "1rem"];
const tokenFontSizesUserAgent = 16;

// TODO (WEB-83): Create token for large2x and small2x
const sizeMap = {
  small2x: 12,
  small: tokenSizingElementIconSmall[0],
  medium: tokenSizingElementIconMedium[0],
  large: tokenSizingElementIconLarge[0],
  large2x: 64
};

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSpinner = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 25 24",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M12.45 22C18 22 22.5 17.5 22.5 11.95c0-5.027-3.69-9.192-8.508-9.934-.845-.13-1.543.575-1.543 1.43 0 .85.695 1.523 1.522 1.717a6.91 6.91 0 11-8.308 8.309c-.193-.828-.866-1.522-1.716-1.522-.855 0-1.561.697-1.431 1.542C3.258 18.31 7.423 22 12.45 22z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconAccount = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "#565656",
  strokeWidth: 1.4,
  d: "M15.75 9a6.718 6.718 0 01-1.145 3.762A6.743 6.743 0 019 15.75a6.742 6.742 0 01-6.698-5.911A6.75 6.75 0 1115.75 9z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M9.75 6.75A.75.75 0 019 7.5V9a2.25 2.25 0 002.25-2.25h-1.5zM9 7.5a.75.75 0 01-.75-.75h-1.5A2.25 2.25 0 009 9V7.5zm-.75-.75A.75.75 0 019 6V4.5a2.25 2.25 0 00-2.25 2.25h1.5zM9 6a.75.75 0 01.75.75h1.5A2.25 2.25 0 009 4.5V6zm-5.125 7.392l-.72-.214-.116.392.266.31.57-.488zm10.25 0l.57.488.266-.31-.116-.392-.72.214zM6.75 12h4.5v-1.5h-4.5V12zm0-1.5a3.751 3.751 0 00-3.595 2.678l1.438.428A2.252 2.252 0 016.75 12v-1.5zM9 15a5.983 5.983 0 01-4.556-2.096l-1.139.976A7.485 7.485 0 009 16.5V15zm2.25-3c1.018 0 1.88.677 2.157 1.607l1.438-.429A3.753 3.753 0 0011.25 10.5V12zm2.306.904A5.984 5.984 0 019 15v1.5a7.486 7.486 0 005.696-2.62l-1.14-.976z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconAdjust = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M2.5 13.235c0 .389.325.706.722.706h11.556s.722 0 .722-.706-.722-.706-.722-.706H3.222a.716.716 0 00-.722.706zm0-8.47c0 .388.325.706.722.706h11.556s.722 0 .722-.706-.722-.706-.722-.706H3.222a.716.716 0 00-.722.706zM15.5 9a.716.716 0 00-.722-.706H3.222S2.5 8.294 2.5 9s.722.706.722.706h11.556A.716.716 0 0015.5 9z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.722 4.765c0 .974-.808 1.764-1.805 1.764-.998 0-1.806-.79-1.806-1.764C6.111 3.79 6.92 3 7.917 3c.997 0 1.805.79 1.805 1.765zM14.056 9c0 .975-.809 1.765-1.806 1.765S10.444 9.975 10.444 9c0-.975.809-1.765 1.806-1.765s1.806.79 1.806 1.765zM8.278 13.235c0 .975-.809 1.765-1.806 1.765s-1.805-.79-1.805-1.765c0-.974.808-1.764 1.805-1.764.997 0 1.806.79 1.806 1.764z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconAlert = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M2.68 16.135h12.64c1.293 0 2.099-1.402 1.452-2.518l-6.32-10.92a1.678 1.678 0 00-2.904 0l-6.32 10.92c-.647 1.116.16 2.518 1.452 2.518zM9 10.26a.842.842 0 01-.84-.84V7.742c0-.462.378-.84.84-.84.462 0 .84.378.84.84V9.42c0 .462-.378.84-.84.84zm.84 3.357H8.16v-1.679h1.68v1.68z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconAvatar = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 14 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M7.147 0a3.43 3.43 0 10.002 6.859A3.43 3.43 0 007.147 0zM5.433 8A5.133 5.133 0 00.29 13.143v.571A2.279 2.279 0 002.576 16h9.143a2.279 2.279 0 002.285-2.286v-.571A5.133 5.133 0 008.861 8H5.433z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconBaby = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 17 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeWidth: 1.5,
  d: "M6.62 10.903a14.58 14.58 0 003.78-.008c-.414.432-1.09.777-1.894.777-.8 0-1.471-.34-1.886-.769z"
}), /*#__PURE__*/React.createElement("circle", {
  cx: 6.551,
  cy: 8.063,
  r: 1.134,
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("circle", {
  cx: 10.509,
  cy: 8.063,
  r: 1.087,
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeWidth: 1.5,
  d: "M9.972 1.903l-.755 1.109"
}), /*#__PURE__*/React.createElement("mask", {
  id: "iconBaby_svg__a",
  fill: "#fff"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  d: "M14.15 11.606a6.294 6.294 0 01-5.657 3.533 6.294 6.294 0 01-5.658-3.533C1.75 11.568.779 10.738.545 9.562c-.271-1.36.545-2.67 1.822-2.924.081-.016.162-.028.242-.035a6.296 6.296 0 0111.767 0c.08.007.16.019.242.035 1.277.254 2.094 1.563 1.823 2.924-.235 1.176-1.206 2.006-2.29 2.044z",
  clipRule: "evenodd"
})), /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M14.15 11.606l-.052-1.5-.9.032-.395.809 1.347.659zm-11.315 0l1.347-.66-.395-.808-.9-.031-.052 1.499zM.545 9.562l-1.472.293 1.471-.293zm1.822-2.924l.294 1.471-.294-1.471zm.242-.035l.132 1.494.935-.082.335-.878-1.402-.534zm11.767 0l-1.402.534.335.878.936.082.13-1.494zm.242.035l.293-1.471-.293 1.47zm1.823 2.924l1.47.293-1.47-.293zm-7.948 7.077c3.08 0 5.74-1.787 7.005-4.374l-2.695-1.318a4.794 4.794 0 01-4.31 2.692v3zm-7.006-4.374a7.794 7.794 0 007.006 4.373v-3a4.794 4.794 0 01-4.31-2.691l-2.696 1.318zm-2.414-2.41c.362 1.814 1.885 3.186 3.71 3.25l.104-2.998c-.345-.012-.764-.3-.871-.838l-2.943.586zm3.001-4.688C-.1 5.6-1.342 7.77-.926 9.855l2.942-.586c-.127-.636.265-1.084.645-1.16l-.587-2.942zm.404-.058a3.74 3.74 0 00-.404.058l.587 2.942a.744.744 0 01.08-.012L2.478 5.11zM8.493 1.05a7.796 7.796 0 00-7.285 5.02L4.01 7.137A4.796 4.796 0 018.493 4.05v-3zm7.285 5.019A7.797 7.797 0 008.493 1.05v3a4.797 4.797 0 014.481 3.087l2.804-1.068zm-.867-.902a3.743 3.743 0 00-.404-.058l-.262 2.988a.744.744 0 01.08.012l.586-2.942zm3 4.688c.416-2.085-.825-4.255-3-4.688l-.586 2.942c.38.076.771.524.645 1.16l2.942.586zm-3.709 3.25c1.826-.064 3.348-1.436 3.71-3.25l-2.942-.586c-.108.539-.527.826-.872.838l.104 2.998z",
  mask: "url(#iconBaby_svg__a)"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconBell = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 13 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M6.192 0a.879.879 0 00-.885.875V1.4C3.29 1.805 1.77 3.571 1.77 5.687v.515A5.23 5.23 0 01.428 9.69l-.205.227a.87.87 0 00-.146.94.884.884 0 00.807.517H11.5a.887.887 0 00.808-.517.866.866 0 00-.147-.94l-.204-.227a5.225 5.225 0 01-1.341-3.49v-.513c0-2.117-1.52-3.883-3.539-4.288V.875A.879.879 0 006.192 0zm1.252 13.489c.332-.329.517-.774.517-1.239H4.423c0 .465.185.91.516 1.239A1.78 1.78 0 006.192 14c.47 0 .92-.183 1.252-.511z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconBookings = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.997 9.275c.587 0 1.147.341 1.49.966l.003.007.59 1.26c.28.598.193 1.188-.14 1.571-.338.387-.897.525-1.45.26a1.126 1.126 0 00-.493-.129c-.149 0-.306.038-.494.129-.552.265-1.112.127-1.45-.26-.333-.383-.42-.974-.14-1.571l.59-1.26.004-.007c.342-.625.902-.966 1.49-.966zM7.802 8.991c.552-.028.725-.587.694-1.171-.03-.584-.255-.973-.807-.945-.551.029-.973.526-.942 1.11.03.584.503 1.035 1.055 1.006zM6.05 10.8c.396-.12.586-.638.423-1.157-.162-.52-.615-.843-1.011-.723-.396.12-.586.638-.423 1.157.162.52.615.843 1.011.723zM12.531 8.92c-.396-.12-.849.204-1.011.723-.163.52.027 1.037.423 1.157.396.12.849-.204 1.011-.723.163-.52-.027-1.038-.423-1.157zM10.189 8.991c.552.03 1.025-.42 1.057-1.004.032-.585-.389-1.082-.94-1.111-.552-.03-.777.36-.81.944-.031.584.141 1.142.693 1.171z",
  fill: "#565656"
}), /*#__PURE__*/React.createElement("path", {
  d: "M2.285 8.577v4.512c0 .945 0 1.418.184 1.78.162.317.42.575.738.736.36.184.832.184 1.776.184h8.105c.943 0 1.415 0 1.775-.184.318-.161.576-.42.738-.737.184-.36.184-.832.184-1.776V8.577c0-.451 0-.676-.055-.886a1.685 1.685 0 00-.236-.52c-.122-.18-.291-.33-.631-.626L10.813 3c-.63-.55-.945-.827-1.3-.931a1.682 1.682 0 00-.957 0c-.354.104-.668.38-1.298.93l-4.05 3.545c-.34.297-.51.445-.63.624a1.687 1.687 0 00-.238.522c-.055.209-.055.435-.055.886z",
  stroke: "#565656",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconBuilding = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 17",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeWidth: 1.4,
  d: "M1.304 10.915l4.061-4.03 4.002 3.971"
}), /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "bevel",
  strokeWidth: 1.4,
  d: "M5.24 1.222h9.887v14.6H2.004V10.19L4.86 7.15a1.4 1.4 0 00.38-.959V1.222z"
}), /*#__PURE__*/React.createElement("rect", {
  width: 1.657,
  height: 1.648,
  x: 7.787,
  y: 3.723,
  fill: "currentColor",
  rx: 0.2
}), /*#__PURE__*/React.createElement("rect", {
  width: 1.657,
  height: 1.648,
  x: 10.945,
  y: 3.723,
  fill: "currentColor",
  rx: 0.2
}), /*#__PURE__*/React.createElement("rect", {
  width: 1.657,
  height: 1.648,
  x: 10.945,
  y: 6.875,
  fill: "currentColor",
  rx: 0.2
}), /*#__PURE__*/React.createElement("rect", {
  width: 1.671,
  height: 1.661,
  x: 10.938,
  y: 10.121,
  fill: "currentColor",
  rx: 0.2
}), /*#__PURE__*/React.createElement("rect", {
  width: 1.657,
  height: 1.648,
  x: 4.552,
  y: 10.875,
  fill: "currentColor",
  rx: 0.2
}), /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeWidth: 1.4,
  d: "M8.41 10.283v5.18"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCalendar = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M14.453 2.364h-.682v-.682A.684.684 0 0013.089 1a.684.684 0 00-.682.682v.682H5.59v-.682A.684.684 0 004.907 1a.684.684 0 00-.681.682v.682h-.682c-.75 0-1.364.613-1.364 1.363v10.91c0 .75.614 1.363 1.364 1.363h10.909c.75 0 1.363-.614 1.363-1.364V3.727c0-.75-.613-1.363-1.363-1.363zm-.682 12.272H4.226a.684.684 0 01-.682-.681V5.772h10.909v8.181a.684.684 0 01-.682.682z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCalendarBooking = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M3.191 3.728a.846.846 0 00-.845.845V14.89c0 .467.378.845.845.845h11.615a.846.846 0 00.846-.845V4.573a.846.846 0 00-.846-.845H3.191zm-2.111.845c0-1.166.945-2.111 2.111-2.111h11.615c1.166 0 2.111.945 2.111 2.111V14.89A2.111 2.111 0 0114.806 17H3.191a2.111 2.111 0 01-2.111-2.111V4.573z",
  fill: "#2C2C2C"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M1.08 6.72v-.163h1.266v.162H1.08zM15.652 6.72v-.163h1.265v.162h-1.265z",
  fill: "#2C2C2C"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M16.285 7.27H1.713V6.006h14.572v1.266zM5.229 1c.35 0 .632.283.632.633v1.462a.633.633 0 01-1.265 0V1.633c0-.35.283-.633.633-.633zM12.695 1c.35 0 .633.283.633.633v1.462a.633.633 0 01-1.266 0V1.633c0-.35.284-.633.633-.633zM7.542 8.824c0-.35.284-.633.633-.633h6.18a.633.633 0 010 1.266h-6.18a.633.633 0 01-.633-.633zM3.007 10.729c0-.35.283-.633.632-.633h7.145a.633.633 0 010 1.265H3.64a.633.633 0 01-.632-.632z",
  fill: "#2C2C2C"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCalendarCheck = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M12.111 8.412a.587.587 0 00-.824 0l-3.384 3.384-1.236-1.237a.587.587 0 00-.825 0 .587.587 0 000 .824l1.51 1.51a.774.774 0 001.096 0l3.655-3.656a.58.58 0 00.008-.825zm2.333-5.856h-.777v-.778A.78.78 0 0012.889 1a.78.78 0 00-.778.778v.778H5.89v-.778A.78.78 0 005.11 1a.78.78 0 00-.778.778v.778h-.777c-.864 0-1.548.7-1.548 1.555L2 15c0 .856.692 1.556 1.556 1.556h10.888c.856 0 1.556-.7 1.556-1.556V4.111c0-.855-.7-1.555-1.556-1.555zM13.667 15H4.333a.78.78 0 01-.777-.778V6.444h10.888v7.778a.78.78 0 01-.777.778z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCalendarDay = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M5.182 8.77h2.475v2.493H5.182V8.77zM14.6 7.316H3.4V5.94h11.2v1.376z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.6 2.364h-.7v-.682A.693.693 0 0013.2 1c-.385 0-.7.307-.7.682v.682h-7v-.682A.693.693 0 004.8 1c-.385 0-.7.307-.7.682v.682h-.7c-.77 0-1.4.613-1.4 1.363v10.91C2 15.386 2.63 16 3.4 16h11.2c.77 0 1.4-.614 1.4-1.364V3.727c0-.75-.63-1.363-1.4-1.363zm-.7 12.272H4.1a.693.693 0 01-.7-.681V4.194h11.2v9.76a.693.693 0 01-.7.682z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCalendarOutlined = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M6 1.5v3m6-3v3M14.25 3H3.75a1.5 1.5 0 00-1.5 1.5V15a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V4.5a1.5 1.5 0 00-1.5-1.5zM2.25 7.5h13.5",
  stroke: "#565656",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconChat = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M3.866 11.225a1.502 1.502 0 011.393-.2c.828.3 1.756.472 2.743.472 3.896 0 6.498-2.515 6.498-4.999 0-2.483-2.602-4.998-6.498-4.998-3.896 0-6.498 2.515-6.498 4.998 0 1 .387 1.962 1.115 2.787.269.303.4.703.369 1.11a5.533 5.533 0 01-.353 1.543 7.84 7.84 0 001.23-.71v-.003zm-3.2 1.269a5.55 5.55 0 00.16-.253c.312-.519.609-1.2.668-1.966C.557 9.21.004 7.907.004 6.498.004 2.908 3.584 0 8.002 0S16 2.909 16 6.498c0 3.59-3.58 6.499-7.998 6.499a9.54 9.54 0 01-3.252-.56 9.253 9.253 0 01-1.697.957 7.656 7.656 0 01-1.565.502c-.025.007-.05.01-.075.016a4.517 4.517 0 01-.412.06l-.022.003C.819 13.99.66 14 .5 14a.5.5 0 01-.356-.853 4.483 4.483 0 00.503-.637l.009-.016h.01z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCheck = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M6.267 11.415l-2.24-2.24A1.206 1.206 0 002.32 10.88l2.885 2.886a1.5 1.5 0 002.122 0l8.45-8.45a1.022 1.022 0 00-1.42-1.469l-8.091 7.568z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconChevronDown = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M3.452 4.9l5.555 5.303L14.562 4.9a1.474 1.474 0 012.02 0 1.318 1.318 0 010 1.927L10.008 13.1a1.474 1.474 0 01-2.018 0L1.419 6.827a1.318 1.318 0 010-1.927c.558-.52 1.475-.533 2.033 0z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.452 4.9l5.555 5.303L14.562 4.9a1.474 1.474 0 012.02 0 1.318 1.318 0 010 1.927L10.008 13.1a1.474 1.474 0 01-2.018 0L1.419 6.827a1.318 1.318 0 010-1.927c.558-.52 1.475-.533 2.033 0z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconChevronLeft = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M13.1 3.452L7.797 9.007l5.303 5.555c.533.559.533 1.46 0 2.02a1.318 1.318 0 01-1.927 0L4.9 10.008a1.474 1.474 0 010-2.018l6.273-6.572a1.318 1.318 0 011.927 0c.52.558.533 1.475 0 2.033z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M13.1 3.452L7.797 9.007l5.303 5.555c.533.559.533 1.46 0 2.02a1.318 1.318 0 01-1.927 0L4.9 10.008a1.474 1.474 0 010-2.018l6.273-6.572a1.318 1.318 0 011.927 0c.52.558.533 1.475 0 2.033z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconChevronRight = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M4.9 14.548l5.303-5.555L4.9 3.438a1.474 1.474 0 010-2.02 1.318 1.318 0 011.927 0L13.1 7.992c.533.558.533 1.46 0 2.018l-6.273 6.572a1.318 1.318 0 01-1.927 0c-.52-.558-.533-1.474 0-2.033z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M4.9 14.548l5.303-5.555L4.9 3.438a1.474 1.474 0 010-2.02 1.318 1.318 0 011.927 0L13.1 7.992c.533.558.533 1.46 0 2.018l-6.273 6.572a1.318 1.318 0 01-1.927 0c-.52-.558-.533-1.474 0-2.033z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconChevronUp = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M14.548 13.1L8.993 7.797 3.438 13.1a1.474 1.474 0 01-2.02 0 1.318 1.318 0 010-1.927L7.992 4.9a1.474 1.474 0 012.018 0l6.572 6.273a1.318 1.318 0 010 1.927c-.558.52-1.474.533-2.033 0z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.548 13.1L8.993 7.797 3.438 13.1a1.474 1.474 0 01-2.02 0 1.318 1.318 0 010-1.927L7.992 4.9a1.474 1.474 0 012.018 0l6.572 6.273a1.318 1.318 0 010 1.927c-.558.52-1.474.533-2.033 0z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconClock = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M8.992 1C4.576 1 1 4.584 1 9s3.576 8 7.992 8C13.416 17 17 13.416 17 9s-3.584-8-8.008-8zM9 15.4A6.398 6.398 0 012.6 9c0-3.536 2.864-6.4 6.4-6.4 3.536 0 6.4 2.864 6.4 6.4 0 3.536-2.864 6.4-6.4 6.4zM8.824 5h-.048a.574.574 0 00-.576.576v3.776c0 .28.144.544.392.688l3.32 1.992a.57.57 0 00.784-.192.568.568 0 00-.2-.792L9.4 9.208V5.576A.574.574 0 008.824 5z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconClose = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.69 2.321a1.057 1.057 0 00-1.498 0L9 7.503 3.808 2.31A1.057 1.057 0 102.31 3.808L7.503 9 2.31 14.192a1.057 1.057 0 101.497 1.497L9 10.497l5.192 5.192a1.057 1.057 0 101.497-1.497L10.497 9l5.192-5.192a1.064 1.064 0 000-1.487z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCopy = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 15 15",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 1.5,
  d: "M12.329 4.834h-6.39c-.706 0-1.278.572-1.278 1.278v6.39c0 .706.572 1.278 1.278 1.278h6.39c.706 0 1.278-.572 1.278-1.278v-6.39c0-.706-.572-1.278-1.278-1.278z"
}), /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 1.5,
  d: "M2.105 9.946A1.282 1.282 0 01.827 8.668v-6.39C.827 1.575 1.402 1 2.105 1h6.39c.703 0 1.278.575 1.278 1.278"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCredit = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M14.978 2.893c.737 0 1.29.183 1.658.549.349.347.533.854.55 1.52l.003.113v7.667c0 .723-.185 1.266-.553 1.63-.35.344-.863.526-1.543.543l-.115.002H2.855c-.737 0-1.29-.182-1.658-.545-.349-.345-.533-.85-.55-1.518l-.002-.112V5.075c0-.727.184-1.273.552-1.637.35-.344.863-.526 1.543-.544l.115-.001h12.123zm-13.2 9.785c0 .366.094.642.281.828.174.17.417.263.73.276l.08.002h5.618v-3.731h-.05a3.46 3.46 0 01-.552 1.035c-.25.328-.539.625-.859.887a5.36 5.36 0 01-.978.644c-.331.166-.63.27-.898.313-.202.028-.36-.017-.475-.134a.565.565 0 01-.173-.408c0-.17.05-.307.148-.412a.605.605 0 01.35-.177l.066-.01a2.14 2.14 0 00.78-.27c.288-.16.558-.35.807-.564.252-.214.485-.45.697-.704.179-.216.319-.42.42-.612l.041-.081H1.778v3.118zM16.055 9.56h-6.033c.103.216.257.447.46.693.213.254.446.49.698.704.26.223.528.41.802.563.245.136.477.222.697.26l.082.011a.646.646 0 01.422.187.581.581 0 01.148.412c0 .155-.058.29-.173.408-.115.117-.273.162-.475.134a3.075 3.075 0 01-.901-.313 5.419 5.419 0 01-.975-.644 5.306 5.306 0 01-.859-.888 3.507 3.507 0 01-.511-.915l-.042-.12H9.34v3.732h5.625c.347 0 .616-.093.806-.278.175-.172.27-.42.284-.745l.001-.083V9.56zm-1.012-5.532l-.08-.002H9.34v2.415c.146-.288.337-.55.567-.778.212-.211.462-.382.736-.503.263-.117.549-.177.837-.176.507 0 .928.168 1.264.503.335.336.503.762.503 1.278 0 .295-.063.571-.19.827a2.247 2.247 0 01-.531.686 3.081 3.081 0 01-.682.455l-.11.052h4.322V5.14c0-.367-.095-.643-.285-.831-.175-.173-.418-.267-.727-.28zm-6.556-.002H2.87c-.352 0-.622.094-.81.282-.173.173-.266.422-.28.748l-.001.083v3.646h4.315c-.285-.13-.55-.3-.785-.507a2.297 2.297 0 01-.534-.686 1.81 1.81 0 01-.194-.827c0-.516.169-.942.507-1.278.338-.335.758-.503 1.26-.503.295 0 .577.058.845.176.274.121.523.292.735.503.2.197.369.421.502.667l.058.111V4.026zM6.404 5.983c-.254 0-.456.077-.606.23-.15.152-.225.362-.225.63 0 .22.068.44.204.657.136.219.324.416.563.592.252.182.53.326.823.426.276.095.565.149.856.16l.13.002h.211v-.198a2.925 2.925 0 00-.155-.957 2.694 2.694 0 00-.43-.799 2.115 2.115 0 00-.626-.546 1.504 1.504 0 00-.746-.197m5.02 0c-.26 0-.515.069-.74.197a2.114 2.114 0 00-.626.546 2.83 2.83 0 00-.589 1.63l-.003.126v.198h.212a3.047 3.047 0 001.813-.588c.236-.176.423-.373.56-.592a1.24 1.24 0 00.203-.658c0-.267-.074-.477-.222-.63-.147-.152-.35-.229-.609-.229z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCreditCard = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 13",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M14.222 1.333c.245 0 .445.2.445.445v.889H1.333v-.89c0-.244.2-.444.445-.444h12.444zm.445 4v5.334c0 .244-.2.444-.445.444H1.778a.446.446 0 01-.445-.444V5.333h13.334zM1.777 0A1.78 1.78 0 000 1.778v8.889c0 .98.797 1.777 1.778 1.777h12.444A1.78 1.78 0 0016 10.667v-8.89A1.78 1.78 0 0014.222 0H1.778zm1.556 8.444a.665.665 0 00-.666.667c0 .37.297.667.666.667h1.334c.37 0 .666-.297.666-.667a.665.665 0 00-.666-.667H3.333zm3.556 0a.665.665 0 00-.667.667c0 .37.297.667.667.667H10c.37 0 .667-.297.667-.667A.665.665 0 0010 8.444H6.889z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconCross = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M10 16H6a1.335 1.335 0 01-1.333-1.333v-3.334H1.333A1.334 1.334 0 010 10V6a1.335 1.335 0 011.333-1.333h3.334V1.333A1.335 1.335 0 016 0h4a1.335 1.335 0 011.333 1.333v3.334h3.334A1.335 1.335 0 0116 6v4a1.335 1.335 0 01-1.333 1.333h-3.334v3.334A1.335 1.335 0 0110 16zM1.333 6v4H6v4.667h4V10h4.667V6H10V1.333H6V6H1.333z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconDashboard = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M3.75 15.75c-.413 0-.765-.147-1.059-.44a1.447 1.447 0 01-.441-1.06V3.75c0-.413.147-.765.441-1.059.294-.293.647-.44 1.059-.441h10.5c.412 0 .766.147 1.06.441.294.294.44.647.44 1.059v10.5c0 .412-.147.766-.44 1.06a1.44 1.44 0 01-1.06.44H3.75zm3.75-1.5v-4.5H3.75v4.5H7.5zm1.5 0h5.25v-4.5H9v4.5zm-5.25-6h10.5v-4.5H3.75v4.5z",
  fill: "#565656"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconDogGrooming = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M3.799 7.834c-.29.156-.506.42-.606.747-.336 1.086.743 2.063 1.71 1.545.29-.155.506-.42.606-.746.337-1.09-.745-2.063-1.71-1.546zm.736 1.528a.39.39 0 01-.46-.068.44.44 0 01.092-.695.39.39 0 01.46.067c.206.203.16.56-.092.696z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.138 14.931c.464-1.501-.26-3.117-1.625-3.707l.185-.78 4.085-1.73c1.435-.609 2.106-2.345 1.485-3.84a.4.4 0 00-.497-.235l-4.018 1.368 1.028-4.326a.434.434 0 00-.256-.508c-1.454-.554-3.05.267-3.531 1.819l-1.368 4.42-.729.25C6.25 6.263 4.672 5.607 3.283 6.196 1.825 6.815 1.12 8.561 1.707 10.1c.587 1.538 2.248 2.28 3.7 1.664l2.001-.857-.676 2.16c-.485 1.564.323 3.267 1.82 3.783 1.494.516 3.098-.342 3.586-1.919zm3.501-9.346c.203.932-.249 1.947-1.159 2.333l-3.537 1.5.577-2.43 4.119-1.403zM5.103 10.969c-1.039.44-2.224-.093-2.642-1.19-.42-1.098.083-2.344 1.125-2.786 1.084-.46 2.325.142 2.69 1.33.07.226.299.349.512.276l.527-.18-.348 1.123a.445.445 0 00.11.446l.095.095-2.07.886zm3.702 5.066c-1.065-.367-1.648-1.58-1.301-2.7l.899-2.871a.445.445 0 00-.109-.447l-.474-.469 1.947-6.29C10.07 2.275 11 1.73 11.896 1.883L9.633 11.4a.43.43 0 00.295.52c1.147.306 1.8 1.571 1.437 2.744-.348 1.126-1.493 1.739-2.56 1.37z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.812 12.777c-.639-.22-1.327.147-1.536.822-.205.664.131 1.397.78 1.621.64.221 1.328-.147 1.537-.822.205-.663-.132-1.397-.78-1.621zM9.62 14.38a.383.383 0 01-.31.025.387.387 0 01-.15-.092.448.448 0 01-.11-.448.418.418 0 01.2-.248.384.384 0 01.31-.025c.06.02.107.05.151.093.116.114.159.29.11.447a.418.418 0 01-.201.248zM8.85 9.277a.392.392 0 00.575-.02.446.446 0 00-.02-.606.392.392 0 00-.574.02.446.446 0 00.02.606z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconDogTraining = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M3.474 14.21a17.665 17.665 0 000-5.835h-.197A2.28 2.28 0 011 6.098c0-.496.404-.9.9-.9h.9A2.609 2.609 0 015.36 3.063c.184 0 .58-.007.729.026a2.595 2.595 0 012.201 2.15l.235 1.407a1.772 1.772 0 01-.407 1.452c2.11 2.654 3.083 4.212 3.325 6.23.533-.235 1-.866 1.324-1.414.512-.868 1.855-.42 1.74.582C14.25 15.756 13.014 17 11.024 17H2.538a.975.975 0 01-.975-.975c0-.93.71-1.815 1.911-1.815zM6.39 4.14L5.945 6.8a.84.84 0 101.656.001l-.235-1.408v-.001a1.66 1.66 0 00-.977-1.253zM2.54 16.063h2.147a1.891 1.891 0 011.89-1.875h.172a2.527 2.527 0 01-.451-1.822.469.469 0 01.93.115c-.148 1.2.917 1.73.96 1.75a.469.469 0 01-.199.894H6.575c-.52 0-.944.42-.951.938h5.4c1.512 0 2.347-.874 2.553-2.672h-.002c-.77 1.306-1.629 1.968-2.551 1.968a.47.47 0 01-.468-.438c-.122-1.87-.703-3.135-3.218-6.298A1.777 1.777 0 015.02 6.646L5.462 4h-.1c-.92 0-1.667.748-1.667 1.667 0 .259-.21.468-.47.468H1.939c.02.722.613 1.303 1.34 1.303h.589c.227 0 .421.162.461.385l.029.158a18.608 18.608 0 01-.03 6.785.469.469 0 01-.46.382h-.49a.878.878 0 00-.876.877c0 .02.017.038.037.038zM12.746 7.832l-2.92-2.92a2.29 2.29 0 010-3.242 2.296 2.296 0 013.242 0l.01.01.009-.01a2.296 2.296 0 013.242 0 2.295 2.295 0 010 3.242l-2.92 2.92a.469.469 0 01-.663 0zm2.92-5.499a1.355 1.355 0 00-1.916 0l-.34.341a.469.469 0 01-.664 0l-.34-.34a1.354 1.354 0 10-1.917 1.916l2.589 2.588 2.588-2.589a1.356 1.356 0 000-1.916z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconDogWalking = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M1.393 4.194l1.602-.266A2.341 2.341 0 014.9 2.486l.597-.066.941-.105a1.41 1.41 0 011.464 1.403v1.25c0 .17-.03.333-.086.483.097.232.24.438.415.609l6.442-4.962A.472.472 0 0114.96 1h1.568a.47.47 0 110 .938h-1.408L9.145 6.54c.045.01.09.017.136.023l3.853.48c.14.017.276.044.409.08l.559-.322c.592-.34.971-.95 1.015-1.629a.742.742 0 01.577-.678.746.746 0 01.82.353v.003a3.604 3.604 0 01-.823 4.581c.036.18.054.367.054.557v2.004l1.152 1.434a.468.468 0 01.103.293v2.812a.47.47 0 01-.47.469h-1.883a.47.47 0 01-.47-.469v-1.82a2.704 2.704 0 01-1.883-2.58v-.184l-.786.356a10.63 10.63 0 01-2.037.685v3.543A.47.47 0 019 17H5.235a.47.47 0 01-.47-.469v-4.389a2.937 2.937 0 01-1.569-2.606V7h-.47A1.724 1.724 0 011 5.281v-.625a.47.47 0 01.393-.462zm5.568-.475a.47.47 0 00-.45-.468l-.491.054v1.664a.47.47 0 00.94 0v-1.25zm8.981 2.16a2.964 2.964 0 01-1.37 1.734l-.013.007c.311.235.574.528.772.862.65-.694.884-1.695.611-2.603zM8.53 13.157c-.31.041-.625.07-.94.083v2.822h.94v-2.905zM5.41 11.409a.469.469 0 01.296.435v4.219h.941V12.78c0-.105.035-.202.094-.28V12.5l.188-.25a3.293 3.293 0 00.66-1.969.47.47 0 01.94 0c0 .692-.175 1.38-.5 1.989a9.625 9.625 0 003.09-.82l1.45-.658a.472.472 0 01.666.427v.911c0 .872.627 1.608 1.49 1.751a.47.47 0 01.393.463v1.719h.94v-2.18l-1.151-1.434a.468.468 0 01-.103-.293V9.988a2.037 2.037 0 00-1.786-2.015l-3.854-.48a2.772 2.772 0 01-.886-.267 6.219 6.219 0 01-4.14 1.375v.935c0 .83.499 1.565 1.272 1.873zM1.941 5.28c0 .431.352.782.784.782h.942c.26 0 .47.21.47.468v1.132a5.276 5.276 0 003.355-1.016 2.767 2.767 0 01-.339-.437 1.41 1.41 0 01-2.075-1.241v-1.56l-.074.008a1.394 1.394 0 00-1.195 1.04.47.47 0 01-.379.35l-1.489.246v.228z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconEasterEgg = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 15 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9.635.857C8.26-.286 6.14-.286 4.765.857-.325 5.087.005 11.399.005 11.399.004 14.99 3.224 18 7.2 18c3.974 0 7.196-3.009 7.196-6.601 0 0 .329-6.311-4.761-10.542z",
  fill: "#7CDEFF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.422 11.399S3.171 5.088 7.052.857A2.645 2.645 0 018.17.12C7.005-.174 5.71.072 4.765.857-.325 5.087.005 11.399.005 11.399c0 3.901 3.793 7.088 8.165 6.539-2.68-.442-4.748-3.244-4.748-6.54z",
  fill: "#7ED5FF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.794 4.78a12.895 12.895 0 00-1.33-2.052c-.247.102-.54.169-.92.169-1.672 0-1.672-1.288-3.346-1.288-1.673 0-1.673 1.288-3.346 1.288-.38 0-.672-.067-.918-.17a12.888 12.888 0 00-1.33 2.053c.71.375 1.05 1.035 2.248 1.035 1.673 0 1.673-1.288 3.346-1.288 1.674 0 1.674 1.288 3.347 1.288 1.198 0 1.538-.66 2.249-1.035z",
  fill: "#C6F7FF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M4.281 5.782C4.71 4.453 5.348 3.05 6.3 1.77c-.855.345-1.143 1.126-2.447 1.126-.38 0-.672-.067-.918-.169-.5.63-.95 1.323-1.33 2.053.73.385 1.224 1.231 2.677 1.002z",
  fill: "#ACEAFF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M10.374 7.445C8.7 7.445 8.7 8.733 7.027 8.733S5.354 7.445 3.68 7.445C1.977 7.445 2.005 8.78.246 8.73c-.18.955-.274 1.92-.236 2.902 1.934.22 1.904-1.269 3.67-1.269 1.674 0 1.674 1.287 3.347 1.287s1.673-1.287 3.347-1.287c1.673 0 1.673 1.287 3.346 1.287.26 0 .48-.03.67-.083a12.82 12.82 0 00-.243-2.866c-.13.02-.27.032-.427.032-1.673 0-1.673-1.288-3.346-1.288z",
  fill: "#C6F7FF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.837 7.45C1.995 7.356 2.053 8.782.247 8.73c-.045.318-.294 1.412-.236 2.903 1.841.21 1.903-1.13 3.43-1.259.04-.713.142-1.745.396-2.924z",
  fill: "#ACEAFF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.085 13.288a2.928 2.928 0 00-.196-.007c-1.673 0-1.673 1.287-3.346 1.287-1.674 0-1.674-1.287-3.347-1.287s-1.673 1.287-3.346 1.287c-1.743 0-1.687-1.402-3.542-1.28.612 1.852 2.097 3.38 4.02 4.157 1.213-.218 1.372-1.246 2.868-1.246 1.497 0 1.656 1.028 2.87 1.246 1.922-.777 3.407-2.305 4.02-4.157z",
  fill: "#C6F7FF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5.68 16.723a6.495 6.495 0 01-1.582-2.165c-1.958.165-1.855-1.396-3.79-1.27 1.031 3.12 4.009 4.15 4.02 4.158.62-.111.964-.434 1.352-.723z",
  fill: "#ACEAFF"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconEllipses = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M10.6 9a1.6 1.6 0 11-3.2 0 1.6 1.6 0 013.2 0zM4.2 9A1.6 1.6 0 111 9a1.6 1.6 0 013.2 0zM17 9a1.6 1.6 0 11-3.2 0A1.6 1.6 0 0117 9z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconEnvelope = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.4 2.6H2.6c-.88 0-1.592.72-1.592 1.6L1 13.8c0 .88.72 1.6 1.6 1.6h12.8c.88 0 1.6-.72 1.6-1.6V4.2c0-.88-.72-1.6-1.6-1.6zm-.8 11.2H3.4c-.44 0-.8-.36-.8-.8V5.8l5.552 3.472c.52.328 1.176.328 1.696 0L15.4 5.8V13c0 .44-.36.8-.8.8zM9 8.2l-6.4-4h12.8L9 8.2z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconEnvelopeNav = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M1.709 3.383a2.42 2.42 0 011.71-.709h11.162A2.419 2.419 0 0117 5.094v7.813a2.419 2.419 0 01-2.42 2.419H3.42A2.42 2.42 0 011 12.906V5.094c0-.641.255-1.256.709-1.71zm.78 1.71v.182a.93.93 0 00.442.791L8.512 9.5a.93.93 0 00.975 0l5.581-3.434a.93.93 0 00.442-.791v-.182a.93.93 0 00-.93-.929H3.42a.93.93 0 00-.93.93zm13.021 2.45l-5.241 3.225a2.42 2.42 0 01-2.538 0L2.49 7.544v5.363a.93.93 0 00.93.929h11.16a.93.93 0 00.93-.93V7.544z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconExclamation = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 16 16",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.1 8A7.1 7.1 0 11.9 8a7.1 7.1 0 0114.2 0z",
  stroke: "currentColor",
  strokeWidth: 1.8
}), /*#__PURE__*/React.createElement("path", {
  d: "M8 4.5v4.106m0 2.828v.254",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFaceHappy = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M9 2.376a6.624 6.624 0 100 13.248A6.624 6.624 0 009 2.376zM1 9a8 8 0 1116 0A8 8 0 011 9zm7.324-1.192a.688.688 0 01-.343.91l-.284-.627.283.627-.001.001-.003.001a2.339 2.339 0 01-.091.038 4.79 4.79 0 01-.967.258c-.603.094-1.45.104-2.275-.31a.688.688 0 01.617-1.23c.477.24 1.003.25 1.447.18a3.415 3.415 0 00.71-.193.688.688 0 01.907.345zm1.369-.026a.688.688 0 01.928-.293c.524.273 1.079.307 1.526.26a3.374 3.374 0 00.708-.152l.004-.001a.688.688 0 01.504 1.28l-.253-.64.253.64h-.002l-.003.002-.003.001-.004.002-.009.003-.01.004a3.836 3.836 0 01-.288.09 4.75 4.75 0 01-.756.14c-.615.063-1.464.028-2.302-.408a.688.688 0 01-.293-.928zm3 2.625c.355.138.53.536.393.89l-.641-.248c.641.249.64.249.64.25v.002l-.002.004-.004.01a3.376 3.376 0 01-.053.118 4.258 4.258 0 01-.817 1.13c-.63.622-1.637 1.225-3.126 1.225-1.484 0-2.534-.599-3.205-1.202a4.822 4.822 0 01-.91-1.105 2.743 2.743 0 01-.07-.127v-.002l.617-.303-.618.302a.688.688 0 011.234-.608l.004.006.024.043c.023.041.062.105.117.184.11.158.282.372.52.587.472.423 1.207.85 2.287.85 1.076 0 1.749-.423 2.16-.828a2.88 2.88 0 00.542-.745l.018-.04a.688.688 0 01.89-.393zm-6.56.338z",
  fill: "#2C2C2C"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFacebook = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M17 9.049C17 4.604 13.418 1 9 1S1 4.604 1 9.049C1 13.066 3.925 16.396 7.75 17v-5.624H5.719V9.049H7.75V7.276c0-2.018 1.194-3.132 3.022-3.132.875 0 1.79.157 1.79.157v1.981h-1.008c-.994 0-1.304.62-1.304 1.258v1.509h2.219l-.355 2.327H10.25V17c3.825-.604 6.75-3.934 6.75-7.951z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFacebookColoured = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconFacebookColoured_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#1877F2",
  d: "M18 9a9 9 0 10-10.406 8.89v-6.288H5.309V9h2.285V7.017c0-2.255 1.343-3.501 3.4-3.501.984 0 2.014.175 2.014.175v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.399 2.602h-2.097v6.289A9.002 9.002 0 0018 9z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M12.503 11.602l.4-2.602h-2.497V7.312c0-.712.349-1.406 1.467-1.406h1.135V3.691s-1.03-.175-2.015-.175c-2.056 0-3.4 1.246-3.4 3.501V9H5.31v2.602h2.285v6.289a9.07 9.07 0 002.812 0V11.6h2.097z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconFacebookColoured_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFacebookMessenger = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 14 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconFacebookMessenger_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "url(#iconFacebookMessenger_svg__b)",
  d: "M7 0C3.057 0 0 2.888 0 6.79c0 2.04.836 3.804 2.198 5.022a.56.56 0 01.188.4l.039 1.245a.56.56 0 00.785.495l1.39-.614a.559.559 0 01.374-.027A7.636 7.636 0 007 13.58c3.943 0 7-2.889 7-6.79C14 2.888 10.943 0 7 0z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M2.797 8.775l2.056-3.262a1.05 1.05 0 011.518-.28L8.007 6.46a.42.42 0 00.505-.002l2.21-1.676c.294-.224.679.129.482.442L9.147 8.486a1.05 1.05 0 01-1.518.28L5.994 7.54a.42.42 0 00-.506.001l-2.21 1.677c-.294.223-.679-.13-.481-.443z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("radialGradient", {
  id: "iconFacebookMessenger_svg__b",
  cx: 0,
  cy: 0,
  r: 1,
  gradientTransform: "translate(269.458 1392.51) scale(1525.44)",
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#09F"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.61,
  stopColor: "#A033FF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.935,
  stopColor: "#FF5280"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#FF7061"
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "iconFacebookMessenger_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h14v14H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFavourite = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 19",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M6.138 3.658a3.196 3.196 0 00-2.568 5.47l.005.004 5.432 5.38 5.432-5.375.002-.002a3.196 3.196 0 10-4.795-4.202l-.643.855-.638-.858a3.196 3.196 0 00-2.227-1.272zM4.352 2.263a4.796 4.796 0 014.658 1.01 4.796 4.796 0 016.553 7.002l-.001.001-5.993 5.93a.8.8 0 01-1.125 0L2.45 10.271a4.797 4.797 0 011.901-8.008z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFence = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.425 9.087V4.736a1 1 0 00-.341-.752l-1.72-1.505a1 1 0 00-1.304-.012l-1.012.855-1.422-1a1 1 0 00-1.161.009l-1.37.99-1.123-.892a1 1 0 00-1.275.025l-1.774 1.53a1 1 0 00-.347.757v10.01a1 1 0 001 1h10.85a1 1 0 001-1V9.086zm-3.28-4.4a.857.857 0 011.174-.031l.141.126a1 1 0 01.335.746v3.56h-2.059V5.504a1 1 0 01.306-.72l.103-.099zm-3.739 0a.955.955 0 011.272 0l.108.095a1 1 0 01.334.746v3.56H7.965v-3.56a1 1 0 01.334-.746l.107-.096zm-.441 8.244V9.087h2.155v3.844H7.965zm2.155 0v1.282H7.965V12.93h2.155zM4.193 5.543a1 1 0 01.352-.762l.151-.128a.918.918 0 011.226.033l.105.097a1 1 0 01.321.735v3.57H4.193V5.542zm0 3.544h2.155v3.844H4.193V9.087zm0 3.844h2.155v1.282H4.193V12.93zm9.602 1.282h-2.059V12.93h2.059v1.282zm0-1.282h-2.059V9.087h2.059v3.844z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFilter = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.179 2H2.829c-.855 0-.888 0-1.112.217a.745.745 0 00-.067.974l5.024 6.644v5.421a.744.744 0 00.741.744h.77c.835 0 1.636-.333 2.227-.925.591-.593.923-1.397.923-2.235V9.835L16.35 3.19a.746.746 0 00-.067-.974C16.066 2 16.033 2 15.18 2zM10 9.136a.745.745 0 00-.15.45v3.254a1.677 1.677 0 01-1.03 1.546c-.202.084-.42.127-.638.127h-.028V9.585a.745.745 0 00-.15-.45L3.742 3.488h10.525L10 9.136z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconFlag = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 13 17",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M1 8.597h10.457S9.662 5.167 9.296 4.8L11.456 1H1v14.504",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconGlobe = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  stroke: "#565656",
  strokeWidth: 1.125,
  clipPath: "url(#iconGlobe_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  d: "M16.5 9a7.501 7.501 0 11-15 0 7.501 7.501 0 0115 0z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 9c0 .985-.078 1.96-.229 2.87-.15.91-.371 1.737-.65 2.433-.278.697-.609 1.249-.973 1.626-.364.377-.754.571-1.148.571-.394 0-.784-.194-1.147-.57-.365-.378-.696-.93-.974-1.627-.279-.697-.5-1.522-.651-2.433A17.708 17.708 0 016 9c0-.985.077-1.96.228-2.87.15-.91.372-1.736.651-2.433.278-.697.609-1.249.973-1.626C8.216 1.695 8.606 1.5 9 1.5c.394 0 .784.194 1.148.57.364.378.695.93.973 1.627.279.697.5 1.522.65 2.433.152.91.229 1.885.229 2.87z"
}), /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  d: "M1.5 9h15"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconGlobe_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconGreenSproute = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 16 16",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.757 5.537c-.039-2.013-2.01-4.065-3.49-5.433a.397.397 0 00-.545.01c-1.426 1.42-3.317 3.54-3.278 5.554.07.892.5 1.722 1.198 2.316a3.915 3.915 0 002.526 1.128c1.563-.028 3.624-1.8 3.59-3.575z",
  fill: "#70D374"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.882 11.954c-1.48-1.369-3.69-3.183-5.787-3.147-1.854.033-3.618 2.075-3.59 3.575.099.918.551 1.767 1.27 2.38a3.66 3.66 0 002.456 1.065c2.098-.037 4.236-1.93 5.662-3.35a.36.36 0 00-.011-.523z",
  fill: "#4CAF50"
}), /*#__PURE__*/React.createElement("path", {
  d: "M.118 15.898a.36.36 0 01-.01-.523C5.783 9.723 5.048 6.307 4.77 5.022a2.787 2.787 0 01-.075-.459c0-.204.172-.37.385-.37.208 0 .379.157.386.357.012.109.031.216.06.323.245 1.141.824 3.83-2.265 8.036a9.572 9.572 0 018.03-.98.398.398 0 01.27.477.36.36 0 01-.457.242l-.065-.022c-4.423-1.442-8.085.977-10.377 3.26a.397.397 0 01-.544.012z",
  fill: "#388E3C"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconHeartFilled = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M7.625 1.308l.347.375.375-.375A4.546 4.546 0 0112.269.061 4.466 4.466 0 0116 4.467v.181a4.675 4.675 0 01-1.488 3.422l-5.646 5.272a1.268 1.268 0 01-1.732 0L1.487 8.07A4.68 4.68 0 010 4.648v-.18A4.466 4.466 0 013.731.06a4.492 4.492 0 013.894 1.247z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M7.625 1.308l.347.375.375-.375A4.546 4.546 0 0112.269.061 4.466 4.466 0 0116 4.467v.181a4.675 4.675 0 01-1.488 3.422l-5.646 5.272a1.268 1.268 0 01-1.732 0L1.487 8.07A4.68 4.68 0 010 4.648v-.18A4.466 4.466 0 013.731.06a4.492 4.492 0 013.894 1.247s-.028 0 0 0zm.347 2.497L6.566 2.342a3.032 3.032 0 00-2.588-.802A2.967 2.967 0 001.5 4.467v.181c0 .882.366 1.725 1.01 2.325L8 12.098l5.49-5.125a3.18 3.18 0 001.01-2.325v-.18a2.97 2.97 0 00-2.478-2.928 3.032 3.032 0 00-2.588.802L7.972 3.805z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconHeartOutlined = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M8.625 3.468l.347.375.375-.375a4.546 4.546 0 013.922-1.247A4.466 4.466 0 0117 6.627v.181a4.675 4.675 0 01-1.488 3.422l-5.646 5.272a1.268 1.268 0 01-1.732 0L2.487 10.23A4.68 4.68 0 011 6.808v-.181a4.466 4.466 0 013.731-4.406 4.492 4.492 0 013.894 1.247s-.028 0 0 0zm.347 2.497L7.566 4.502A3.032 3.032 0 004.978 3.7 2.967 2.967 0 002.5 6.627v.181c0 .882.366 1.725 1.01 2.325L9 14.258l5.49-5.125a3.18 3.18 0 001.01-2.325v-.181A2.97 2.97 0 0013.022 3.7a3.032 3.032 0 00-2.588.802L8.972 5.965z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconHelp = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M8.963 13.5c.262 0 .484-.09.666-.271a.902.902 0 00.271-.666.905.905 0 00-.271-.666.908.908 0 00-.666-.272.903.903 0 00-.666.272.909.909 0 00-.272.665c0 .263.09.485.272.667a.902.902 0 00.665.271zm-.675-2.887h1.387c0-.413.047-.738.141-.976.094-.237.36-.562.796-.975.325-.325.582-.634.77-.928.187-.294.28-.647.28-1.059 0-.7-.256-1.237-.768-1.612-.513-.375-1.119-.563-1.819-.563-.712 0-1.29.188-1.735.563a2.97 2.97 0 00-.928 1.35L7.65 6.9c.063-.225.203-.469.422-.731.22-.263.554-.394 1.003-.394.4 0 .7.11.9.329.2.218.3.459.3.721 0 .25-.075.484-.225.704a3.02 3.02 0 01-.563.608c-.55.488-.887.857-1.012 1.107s-.188.706-.188 1.369zM9 16.5a7.307 7.307 0 01-2.925-.59 7.562 7.562 0 01-2.381-1.604 7.569 7.569 0 01-1.603-2.381A7.316 7.316 0 011.5 9a7.3 7.3 0 01.591-2.925 7.586 7.586 0 011.603-2.381A7.58 7.58 0 016.075 2.09 7.29 7.29 0 019 1.5a7.3 7.3 0 012.925.591 7.586 7.586 0 012.381 1.603 7.579 7.579 0 011.604 2.381A7.29 7.29 0 0116.5 9a7.3 7.3 0 01-.591 2.925 7.586 7.586 0 01-1.603 2.381 7.58 7.58 0 01-2.381 1.604A7.29 7.29 0 019 16.5zM9 15c1.675 0 3.094-.581 4.256-1.744C14.42 12.094 15 10.675 15 9c0-1.675-.581-3.094-1.744-4.256C12.094 3.58 10.675 3 9 3c-1.675 0-3.094.581-4.256 1.744C3.58 5.906 3 7.325 3 9c0 1.675.581 3.094 1.744 4.256C5.906 14.42 7.325 15 9 15z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconHouse = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M16.875 14.344h-.844v-6.22a1.411 1.411 0 00-.453-1.033L9.957 1.785l-.011-.011a1.401 1.401 0 00-1.892 0l-.012.01-5.62 5.307a1.411 1.411 0 00-.453 1.034v6.219h-.844a.844.844 0 000 1.687h15.75a.844.844 0 100-1.687zM3.656 8.246L9 3.201l5.344 5.045v6.098H11.53V11.25a1.406 1.406 0 00-1.406-1.406h-2.25a1.406 1.406 0 00-1.406 1.406v3.094H3.656V8.246zm6.188 6.098H8.156V11.53h1.688v2.813z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconHouseVisiting = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M11.51 12.488c-.426 0-.83-.188-1.106-.503H7.596c-.277.315-.68.503-1.106.503a1.476 1.476 0 01-1.384-1.979A1.476 1.476 0 016.49 8.531c.426 0 .83.187 1.106.503h2.808c.276-.316.68-.503 1.106-.503a1.476 1.476 0 011.384 1.978 1.474 1.474 0 01-1.384 1.979zm-4.152-1.442h3.284c.167 0 .322.09.406.234a.536.536 0 10.925-.536.47.47 0 010-.47.537.537 0 00-.463-.805.536.536 0 00-.462.269.468.468 0 01-.406.235H7.358a.469.469 0 01-.406-.235.536.536 0 10-.925.536.47.47 0 010 .47.537.537 0 00.463.805c.19 0 .367-.103.462-.269a.469.469 0 01.406-.234z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M16.995 13.964l-1.004-7.042a.469.469 0 00-.464-.403h-1.23c-.216-.973-1.068-1.261-1.162-1.293-.462-.926-1.41-.97-1.512-.973-.563-.609-1.32-.611-1.724-.508-.36-.214-1.087-.423-1.798 0-.404-.103-1.161-.1-1.724.508-.1.004-1.05.047-1.512.973-.095.032-.946.322-1.162 1.293h-1.23a.469.469 0 00-.464.403l-1.004 7.042a.47.47 0 00.464.536H16.53a.469.469 0 00.464-.536zM5.267 6.094a.47.47 0 00.398-.346c.094-.354.523-.606.854-.55.19.032.38-.055.48-.22.178-.296.658-.44.971-.286.173.085.38.055.523-.075.255-.234.758-.235 1.014 0 .142.13.35.16.523.075.309-.152.79-.014.971.286.1.165.29.252.48.22.337-.057.761.202.854.55.05.186.208.323.398.345a.8.8 0 01.573.426H4.694a.805.805 0 01.573-.426zM2.009 13.56l.87-6.103H15.12l.87 6.103H2.01z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconImage = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M14.444 3.556v10.888H3.556V3.556h10.888zm0-1.556H3.556C2.7 2 2 2.7 2 3.556v10.888C2 15.3 2.7 16 3.556 16h10.888C15.3 16 16 15.3 16 14.444V3.556C16 2.7 15.3 2 14.444 2zm-3.78 6.891l-2.333 3.01-1.664-2.014-2.334 3.002h9.334L10.664 8.89z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconInstagram = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9 2.443c2.136 0 2.389.008 3.233.046.78.036 1.203.166 1.485.276.348.128.663.332.921.598.266.258.47.573.599.921.11.282.24.706.275 1.485.039.844.047 1.097.047 3.233s-.008 2.389-.047 3.232c-.035.78-.166 1.204-.275 1.486a2.653 2.653 0 01-1.518 1.518c-.282.11-.706.24-1.486.275-.843.039-1.097.047-3.233.047s-2.39-.008-3.232-.047c-.78-.035-1.204-.165-1.486-.275a2.476 2.476 0 01-.921-.599 2.476 2.476 0 01-.599-.92c-.11-.282-.24-.706-.275-1.486-.038-.844-.047-1.096-.047-3.232s.009-2.39.047-3.233c.036-.78.166-1.203.275-1.485.129-.348.333-.663.599-.921.258-.266.573-.47.92-.599.283-.11.707-.24 1.487-.275.843-.038 1.096-.047 3.232-.047L9 2.443zM9.001 1c-2.172 0-2.445.01-3.298.048-.854.04-1.434.176-1.943.373-.534.201-1.018.516-1.417.923-.407.4-.72.883-.921 1.417-.2.509-.335 1.09-.374 1.941C1.01 6.554 1 6.828 1 9.001c0 2.172.01 2.445.048 3.298.04.853.174 1.433.372 1.942.201.533.516 1.017.923 1.417.4.406.883.721 1.417.922.508.198 1.09.333 1.941.372.852.04 1.126.048 3.299.048s2.445-.01 3.299-.048c.853-.039 1.432-.174 1.941-.372a4.094 4.094 0 002.34-2.34c.198-.507.333-1.09.372-1.941.04-.851.048-1.126.048-3.298 0-2.173-.01-2.445-.048-3.299-.039-.853-.174-1.432-.372-1.941a3.928 3.928 0 00-.923-1.417c-.4-.406-.883-.72-1.417-.921-.509-.2-1.09-.335-1.941-.373C11.447 1.01 11.173 1 9 1L9.001 1z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M13.128 8.996a4.112 4.112 0 11-8.225 0 4.112 4.112 0 018.225 0zm-1.445 0a2.667 2.667 0 11-5.335 0 2.667 2.667 0 015.335 0z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.245 4.723a.96.96 0 11-1.92 0 .96.96 0 011.92 0z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconLightbulb = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 13 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M5.361 12.19h.783V8.38h1.524v3.81h.782c.1-.916.568-1.671 1.326-2.497.086-.093.634-.66.699-.741a4.573 4.573 0 10-7.14-.001c.066.081.615.65.7.742.759.826 1.226 1.581 1.326 2.497zm.02 1.524v.762H8.43v-.762H5.382zm-3.234-3.81a6.096 6.096 0 119.516.002c-.472.588-1.71 1.522-1.71 2.665v1.905A1.524 1.524 0 018.43 16H5.382a1.524 1.524 0 01-1.524-1.524v-1.905c0-1.143-1.238-2.077-1.711-2.667z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconLocationFilled = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.995 1C5.58 1 2.49 3.618 2.49 7.668c0 2.585 1.992 5.626 5.968 9.13a.827.827 0 001.082 0c3.968-3.504 5.96-6.545 5.96-9.13C15.5 3.618 12.41 1 8.995 1zm0 8.131a1.631 1.631 0 01-1.626-1.626c0-.895.731-1.626 1.626-1.626.894 0 1.626.731 1.626 1.626 0 .894-.732 1.626-1.626 1.626z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconLocationOutlined = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.995 9.131a1.631 1.631 0 01-1.626-1.626c0-.895.731-1.626 1.626-1.626.894 0 1.626.731 1.626 1.626 0 .894-.732 1.626-1.626 1.626z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M8.995 1C5.58 1 2.49 3.618 2.49 7.668c0 2.585 1.992 5.626 5.968 9.13a.827.827 0 001.082 0c3.968-3.504 5.96-6.545 5.96-9.13C15.5 3.618 12.41 1 8.995 1zm-.001 1.6C6.419 2.6 4.09 4.63 4.09 7.767c0 2.004 1.502 4.361 4.5 7.077a.61.61 0 00.815 0c2.991-2.716 4.493-5.073 4.493-7.077 0-3.138-2.33-5.167-4.904-5.167z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconLogin = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  fill: "#565656",
  clipPath: "url(#iconLogin_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  d: "M6.75 1.5h6.75c.825 0 1.5.675 1.5 1.5v12c0 .825-.675 1.5-1.5 1.5H6.75c-.825 0-1.5-.675-1.5-1.5v-1.5h1.5V15h6.75V3H6.75v1.5h-1.5V3c0-.825.675-1.5 1.5-1.5z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M7.567 11.693l1.058 1.057L12.375 9l-3.75-3.75-1.058 1.058L9.502 8.25H2.25v1.5h7.252l-1.935 1.943z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconLogin_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconLogout = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M14.25 3h-1.5a.75.75 0 100 1.5h.75v9h-.75a.75.75 0 100 1.5h1.5a.75.75 0 00.75-.75V3.75a.75.75 0 00-.75-.75zm-5.4 2.55a.75.75 0 00-1.2.9L9 8.25H3a.75.75 0 000 1.5h6.068l-1.29 1.83A.75.75 0 109 12.45l2.115-3a.75.75 0 000-.885L8.85 5.55z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconMenu = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M1.889 14.5H16.11c.489 0 .889-.412.889-.917 0-.504-.4-.916-.889-.916H1.89c-.489 0-.889.412-.889.916 0 .505.4.917.889.917zm0-4.583H16.11C16.6 9.917 17 9.504 17 9s-.4-.917-.889-.917H1.89C1.4 8.083 1 8.496 1 9s.4.917.889.917zM1 4.417c0 .504.4.916.889.916H16.11c.489 0 .889-.412.889-.916s-.4-.917-.889-.917H1.89C1.4 3.5 1 3.913 1 4.417z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconMessage = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M12.75 5.25h-7.5a.75.75 0 000 1.5h7.5a.75.75 0 100-1.5zm0 3h-7.5a.75.75 0 000 1.5h7.5a.75.75 0 100-1.5zm1.5-6.75H3.75A2.25 2.25 0 001.5 3.75v7.5a2.25 2.25 0 002.25 2.25h8.693l2.774 2.782a.75.75 0 001.155-.117.75.75 0 00.128-.415v-12a2.25 2.25 0 00-2.25-2.25zM15 13.943l-1.717-1.726A.75.75 0 0012.75 12h-9a.75.75 0 01-.75-.75v-7.5A.75.75 0 013.75 3h10.5a.75.75 0 01.75.75v10.193z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconMinus = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.543 7.502h-5.037c-2.006 0-1.006.001-3.012-.002v.002H2.456c-1.941 0-1.941 3.013 0 3.013h5.038c1.506 0 1.006 0 3.006-.007l.006.007h5.037c1.942 0 1.942-3.013 0-3.013z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconMoney = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M8.015 3.333c.326 0 .59.261.59.584v.65l.086.013.02.004.884.16c.32.059.533.363.474.678a.59.59 0 01-.686.469l-.875-.159c-.576-.084-1.085-.027-1.442.113s-.501.334-.534.513c-.037.195-.01.304.022.371a.625.625 0 00.236.241c.3.195.76.323 1.357.48l.054.014c.527.139 1.171.306 1.65.616.262.17.509.4.662.72.156.327.19.691.117 1.08-.127.692-.61 1.155-1.208 1.398a3.103 3.103 0 01-.818.2v.605a.586.586 0 01-.59.584.586.586 0 01-.589-.584v-.636c-.007-.002-.016-.002-.024-.003h-.003a7.851 7.851 0 01-1.686-.48.58.58 0 01-.298-.77.592.592 0 01.777-.295c.385.17 1.019.338 1.385.394.588.086 1.072.037 1.4-.096.312-.126.453-.308.494-.527.035-.193.007-.305-.024-.372a.63.63 0 00-.24-.243c-.302-.195-.764-.322-1.363-.479l-.051-.013c-.525-.138-1.17-.308-1.649-.618a1.78 1.78 0 01-.66-.722c-.154-.326-.185-.69-.112-1.079.133-.696.66-1.148 1.258-1.383.246-.097.514-.163.796-.2v-.644c0-.323.264-.584.59-.584z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  fillRule: "evenodd",
  d: "M8 14.667A6.667 6.667 0 108 1.333a6.667 6.667 0 000 13.334zM8 16A8 8 0 108 0a8 8 0 000 16z",
  clipRule: "evenodd"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconMoon = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 15 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M14.806 13.168c-3.27 3.858-9.262 3.733-12.373-.124C-1.46 8.208 1.277 1.097 7.215.013c.605-.107 1.12.48.907 1.066-.818 2.187-.694 4.694.569 6.862a8.019 8.019 0 005.653 3.912c.604.088.871.835.462 1.315z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconNoHouse = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M16.747 4.966L9.215 1.053a.467.467 0 00-.43 0L1.253 4.966a.472.472 0 00-.2.635l.856 1.663a.468.468 0 00.573.227v8.038c0 .26.21.471.468.471h12.1a.47.47 0 00.468-.47V7.491c.223.08.466-.02.573-.228l.857-1.663a.472.472 0 00-.201-.635zM3.419 15.058V7.01L9 4.11l5.58 2.9v8.048H3.42zm12.055-8.645L9.215 3.161a.467.467 0 00-.43 0L2.526 6.413l-.425-.826L9 2l6.9 3.586-.426.826z"
}), /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeWidth: 1.1,
  d: "M16.401 1.615L1.559 16.457"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconNotifications = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M16.5 15h-15v-1.5h.75V8.273C2.25 4.533 5.272 1.5 9 1.5c3.727 0 6.75 3.032 6.75 6.773V13.5h.75V15zM3.75 13.5h10.5V8.273C14.25 5.361 11.9 3 9 3S3.75 5.361 3.75 8.273V13.5zm3.375 2.25h3.75a1.875 1.875 0 11-3.75 0z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconOpenInNew = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M13.667 14.444H4.333a.78.78 0 01-.777-.777V4.333a.78.78 0 01.777-.777h3.89A.78.78 0 009 2.778.78.78 0 008.222 2H3.556C2.692 2 2 2.7 2 3.556v10.888C2 15.3 2.7 16 3.556 16h10.888C15.3 16 16 15.3 16 14.444V9.778A.78.78 0 0015.222 9a.78.78 0 00-.778.778v3.889a.78.78 0 01-.777.777zM10.556 2.778c0 .428.35.778.777.778h2.015l-7.101 7.1a.774.774 0 101.096 1.097l7.101-7.1v2.014c0 .427.35.777.778.777A.78.78 0 0016 6.667V2h-4.667a.78.78 0 00-.777.778z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPassword = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M9 12.75a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM13.5 15V7.5h-9V15h9zm0-9A1.5 1.5 0 0115 7.5V15a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 013 15V7.5C3 6.668 3.667 6 4.5 6h.75V4.5a3.75 3.75 0 017.5 0V6h.75zM9 2.25A2.25 2.25 0 006.75 4.5V6h4.5V4.5A2.25 2.25 0 009 2.25z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPaw = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M7.991 3.472c-.17-.138-.453-.295-1.05-.123-.54.155-.748.491-.854.85-.056.192-.08.385-.097.563l-.006.063a5.508 5.508 0 01-.02.196c-.004.04-.013.097-.029.158-.01.04-.047.179-.16.305a.72.72 0 01-.55.239c-.114-.001-.216-.027-.261-.038a5.988 5.988 0 01-.098-.026c-.068-.018-.117-.03-.188-.041-.148-.022-.384-.026-.737.201-.322.208-.454.47-.5.768-.049.324.009.7.114 1.07.044.154.144.397.292.726.057.128.126.28.2.44.093.206.194.425.282.625.167.376.333.772.459 1.142.12.356.225.747.225 1.095v3.678a.637.637 0 11-1.274 0v-3.678c0-.13-.046-.357-.157-.686-.107-.315-.255-.669-.417-1.035-.094-.212-.182-.403-.268-.59a57.193 57.193 0 01-.213-.47 7.442 7.442 0 01-.353-.897c-.128-.445-.238-1.025-.149-1.611a2.314 2.314 0 011.069-1.647c.583-.376 1.096-.444 1.506-.404.022-.153.056-.326.109-.507.192-.652.654-1.407 1.723-1.713 1.01-.29 1.722-.034 2.205.358.076.062.145.126.207.188.048-.047.102-.095.16-.141.457-.37 1.13-.623 2.113-.417 1.092.23 1.575.995 1.794 1.642.08.24.13.472.163.656.37-.037.855-.005 1.436.316.665.367.997.986 1.116 1.594a3.337 3.337 0 01-.14 1.727c-.09.252-.264.642-.45 1.062-.125.282-.256.577-.373.855A12.74 12.74 0 0014.427 11c-.104.328-.147.556-.147.684v3.678a.637.637 0 01-1.274 0v-3.678c0-.333.095-.716.207-1.07.117-.368.274-.764.433-1.144.147-.35.291-.673.418-.958.165-.37.301-.676.38-.897.113-.312.157-.705.089-1.05-.066-.335-.223-.582-.481-.724-.327-.18-.549-.18-.709-.162a2.45 2.45 0 00-.165.025c-.044.008-.106.019-.162.026h-.001c-.1.012-.435.052-.71-.2a.777.777 0 01-.19-.278 1.224 1.224 0 01-.057-.179 4.252 4.252 0 01-.053-.276l-.003-.02a3.93 3.93 0 00-.14-.615c-.132-.389-.35-.698-.85-.803-.609-.128-.892.032-1.052.161-.098.08-.172.167-.264.275l-.01.01c-.038.045-.102.12-.171.183a.782.782 0 01-.522.213.783.783 0 01-.533-.227l-.002-.001c-.067-.064-.13-.14-.171-.19l-.006-.006a2.105 2.105 0 00-.29-.306zm4.855.995h.017a.07.07 0 01-.017 0zm-7.552-.013h-.002a.07.07 0 01.002 0z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.992 6.868c.83.433.38 1.406.168 1.728-.213.321-.724.861-1.361.487s-.415-1.093-.312-1.346c.102-.253.674-1.302 1.505-.869zM10.581 4.8c.915.203.732 1.26.61 1.625-.122.366-.487.978-1.189.823-.701-.155-.688-.91-.65-1.219.039-.309.315-1.432 1.23-1.229zM7.186 4.815c.893-.28 1.263.725 1.34 1.103.078.378.066 1.159-.617 1.307-.683.148-1.075-.499-1.173-.73-.098-.233-.443-1.399.45-1.68zM4.718 6.94c.772-.53 1.42.324 1.604.663.185.338.403 1.065-.209 1.43-.611.365-1.2-.195-1.334-.356-.135-.161-.833-1.207-.06-1.737zM8.958 7.856c1.179 0 1.672 1.28 1.97 1.625.298.345.805 1.266.805 1.726 0 .46-.518 1.107-1.093 1.107s-1.25-.086-1.682-.086c-.432 0-1.294.086-1.769.086-.474 0-1.093-.474-1.093-1.107s.374-1.194.777-1.726c.402-.532.906-1.625 2.085-1.625z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPawPrint = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.999 7.365c1.05 0 2.05.62 2.662 1.755l.007.012 1.055 2.291c.5 1.086.344 2.16-.252 2.856-.602.704-1.603.954-2.59.472-.336-.164-.618-.234-.883-.234-.264 0-.546.07-.881.234-.988.482-1.988.232-2.591-.472-.596-.696-.752-1.77-.252-2.856l1.055-2.29.007-.013c.611-1.135 1.612-1.755 2.663-1.755zM6.864 6.849c.986-.052 1.296-1.068 1.24-2.13-.055-1.062-.456-1.768-1.442-1.716-.986.051-1.74.954-1.685 2.016.056 1.062.9 1.881 1.887 1.83zM3.732 10.137c.708-.218 1.046-1.16.756-2.104-.29-.944-1.1-1.533-1.808-1.315-.708.218-1.046 1.16-.756 2.104.29.944 1.1 1.533 1.808 1.315zM15.317 6.718c-.708-.218-1.518.371-1.808 1.315-.29.944.048 1.886.756 2.104.708.218 1.518-.37 1.808-1.315.29-.944-.048-1.886-.756-2.104zM11.13 6.848c.986.054 1.832-.764 1.89-1.826.057-1.061-.695-1.966-1.682-2.02-.986-.053-1.388.655-1.445 1.717-.058 1.062.25 2.076 1.237 2.13z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPencil = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 14 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M1.556 12.444h1.108l7.603-7.602-1.109-1.109-7.602 7.603v1.108zM0 14v-3.306L10.267.447c.155-.142.327-.253.515-.33.188-.078.386-.117.593-.117.207 0 .408.039.603.117.194.077.363.194.505.35l1.07 1.089c.155.142.269.31.34.505a1.684 1.684 0 010 1.176c-.071.188-.185.36-.34.516L3.306 14H0zm9.703-9.703l-.545-.564 1.109 1.109-.564-.545z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPetDayCare = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M16.747 4.966L9.215 1.053a.467.467 0 00-.43 0L1.253 4.966a.472.472 0 00-.2.635l.856 1.663a.468.468 0 00.573.227v8.038c0 .26.21.471.468.471h12.1a.47.47 0 00.468-.47V7.491c.223.08.466-.02.573-.228l.857-1.663a.472.472 0 00-.201-.635zM3.419 15.058V7.01L9 4.11l5.58 2.9v8.048H3.42zm12.055-8.645L9.215 3.161a.467.467 0 00-.43 0L2.526 6.413l-.425-.826L9 2l6.9 3.586-.426.826z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9 9.801c.515 0 1.006.299 1.306.846l.003.006.517 1.105c.246.524.169 1.041-.123 1.377-.296.34-.786.46-1.27.228A.987.987 0 009 13.25a.988.988 0 00-.432.113c-.485.232-.975.112-1.27-.228-.293-.336-.37-.853-.124-1.377l.517-1.105.003-.006c.3-.547.79-.846 1.306-.846zM7.953 9.552c.484-.025.636-.515.609-1.027-.028-.512-.224-.852-.708-.827-.483.025-.853.46-.826.972.027.512.442.907.925.882zM6.418 11.138c.347-.105.513-.56.37-1.015-.142-.455-.54-.739-.886-.634-.347.105-.513.56-.371 1.015.142.455.54.739.887.634zM12.098 9.49c-.347-.106-.744.178-.886.633-.143.456.023.91.37 1.015.348.105.744-.18.887-.634.142-.455-.024-.91-.37-1.015zM10.045 9.552c.484.026.899-.368.927-.88.028-.513-.341-.949-.825-.974-.483-.026-.68.315-.709.827-.028.512.123 1.001.607 1.027z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPetHosting = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9.379 9.29c.36.23.569.663.53 1.187v.005l-.14 1.018c-.066.482-.355.816-.713.925-.361.109-.76-.024-.994-.407a.81.81 0 00-.252-.273.812.812 0 00-.354-.113c-.445-.051-.734-.357-.787-.73-.052-.37.13-.773.54-1.036l.865-.555.005-.003c.459-.255.939-.248 1.3-.018zM8.758 8.645c.35.198.68-.082.894-.459.213-.377.23-.707-.12-.905-.35-.199-.807-.055-1.021.322-.214.377-.103.843.247 1.042zM6.96 9.086c.292.081.615-.168.722-.556.107-.387-.042-.767-.333-.847-.291-.081-.614.168-.721.556-.108.387.042.767.333.847zM11.692 10.454c-.195-.23-.603-.206-.91.055-.306.26-.396.657-.2.887.195.23.602.206.909-.054.307-.26.397-.658.201-.888zM10.225 9.58c.327.235.797.14 1.05-.212.252-.352.192-.827-.135-1.062-.328-.234-.621-.08-.874.272-.252.352-.369.768-.041 1.002z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M14.6 5.05H3.4c-.82 0-1.486.665-1.486 1.486v6.628c0 .82.665 1.486 1.486 1.486h11.2c.82 0 1.486-.665 1.486-1.486V6.536c0-.82-.665-1.486-1.486-1.486zM3.4 4.136a2.4 2.4 0 00-2.4 2.4v6.628a2.4 2.4 0 002.4 2.4h11.2a2.4 2.4 0 002.4-2.4V6.536a2.4 2.4 0 00-2.4-2.4H3.4z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M10.029 2.764H7.97a.914.914 0 00-.914.915v.457h3.886v-.457a.914.914 0 00-.914-.915zM7.97 1.85c-1.01 0-1.828.819-1.828 1.829V5.05h5.714V3.679c0-1.01-.818-1.829-1.828-1.829H7.97zM4.006 15.45V4.25h1.142v11.2H4.006zM12.886 15.45V4.25h1.143v11.2h-1.143z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.057 15.507c0 .347-.332.629-.743.629-.41 0-.743-.282-.743-.629 0-.347.333-.628.743-.628s.743.281.743.628zM4.429 15.507c0 .347-.333.629-.743.629s-.743-.282-.743-.629c0-.347.332-.628.743-.628.41 0 .743.281.743.628z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPetSitting = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9.924 11.877a.253.253 0 01-.243.044.303.303 0 01-.207-.3v-.172c.14-.038.276-.093.401-.165.212-.123.26-.36.107-.53-.154-.17-.45-.208-.662-.085a.66.66 0 01-.64 0c-.212-.123-.508-.085-.662.085-.153.17-.105.407.107.53.125.072.26.127.401.165v.172a.303.303 0 01-.207.3c-.105.035-.183.002-.258-.055a.56.56 0 00-.67.013c-.18.152-.173.392.017.536l.014.01c.23.176.52.268.816.268.274 0 .544-.079.762-.226.217.147.487.226.762.226.295 0 .586-.092.816-.267l.014-.01c.19-.145.197-.386.016-.537a.56.56 0 00-.684-.002z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.132 7.602a1.102 1.102 0 00-1.453-.363l-.459.24a2.505 2.505 0 00-2.44 0l-.46-.24a1.102 1.102 0 00-1.452.363L4.724 9.317c-.38.571-.313 1.343.16 1.837.272.283.633.439 1.02.44.107 1.19.685 1.792 1.153 2.087.545.344 1.13.404 1.43.41h1.026c.3-.006.886-.066 1.43-.41.468-.295 1.046-.897 1.154-2.087a1.41 1.41 0 001.019-.44 1.482 1.482 0 00.16-1.837l-1.144-1.715zM5.425 9.8l1.144-1.715a.267.267 0 01.223-.121c.083 0 .092.015.472.212l-.783 2.117a.585.585 0 01-.99.26.607.607 0 01-.066-.753zm4.076 3.43H8.5c-1.021-.02-1.7-.606-1.765-1.898.254-.18.446-.44.543-.747l.86-2.325a1.682 1.682 0 011.726 0l.86 2.325c.097.307.29.568.543.747-.064 1.283-.736 1.878-1.765 1.899zm3.008-2.677a.584.584 0 01-.99-.26l-.783-2.117.343-.178a.266.266 0 01.352.087L12.575 9.8a.607.607 0 01-.066.753z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M16.747 4.966L9.215 1.053a.467.467 0 00-.43 0L1.253 4.966a.472.472 0 00-.2.635l.856 1.663a.468.468 0 00.573.227v8.038c0 .26.21.471.468.471h12.1a.47.47 0 00.468-.47V7.491c.223.08.466-.02.573-.228l.857-1.663a.472.472 0 00-.201-.635zM3.419 15.058V7.01L9 4.11l5.58 2.9v8.048H3.42zm12.055-8.645L9.215 3.161a.467.467 0 00-.43 0L2.526 6.413l-.425-.826L9 2l6.9 3.586-.426.826z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPlane = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconPlane_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M16.423 1.158c.183 0 .312.034.38.06.08.215.189.985-.607 1.782l-4.06 4.06.029.504c.061 1.054.175 2.943.274 4.562.087 1.442.17 2.803.174 2.919.003.08.004.098-.07.188-.166.201-.475.543-.736.826a913.952 913.952 0 01-1.725-4.683l-.604-1.658-1.248 1.248-2.296 2.208-.32.319-.01.451a94.71 94.71 0 00-.005 1.577c.001.397.002.758 0 .957-.006.01.076.02.069.034l-.158-.252c-.462-.738-1.103-1.76-1.299-2.078l-.142-.233-.235-.142c-.853-.515-1.798-1.11-2.375-1.482.02-.011.037.05.05.043h.037c.182 0 .481.004.818.009.44.005.938.013 1.32.013.146 0 .276-.001.38-.004l.452-.01.319-.32 2.21-2.28L8.29 8.534l-1.652-.607a974.911 974.911 0 01-4.73-1.762 25.7 25.7 0 01.83-.74c.056-.047.1-.07.13-.07h.04c.124.007 1.664.107 3.153.205 1.581.103 3.365.22 4.38.284l.507.031 4.06-4.06c.571-.57 1.131-.656 1.415-.656zm0-1.125c-.665 0-1.48.256-2.21.985l-3.7 3.702c-2.085-.132-7.426-.486-7.558-.49a1.778 1.778 0 00-.086-.001c-.18 0-.492.034-.846.327a38.85 38.85 0 00-1.284 1.16c-.21.21-.316.47-.29.714.015.147.095.416.458.558.22.087 3.725 1.398 5.343 1.993l-2.21 2.28c-.098.003-.22.004-.357.004-.673 0-1.718-.022-2.137-.022h-.131c-.128.005-.392-.058-1.087.524l-.05.046a.816.816 0 00-.262.554.61.61 0 00.238.51c.124.1 1.703 1.11 2.998 1.893.31.507 1.74 2.777 1.814 2.9a.585.585 0 00.5.296h.017c.207 0 .42-.102.628-.305.589-.688.508-.974.51-1.093.01-.376-.006-1.968.007-2.6l2.296-2.207c.587 1.612 1.869 5.08 1.955 5.301.143.362.412.442.56.457a.64.64 0 00.073.004.92.92 0 00.64-.293s.813-.863 1.159-1.283c.345-.42.332-.777.325-.946-.003-.117-.328-5.429-.45-7.503l3.704-3.703C18.302 2.482 18.093.9 17.606.413c-.215-.216-.65-.38-1.183-.38z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconPlane_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPlay = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M14.25 7.7c1 .577 1 2.02 0 2.597l-9 5.197a1.5 1.5 0 01-2.25-1.3V3.803a1.5 1.5 0 012.25-1.299l9 5.196z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPlus = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M15.543 7.494h-5.037V2.457c0-1.943-3.012-1.943-3.012 0v5.037H2.456c-1.941 0-1.941 3.012 0 3.012h5.038v5.037c0 1.943 3.012 1.943 3.012 0v-5.037h5.037c1.942 0 1.942-3.012 0-3.012z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconPresent = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconPresent_svg__clip0_6842_2274)"
}, /*#__PURE__*/React.createElement("path", {
  d: "M16.5 9v6a1.5 1.5 0 01-1.5 1.5H3A1.5 1.5 0 011.5 15V9a.75.75 0 01-.75-.75V6a1.5 1.5 0 011.5-1.5h2.378a2.25 2.25 0 01.531-2.341A2.25 2.25 0 016.75 1.5c.75 0 1.41.375 1.822.93v-.008L9 3l.428-.578v.008a2.275 2.275 0 011.822-.93 2.25 2.25 0 012.123 3h2.377a1.5 1.5 0 011.5 1.5v2.25a.75.75 0 01-.75.75zM3 15h5.25V9H3v6zm12 0V9H9.75v6H15zM6.75 3a.75.75 0 100 1.5.75.75 0 000-1.5zm4.5 0a.75.75 0 100 1.5.75.75 0 000-1.5zm-9 3v1.5h6V6h-6zm7.5 0v1.5h6V6h-6z",
  fill: "currentColor"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconPresent_svg__clip0_6842_2274"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconProfile = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M11.25 6.75h2.25M11.25 9h2.25m-2.25 2.25h2.25M9 12c-.225-.45-.75-.75-1.2-.75H5.7c-.525 0-.975.3-1.2.75M3 3.75h12c.45 0 .75.3.75.75v9c0 .45-.3.75-.75.75H3a.75.75 0 01-.75-.75v-9c0-.45.3-.75.75-.75zM8.25 7.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z",
  stroke: "#565656",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconQuestion = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 14 14",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("circle", {
  cx: 7,
  cy: 7,
  r: 7,
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M5.469 5.198c0-.664.55-1.204 1.225-1.204h.612c.676 0 1.225.54 1.225 1.204v.067c0 .41-.212.792-.562 1.012l-.808.509a1.647 1.647 0 00-.773 1.391v.028c0 .333.273.602.612.602a.606.606 0 00.613-.602V8.18c0-.154.08-.297.21-.38l.808-.51a2.399 2.399 0 001.125-2.024v-.067c0-1.33-1.096-2.407-2.45-2.407h-.612c-1.353 0-2.45 1.077-2.45 2.407 0 .332.273.601.612.601a.606.606 0 00.613-.601zM7 11.213c.203 0 .398-.08.541-.22a.745.745 0 000-1.064.773.773 0 00-1.082 0 .745.745 0 000 1.064c.143.14.338.22.541.22z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconQuestionMark = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("circle", {
  cx: 9,
  cy: 9,
  r: 8,
  fill: "#E9F2F2"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#227979",
  d: "M7.25 6.94c0-.758.628-1.375 1.4-1.375h.7c.772 0 1.4.617 1.4 1.375v.077c0 .469-.243.905-.643 1.156l-.923.582a1.882 1.882 0 00-.884 1.59v.032c0 .38.313.688.7.688.387 0 .7-.307.7-.688v-.03c0-.176.092-.34.24-.434l.924-.582a2.741 2.741 0 001.286-2.314V6.94c0-1.519-1.254-2.75-2.8-2.75h-.7c-1.547 0-2.8 1.231-2.8 2.75 0 .38.313.687.7.687.387 0 .7-.307.7-.687zM9 13.815a.88.88 0 00.619-.252.851.851 0 000-1.215.883.883 0 00-1.238 0 .852.852 0 000 1.215.883.883 0 00.619.252z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconRepeat = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9.001 14c-1.38 0-2.63-.56-3.54-1.46l1.69-1.691a.5.5 0 00-.36-.85h-4.29c-.281 0-.501.22-.501.5v4.29c0 .45.54.67.85.35l1.2-1.2A6.945 6.945 0 009.001 16c3.041 0 5.622-1.94 6.582-4.641.24-.66-.23-1.36-.93-1.36a.98.98 0 00-.93.66A5.008 5.008 0 019 13.999zM9 4c1.381 0 2.631.56 3.541 1.46l-1.69 1.691a.5.5 0 00.36.85h4.291c.28 0 .5-.22.5-.5v-4.29c0-.45-.54-.67-.85-.35l-1.2 1.2A6.945 6.945 0 009 2a6.985 6.985 0 00-6.582 4.641c-.24.66.23 1.36.93 1.36.42 0 .79-.26.93-.66a5.008 5.008 0 014.722-3.34z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconRepeatClient = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M8.794 1.002a7.95 7.95 0 00-5.865 2.809L1.755 2.647a.444.444 0 00-.755.32v3.812a.44.44 0 00.444.445h3.813c.4 0 .595-.48.311-.756L4.19 5.082C5.337 3.686 7.061 2.78 8.998 2.78a6.232 6.232 0 016.097 7.474c-.48 2.462-2.497 4.426-4.959 4.862a6.218 6.218 0 01-7.154-4.586.896.896 0 00-.853-.64c-.578 0-1.013.542-.871 1.093C2.138 14.44 5.266 17 8.998 17a8.001 8.001 0 007.999-8.23C16.872 4.433 13.13.896 8.794 1.002z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9 9a1.714 1.714 0 100-3.427A1.714 1.714 0 009 9zm0 .857c-1.145 0-3.429.574-3.429 1.714V12a.43.43 0 00.429.429h6a.43.43 0 00.428-.429v-.429c0-1.14-2.284-1.714-3.428-1.714z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSantasHat = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 24 24",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M18.352 19.89H2.619A1.619 1.619 0 011 18.27v-.682c0-.893.725-1.618 1.619-1.618h15.733c.894 0 1.618.724 1.618 1.618v.682c0 .894-.724 1.619-1.618 1.619z",
  fill: "#E6F4FF"
}), /*#__PURE__*/React.createElement("path", {
  d: "M1 18.242v.029c0 .894.725 1.618 1.619 1.618h15.733c.894 0 1.618-.724 1.618-1.618v-.029H1z",
  fill: "url(#iconSantasHat_svg__paint0_linear_3457:23551)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.129 4.661c2.67 1.062 4.858 3.217 5.784 6.068h-3.748l.437 1.347c.412 1.27.705 2.573.875 3.893H2.482c.17-1.32.463-2.623.876-3.893l.437-1.347A9.858 9.858 0 019.555 4.6a7.399 7.399 0 014.343-.32 8.686 8.686 0 011.23.381z",
  fill: "#FD655F"
}), /*#__PURE__*/React.createElement("path", {
  d: "M17.164 10.729l-.263-.793a1.52 1.52 0 011.447-1.99h.315a1.511 1.511 0 011.29.715c.395.637.72 1.329.96 2.068h-3.75z",
  fill: "url(#iconSantasHat_svg__paint1_linear_3457:23551)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M23 10.729a2.087 2.087 0 11-4.174 0 2.087 2.087 0 014.174 0z",
  fill: "url(#iconSantasHat_svg__paint2_radial_3457:23551)"
}), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "iconSantasHat_svg__paint0_linear_3457:23551",
  x1: 10.485,
  y1: 18.555,
  x2: 10.485,
  y2: 20.756,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  offset: 0.003,
  stopColor: "#C2E0FF",
  stopOpacity: 0
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.076,
  stopColor: "#BFDCFD",
  stopOpacity: 0.075
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#9EB2E7"
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "iconSantasHat_svg__paint1_linear_3457:23551",
  x1: 18.933,
  y1: 9.545,
  x2: 16.351,
  y2: 10.511,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#F43B36",
  stopOpacity: 0
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.436,
  stopColor: "#D02021",
  stopOpacity: 0.435
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#A60108"
})), /*#__PURE__*/React.createElement("radialGradient", {
  id: "iconSantasHat_svg__paint2_radial_3457:23551",
  cx: 0,
  cy: 0,
  r: 1,
  gradientUnits: "userSpaceOnUse",
  gradientTransform: "translate(19.394 8.755) scale(3.77865)"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#ECF7FF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.535,
  stopColor: "#EAF6FF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.728,
  stopColor: "#E3F2FF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.865,
  stopColor: "#D8ECFF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.976,
  stopColor: "#C7E3FF"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#C2E0FF"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSavings = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M11.4 7.8c0-.44.36-.8.8-.8.44 0 .8.36.8.8 0 .44-.36.8-.8.8-.44 0-.8-.36-.8-.8zM5.8 7h4V5.4h-4V7zM17 5.8v5.576l-2.256.752L13.4 16.6H9V15H7.4v1.6H3S1 9.832 1 7.4C1 4.968 2.968 3 5.4 3h4a3.996 3.996 0 013.2-1.6 1.198 1.198 0 011.104 1.664 3.867 3.867 0 00-.256.92L15.264 5.8H17zm-1.6 1.6h-.8l-2.8-2.8c0-.52.072-1.032.208-1.528-.776.2-1.408.776-1.672 1.528H5.4a2.803 2.803 0 00-2.8 2.8c0 1.504.976 5.32 1.608 7.6H5.8v-1.6h4.8V15h1.608l1.24-4.12 1.952-.656V7.4z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSearch = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M13.732 12.318h-.741l-.263-.253a6.095 6.095 0 001.389-5.009c-.441-2.607-2.619-4.69-5.246-5.009a6.103 6.103 0 00-6.823 6.82c.319 2.626 2.402 4.802 5.011 5.243a6.103 6.103 0 005.012-1.388l.253.262v.741l3.989 3.987a.992.992 0 001.398 0 .99.99 0 000-1.398l-3.979-3.996zm-5.631 0a4.216 4.216 0 01-4.223-4.22A4.216 4.216 0 018.1 3.876a4.216 4.216 0 014.223 4.22A4.216 4.216 0 018.1 12.317z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSearchRounded = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M8.744 2.839a4.555 4.555 0 100 9.11 4.555 4.555 0 000-9.11zM3 7.394a5.744 5.744 0 119.306 4.507l2.578 3.501a.595.595 0 01-.957.705l-2.585-3.51a.596.596 0 01-.038-.059A5.744 5.744 0 013 7.394zm7.046-3.091a.595.595 0 01.814-.212l-.301.513.301-.513.001.001.003.001.005.004a2.548 2.548 0 01.075.047 4.75 4.75 0 01.76.634c.423.435.912 1.11 1.046 2.004a.595.595 0 11-1.177.176c-.083-.56-.396-1.017-.721-1.352a3.56 3.56 0 00-.59-.487l-.006-.003h.002-.002a.595.595 0 01-.21-.813z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSettings = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconSettings_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  fillRule: "evenodd",
  d: "M10.809 1.72c.726.18 1.422.468 2.062.855a.75.75 0 01.353.764c-.085.517.044.912.285 1.154.241.241.637.37 1.153.285a.75.75 0 01.765.352c.387.64.675 1.336.855 2.063a.75.75 0 01-.29.79c-.427.305-.616.675-.616 1.017 0 .342.19.713.615 1.018a.75.75 0 01.291.79 7.458 7.458 0 01-.855 2.062.75.75 0 01-.765.353c-.516-.085-.91.044-1.152.284-.242.242-.37.638-.285 1.154a.75.75 0 01-.353.765 7.46 7.46 0 01-2.064.855.75.75 0 01-.79-.291c-.304-.426-.675-.615-1.017-.615-.34 0-.712.189-1.017.615a.75.75 0 01-.79.291 7.46 7.46 0 01-2.063-.855.75.75 0 01-.353-.765c.086-.516-.043-.911-.285-1.153-.24-.241-.636-.37-1.153-.285a.75.75 0 01-.764-.352 7.46 7.46 0 01-.855-2.064.75.75 0 01.291-.79c.425-.304.615-.675.615-1.017 0-.341-.19-.712-.615-1.017a.75.75 0 01-.29-.79 7.46 7.46 0 01.854-2.063.75.75 0 01.764-.352c.517.084.912-.044 1.154-.285.241-.242.37-.638.285-1.154a.75.75 0 01.352-.764 7.46 7.46 0 012.063-.855.75.75 0 01.79.29c.306.426.675.616 1.017.616.342 0 .713-.19 1.018-.615a.75.75 0 01.79-.291zm.076 1.582c-.492.493-1.14.823-1.884.823s-1.39-.33-1.884-.823c-.279.092-.55.205-.813.337.001.698-.224 1.388-.75 1.914-.525.526-1.216.75-1.914.75a5.946 5.946 0 00-.337.813c.494.493.823 1.14.823 1.884s-.329 1.391-.823 1.884c.093.28.206.553.337.814.698-.002 1.388.223 1.914.75.526.525.751 1.216.75 1.913.26.132.533.244.813.337.493-.494 1.14-.823 1.884-.823s1.392.33 1.884.823a5.91 5.91 0 00.814-.337c-.001-.697.224-1.388.75-1.914.525-.526 1.216-.75 1.913-.75.132-.26.245-.532.338-.813-.494-.493-.824-1.14-.824-1.884s.33-1.391.824-1.884a5.931 5.931 0 00-.338-.813c-.697 0-1.387-.224-1.914-.75-.525-.526-.75-1.216-.75-1.914a5.952 5.952 0 00-.813-.337zM9.001 6a3 3 0 110 6 3 3 0 010-6zm0 1.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z",
  clipRule: "evenodd"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconSettings_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconShare = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 12 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M1.75 7a.25.25 0 00-.25.25v6.5c0 .138.112.25.25.25h8.5a.25.25 0 00.25-.25v-6.5a.25.25 0 00-.25-.25h-1a.75.75 0 010-1.5h1c.966 0 1.75.784 1.75 1.75v6.5a1.75 1.75 0 01-1.75 1.75h-8.5A1.75 1.75 0 010 13.75v-6.5C0 6.284.784 5.5 1.75 5.5h1a.75.75 0 010 1.5h-1zM5.823.677a.25.25 0 01.354 0l2.896 2.896A.25.25 0 018.896 4H6.75v5.75a.75.75 0 01-1.5 0V4H3.104a.25.25 0 01-.177-.427L5.823.677z"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconShieldCheckFilled = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M8.623 1.169a.923.923 0 01.754 0l5.572 2.493c.335.15.551.485.551.855v3.756c0 3.956-2.665 7.661-6.28 8.67a.817.817 0 01-.44 0c-3.615-1.009-6.28-4.714-6.28-8.67V4.517c0-.37.216-.705.551-.855L8.623 1.17zM7.2 8.726l1.15 1.157 2.804-3.227a.797.797 0 011.022-.156.81.81 0 01.177 1.225l-3.348 3.76a.924.924 0 01-1.348.038L6.04 9.895a.83.83 0 01-.103-1.043.817.817 0 011.264-.126z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconShieldCheckOutlined = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9 2.593l5.056 2.261v3.419c0 3.287-2.153 6.32-5.056 7.222-2.903-.902-5.056-3.935-5.056-7.222V4.854L9 2.593zm.377-1.424a.923.923 0 00-.754 0L3.05 3.662a.935.935 0 00-.551.855v3.756c0 3.956 2.665 7.661 6.28 8.67.144.04.296.04.44 0 3.615-1.009 6.28-4.714 6.28-8.67V4.517a.935.935 0 00-.551-.855L9.377 1.17z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.35 9.883L7.2 8.726a.817.817 0 00-1.262.126.83.83 0 00.102 1.043l1.617 1.628a.924.924 0 001.348-.037l3.348-3.761a.81.81 0 00-.177-1.225.797.797 0 00-1.022.156L8.35 9.883z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconShieldPaw = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M8.754 1.066a.531.531 0 01.482 0l6.94 3.533a.531.531 0 01.283.381l-.524.093.524-.092v.003l.001.007.004.022.012.081a11.49 11.49 0 01.094 1.345c.01.876-.07 2.08-.426 3.381-.717 2.625-2.56 5.63-6.976 7.152a.531.531 0 01-.347 0c-4.416-1.521-6.259-4.527-6.976-7.152a12.438 12.438 0 01-.368-4.431 7.827 7.827 0 01.052-.398v-.007l.001-.002V4.98l.524.092-.524-.093a.531.531 0 01.283-.38l6.94-3.534zM2.543 5.42l-.009.078A11.376 11.376 0 002.87 9.54c.632 2.312 2.222 4.962 6.125 6.367 3.902-1.405 5.492-4.055 6.124-6.367a11.376 11.376 0 00.327-4.12L8.995 2.136 2.543 5.42z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M11.563 9.976c-.919-2.003-3.967-2.368-5.144 0-.518 1.042-.092 2.425.844 2.647.408.097.804-.07 1.194-.357a.891.891 0 011.053-.003c.395.288.8.464 1.21.36 1.049-.267 1.328-1.589.843-2.646zM7.993 8.153c.55-.104.878-.811.733-1.58-.146-.769-.71-1.307-1.26-1.203-.55.104-.879.812-.733 1.58.145.77.71 1.308 1.26 1.203zM11.256 6.95c.146-.769-.182-1.476-.732-1.58-.55-.105-1.115.434-1.26 1.202-.146.769.182 1.477.732 1.58.55.105 1.115-.433 1.26-1.202zM6.413 9.134c.402-.172.532-.767.291-1.329-.24-.561-.762-.876-1.164-.704-.402.173-.532.768-.291 1.329.24.561.762.877 1.164.704zM12.795 8.43c.241-.562.11-1.156-.291-1.329-.402-.172-.923.143-1.164.704-.241.561-.11 1.156.291 1.329.402.172.923-.143 1.164-.704z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconShieldStar = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M10.787 9.422l.377 1.627a.5.5 0 01-.745.54l-1.422-.857-1.421.857a.5.5 0 01-.745-.541l.376-1.618-1.25-1.08a.5.5 0 01.284-.876l1.653-.142.643-1.523a.5.5 0 01.921 0l.641 1.516 1.654.142a.5.5 0 01.284.876l-1.25 1.08zm-1.79-6.73l5.011 2.227v3.365c0 3.236-2.133 6.221-5.011 7.11-2.878-.889-5.011-3.874-5.011-7.11V4.92l5.01-2.226zm.406-1.386a1 1 0 00-.812 0L3.148 3.725a1 1 0 00-.594.914v3.645c0 3.888 2.632 7.53 6.207 8.53a.878.878 0 00.472 0c3.575-1 6.207-4.642 6.207-8.53V4.64a1 1 0 00-.594-.914l-5.443-2.42z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSleep = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 14 15",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 1.3,
  d: "M1 1h5.963L1 7.261h6.261m1.392-3.13h4.174L8.653 9h4.174m-9.044 1.043H6.93l-3.147 3.479H7.26"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSnowFlake = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 512.001 512.001",
  "aria-hidden": "true",
  focusable: "false",
  height: sizeMap[size],
  width: sizeMap[size]
}, /*#__PURE__*/React.createElement("path", {
  d: "M470.373 356.728l-37.649-21.736 28.484-12.136c10.138-4.319 14.855-16.041 10.536-26.179-4.32-10.137-16.039-14.852-26.179-10.535l-56.073 23.891-69.18-39.941c.995-4.543 1.534-9.254 1.534-14.091s-.539-9.548-1.534-14.091l69.18-39.941 56.073 23.891c10.139 4.32 21.859-.398 26.179-10.535 4.319-10.138-.398-21.859-10.536-26.179l-28.484-12.136 37.649-21.736c9.543-5.509 12.813-17.713 7.303-27.257-5.51-9.544-17.713-12.816-27.257-7.303L412.77 142.45l3.732-30.738c1.328-10.939-6.463-20.885-17.403-22.213-10.942-1.332-20.885 6.463-22.213 17.403l-7.346 60.506-69.202 39.954a65.878 65.878 0 00-24.384-14.11v-79.876l48.726-36.615c8.81-6.62 10.585-19.129 3.965-27.939-6.62-8.809-19.127-10.582-27.938-3.966l-24.753 18.601V19.985c0-11.02-8.934-19.953-19.953-19.953-11.02 0-19.953 8.934-19.953 19.953v43.474l-24.754-18.601c-8.811-6.621-21.319-4.844-27.938 3.966-6.62 8.81-4.845 21.319 3.965 27.939l48.727 36.614v79.876a65.878 65.878 0 00-24.384 14.11l-69.202-39.954-7.346-60.506c-1.328-10.939-11.274-18.734-22.213-17.403-10.939 1.328-18.731 11.273-17.403 22.213l3.732 30.738-37.65-21.737c-9.544-5.513-21.747-2.238-27.257 7.303-5.51 9.544-2.24 21.747 7.303 27.257l37.649 21.736-28.484 12.136c-10.138 4.319-14.855 16.039-10.536 26.179 3.233 7.586 10.609 12.137 18.366 12.137 5.405 0 2.417.697 63.885-25.493l69.18 39.941a65.491 65.491 0 000 28.181l-69.18 39.941-56.074-23.891c-10.137-4.319-21.858.396-26.178 10.535-4.319 10.139.398 21.859 10.536 26.179l28.484 12.136-37.649 21.736c-17.644 10.186-10.299 37.237 9.996 37.237 3.385 0 6.816-.863 9.958-2.677l37.65-21.737-3.732 30.738c-1.436 11.826 7.771 22.361 19.833 22.361 9.946 0 18.554-7.428 19.783-17.551l7.346-60.506 69.202-39.954a65.874 65.874 0 0024.383 14.11v79.876l-48.727 36.614c-8.81 6.62-10.585 19.129-3.965 27.939 6.619 8.811 19.128 10.585 27.938 3.966l24.754-18.601v43.473c0 11.02 8.934 19.953 19.953 19.953 11.02 0 19.953-8.934 19.953-19.953v-43.473l24.753 18.601c8.807 6.619 21.318 4.845 27.938-3.966 6.62-8.81 4.845-21.319-3.965-27.939l-48.726-36.615v-79.876a65.878 65.878 0 0024.384-14.11l69.202 39.954 7.346 60.506c1.324 10.908 11.232 18.731 22.213 17.403 10.939-1.328 18.731-11.273 17.403-22.213l-3.732-30.738 37.649 21.736c9.546 5.513 21.749 2.237 27.257-7.303 5.512-9.542 2.242-21.745-7.301-27.254zM230.061 256c0-14.303 11.636-25.939 25.939-25.939s25.939 11.637 25.939 25.939c0 14.303-11.636 25.939-25.939 25.939-14.302.001-25.939-11.636-25.939-25.939z",
  fill: "#5bddfc"
}), /*#__PURE__*/React.createElement("g", {
  fill: "#00c3ff"
}, /*#__PURE__*/React.createElement("path", {
  d: "M275.954 72.812c0 2.703 3.085 4.246 5.248 2.625 44.475-33.339 40.914-30.712 42.135-31.484-6.802-4.291-15.816-4.212-22.629.904l-24.754 18.601v9.354zM132.017 198.234l-9.509 3.735 69.181 39.941a65.491 65.491 0 000 28.181l-69.18 39.941 11.283 4.362a3.572 3.572 0 003.077-.24l71.504-41.403a7.583 7.583 0 003.712-7.631 65.31 65.31 0 01.077-18.772c.408-2.737-.925-5.439-3.325-6.819l-71.056-40.868a6.668 6.668 0 00-5.764-.427zM252.366 74.274c2.128 1.411 4.971-.116 4.971-2.669v-51.62c0-7.102 3.721-13.32 9.309-16.857-13.514-8.552-30.598 1.487-30.598 17.344v42.987l16.318 10.815zM282.678 437.512c-2.766-2.084-6.724-.111-6.724 3.352v7.678l24.754 18.601c6.79 5.103 15.803 5.231 22.632.926-1.112-.703-41.692-31.334-40.662-30.557zM257.336 492.015v-46.48c0-4.73-5.26-7.562-9.209-4.958l-12.08 7.966v42.986c0 15.87 17.092 25.891 30.598 17.344-5.587-3.538-9.309-9.757-9.309-16.858zM41.628 155.273l37.649 21.736-28.034 11.945c-10.015 4.267-15.19 15.725-11.198 25.853 3.113 7.896 10.646 12.654 18.578 12.654 4.604 0 7.327-1.395 10.912-2.923-13.824-8.397-12.398-29.026 2.547-35.393l20.989-8.943c3.086-1.315 3.345-5.59.44-7.267l-30.593-17.663c-12.972-7.489-13.381-26.164-.648-34.163l-.277-.16c-9.37-5.408-21.556-2.729-27.294 6.443-6.024 9.631-2.823 22.251 6.929 27.881zM62.918 356.728l32.326-18.664c2.191-1.265 1.996-4.49-.332-5.481l-22.831-9.728c-14.947-6.368-16.369-26.994-2.54-35.39l-2.669-1.137c-9.954-4.241-21.729-.112-26.32 9.686-4.82 10.286-.121 22.428 10.239 26.841l28.484 12.136-37.649 21.736c-17.644 10.186-10.299 37.237 9.996 37.237 3.385 0 6.816-.863 9.958-2.677l.686-.396c-12.696-7.977-12.348-26.659.652-34.163zM116.789 400.289l5.006-38.801c.291-2.257-2.136-3.866-4.102-2.718l-18.461 10.781-3.678 30.29c-2.033 16.745 16.066 28.782 30.404 19.712-6.312-3.991-10.129-11.359-9.169-19.264zM99.232 142.45l12.253 7.164c4.455 2.605 9.962-1.032 9.315-6.152l-4.011-31.75c-.96-7.909 2.86-15.28 9.177-19.269a19.868 19.868 0 00-13.064-2.944c-10.939 1.328-18.731 11.273-17.403 22.213l3.733 30.738zM236.047 193.253a64.927 64.927 0 00-2.966 1.036c-.233.087-.467.171-.698.261a65.948 65.948 0 00-20.72 12.813l10.367 5.887a3.264 3.264 0 004.038-.658c10.769-11.971 24.687-17.483 29.436-18.86a2.537 2.537 0 001.832-2.443v-70.964a13.91 13.91 0 00-5.555-11.122l-42.73-32.109c-7.811-5.869-10.66-16.527-6.273-25.256 1.73-3.442 4.259-6.046 7.174-7.885-9.561-6.051-22.397-2.959-28.052 7.144-4.807 8.668-2.326 19.845 5.809 25.958l48.338 36.323v79.875zM236.047 318.757a64.927 64.927 0 01-2.966-1.036c-.233-.087-.467-.171-.698-.261a65.948 65.948 0 01-20.72-12.813l10.367-5.887a3.264 3.264 0 014.038.658c10.769 11.971 24.687 17.483 29.436 18.86a2.537 2.537 0 011.832 2.443v70.964a13.91 13.91 0 01-5.555 11.122l-42.731 32.109c-7.811 5.869-10.66 16.527-6.273 25.256 1.73 3.442 4.259 6.046 7.174 7.885-9.572 6.058-22.408 2.941-28.052-7.144-4.807-8.668-2.326-19.845 5.809-25.958l48.338-36.323v-79.875h.001z"
}))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconStar = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M10.546 1.97c-.62-1.293-2.471-1.293-3.092 0L6.36 4.248c-.254.53-.765.892-1.351.959L2.52 5.49c-1.47.168-2.05 1.986-.947 2.967l1.757 1.56a1.7 1.7 0 01.537 1.616l-.479 2.314c-.293 1.419 1.213 2.533 2.494 1.844l2.302-1.238a1.721 1.721 0 011.63 0l2.302 1.238c1.28.689 2.787-.425 2.493-1.844l-.478-2.314a1.7 1.7 0 01.537-1.616l1.756-1.56c1.104-.981.524-2.8-.946-2.967l-2.488-.283a1.714 1.714 0 01-1.351-.96L10.546 1.97z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconStore = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconStore_svg__clip0_6842_2250)"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconStore_svg__clip1_6842_2250)"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconStore_svg__clip2_6842_2250)",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round"
}, /*#__PURE__*/React.createElement("path", {
  d: "M1.5 5.25l3.308-3.308A1.5 1.5 0 015.872 1.5h6.255a1.5 1.5 0 011.066.442L16.5 5.25M3 9v6a1.5 1.5 0 001.5 1.5h9A1.5 1.5 0 0015 15V9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M11.25 16.5v-3a1.5 1.5 0 00-1.5-1.5h-1.5a1.5 1.5 0 00-1.5 1.5v3M1.5 5.25h15V7.5A1.5 1.5 0 0115 9a2.025 2.025 0 01-1.193-.473.525.525 0 00-.614 0A2.025 2.025 0 0112 9a2.025 2.025 0 01-1.193-.473.525.525 0 00-.614 0A2.025 2.025 0 019 9a2.025 2.025 0 01-1.192-.473.525.525 0 00-.615 0A2.025 2.025 0 016 9a2.025 2.025 0 01-1.192-.473.525.525 0 00-.615 0A2.025 2.025 0 013 9a1.5 1.5 0 01-1.5-1.5V5.25z"
})))), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconStore_svg__clip0_6842_2250"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "iconStore_svg__clip1_6842_2250"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "iconStore_svg__clip2_6842_2250"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconSuitcase = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 18 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconSuitcase_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "#565656",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeWidth: 1.5,
  d: "M11.412 5.098V3.49a1.61 1.61 0 00-1.61-1.609H8.195a1.609 1.609 0 00-1.61 1.61v1.608m4.828 0h3.217a1.609 1.609 0 011.61 1.61v6.435a1.609 1.609 0 01-1.61 1.609H3.367a1.61 1.61 0 01-1.61-1.609V6.707a1.609 1.609 0 011.61-1.609h3.218m4.827 0H6.585"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#565656",
  d: "M9.221 9.275c.433.276.682.793.636 1.421v.007l-.168 1.22c-.08.578-.426.978-.854 1.108-.433.13-.911-.03-1.191-.487a.971.971 0 00-.302-.328.973.973 0 00-.425-.136c-.533-.06-.88-.427-.943-.875-.062-.443.155-.925.647-1.24L7.657 9.3l.006-.003c.55-.307 1.126-.298 1.558-.022zm-.743-.774c.42.238.814-.098 1.07-.55.256-.45.277-.846-.143-1.084-.42-.238-.968-.065-1.224.386-.256.452-.123 1.01.297 1.248zm-2.154.529c.348.097.735-.201.864-.666.128-.464-.05-.92-.4-1.016-.348-.096-.735.202-.864.667-.128.464.05.92.4 1.016zm5.669 1.64c-.234-.277-.722-.247-1.09.065-.367.312-.475.788-.241 1.064.234.275.722.246 1.09-.066.368-.312.476-.788.241-1.064zm-1.758-1.048c.392.281.955.168 1.258-.254.303-.422.23-.991-.162-1.272-.392-.282-.744-.096-1.046.326-.303.421-.442.919-.05 1.2z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconSuitcase_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h18v18H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconVisibilityHide = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  fillRule: "evenodd",
  clipRule: "evenodd",
  d: "M3.269 3.45a.782.782 0 00-1.033 0 .622.622 0 000 .939l1.44 1.309C2.494 6.556 1.562 7.694 1 9.002c1.258 2.927 4.364 5 8 5a9.24 9.24 0 003.192-.561l1.916 1.742c.286.26.748.26 1.033 0a.622.622 0 000-.938l-1.588-1.44-8.64-7.856L3.269 3.45zm2.453 4.108c-.23.437-.358.927-.358 1.444 0 1.84 1.629 3.334 3.636 3.334a3.89 3.89 0 001.602-.34l-1.14-1.038c-.15.029-.304.044-.462.044-1.207 0-2.182-.893-2.182-2 0-.14.016-.275.045-.406L5.722 7.558z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.249 7.014l1.917 1.743c-.121-.916-.915-1.639-1.917-1.743z",
  fill: "currentColor"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.636 9.002c0 .332-.053.652-.151.955l2.242 2.038A7.952 7.952 0 0017 9.002c-1.258-2.927-4.364-5-8-5-.923 0-1.813.133-2.646.38L7.93 5.815A3.94 3.94 0 019 5.668c2.007 0 3.636 1.494 3.636 3.334z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconVisibilityShow = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  width: sizeMap[size],
  height: sizeMap[size],
  viewBox: "0 0 18 18",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M9 4C5.364 4 2.258 6.073 1 9c1.258 2.927 4.364 5 8 5s6.742-2.073 8-5c-1.258-2.927-4.364-5-8-5zm0 8.333c-2.007 0-3.636-1.493-3.636-3.333S6.993 5.667 9 5.667 12.636 7.16 12.636 9 11.007 12.333 9 12.333zM9 7c-1.207 0-2.182.893-2.182 2S7.793 11 9 11c1.207 0 2.182-.893 2.182-2S10.207 7 9 7z",
  fill: "currentColor"
})));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconWhatsapp = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 16 16",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconWhatsapp_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "url(#iconWhatsapp_svg__b)",
  d: "M.341 7.904a7.57 7.57 0 001.027 3.814L.278 15.67l4.076-1.06a7.735 7.735 0 003.675.928h.003c4.239 0 7.69-3.423 7.69-7.629a7.552 7.552 0 00-2.25-5.397A7.673 7.673 0 008.032.276C3.793.276.343 3.698.342 7.904"
}), /*#__PURE__*/React.createElement("path", {
  fill: "url(#iconWhatsapp_svg__c)",
  d: "M.067 7.902c0 1.392.366 2.752 1.063 3.95L0 15.947l4.223-1.099a8.012 8.012 0 003.807.962h.004c4.39 0 7.964-3.545 7.966-7.902a7.824 7.824 0 00-2.331-5.59A7.948 7.948 0 008.033 0C3.644 0 .07 3.545.067 7.902zm2.515 3.744l-.158-.248a6.513 6.513 0 01-1.012-3.496c.001-3.621 2.972-6.567 6.624-6.567 1.769 0 3.431.684 4.681 1.926a6.503 6.503 0 011.938 4.647c-.002 3.621-2.972 6.568-6.621 6.568H8.03a6.654 6.654 0 01-3.37-.916l-.242-.142-2.506.652.669-2.424z"
}), /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M6.012 4.597c-.15-.329-.306-.336-.448-.341-.116-.005-.249-.005-.382-.005a.734.734 0 00-.53.247c-.183.198-.697.676-.697 1.647 0 .972.713 1.911.813 2.043.1.132 1.377 2.19 3.4 2.981 1.681.658 2.024.527 2.389.494.364-.033 1.177-.477 1.343-.938.166-.462.166-.857.116-.94-.05-.082-.182-.131-.381-.23-.2-.099-1.178-.577-1.36-.643-.183-.065-.316-.098-.448.1-.133.197-.514.642-.63.773-.117.133-.233.149-.432.05-.199-.099-.84-.307-1.6-.98a5.956 5.956 0 01-1.108-1.368c-.116-.197-.012-.304.087-.403.09-.088.2-.23.3-.346.098-.115.132-.197.198-.33.066-.13.033-.246-.017-.345-.05-.099-.436-1.076-.613-1.466z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "iconWhatsapp_svg__b",
  x1: 772.568,
  x2: 772.568,
  y1: 1539.78,
  y2: 0.276,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#1FAF38"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#60D669"
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "iconWhatsapp_svg__c",
  x1: 800,
  x2: 800,
  y1: 1594.71,
  y2: 0,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "#F9F9F9"
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "#fff"
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "iconWhatsapp_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h16v16H0z"
})))));

/**
 * The Icon[Name] component gets used to display an icon.
 *
 * ## Usage Guidelines
 *
 * - For available icons, see Foundations → Iconography. The [Name]
 *   placeholder represents an icon name. For example, the "Calendar" icon
 *   gets written like this: `<IconCalendar />`.
 * - All icons are decorative, meaning they don't get exposed to the
 *   [Accessibility Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 *   This is intentional as icons should not get used as standalone elements
 *   in a User Interface. Instead, they should always get paired with a text
 *   label. The one exception is for icon-only buttons, for example, a
 *   favourite button. In this case, the IconButton component gets used.
 */
const IconX = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("svg", {
  xmlns: "http://www.w3.org/2000/svg",
  width: sizeMap[size],
  height: sizeMap[size],
  fill: "none",
  viewBox: "0 0 17 18",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#iconX_svg__a)"
}, /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M10.117 7.618L16.446 0h-1.5L9.451 6.614 5.062 0H0l6.637 10.002L0 17.991h1.5l5.803-6.985 4.635 6.985H17L10.117 7.618zM8.063 10.09l-.672-.996L2.04 1.17h2.303l4.318 6.396.672.996 5.613 8.314h-2.304l-4.58-6.784z"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "iconX_svg__a"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  d: "M0 0h17v18H0z"
})))));

const ID_DELIMITER = '_';
let uniqueId = 0;
// In HTML5, the one rule for valid ids is they cannot contain any space
// characters. In addition to that, all punctuation marks are removed.
// NOTE: Exported for testing purposes
const sanitiseId = id => id.replace(/[^\w\s]/g, '') // Remove punctuation marks
.replace(/\s+/g, ID_DELIMITER); // Replace spaces with the delimiter
// NOTE: Exported for testing purposes
const generateId = (id, prefix) => sanitiseId(`${prefix}${ID_DELIMITER}${id}`);
const getUniqueId = () => (uniqueId++).toString();
// Create all ids
const createAccordionId = () => generateId(getUniqueId(), "ACCORDION" /* ACCORDION */);
const createCounterId = id => generateId(id, "COUNTER_GROUP" /* COUNTER_GROUP */);
const createDialogHeadingId = id => generateId(id, "DIALOG_HEADING" /* DIALOG_HEADING */);
const createRadioGroupId = id => generateId(id, "RADIO_GROUP" /* RADIO_GROUP */);
const createCheckboxGroupId = id => generateId(id, "CHECKBOX_GROUP" /* CHECKBOX_GROUP */);
const createTextFieldId = id => generateId(id, "TEXT_FIELD" /* TEXT_FIELD */);
const createTextAreaId = id => generateId(id, "TEXT_AREA" /* TEXT_AREA */);
const createToggleId = id => generateId(id, "TOGGLE" /* TOGGLE */);
const createSelectId = id => generateId(id, "SELECT" /* SELECT */);
const createFieldsetId = id => generateId(id, "FIELDSET" /* FIELDSET */);
const createTooltipId = () => generateId(getUniqueId(), "TOOL_TIP" /* TOOL_TIP */);

var styles = {"mainHeader":"Accordion-module_mainHeader__CjqGq","largeLabel":"Accordion-module_largeLabel__66sBe","mediumLabel":"Accordion-module_mediumLabel__UxJkO","arrowIconDefault":"Accordion-module_arrowIconDefault__-clNd","rotateArrowUp":"Accordion-module_rotateArrowUp__8zlRz","rotateArrowDown":"Accordion-module_rotateArrowDown__37zyR","secondaryLabel":"Accordion-module_secondaryLabel__kiqDI","accordionContent":"Accordion-module_accordionContent__Qnx7K","fadeOut":"Accordion-module_fadeOut__RcTfY","showContent":"Accordion-module_showContent__YaTGt","fadeIn":"Accordion-module_fadeIn__WxG5U"};

/**
 * The Accordion component is used to lets users show and hide sections
 *  of related content on a page when the user clicks on the accordion.
 */
const Accordion = ({
  legend,
  secondaryLegend,
  headerSize = 'large',
  children
}) => {
  const descriptionId = createAccordionId();
  const [isOpen, setIsOpen] = useState(false);
  const isDefaultHeaderType = headerSize === 'large';
  const handleAccordionToggle = () => {
    setIsOpen(!isOpen);
  };
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": "Accordion"
  }, /*#__PURE__*/React.createElement("button", {
    "aria-controls": `${descriptionId}-panel`,
    "aria-expanded": "false",
    className: styles.mainHeader,
    id: descriptionId,
    onClick: handleAccordionToggle,
    type: "button"
  }, /*#__PURE__*/React.createElement("h3", {
    className: classnames(styles.largeLabel, {
      [styles.mediumLabel]: !isDefaultHeaderType
    })
  }, legend), /*#__PURE__*/React.createElement("div", {
    className: styles.secondaryLabel
  }, secondaryLegend && /*#__PURE__*/React.createElement("div", null, " ", secondaryLegend), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles.arrowIconDefault, {
      [styles.rotateArrowUp]: isOpen,
      [styles.rotateArrowDown]: !isOpen
    })
  }, /*#__PURE__*/React.createElement(IconChevronDown, null)))), /*#__PURE__*/React.createElement("div", {
    "aria-labelledby": descriptionId,
    className: classnames(styles.accordionContent, {
      [styles.showContent]: isOpen
    }),
    "data-testid": "accordionBody",
    hidden: !isOpen,
    id: `${descriptionId}-panel`,
    role: "region"
  }, children));
};

var styles$1 = {"spinner":"AnimatedSpinner-module_spinner__mAUcT","spin":"AnimatedSpinner-module_spin__DPf0o"};

const AnimatedSpinner = ({
  size = 'small'
}) => ( /*#__PURE__*/React.createElement("div", {
  "aria-live": "polite",
  className: styles$1.spinner
}, /*#__PURE__*/React.createElement(IconSpinner, {
  size: size
})));

var styles$2 = {"root":"Avatar-module_root__fXu-J","squircle":"Avatar-module_squircle__Gdrl2","button":"Avatar-module_button__03MET","small":"Avatar-module_small__7vy-3","medium":"Avatar-module_medium__C-r6w","large":"Avatar-module_large__o6L9B","large2x":"Avatar-module_large2x__rOyTC"};

/**
 * The Avatar component is used to display an image of a user.
 */
// Custom container component to handle both button and fragment
const Container = ({
  children,
  onClick,
  size = 'small'
}) => {
  if (!!onClick) {
    return /*#__PURE__*/React.createElement("button", {
      className: classnames(styles$2.button, {
        [styles$2[size]]: size
      }),
      onClick: onClick,
      type: "button"
    }, children);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
};
const Avatar = ({
  alt,
  onClick,
  size = 'small',
  src,
  variant = 'default'
}) => ( /*#__PURE__*/React.createElement(Container, {
  onClick: onClick,
  size: size
}, /*#__PURE__*/React.createElement("img", {
  alt: alt,
  className: classnames(styles$2.root, {
    [styles$2[variant]]: variant,
    [styles$2[size]]: size
  }),
  src: src
})));

var styles$3 = {"root":"Banner-module_root__npinV","body":"Banner-module_body__-Qm4K","title":"Banner-module_title__AeTHT","descriptionWithTitle":"Banner-module_descriptionWithTitle__BYCMc","descriptionWithoutTitle":"Banner-module_descriptionWithoutTitle__7-or-","text":"Banner-module_text__Uq-bW","info":"Banner-module_info__s2q0P","coupon":"Banner-module_coupon__1bzhm","success":"Banner-module_success__uLYQ3","warning":"Banner-module_warning__9rua8","error":"Banner-module_error__vWRjZ"};

var styles$4 = {"root":"Button-module_root__Q2R8-","hasIcon":"Button-module_hasIcon__0lftf","iconLeading":"Button-module_iconLeading__g7jP5","iconTrailing":"Button-module_iconTrailing__UhZOZ","isFullBleed":"Button-module_isFullBleed__VucHv","primary":"Button-module_primary__s1sM6","secondary":"Button-module_secondary__R0waJ","tertiary":"Button-module_tertiary__xmg08","transparent":"Button-module_transparent__IN8oW","petChemist":"Button-module_petChemist__RNXWw","waggly":"Button-module_waggly__ugmYm","sash":"Button-module_sash__JfJZR","small1x":"Button-module_small1x__vamcI","default":"Button-module_default__RgsIK","large1x":"Button-module_large1x__6ayIM","isUsedForPagination":"Button-module_isUsedForPagination__gl2xu"};

/**
 * The Button component is the main button used for initialising an action, for
 * example, submitting a form, launching a dialog, etc.
 *
 * ## Usage Guidelines
 *
 * - The primary or cta button variant can only get used once per page as they
 *   get reserved for the highest-priority action. For lower emphasis actions,
 *   use the secondary and tertiary variants.
 * - The tertiary button variant must be accompanied with another button
 *   variant, typically the primary. This is due to the tertiary variant having
 *   no border or background colour to give it a button shape, yet still has the
 *   button shape's padding to maintain horizontal alignment when adjacent with
 *   other button variants.
 * - The button label must express what action will occur when the user
 *   interacts with it, and the label should be in sentence case and start with
 *   a verb. For example, in a dialog, use “Agree” instead of “Yes” or in a
 *   form, use “Sign up” instead of “Submit”.
 * - Remembering that buttons are for "actions" and links are for "navigation".
 *   The button cannot get used for navigation, i.e. when the desired action
 *   takes the user to a new page.
 */
const Button = ({
  associatedForm,
  iconLeading,
  iconTrailing,
  isDisabled,
  isFullBleed,
  isSubmit,
  isUsedForPagination = false,
  label,
  onClick,
  variant = 'primary',
  size = 'default'
}) => ( /*#__PURE__*/React.createElement("button", {
  className: classnames(styles$4.root, {
    [styles$4[variant]]: variant,
    [styles$4[size]]: size,
    [styles$4.hasIcon]: iconLeading || iconTrailing,
    [styles$4.isFullBleed]: isFullBleed,
    [styles$4.isUsedForPagination]: isUsedForPagination
  }),
  disabled: isDisabled,
  form: isSubmit && associatedForm ? associatedForm : undefined,
  onClick: onClick,
  type: isSubmit ? 'submit' : 'button'
}, iconLeading && !iconTrailing && /*#__PURE__*/React.createElement("span", {
  className: styles$4.iconLeading
}, iconLeading), label, iconTrailing && !iconLeading && /*#__PURE__*/React.createElement("span", {
  className: styles$4.iconTrailing
}, iconTrailing)));

var styles$5 = {"root":"IconButton-module_root__FfYcw","primary":"IconButton-module_primary__VndZ0","secondary":"IconButton-module_secondary__bawOo","tertiary":"IconButton-module_tertiary__mbymH","transparent":"IconButton-module_transparent__3Cpxj","small2x":"IconButton-module_small2x__1HHfm","small1x":"IconButton-module_small1x__wSj2I","default":"IconButton-module_default__dcRQL","large1x":"IconButton-module_large1x__XAEe-","large2x":"IconButton-module_large2x__Q22W9","isUsedForPagination":"IconButton-module_isUsedForPagination__sWtcw"};

/**
 * The IconButton component gets used to display an icon-only button.
 *
 * ## Usage Guidelines
 *
 * - The Icon[Name] component gets used for the IconButton component's
 *   `children` to display an icon. For available icons, see Foundations →
 *   Iconography.
 * - An icon's size can get changed via the Icon[Name] component's `size` prop.
 *   See the Icon[Name] component.
 * - The `label` prop gets used to address an IconButton's contextual
 *   shortcomings, i.e., when an IconButton may be apparent visually but may not
 *   be evident to screen reader users. By labelling the button correctly,
 *   screen reader users get the context they need. For example, a "close"
 *   IconButton uses the "X" icon. However, this doesn't provide any context to
 *   a screen reader user. Therefore, providing the following label to the
 *   button: `label="Close"` provides the button's purpose to a screen reader
 *   user. Additionally, labels should be short and concise. Think of it as what
 *   would get displayed in a regular button, i.e., without an icon.
 * - Remembering that buttons are for "actions" and links are for "navigation".
 *   The button cannot get used for navigation, i.e. when the desired action
 *   takes the user to a new page.
 */
const IconButton = ({
  children,
  isDisabled,
  isUsedForPagination,
  label,
  onClick,
  size = 'default',
  variant = 'transparent'
}) => ( /*#__PURE__*/React.createElement("button", {
  "aria-label": label,
  className: classnames(styles$5.root, {
    [styles$5[variant]]: variant,
    [styles$5[size]]: size,
    [styles$5.isUsedForPagination]: isUsedForPagination
  }),
  disabled: isDisabled,
  onClick: onClick,
  type: "button"
}, children));

const defaultIcon = {
  info: /*#__PURE__*/React.createElement(IconPaw, {
    size: "medium"
  }),
  success: /*#__PURE__*/React.createElement(IconCheck, {
    size: "medium"
  }),
  warning: /*#__PURE__*/React.createElement(IconAlert, {
    size: "medium"
  }),
  error: /*#__PURE__*/React.createElement(IconExclamation, {
    size: "medium"
  }),
  coupon: null
};
/**
 * The banner component is for purpose of notification to users,
 * it contains a default icon and a simple text string named `description`,
 * also can be expended to include a title, a close button or a CTA button
 */
const Banner = ({
  type = 'info',
  description,
  title,
  isDismissible = false,
  icon,
  cta,
  ctaOnClick,
  onClose
}) => {
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$3.root, {
      [styles$3[type]]: type
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$3.body, {
      [styles$3[type]]: type
    })
  }, icon ? icon : defaultIcon[type], /*#__PURE__*/React.createElement("div", {
    className: styles$3.text
  }, title && /*#__PURE__*/React.createElement("p", {
    className: styles$3.title
  }, title), /*#__PURE__*/React.createElement("p", {
    className: title ? styles$3.descriptionWithTitle : styles$3.descriptionWithoutTitle
  }, description)), cta && ctaOnClick && /*#__PURE__*/React.createElement(Button, {
    label: cta,
    onClick: ctaOnClick
  })), isDismissible && ( /*#__PURE__*/React.createElement(IconButton, {
    label: "Close",
    onClick: handleClose
  }, /*#__PURE__*/React.createElement(IconClose, null))));
};

var styles$6 = {"large1x":"Body-module_large1x__rkmeF","large2x":"Body-module_large2x__NpRbT","default":"Body-module_default__fz-wk"};

/**
 * The Body component gets used to create styled blobs of text
 *
 */
const Body = ({
  children,
  size = 'default'
}) => {
  const Element = 'div';
  return /*#__PURE__*/React.createElement(Element, {
    className: classnames({
      [styles$6[size]]: size
    })
  }, children);
};

var styles$7 = {"root":"ButtonLink-module_root__-JjS0"};

/**
 * The ButtonLink component gets used when a button needs to look like a link
 * visually.
 *
 * ## Usage Guidelines
 *
 * - Unlike the tertiary variant of the Button component, this component mimics
 *   a default text link exactly, meaning it has zero padding, i.e., the tap
 *   target is only the text. If an adequate tap target is required, wrap the
 *   button with the TappableLinkContainer component.
 * - Remembering that buttons are for "actions" and links are for "navigation".
 *   The button cannot get used for navigation, i.e. when the desired action
 *   takes the user to a new page.
 */
const ButtonLink = ({
  children,
  onClick
}) => ( /*#__PURE__*/React.createElement("button", {
  className: styles$7.root,
  onClick: onClick,
  type: "button"
}, children));

var styles$8 = {"root":"VisuallyHidden-module_root__-V2xg"};

/**
 * The VisuallyHidden component gets used to address a User Interface's
 * contextual shortcomings. That is when some part of a User Interface may be
 * apparent visually but may not be evident to screen reader users.
 *
 * Icon-only buttons are a classic example of such a shortcoming. For example, a
 * standard "close" button used to close a dialog, represented in HTML like
 * this:
 *
 * ```html
 * <button type="button">
 *   <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
 *     <path d="M13.7338 2.27531C13.3788…" />
 *   </svg>
 * </button>
 * ```
 *
 * There is nothing in this button's HTML for screen reader users to understand
 * the button's actual purpose. In other words, there is no text alternative in
 * the [Accessibility
 * Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree)
 * for the visual-only treatment of an "X" icon.
 *
 * The VisuallyHidden component gets used to provide screen reader users with
 * the context they need, like so:
 *
 * ```html
 * <button type="button">
 *   <VisuallyHidden isInline>Close</VisuallyHidden>
 *   <!-- the rest -->
 * </button>
 * ```
 *
 * However, and this is covered in the "Usage Guidelines" below, for interactive
 * elements such as a `button`, the `aria-label` property is what should get
 * used:
 *
 * ```html
 * <button aria-label="Close" type="button">
 *   <!-- the rest -->
 * ```
 *
 * **⚠️ NOTE:** For icon-only buttons, use the IconButton component, which
 * ensures accessibility.
 *
 * ## Usage Guidelines
 *
 * - In the vast majority of cases, a part of a User Interface that is visually
 *   available should be available to screen reader users and vice versa.
 *   Therefore, using this component should be a last resort as its usage may
 *   indicate the User Interface gets improved for all users.
 * - The primary method of handling a User Interface's contextual shortcomings
 *   is to use the `aria-label` property. However, the `aria-label` property
 *   gets supported on only the elements listed below. Therefore, in unsupported
 *   cases, the VisuallyHidden component gets used.
 *
 *    - Interactive elements.
 *    - Landmark elements.
 *    - Elements that have an explicit widget role.
 *    - `iframe` and `img` elements.
 */
const VisuallyHidden = ({
  children,
  isInline = false
}) => {
  const Element = isInline ? 'span' : 'div';
  return /*#__PURE__*/React.createElement(Element, {
    className: styles$8.root
  }, children);
};

const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
const DATE_DISPLAY_SHORT_FORMAT = 'DD/MM/YY';
const DATE_DISPLAY_FANCY_FORMAT = 'DD MMM';
const DATE_DISPLAY_FANCY_WEEK_FORMAT = '[Week of] DD MMM';
const DATE_DATA_FORMAT = 'YYYY-MM-DD';
const DATE_FORMATS = {
  default: DATE_DISPLAY_FORMAT,
  short: DATE_DISPLAY_SHORT_FORMAT,
  fancy: DATE_DISPLAY_FANCY_FORMAT,
  fancyWeek: DATE_DISPLAY_FANCY_WEEK_FORMAT
};
const DIALOG_CONTAINER_ID = 'mp-dialog-container';
const DIALOG_CONTENT_ID = 'mp-dialog-content';

const useIsVisible = ref => {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting));
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, [ref]);
  return isIntersecting;
};

const useSmallToMediaQuery = () => useMediaQuery({
  maxWidth: tokenSizingBreakpointSmallTo[1]
});
const useMediumFromMediaQuery = () => useMediaQuery({
  minWidth: tokenSizingBreakpointMediumFrom[1]
});
const useMediumToMediaQuery = () => useMediaQuery({
  maxWidth: tokenSizingBreakpointMediumTo[1]
});
const useLargeFromMediaQuery = () => useMediaQuery({
  minWidth: tokenSizingBreakpointLarge1XFrom[1]
});
const useLargeToMediaQuery = () => useMediaQuery({
  maxWidth: tokenSizingBreakpointLarge1XTo[1]
});

var styles$9 = {"container":"ToolTip-module_container__l3fLB","tipTrigger":"ToolTip-module_tipTrigger__vuGRk","tooltip":"ToolTip-module_tooltip__6ZDz5","tooltipVisible":"ToolTip-module_tooltipVisible__vbQav"};

/**
 * The ToolTip component is used to to specify extra information about any
 * element when the user clicks/press or hovers over a specified element.
 */
const ToolTip = ({
  tip,
  children,
  triggerHoverable,
  position = 'bottom',
  triggerClickable = true
}) => {
  const tipRef = useRef(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const dialogContainerRef = useRef(null);
  const dialogContentRef = useRef(null);
  const [tipClickVisible, setTipClickVisible] = useState(false);
  const [tipHoverVisible, setTipHoverVisible] = useState(false);
  const [tipPositionTop, setTipPositionTop] = useState(0);
  const [tipMarginLeft, setTipMarginLeft] = useState('unset');
  const isMediumFromViewport = useMediumFromMediaQuery();
  const isVisible = useIsVisible(triggerRef);
  const getTipPosition = useCallback(() => {
    var _containerRef$current, _containerRef$current2, _containerRef$current3, _tipRef$current, _tipRef$current2, _tipRef$current3;
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const containerHeight = (containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.clientHeight) || 0;
    const containerWidth = (containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.clientWidth) || 0;
    const containerPosition = (containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current3 = containerRef.current) === null || _containerRef$current3 === void 0 ? void 0 : _containerRef$current3.offsetTop) || 0; // only by parent!!!
    const tooltipHeight = (tipRef === null || tipRef === void 0 ? void 0 : (_tipRef$current = tipRef.current) === null || _tipRef$current === void 0 ? void 0 : _tipRef$current.clientHeight) || 0;
    const tooltipWidth = (tipRef === null || tipRef === void 0 ? void 0 : (_tipRef$current2 = tipRef.current) === null || _tipRef$current2 === void 0 ? void 0 : _tipRef$current2.clientWidth) || 0;
    const tooltipOffsetLeft = (tipRef === null || tipRef === void 0 ? void 0 : (_tipRef$current3 = tipRef.current) === null || _tipRef$current3 === void 0 ? void 0 : _tipRef$current3.offsetLeft) || 0;
    let toolTipReachedTop = tooltipHeight >= containerPosition;
    let toolTipReachedBottom = windowHeight - containerPosition <= tooltipHeight;
    let toolTipReachedRight = tooltipWidth + tooltipOffsetLeft + Number(tokenSpacingLarge2X[0]) >= windowWidth;
    // For opened in Dialog case
    if (dialogContainerRef.current && dialogContentRef.current) {
      // offset to parent plus hardcoded padding for dialog content
      // check .content class in Dialogue styles for details
      toolTipReachedTop = tooltipHeight + dialogContentRef.current.offsetTop + Number(tokenSpacingLarge1X[0]) >= containerPosition;
      toolTipReachedBottom = dialogContentRef.current.scrollHeight + dialogContentRef.current.offsetTop - containerPosition - containerHeight <= tooltipHeight + Number(tokenSpacingSmall3X[0]);
      toolTipReachedRight = tooltipWidth + tooltipOffsetLeft + Number(tokenSpacingLarge2X[0]) >= dialogContainerRef.current.clientWidth;
    }
    const tipBottomPosition = containerHeight + containerPosition + Number(tokenSpacingSmall3X[0]);
    const tipTopPosition = containerPosition - tooltipHeight - Number(tokenSpacingSmall3X[0]);
    if (position === 'top') {
      const viewPortEndReached = toolTipReachedTop && !toolTipReachedBottom;
      setTipPositionTop(viewPortEndReached ? tipBottomPosition : tipTopPosition);
    } else {
      const viewPortEndReached = toolTipReachedBottom && !toolTipReachedTop;
      setTipPositionTop(viewPortEndReached ? tipTopPosition : tipBottomPosition);
    }
    if (toolTipReachedRight && typeof tipMarginLeft !== 'number' && isMediumFromViewport) {
      let tipLeftPosition = containerWidth - tooltipWidth;
      // no reverse margin for dialog case
      if (dialogContainerRef.current && dialogContentRef.current && tipRef.current) {
        // Check if tooltip goes outside the window viewport
        const dialogContentWidth = dialogContentRef.current.clientWidth;
        const tipWidth = tipRef.current.clientWidth;
        const tipRight = dialogContentWidth - tipRef.current.offsetLeft;
        const dialogContainerRight = (window.innerWidth - dialogContentWidth) / 2;
        const spaceFromTooltipTopToWindowRight = tipRight + dialogContainerRight - tipWidth;
        tipLeftPosition = spaceFromTooltipTopToWindowRight < 0 ? spaceFromTooltipTopToWindowRight : 'unset';
      }
      setTipMarginLeft(isMediumFromViewport ? tipLeftPosition : 'unset');
    } else if (typeof tipMarginLeft === 'number' && !isMediumFromViewport) {
      setTipMarginLeft('unset');
    }
  }, [position, tipMarginLeft, isMediumFromViewport]);
  useEffect(() => {
    var _document, _document2, _dialogContentRef$cur;
    const scrollEvent = () => {
      // To avoid glitches caused by reposition of a tooltip
      // we hide tooltip during scrolling and calculate its position
      setTipClickVisible(false);
      if (triggerRef.current && dialogContainerRef.current && dialogContentRef.current && containerRef.current && tipRef.current) {
        // Calculate tooltip positions assuming its nearest parent
        // is a Dialog content container
        const triggerTop = triggerRef.current.getBoundingClientRect().top;
        const dialogContainerTop = dialogContainerRef.current.offsetTop;
        const triggerHeight = triggerRef.current.clientHeight;
        const tipHeight = tipRef.current.clientHeight;
        const newPositionForBottom = triggerTop - dialogContainerTop + triggerHeight + Number(tokenSpacingSmall3X[0]);
        const newPositionForTop = triggerTop - dialogContainerTop - tipHeight - Number(tokenSpacingSmall3X[0]);
        const newTopPosition = position === 'bottom' ? newPositionForBottom : newPositionForTop;
        const spaceFromTooltipTopToWindowBottom = window.innerHeight - dialogContainerTop - newTopPosition;
        setTipPositionTop(spaceFromTooltipTopToWindowBottom > tipHeight ? newTopPosition : newPositionForTop);
        // Check if tooltip goes outside the window viewport to the right
        const dialogContentWidth = dialogContentRef.current.clientWidth;
        const tipWidth = tipRef.current.clientWidth;
        const tipRight = dialogContentWidth - tipRef.current.offsetLeft;
        const dialogContainerRight = (window.innerWidth - dialogContentWidth) / 2;
        const spaceFromTooltipTopToWindowRight = tipRight + dialogContainerRight - tipWidth;
        if (spaceFromTooltipTopToWindowRight < 0) {
          setTipMarginLeft(spaceFromTooltipTopToWindowRight);
        }
      }
    };
    dialogContainerRef.current = (_document = document) === null || _document === void 0 ? void 0 : _document.getElementById(DIALOG_CONTAINER_ID);
    dialogContentRef.current = (_document2 = document) === null || _document2 === void 0 ? void 0 : _document2.getElementById(DIALOG_CONTENT_ID);
    (_dialogContentRef$cur = dialogContentRef.current) === null || _dialogContentRef$cur === void 0 ? void 0 : _dialogContentRef$cur.addEventListener('scroll', scrollEvent);
    return () => {
      var _dialogContentRef$cur2;
      (_dialogContentRef$cur2 = dialogContentRef.current) === null || _dialogContentRef$cur2 === void 0 ? void 0 : _dialogContentRef$cur2.removeEventListener('scroll', scrollEvent);
    };
  }, [position]);
  // Calculate initial position of the Tooltip
  useEffect(() => {
    getTipPosition();
  }, [getTipPosition, isVisible]);
  // Handle user's click outside the Tooltip
  const onScreenClick = useCallback(event => {
    var _containerRef$current4;
    if (!tipClickVisible && !tipHoverVisible) {
      return;
    }
    if (event.target instanceof Element && !((_containerRef$current4 = containerRef.current) !== null && _containerRef$current4 !== void 0 && _containerRef$current4.contains(event.target))) {
      setTipClickVisible(false);
      setTipHoverVisible(false);
    }
  }, [tipClickVisible, tipHoverVisible]);
  useEffect(() => {
    window.addEventListener('resize', getTipPosition);
    window.addEventListener('mousedown', onScreenClick);
    return () => {
      window.removeEventListener('resize', getTipPosition);
      window.removeEventListener('mousedown', onScreenClick);
    };
  }, [getTipPosition, onScreenClick]);
  // Callback to open the tooltip
  const onTriggerPress = event => {
    event.preventDefault();
    if (!triggerClickable) {
      return;
    }
    setTipClickVisible(current => !current);
    setTipHoverVisible(false);
  };
  const onMouseOver = () => {
    if (tipHoverVisible || !triggerHoverable) {
      return;
    }
    setTipHoverVisible(true);
  };
  const onMouseLeave = () => {
    if (!tipHoverVisible || !triggerHoverable) {
      return;
    }
    setTipHoverVisible(false);
  };
  const onFocus = () => {
    setTipHoverVisible(true);
  };
  const onBlur = () => {
    setTipHoverVisible(false);
  };
  const tipStyles = classnames(styles$9.tooltip, {
    [styles$9.tooltipVisible]: tipClickVisible || tipHoverVisible
  });
  const descriptionId = createTooltipId();
  return /*#__PURE__*/React.createElement("div", {
    className: styles$9.container,
    onBlur: onBlur,
    onFocus: onFocus,
    onMouseLeave: onMouseLeave,
    onMouseOver: onMouseOver,
    ref: containerRef
  }, /*#__PURE__*/React.createElement("button", {
    "aria-describedby": descriptionId,
    "aria-label": "tooltip",
    className: styles$9.tipTrigger,
    onMouseDown: onTriggerPress,
    ref: triggerRef,
    type: "button"
  }, children || /*#__PURE__*/React.createElement(IconQuestionMark, null)), /*#__PURE__*/React.createElement("span", {
    className: tipStyles,
    id: descriptionId,
    ref: tipRef,
    role: "tooltip",
    style: {
      top: tipPositionTop,
      marginLeft: tipMarginLeft
    }
  }, tip));
};

var styles$a = {"root":"FieldLabel-module_root__bnZLL","isAlignCenter":"FieldLabel-module_isAlignCenter__i2qVH","isFlexAlignCenter":"FieldLabel-module_isFlexAlignCenter__K35qb","hasTighterSpacing":"FieldLabel-module_hasTighterSpacing__NAuXQ","withToolTip":"FieldLabel-module_withToolTip__Wvzfb","tooltip":"FieldLabel-module_tooltip__e3Ye1","isDisabled":"FieldLabel-module_isDisabled__QO-Gn","isLabelVisuallyHidden":"FieldLabel-module_isLabelVisuallyHidden__OPRND","isGroupField":"FieldLabel-module_isGroupField__nGLz3"};

const FieldLabel = ({
  isDisabled = false,
  isGroupField = false,
  isLegend = false,
  isOptional = false,
  isLabelVisuallyHidden = false,
  id,
  hasTighterSpacing = false,
  label,
  tip,
  isAlignCenter
}) => {
  const renderContents = () => ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(VisuallyHidden, {
    isInline: true
  }, isOptional ? 'Optional: ' : 'Required: '), label, isOptional && /*#__PURE__*/React.createElement("span", {
    "aria-hidden": "true"
  }, " (optional)"), !!tip && ( /*#__PURE__*/React.createElement("div", {
    className: styles$a.tooltip
  }, /*#__PURE__*/React.createElement(ToolTip, {
    tip: tip
  })))));
  if (isLegend) {
    return /*#__PURE__*/React.createElement("legend", {
      className: classnames(styles$a.root, {
        [styles$a.isGroupField]: isGroupField,
        [styles$a.withToolTip]: !!tip,
        [styles$a.hasTighterSpacing]: hasTighterSpacing,
        [styles$a.isAlignCenter]: isAlignCenter,
        [styles$a.isFlexAlignCenter]: isAlignCenter
      })
    }, renderContents());
  }
  return /*#__PURE__*/React.createElement("label", {
    className: classnames(styles$a.root, {
      [styles$a.isDisabled]: isDisabled
    }, {
      [styles$a.hasTighterSpacing]: hasTighterSpacing
    }, {
      [styles$a.withToolTip]: !!tip
    }, {
      [styles$a.isLabelVisuallyHidden]: isLabelVisuallyHidden
    }, {
      [styles$a.isFlexAlignCenter]: isAlignCenter
    }),
    htmlFor: id
  }, renderContents());
};

const formatDistanceFromLocation = distance => `${distance.toFixed(1)} km`;
const formatRatingScore = score => score.toFixed(1);
const formatCurrency = (number, isWholeNumber = false) => new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: isWholeNumber ? 0 : 2,
  minimumFractionDigits: isWholeNumber ? 0 : undefined
}).format(number);
const formatServiceTypeProviders = serviceType => serviceType.replace(/ing*$/, 'ers');
const formatMultipleAriaDescribedBy = ids => ids.join(' ');
const formatDate = (date, dateFormat = 'default') => {
  const parsedDate = dayjs(date);
  const parsedDateFormat = DATE_FORMATS[dateFormat];
  return parsedDate.isValid() ? parsedDate.format(parsedDateFormat) : '';
};
const formatFirstCharacterToUppercase = string => string.charAt(0).toUpperCase() + string.slice(1);
const formatNumbersByMask = (value, mask) => {
  const numbers = value.replace(/\D/g, '');
  let index = 0;
  let formattedValue = '';
  for (let i = 0; i < mask.length && index < numbers.length; i++) {
    const char = mask.charAt(i);
    if (char === 'x') {
      formattedValue += numbers.charAt(index);
      index++;
    } else {
      formattedValue += char;
    }
  }
  return formattedValue;
};
const processMaskedInputEvent = (value, originalEvent, mask) => {
  const previousValue = value;
  const event = originalEvent;
  const newValue = event.target.value;
  let formattedValue = previousValue;
  let cursorPosition = event.target.selectionStart || 0;
  // Check if new value is longer than previous one
  if (newValue.length > previousValue.length) {
    // Check if new value longer than allowed by mask
    if (newValue.length > mask.length) {
      cursorPosition--;
      formattedValue = previousValue;
    } else if (newValue.length < mask.length) {
      formattedValue = formatNumbersByMask(newValue, mask);
      if (cursorPosition === newValue.length) {
        cursorPosition++;
      }
      if (value === formattedValue) {
        cursorPosition--;
      }
    } else {
      formattedValue = formatNumbersByMask(newValue, mask);
      if (value === formattedValue) {
        cursorPosition--;
      }
    }
    // Check if new value is shorter or equal than previous one
  } else if (newValue.length <= previousValue.length) {
    formattedValue = formatNumbersByMask(newValue, mask);
  }
  event.target.value = formattedValue;
  event.target.setSelectionRange(cursorPosition, cursorPosition);
  return event;
};

var styles$b = {"root":"FieldDescription-module_root__ImpOD","isTextField":"FieldDescription-module_isTextField__tyixP","isTextArea":"FieldDescription-module_isTextArea__ysj-z","descriptionTop":"FieldDescription-module_descriptionTop__YF6kN"};

const FieldDescription = ({
  children,
  id,
  isTextField = false,
  isTextArea = false,
  isTopDescription = false
}) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$b.root, {
    [styles$b.isTextField]: isTextField,
    [styles$b.isTextArea]: isTextArea,
    [styles$b.descriptionTop]: isTopDescription
  }),
  id: id
}, children));

/**
 * The Fieldset component gets used to semantically group related form fields
 * under a legend heading.
 *
 * ## Usage Guidelines
 *
 * - An example of using a fieldset might be asking a user to provide their
 *   pet's age via two text fields: "Months" and "Years". As these two fields
 *   are related, the fieldset component would contain them using its `legend`
 *   prop as the fieldset's heading. Like this:
 *
 *  ```jsx
 *  <Fieldset legend="How old is Ollie?">
 *    <TextField label="Months" name="petsAgeMonths" />
 *    <TextField label="Years" name="petsAgeYears" />
 *  </Fieldset>
 *  ```
 * - As the component name suggests, a fieldset's contents can only be form
 *   field components, e.g., RadioGroup, TextField, etc., and the LayoutGrid
 *   components.
 * - Form field "group" components such as RadioGroup and CheckboxGroup get
 *   contained in a fieldset out-of-the-box. Therefore, a **single** form field
 *   "group" component should never get nested in a Fieldset component. However,
 *   **multiple** form field "group" components can get nested but only if
 *   they're related.
 * - There are edge cases when a fieldset's legend should not be visible on the
 *   screen; that is, it's hidden visually, applied via the
 *   `isLegendVisuallyHidden` prop. A primary use case for this treatment is for
 *   "compact" forms, that is, forms intended to take up as little space on the
 *   screen as possible. Typically compact forms get associated with the Form
 *   component where its `hasTighterSpacing` prop gets set to "true".
 * - Nested fieldsets are not allowed, and a Fieldset component can only get
 *   contained by a Form component. If a Form component does not contain a
 *   fieldset, none of the fieldset's contents will have spacing applied.
 */
const Fieldset = ({
  children,
  describedBy,
  legend,
  hasTighterSpacing,
  isGroupField = false,
  isLegendVisuallyHidden = false,
  isOptional = false,
  subtitle,
  tip,
  isLegendCentered = false
}) => {
  const subtitleId = createFieldsetId("subtitle" /* FORM_FIELD_SUBTITLE */);
  const renderLegend = () => ( /*#__PURE__*/React.createElement(FieldLabel, {
    hasTighterSpacing: hasTighterSpacing,
    isAlignCenter: isLegendCentered,
    isGroupField: isGroupField,
    isLegend: true,
    isOptional: isOptional,
    label: legend,
    tip: tip
  }));
  const renderSubTitle = () => ( /*#__PURE__*/React.createElement(FieldDescription, {
    id: subtitleId,
    isTopDescription: true
  }, subtitle));
  return /*#__PURE__*/React.createElement("fieldset", {
    "aria-describedby": describedBy ? formatMultipleAriaDescribedBy(describedBy) : undefined,
    "data-faux-role": isLegendVisuallyHidden ? "fieldset fieldsetWithVisuallyHiddenLegend" /* FIELDSET_WITH_VISUALLY_HIDDEN_LEGEND */ : "fieldset" /* FIELDSET */
  }, isLegendVisuallyHidden ? /*#__PURE__*/React.createElement(VisuallyHidden, null, renderLegend()) : renderLegend(), subtitle && renderSubTitle(), children);
};

var styles$c = {"textareaContainer":"TextArea-module_textareaContainer__m-V9p"};

/**
 * The TextArea component is a form field component that accepts text input
 * longer than a single line, such as a description.
 *
 * ## Usage Guidelines
 *
 * - A text area is a mandatory form field by default. For optional text
 *   fields, apply the `isOptional` prop.
 * - There are edge cases when a text area's label should not be visible on the
 *   screen; that is, it's hidden visually, applied via the
 *   `isLabelVisuallyHidden` prop. A primary use case for this treatment is when
 *   form fields do not follow the standard structure. E.g., a form field may
 *   exist within a table where its visible label gets placed in a different
 *   position to the default, which is directly above the input. Therefore, to
 *   make this custom form field layout work, the default label has to disappear
 *   from the screen to make way for the "fake" label. It's a fake label as it's
 *   not connected to the field's input in any way—it's purely visual while the
 *   actual connected label gets hidden visually. Fake labels should get wrapped
 *   in a `div` or `span` element with the `aria-hidden="true"` attribute set;
 *   this ensures they don't get exposed to the [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 * - A text area's placeholder, applied via the `placeholder` prop, gets used
 *   to provide a brief hint as to what kind of information gets expected in the
 *   field. It should be a short phrase that hints about the expected
 *   type of data rather than an explanation or prompt. However, placeholders
 *   are best avoided due to their [numerous accessibility and usability
 *   issues](https://www.smashingmagazine.com/2018/06/placeholder-attribute/).
 *   Therefore, the most desirable location for any instructional type text is
 *   within a text area's label applied via the `label` prop; second to that is
 *   a text area's description applied via the `description` prop. The last
 *   resort would be a placeholder, but only if the placeholder text is not
 *   fundamental to completing the field—one of the annoying issues with
 *   placeholders is that they disappear when the focus gets set to the input
 *   resulting in recall issues for many users.
 * - The TextArea component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the input's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting a value,
 *   the default value of `useState` needs to match the corresponding `value`
 *   prop. E.g., if a text area should have a default value of "Bunny rabbit",
 *   the default value of `useState` should be: `useState('Bunny rabbit')`.
 * - Text areas support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *   - `minLength`
 *   - `maxLength`
 *
 * - If additional context is required for a text area, then a description can
 *   get used via the `description` prop. The description provides context for
 *   the entire field and gets displayed underneath the input.
 */
// eslint-disable-next-line react/display-name
const TextArea = ({
  description,
  hasSpellCheck = false,
  hasTighterSpacing,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isOptional = false,
  isReadOnly = false,
  label,
  maxLength,
  minLength,
  name,
  onChange,
  placeholder,
  rows = 6,
  value
}) => {
  const createId = id => createTextAreaId(`${name}${id ? ` ${id}` : ''}`);
  const inputId = createId();
  const descriptionId = createId("description" /* FORM_FIELD_DESCRIPTION */);
  const ariaDescribedBy = description ? formatMultipleAriaDescribedBy([descriptionId]) : undefined;
  const renderLabel = () => ( /*#__PURE__*/React.createElement(FieldLabel, {
    hasTighterSpacing: hasTighterSpacing,
    id: inputId,
    isDisabled: isDisabled,
    isOptional: isOptional,
    label: label
  }));
  return /*#__PURE__*/React.createElement("div", {
    "data-faux-role": "formField" /* FORM_FIELD */
  }, isLabelVisuallyHidden ? /*#__PURE__*/React.createElement(VisuallyHidden, null, renderLabel()) : renderLabel(), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$c.textareaContainer)
  }, /*#__PURE__*/React.createElement("textarea", {
    "aria-describedby": ariaDescribedBy,
    disabled: isDisabled,
    id: inputId,
    maxLength: maxLength,
    minLength: minLength,
    name: name,
    onChange: onChange,
    placeholder: placeholder,
    readOnly: isReadOnly,
    required: !isOptional,
    rows: rows,
    spellCheck: hasSpellCheck,
    value: value
  })), description && ( /*#__PURE__*/React.createElement(FieldDescription, {
    id: descriptionId,
    isTextArea: true
  }, description)));
};

var styles$d = {"options":"CheckboxRadioGroup-module_options__-MBL2","hasOptionDescription":"CheckboxRadioGroup-module_hasOptionDescription__i1CVF","checkmark":"CheckboxRadioGroup-module_checkmark__y-J-V","isCheckbox":"CheckboxRadioGroup-module_isCheckbox__2ukCu","optionDescription":"CheckboxRadioGroup-module_optionDescription__m3pqk","tiled":"CheckboxRadioGroup-module_tiled__6zukT","tileLabel":"CheckboxRadioGroup-module_tileLabel__AjaOZ","isLabelCentered":"CheckboxRadioGroup-module_isLabelCentered__4pFdn","isCheckboxHidden":"CheckboxRadioGroup-module_isCheckboxHidden__h9S8r","tileInput":"CheckboxRadioGroup-module_tileInput__3PnNW","tiledWithImage":"CheckboxRadioGroup-module_tiledWithImage__Q9-Zh","tiledWithImageCompressed":"CheckboxRadioGroup-module_tiledWithImageCompressed__7JvD-","tileLabelWithImageLabel":"CheckboxRadioGroup-module_tileLabelWithImageLabel__cJlxv","isLabelWithImage":"CheckboxRadioGroup-module_isLabelWithImage__AL6so","optionWithLabelAndDescription":"CheckboxRadioGroup-module_optionWithLabelAndDescription__2xdUH","isCheckboxHiddenWithImage":"CheckboxRadioGroup-module_isCheckboxHiddenWithImage__gnc2I","tiledIcon":"CheckboxRadioGroup-module_tiledIcon__-FBw5","tileInputWithImage":"CheckboxRadioGroup-module_tileInputWithImage__of-F9","optionLabel":"CheckboxRadioGroup-module_optionLabel__Dizwc","horizontal":"CheckboxRadioGroup-module_horizontal__p1SRB","isLabelUnderButton":"CheckboxRadioGroup-module_isLabelUnderButton__-QypG","isHorizontal":"CheckboxRadioGroup-module_isHorizontal__ZefyW","isHorizontalCheckbox":"CheckboxRadioGroup-module_isHorizontalCheckbox__Wzu7i","horizontalLabels":"CheckboxRadioGroup-module_horizontalLabels__ohNwU","textAreaContainer":"CheckboxRadioGroup-module_textAreaContainer__Eg5RX","buttonMultiselect":"CheckboxRadioGroup-module_buttonMultiselect__zhJZD","buttonMultiselectCompressed":"CheckboxRadioGroup-module_buttonMultiselectCompressed__YZS-7","centerAligned":"CheckboxRadioGroup-module_centerAligned__lWCsZ","buttonMultiselectLabel":"CheckboxRadioGroup-module_buttonMultiselectLabel__FG-31","buttonMultiselectCompressedButton":"CheckboxRadioGroup-module_buttonMultiselectCompressedButton__PUaEG","isLabelCenteredWithButton":"CheckboxRadioGroup-module_isLabelCenteredWithButton__7VQbZ","isCheckboxHiddenWithButton":"CheckboxRadioGroup-module_isCheckboxHiddenWithButton__OktoZ","checkIcon":"CheckboxRadioGroup-module_checkIcon__LPuwO","buttonMultiselectInput":"CheckboxRadioGroup-module_buttonMultiselectInput__XuIKC"};

const CheckboxRadioGroup = ({
  description,
  isCheckbox = false,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isLegendVisuallyHidden = false,
  isOptional = false,
  subtitle,
  leftLabel = 'Not likely',
  legend,
  name,
  onChange,
  onChangeText = () => {},
  options,
  rightLabel = 'Very likely',
  value,
  variant = 'default',
  textAreaPlaceHolder,
  tip,
  isLegendCentered = false,
  isTileWithImageCompressed = false,
  isButtonMultiselectCompressed = false,
  isButtonMultiselectCenterAligned = false
}) => {
  var _value$value;
  const isMediumFromViewport = useMediumFromMediaQuery();
  const createId = id => isCheckbox ? createCheckboxGroupId(`${name} ${id}`) : createRadioGroupId(`${name} ${id}`);
  const descriptionId = createId("description" /* FORM_FIELD_DESCRIPTION */);
  const renderHorizontalLabels = () => {
    if (variant === 'horizontal') {
      return /*#__PURE__*/React.createElement("div", {
        className: styles$d.horizontalLabels
      }, /*#__PURE__*/React.createElement("span", null, leftLabel), /*#__PURE__*/React.createElement("span", null, rightLabel));
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Fieldset, {
    describedBy: description ? [descriptionId] : undefined,
    isGroupField: true,
    isLegendCentered: isLegendCentered,
    isLegendVisuallyHidden: isLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend,
    subtitle: subtitle,
    tip: tip
  }, isLabelVisuallyHidden ? ( /*#__PURE__*/React.createElement(VisuallyHidden, null, renderHorizontalLabels())) : renderHorizontalLabels(), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$d.options, {
      [styles$d[variant]]: variant,
      [styles$d.tiledWithImageCompressed]: variant === 'tiledWithImage' && isTileWithImageCompressed,
      [styles$d.buttonMultiselectCompressed]: variant === 'buttonMultiselect' && isButtonMultiselectCompressed,
      [styles$d.centerAligned]: variant === 'buttonMultiselect' && isButtonMultiselectCenterAligned
    })
  }, options.map(({
    description: optionDescription,
    isDisabled: optionIsDisabled,
    label,
    value: optionValue,
    icon: optionIconUri
  }) => {
    const inputId = createId(optionValue);
    return /*#__PURE__*/React.createElement("div", {
      key: optionValue
    }, /*#__PURE__*/React.createElement("input", {
      checked: isCheckbox ? value.value.includes(optionValue) : value.value === optionValue,
      className: classnames(styles$d.tileInput, styles$d.tileInputWithImage, styles$d.buttonMultiselectInput),
      disabled: isDisabled || optionIsDisabled,
      id: inputId,
      name: name,
      onChange: onChange,
      required: !isCheckbox && !isOptional ? true : undefined,
      type: isCheckbox ? 'checkbox' : 'radio',
      value: optionValue
    }), /*#__PURE__*/React.createElement("label", {
      className: classnames(styles$d.tileLabel, styles$d.tileLabelWithImageLabel, styles$d.buttonMultiselectLabel, styles$d.horizontalLabel, {
        [styles$d.hasOptionDescription]: optionDescription,
        [styles$d.isLabelCentered]: variant === 'tiled',
        [styles$d.isLabelWithImage]: variant === 'tiledWithImage',
        [styles$d.isLabelUnderButton]: variant === 'horizontal',
        [styles$d.isLabelCenteredWithButton]: variant === 'buttonMultiselect',
        [styles$d.buttonMultiselectCompressedButton]: variant === 'buttonMultiselect' && isButtonMultiselectCompressed
      }),
      htmlFor: inputId
    }, /*#__PURE__*/React.createElement("span", {
      className: classnames(styles$d.checkmark, {
        [styles$d.isCheckbox]: isCheckbox,
        [styles$d.isCheckboxHidden]: variant === 'tiled',
        [styles$d.isCheckboxHiddenWithImage]: variant === 'tiledWithImage',
        [styles$d.isHorizontalCheckbox]: variant === 'horizontal',
        [styles$d.isCheckboxHiddenWithButton]: variant === 'buttonMultiselect'
      })
    }, isCheckbox && /*#__PURE__*/React.createElement(IconCheck, null)), variant === 'tiledWithImage' && optionIconUri && typeof optionIconUri === 'string' && ( /*#__PURE__*/React.createElement("img", {
      alt: `icon ${label}`,
      className: styles$d.tiledIcon,
      src: optionIconUri
    })), variant === 'tiledWithImage' && optionIconUri && typeof optionIconUri !== 'string' && /*#__PURE__*/React.createElement(React.Fragment, null, optionIconUri), variant === 'tiledWithImage' ? ( /*#__PURE__*/React.createElement("div", {
      className: styles$d.optionWithLabelAndDescription
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$d.optionLabel
    }, label), optionDescription && ( /*#__PURE__*/React.createElement("span", {
      className: styles$d.optionDescription
    }, optionDescription)))) : ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, label), optionDescription && ( /*#__PURE__*/React.createElement("span", {
      className: styles$d.optionDescription
    }, optionDescription)))), variant === 'buttonMultiselect' && !isButtonMultiselectCompressed && ( /*#__PURE__*/React.createElement("span", {
      className: styles$d.checkIcon
    }, /*#__PURE__*/React.createElement(IconCheck, {
      size: isMediumFromViewport ? 'medium' : 'small'
    })))));
  })), (isCheckbox && (value === null || value === void 0 ? void 0 : (_value$value = value.value) === null || _value$value === void 0 ? void 0 : _value$value.includes('other')) || (value === null || value === void 0 ? void 0 : value.value) === 'other') && ( /*#__PURE__*/React.createElement("div", {
    className: styles$d.textAreaContainer
  }, /*#__PURE__*/React.createElement(TextArea, {
    isDisabled: isDisabled,
    isLabelVisuallyHidden: true,
    label: legend,
    name: `${name}Other`,
    onChange: onChangeText,
    placeholder: textAreaPlaceHolder,
    value: (value === null || value === void 0 ? void 0 : value.subValue) || ''
  }))), description && /*#__PURE__*/React.createElement(FieldDescription, {
    id: descriptionId
  }, description));
};

/**
 * The CheckboxGroup component is a form field component that provides a list of
 * multiple selectable options.
 *
 * ## Usage Guidelines
 *
 * - A checkbox group is a mandatory field by default. For optional checkbox
 *   group fields, apply the `isOptional` prop.
 * - A checkbox group gets comprised of at least two options where a user can
 *   select multiple options at a time. If a user needs to select only one
 *   option, the RadioGroup is the component to use as radio buttons only allow
 *   one option to be selected.
 * - The CheckboxGroup component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning a checkbox's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting an option,
 *   the default value of `useState` needs to match the corresponding option's
 *   `value` prop: `options[x].value`. E.g., if this were the object item of the
 *   preselected option:

 *  ```js
 *  {
 *    label: 'Dog Walking',
 *    value: 'dogWalking',
 *  }
 *  ```
 *
 *   Then the default value of `useState`
 *   would be: `useState({value: ['dogWalking'], subValue: null})`.
 *
 *   **⚠️ NOTE:** The `value` prop uses a type that has `value` an array of
 *   strings (`string[]`) and subValue a string or undefined
 *   (`string|undefined`). This means the `ChangeEvent<HTMLInputElement>`
 *   type gets modified as the default type for an input element's `value`
 *   attribute is a `string`. Therefore, your `handleChange` function's `event`
 *   parameter has to get assigned to the modified `CheckboxGroupOnChange` type.
 * - If additional context is required for a checkbox group, then a description
 *   can get used via the `description` prop. The description provides context
 *   for the entire field and gets displayed underneath all of the options.
 *   Conversely, suppose additional context gets required for an individual
 *   option. In that case, there needs to be a description provided to the
 *   corresponding `options[x].description` prop and gets displayed underneath
 *   the option's label.
 */
const CheckboxGroup = ({
  description,
  isDisabled = false,
  isLegendVisuallyHidden = false,
  isOptional = false,
  legend,
  subtitle,
  name,
  onChange,
  options,
  tip,
  value,
  variant = 'default',
  textAreaPlaceHolder,
  isButtonMultiselectCompressed = false,
  isButtonMultiselectCenterAligned = false
}) => {
  const handleChange = ({
    target: {
      value: inputValue
    }
  }) => {
    const updatedValue = [...(value === null || value === void 0 ? void 0 : value.value)];
    let updateSubValue = value.subValue;
    const inputValueIndex = updatedValue.indexOf(inputValue);
    if (inputValueIndex !== -1) {
      updatedValue.splice(inputValueIndex, 1);
      if (inputValue === 'other') {
        updateSubValue = undefined;
      }
    } else {
      updatedValue.push(inputValue);
    }
    onChange({
      target: {
        value: {
          value: updatedValue,
          subValue: updateSubValue
        }
      }
    });
  };
  const handleTextChange = ({
    target: {
      value: inputValue
    }
  }) => {
    onChange({
      target: {
        value: {
          value: value === null || value === void 0 ? void 0 : value.value,
          subValue: inputValue
        }
      }
    });
  };
  return /*#__PURE__*/React.createElement(CheckboxRadioGroup, {
    description: description,
    isButtonMultiselectCenterAligned: isButtonMultiselectCenterAligned,
    isButtonMultiselectCompressed: isButtonMultiselectCompressed,
    isCheckbox: true,
    isDisabled: isDisabled,
    isLegendVisuallyHidden: isLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend,
    name: name,
    onChange: handleChange,
    onChangeText: handleTextChange,
    options: options,
    subtitle: subtitle,
    textAreaPlaceHolder: textAreaPlaceHolder,
    tip: tip,
    value: value,
    variant: variant
  });
};

var styles$e = {"root":"CommonPlatformHeader-module_root__DxOPL"};

var styles$f = {"root":"CommonPlatformHeaderLogo-module_root__hdGH8"};

const defaultDimensions = [25, 104];
const PlatformHeaderLogo = ({
  folderPath
}) => {
  const [height, width] = defaultDimensions;
  return /*#__PURE__*/React.createElement("div", {
    className: styles$f.root
  }, /*#__PURE__*/React.createElement("a", {
    href: "/"
  }, /*#__PURE__*/React.createElement("img", {
    alt: "Mad Paws",
    height: height,
    src: `${folderPath}/madpaws-platformheader-logo.png`,
    srcSet: `${folderPath}/madpaws-platformheader-logo.svg`,
    width: width
  })));
};

const validatePlacement = (placements, name) => {
  const [start, end] = placements;
  const warningMessageStart = name ? `${name} placement` : `Placement`;
  if (start >= end) {
    // eslint-disable-next-line no-console
    console.warn('%c@madpaws/design-system component API warning:', 'font-weight: bold', `${warningMessageStart} values are invalid. Please, ensure the end value: ${end} is greater than the start value: ${start}. Until valid, the default placement occurs, which is to span all columns.`);
    return null;
  }
  return placements;
};
const getInlineStyle = value => value !== null ? {
  gridColumn: `${value[0]} / ${value[1]}`
} : undefined;
const getColumnGapClass = (columnGap, isLargeFromViewport, isMediumFromViewport) => {
  const baseColumnGapClass = 'columnGapBase';
  if (!columnGap) {
    return baseColumnGapClass;
  }
  if (typeof columnGap === 'string') {
    return `columnGap${formatFirstCharacterToUppercase(columnGap)}`;
  }
  const {
    small,
    large,
    medium
  } = columnGap;
  if (!small && !medium && !large) {
    return baseColumnGapClass;
  }
  let finalColumnGap;
  if (isLargeFromViewport) {
    finalColumnGap = large;
  } else if (isMediumFromViewport) {
    finalColumnGap = medium;
  } else {
    finalColumnGap = small;
  }
  return `columnGap${formatFirstCharacterToUppercase(finalColumnGap)}`;
};

var styles$g = {"root":"LayoutGrid-module_root__etcau","grid":"LayoutGrid-module_grid__cBLIv","isRootLayout":"LayoutGrid-module_isRootLayout__e1HK2","rowGapLarge1x":"LayoutGrid-module_rowGapLarge1x__Po1u-","rowGapLarge2x":"LayoutGrid-module_rowGapLarge2x__H2bXb","rowGapLarge3x":"LayoutGrid-module_rowGapLarge3x__dxV78","rowGapLarge4x":"LayoutGrid-module_rowGapLarge4x__s0asX","rowGapLarge5x":"LayoutGrid-module_rowGapLarge5x__b-gft","rowGapLarge6x":"LayoutGrid-module_rowGapLarge6x__2evF1","rowGapLarge7x":"LayoutGrid-module_rowGapLarge7x__By4AR","rowGapLarge8x":"LayoutGrid-module_rowGapLarge8x__OA9YE","rowGapLarge9x":"LayoutGrid-module_rowGapLarge9x__i0mtP","rowGapDefault":"LayoutGrid-module_rowGapDefault__KHTxR","rowGapSmall1x":"LayoutGrid-module_rowGapSmall1x__xAuvd","rowGapSmall2x":"LayoutGrid-module_rowGapSmall2x__qei9O","rowGapSmall3x":"LayoutGrid-module_rowGapSmall3x__adnGF","rowGapSmall4x":"LayoutGrid-module_rowGapSmall4x__xJMBC","columnGapBase":"LayoutGrid-module_columnGapBase__sKE-M","columnGapNone":"LayoutGrid-module_columnGapNone__QlPAV","columnGapSmall2x":"LayoutGrid-module_columnGapSmall2x__qZpiP","columnGapSmall1x":"LayoutGrid-module_columnGapSmall1x__cPOcd","columnGapDefault":"LayoutGrid-module_columnGapDefault__LmzpF","columnGapLarge1x":"LayoutGrid-module_columnGapLarge1x__vcDku"};

/* eslint-disable max-len */
/**
 * The LayoutGridContainer and LayoutGridItem components provide a mobile-first
 * responsive layout grid on which all layouts get built.
 *
 * The layout grid enables layouts to change dynamically based on the screen
 * size (viewport) and guarantees consistent layouts across the board.
 * Subsequently, all elements within layouts get proportionally sized rather
 * than fixed. In other words, elements adapt to their fluid containers' size,
 * not to rigidly fixed pixel dimensions.
 *
 * The layout grid uses flexible column widths and fixed-width gutters and
 * margins, see the following for the grid's column configuration:
 *
 * | Viewport name | Viewport size | Column count | Column lines |
 * | ------------- | ------------- | ------------ | ------------ |
 * | `small`       | <= 767px      | 12           | 13           |
 * | `medium`      | 768px–1023px  | 12           | 13           |
 * | `large`       | >= 1024px     | 12           | 13           |
 *
 * ## Usage Guidelines
 *
 * - The layout grid gets comprised of two components: LayoutGridContainer and
 *   LayoutGridItem. As the component names imply, the LayoutGridContainer
 *   component must always wrap the grid items, which are the LayoutGridItem
 *   components. The LayoutGridContainer `children` prop only permits
 *   LayoutGridItem components.
 * - The layout grid only supports grid item placement on columns (row placement
 *   will get added if the need arises). This doesn't mean rows aren't
 *   supported; implicit rows get created when grid items do not fit
 *   horizontally. Use the rowGap prop to apply spacing between rows, which
 *   accepts any "spacing → base" tokens except for "none".
 * - As per the [CSS Grid Layout
 *   system](https://css-tricks.com/snippets/css/complete-guide-grid/), the
 *   placement of a grid item is done by specifying the grid column lines the
 *   item should start and end on. To demonstrate this, here's an example of a
 *   four-item grid that creates three different layouts across each viewport
 *   size:
 *
 *   - `small` viewport:
 *
 *     ```bash
 *     [————————————————]
 *     [----------------]
 *     [----------------]
 *     [----------------]
 *     ```
 *   - `medium` viewport:
 *
 *     ```bash
 *     [----------------]
 *     [-------][-------]
 *     [----------------]
 *     ```
 *   - `large` viewport:
 *
 *     ```bash
 *     [----][----][----]
 *     [----------------]
 *     ```
 *
 *      The implementation:
 *
        ```jsx
        <LayoutGridContainer>
          <LayoutGridItem placement={{ small: [1,13], medium: [1,13], large: [1,5] }} />
          <LayoutGridItem placement={{ small: [1,13], medium: [1,7], large: [5,9] }} />
          <LayoutGridItem placement={{ small: [1,13], medium: [7,13], large: [9,13] }} />
          <LayoutGridItem placement={[1,13]} />
        </LayoutGridContainer>
        ```
 *
 *      **⚠️ NOTE:**
 *
 *      - The above implementation gets implemented in the primary canvas block
 *        below.
 *      - Grid lines reside on either side of a column. Therefore, the placement
 *        of a grid item gets based on the number of grid lines, not the number
 *        of columns. This means there is always one more grid line to grid
 *        column. For example, if you want to span a grid item across all 12
 *        columns, the grid item's end placement is **13**, not 12. See the
 *        [Important Terminology
 *        section](https://css-tricks.com/snippets/css/complete-guide-grid/#grid-terminology)
 *        in the [A Complete Guide to
 *        Grid](https://css-tricks.com/snippets/css/complete-guide-grid/)
 *        article for more info.
 *      - When the placement values are the same across each viewport size, the
 *        placement only needs to get defined once.
 * - Nested layout grids can get used only when a nested grid's columns are
 *   aligned precisely with any parent grid(s) columns. Grid column alignment is
 *   imperative to ensure everything gets lined up correctly. To a user, there
 *   must be only one layout grid applied to the entire page (or viewport) to
 *   which every element gets aligned, the same way it works in a design tool
 *   like Figma. Unfortunately, this is quite tricky to achieve without [CSS
 *   `subgrid` value](https://www.smashingmagazine.com/2018/07/css-grid-2/)
 *   support as a nested grid is independent of a parent grid, meaning it cannot
 *   take its track sizing (column and row sizing). Therefore, if a nested
 *   layout grid has any left or right whitespace applied that isn't coming from
 *   a parent grid's margins, it, unfortunately, won't work as it means it can't
 *   align with any parent grids. This is why the `isRootLayout` prop is `false`
 *   by default to make it easier to use nested grids, which gets explained more
 *   in the following guideline.
 * - Following the previous guideline, the `isRootLayout` prop gets used to
 *   differentiate between non-nested layout grids and nested grids. Non-nested
 *   grids get reserved for major layouts that sit at the root, or close to it,
 *   of an application's component tree where they do not need to worry about
 *   any parent grids. Additionally, a root layout grid gets centred in the
 *   viewport once its max-width is reached.
 */
/* eslint-enable max-len */
const LayoutGridContainer = ({
  children,
  columnGap,
  element: Element = 'div',
  id,
  isRootLayout = false,
  rowGap
}) => {
  const isLargeFromViewport = useLargeFromMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  let finalRowGap;
  let rowGapClass;
  if (rowGap) {
    // Check if `rowGap` is an object, i.e., is of `RowGapConfig` type
    if (typeof rowGap === 'object' && rowGap !== null) {
      const {
        small,
        medium,
        large
      } = rowGap;
      if (isLargeFromViewport) {
        finalRowGap = large;
      } else if (isMediumFromViewport) {
        finalRowGap = medium;
      } else {
        finalRowGap = small;
      }
    } else {
      finalRowGap = rowGap;
    }
    rowGapClass = `rowGap${formatFirstCharacterToUppercase(finalRowGap)}`;
  }
  const columnGapClass = getColumnGapClass(columnGap, isLargeFromViewport, isMediumFromViewport);
  const renderGrid = () => ( /*#__PURE__*/React.createElement(Element, {
    className: classnames(styles$g.grid, styles$g[columnGapClass], {
      [styles$g.isRootLayout]: isRootLayout,
      [styles$g[rowGapClass]]: finalRowGap
    }),
    "data-faux-role": "layoutGrid" /* LAYOUT_GRID */,
    id: id
  }, children));
  if (isRootLayout) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$g.root
    }, renderGrid());
  }
  return renderGrid();
};

const LayoutGridItem = ({
  children,
  element: Element = 'div',
  placement
}) => {
  const isLargeFromViewport = useLargeFromMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  let finalPlacement;
  if (!Array.isArray(placement)) {
    const {
      small,
      medium,
      large
    } = placement;
    if (isLargeFromViewport) {
      finalPlacement = validatePlacement(large, "large" /* LARGE */);
    } else if (isMediumFromViewport) {
      finalPlacement = validatePlacement(medium, "medium" /* MEDIUM */);
    } else {
      finalPlacement = validatePlacement(small, "small" /* SMALL */);
    }
  } else {
    finalPlacement = validatePlacement(placement);
  }
  return /*#__PURE__*/React.createElement(Element, {
    style: getInlineStyle(finalPlacement)
  }, children);
};

var styles$h = {"large1xDefault":"Heading-module_large1xDefault__zhEm0","large2xDefault":"Heading-module_large2xDefault__fOi13","mediumDefault":"Heading-module_mediumDefault__lRIaQ","small1xDefault":"Heading-module_small1xDefault__fmGGA","small2xDefault":"Heading-module_small2xDefault__crH5d","small3xDefault":"Heading-module_small3xDefault__Wvl-a","large1xHeading":"Heading-module_large1xHeading__nvibc","large2xHeading":"Heading-module_large2xHeading__j3B-J","mediumHeading":"Heading-module_mediumHeading__e2VOQ","small1xHeading":"Heading-module_small1xHeading__2KG-r","small2xHeading":"Heading-module_small2xHeading__a66V4","small3xHeading":"Heading-module_small3xHeading__C4mXE","defaultColor":"Heading-module_defaultColor__B5-KQ","brandColor":"Heading-module_brandColor__LqRRz","stoneColor":"Heading-module_stoneColor__eQEUS","leftAlign":"Heading-module_leftAlign__4D0xb","centerAlign":"Heading-module_centerAlign__FyGiF","rightAlign":"Heading-module_rightAlign__D6sND","hasTightLineHeight":"Heading-module_hasTightLineHeight__Gdzhm"};

const postfixMap = {
  default: 'Default',
  heading: 'Heading'
};
/**
 * The Heading component gets used to create levels of hierarchies between text,
 * representing the [HTML `h1`–`h6` elements](https://html.spec.whatwg.org/#the-h1,-h2,-h3,-h4,-h5,-and-h6-elements).
 *
 * ## Usage Guidelines
 *
 * - Headings are a fundamental aspect of applying a [document (page) outline](http://html5doctor.com/outlines/) (it's important to highlight that the HTML5 document outline
 *   algorithm [never got realised](http://html5doctor.com/computer-says-no-to-html5-document-outline/), meaning the classic [HTML4 approach](http://html5doctor.com/outlines/#outlines-in-html4) is required). Therefore, nest
 *   headings by their level, use only **one** level 1 (`h1`) heading per page
 *   and **do not** skip heading levels (more guidance [here](https://www.w3.org/WAI/tutorials/page-structure/headings/)). This gets tricky in a component architecture as
 *   components are only aware of their own outline, not the surrounding page's
 *   outline. However, the design system does its best to abstract headings for
 *   you via the layout components, especially level 1 headings (`h1`), thus
 *   managing heading levels as best it can. Finally, there are tools available
 *   to check correct outlines get applied to pages, for example, Chrome
 *   extension: [HTML5 Outliner](https://chrome.google.com/webstore/detail/html5-outliner/afoibpobokebhgfnknfndkgemglggomo), [HTML5 outliner](https://gsnedders.html5.org/outliner/),
 *   etc.
 * - A heading's presentational concern, i.e., how it looks, represented by the
 *   `size` prop, is not connected to a heading's level, meaning headings can
 *   get assigned a heading level independent of how it looks. [This article](https://medium.com/fed-or-dead/handling-headings-in-a-ui-component-library-2587de93c890) explains
 *   why it's important to decouple a heading's semantics from its presentation.
 * - Headings need to be informative and descriptive, and concise and scannable
 *   to allow content sections to be easily distinguishable. It's good practice
 *   to have headings that use simple, explicit language and stick to a single
 *   sentence.
 * - Write headings in sentence case (capitalise the first word and proper nouns
 *   only).
 */
const Heading = ({
  alignment = 'leftAlign',
  children,
  color = 'defaultColor',
  fontFamily = 'default',
  hasTightLineHeight = false,
  id,
  level = 2,
  size = 'medium'
}) => {
  const Element = `h${level}`;
  const className = `${size}${postfixMap[fontFamily]}`;
  return /*#__PURE__*/React.createElement(Element, {
    className: classnames(styles$h[className], {
      [styles$h[alignment]]: alignment,
      [styles$h[color]]: color,
      [styles$h.hasTightLineHeight]: hasTightLineHeight
    }),
    id: id
  }, children);
};

var styles$i = {"isDialogOpen":"Dialog-module_isDialogOpen__hBbyl","overlay":"Dialog-module_overlay__6zjBZ","isAfterOpen":"Dialog-module_isAfterOpen__x-SZC","grid":"Dialog-module_grid__Qdowr","isDrawer":"Dialog-module_isDrawer__GiV2P","dialog":"Dialog-module_dialog__gyl5p","hasFooter":"Dialog-module_hasFooter__aLE4S","closeButton":"Dialog-module_closeButton__-Z3o-","container":"Dialog-module_container__cuyNB","hasNoSidePadding":"Dialog-module_hasNoSidePadding__pVWBQ","header":"Dialog-module_header__bdJRp","content":"Dialog-module_content__eXA1D","footer":"Dialog-module_footer__uRLHM","secondaryActionWrapper":"Dialog-module_secondaryActionWrapper__xCXJy"};

const DialogHeadingIdContext = /*#__PURE__*/createContext('');
const useDialogHeadingId = () => useContext(DialogHeadingIdContext);

// https://github.com/reactjs/react-modal/issues/960
const ModalSafeForReact18 = ReactModal;
const DEFAULT_HEADING = 'Menu';
// NOTE: Exported for testing purposes
const DialogContainer = ({
  containerId,
  children,
  className,
  element: Element = 'div'
}) => ( /*#__PURE__*/React.createElement(Element, {
  className: styles$i.container,
  id: containerId
}, /*#__PURE__*/React.createElement("div", {
  className: styles$i[className]
}, children)));
// The code up to the "END: `setAppElement`" comment is related to applying
// `setAppElement`:
// http://reactcommunity.org/react-modal/accessibility/#app-element in an SSR
// compatible way.
let hasSetAppElement = false;
const setAppElement = id => {
  if (hasSetAppElement) {
    return;
  }
  hasSetAppElement = true;
  if (document.getElementById(id) !== null) {
    ReactModal.setAppElement(`#${id}`);
  }
};
// END: `setAppElement`
/**
 * The Dialog component gets used to create an overlay on top of the primary
 * User Interface, rendering the content underneath inert.
 *
 * ## Usage Guidelines
 *
 * - Dialogs are disruptive as they overlay the primary User Interface
 *   preventing any user interaction with the underlying content. Therefore,
 *   dialogs should primarily get used for displaying critical information or
 *   for requesting user input when handling it within the primary User
 *   Interface is not suitable. Additionally, only display content in a dialog
 *   that is directly related to the current workflow.
 * - A "drawer" dialog is available via the `isDrawer` prop. As the name
 *   suggests, a drawer dialog slides in from the right edge of the screen and
 *   typically get reserved for navigation menus.
 * - When using a form within a dialog, two critical steps are to label the form
 *   and the dialog's CTA. The labelling takes care of two things:
 *      1. Provides an accessible name for the form using the dialog's
 *         heading—see the Form component's Usage Guidelines for more
 *         information on how form labelling works. The dialog's heading id must
 *         get assigned to the Form component's `label.id` prop, which is
 *         available via the `useDialogHeadingId`
 *         [Hook](https://reactjs.org/docs/hooks-intro.html).
 *      2. Associates the dialog's CTA to the form. The association is necessary
 *         as the form does not contain the CTA. It gets achieved via the Dialog
 *         component's `formId` prop, which gets its value from the Form
 *         component's `id` prop. Without the association, the dialog's CTA is
 *         unaware of the form and thus cannot submit it. Putting this all
 *         together, it looks like:
 *
    ```js
    import { Dialog, Form, useDialogHeadingId } from '@madpaws/design-system';

    const FORM_ID = 'formId';

    const DialogWrapper = () => (
      <Dialog formId={FORM_ID}>
        <ComponentWithForm />
      </Dialog>
    );

    const ComponentWithForm = () => {
      const dialogHeadingId = useDialogHeadingId();

      return (
        <Form id={FORM_ID} label={{ id: dialogHeadingId }}>
          // JSX
        </Form>
      );
    }
    ```
 * - Nested dialogs are not allowed. I.e., a dialog cannot get launched from
 *   within another dialog as there should only be one dialog displayed on the
 *   screen at a time.
 * - Dialogs should always be user-initiated, i.e., as a result of a user's
 *   action, instead of being system-generated, i.e., launched automatically.
 * - A dialog's heading should be clear and concise where the purpose of a
 *   dialog is immediately apparent to the user. Additionally, each dialog gets
 *   labelled via the `aria-labelledby` property to help convey the dialog's
 *   meaning to screen reader users; the dialog's heading gets used for this.
 * - Dialogs should ideally include a Call to Action (CTA) provided by the `cta`
 *   prop. The CTA gets displayed in a dialog's footer area, and if nothing gets
 *   provided to the `cta` prop, the footer area does not display.
 */
const Dialog = ({
  children,
  cta,
  domContainerId,
  formId,
  heading,
  isDrawer = false,
  isOpen,
  onAfterClose,
  onAfterOpen,
  onRequestClose,
  secondaryAction,
  hasNoSidePadding = false
}) => {
  const isMediumFromViewport = useMediumFromMediaQuery();
  const [isAfterOpen, setIsAfterOpen] = useState(false);
  // The code up to the "END: `setAppElement`" comment is related to applying
  // `setAppElement`:
  // http://reactcommunity.org/react-modal/accessibility/#app-element in an SSR
  // compatible way.
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    setAppElement(domContainerId);
  }, [domContainerId]);
  if (!isMounted.current) {
    return null;
  }
  // END: `setAppElement`
  // The following two "handle" functions get around react-modal's CSS
  // Transitions API not working (see here:
  // http://reactcommunity.org/react-modal/styles/transitions/ and here:
  // https://github.com/reactjs/react-modal/issues/846)
  const handleAfterOpen = () => {
    if (onAfterOpen) {
      onAfterOpen();
    }
    setTimeout(() => {
      setIsAfterOpen(true);
    }, 0);
  };
  const handleClose = () => {
    setIsAfterOpen(false);
    onRequestClose();
  };
  const headingId = createDialogHeadingId(heading ? heading : DEFAULT_HEADING);
  const hasFooter = cta;
  const ctaLabel = cta !== undefined ? cta.label : '';
  const ctaOnClick = cta !== undefined ? cta.onClick : () => {};
  const ctaIcon = cta !== undefined ? cta.iconLeading : undefined;
  const ctaDisabled = cta !== undefined ? cta.isDisabled : false;
  const ctaSize = cta !== undefined ? cta.size : 'default';
  const saLabel = secondaryAction !== undefined ? secondaryAction.label : '';
  const saOnClick = secondaryAction !== undefined ? secondaryAction.onClick : () => {};
  const saSize = secondaryAction !== undefined ? secondaryAction.size : cta === null || cta === void 0 ? void 0 : cta.size;
  const shouldCloseDialogAfterClick = secondaryAction !== undefined ? secondaryAction.shouldCloseDialogAfterClick : false;
  const handleSaClick = event => {
    saOnClick(event);
    if (shouldCloseDialogAfterClick) {
      onRequestClose();
    }
  };
  return /*#__PURE__*/React.createElement(ModalSafeForReact18, {
    aria: {
      labelledby: headingId
    },
    className: classnames(styles$i.dialog, {
      [styles$i.hasFooter]: hasFooter
    }),
    // NOTE: This value has to be in sync with the CSS Module's
    // `transition-duration` value that gets applied to the `.dialog`
    // selector.
    closeTimeoutMS: 225,
    data: {
      'faux-role': "dialog" /* DIALOG */
    },
    htmlOpenClassName: styles$i.isDialogOpen,
    id: DIALOG_CONTAINER_ID,
    isOpen: isOpen,
    onAfterClose: onAfterClose,
    onAfterOpen: handleAfterOpen,
    onRequestClose: handleClose,
    overlayClassName: classnames(styles$i.overlay, {
      [styles$i.isDrawer]: isDrawer,
      [styles$i.isAfterOpen]: isAfterOpen,
      [styles$i.hasNoSidePadding]: hasNoSidePadding
    }),
    overlayElement: (props, contentElement) => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement("div", Object.assign({}, props), /*#__PURE__*/React.createElement("div", {
      className: styles$i.grid
    }, contentElement)))
  }, /*#__PURE__*/React.createElement(DialogContainer, {
    className: "header",
    element: "header"
  }, /*#__PURE__*/React.createElement(LayoutGridContainer, null, /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: isDrawer ? [1, 11] : [3, 11]
  }, heading && ( /*#__PURE__*/React.createElement(Heading, {
    alignment: "centerAlign",
    id: headingId,
    size: isMediumFromViewport ? 'small2x' : 'small3x'
  }, heading)), /*#__PURE__*/React.createElement("div", {
    className: styles$i.closeButton
  }, /*#__PURE__*/React.createElement(IconButton, {
    label: "Close dialog window by clicking this button or by pressing the escape key.",
    onClick: handleClose
  }, /*#__PURE__*/React.createElement(IconClose, null)))))), /*#__PURE__*/React.createElement(DialogContainer, {
    className: "content",
    containerId: DIALOG_CONTENT_ID
  }, /*#__PURE__*/React.createElement(DialogHeadingIdContext.Provider, {
    value: headingId
  }, children)), hasFooter && ( /*#__PURE__*/React.createElement(DialogContainer, {
    className: "footer",
    element: "footer"
  }, !!secondaryAction && ( /*#__PURE__*/React.createElement("div", {
    className: styles$i.secondaryActionWrapper
  }, /*#__PURE__*/React.createElement(Button, {
    label: saLabel,
    onClick: handleSaClick,
    size: saSize,
    variant: "tertiary"
  }))), /*#__PURE__*/React.createElement(Button, {
    associatedForm: formId,
    iconLeading: ctaIcon,
    isDisabled: ctaDisabled,
    isFullBleed: !secondaryAction,
    isSubmit: !!formId,
    label: ctaLabel,
    onClick: ctaOnClick,
    size: ctaSize
  }))));
};

var styles$j = {"root":"CommonPlatformHeaderMenu-module_root__ndgO4"};

var styles$k = {"root":"Divider-module_root__IT-0I"};

/**
 * The Divider component is most often displayed as a horizontal rule
 * that is used to separate content.
 */
const Divider = () => /*#__PURE__*/React.createElement("div", {
  className: styles$k.root
});

var styles$l = {"root":"TappableLinkContainer-module_root__YFEw2","isBlock":"TappableLinkContainer-module_isBlock__iNIr-"};

/**
 * The TappableLinkContainer component gets used to provide an adequate tap
 * target to a text link.
 *
 * ## Usage Guidelines
 *
 * - An adequate tap target size is a 48x48 pixel area, which is about the size
 *   of a person's finger pad area; this component ensures that.
 * - The main exception for when an adequate tap target need not apply to a text
 *   link is when links are in a sentence or a block of text (see the [Looking
 *   at WCAG 2.5.5 for Better Target
 *   Sizes](https://css-tricks.com/looking-at-wcag-2-5-5-for-better-target-sizes/)
 *   article for more information).
 * - In the case of a list of links, it's essential that the spacing between
 *   links meets the expected value, which can get applied via the
 *   `tokenSpacingInteractionTapTarget` token.
 * - As part of [Google
 *   Lighthouse's](https://developers.google.com/web/tools/lighthouse)
 *   mobile-friendliness audit, all links on a page get checked to ensure they
 *   have the standard tap target size. If your page is failing this check,
 *   apply the TappableLinkContainer component.
 * - The TappableLinkContainer component can get applied to the following
 *   components:
 *    - ButtonLink
 */
const TappableLinkContainer = ({
  children,
  isBlock = false
}) => {
  const Element = isBlock ? 'div' : 'span';
  return /*#__PURE__*/React.createElement(Element, {
    className: classnames(styles$l.root, {
      [styles$l.isBlock]: isBlock
    })
  }, children);
};

var styles$m = {"root":"CommonPlatformHeaderMenuLinkItem-module_root__OBg-y","indicator":"CommonPlatformHeaderMenuLinkItem-module_indicator__iwWyW","indicatorPill":"CommonPlatformHeaderMenuLinkItem-module_indicatorPill__CFxjf","divider":"CommonPlatformHeaderMenuLinkItem-module_divider__Upn6p","isBold":"CommonPlatformHeaderMenuLinkItem-module_isBold__RUT8Q"};

const CommonPlatformHeaderMenuLinkItem = ({
  hasDivider = false,
  label,
  href,
  onClick,
  icon,
  indicatorValue,
  isLargeFromViewport
}) => {
  const InteractiveElement = href ? 'a' : 'button';
  return /*#__PURE__*/React.createElement("li", {
    className: classnames(styles$m.root, {
      [styles$m.isBold]: !isLargeFromViewport && icon
    })
  }, /*#__PURE__*/React.createElement(TappableLinkContainer, {
    isBlock: true
  }, /*#__PURE__*/React.createElement(InteractiveElement, {
    href: href,
    onClick: onClick,
    type: !href ? 'button' : undefined
  }, !isLargeFromViewport && icon, label, indicatorValue && ( /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$m.indicator, {
      [styles$m.indicatorPill]: indicatorValue > 9
    })
  }, indicatorValue, /*#__PURE__*/React.createElement(VisuallyHidden, {
    isInline: true
  }, "unread"))))), hasDivider && ( /*#__PURE__*/React.createElement("div", {
    className: styles$m.divider
  }, /*#__PURE__*/React.createElement(Divider, null))));
};

// determine if the link should be displayed based on the viewport
const isDisplayed = (isMediumFromViewport, viewport) => {
  if (isMediumFromViewport && viewport.includes("desktop" /* DESKTOP */)) {
    return true;
  }
  if (!isMediumFromViewport && viewport.includes("mobile" /* MOBILE */)) {
    return true;
  }
  return false;
};

const CommonPlatformHeaderMenuLinks = ({
  menuLinks,
  hasDivider,
  isLargeFromViewport
}) => {
  if (!menuLinks || menuLinks.length === 0) {
    return null;
  }
  const menuLinksToDisplay = menuLinks.filter(({
    viewport
  }) => isDisplayed(isLargeFromViewport, viewport));
  return /*#__PURE__*/React.createElement(React.Fragment, null, menuLinksToDisplay.map(({
    href,
    label,
    onClick,
    icon,
    indicatorValue
  }, index) => ( /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinkItem, {
    hasDivider: hasDivider && index === menuLinksToDisplay.length - 1,
    href: href,
    icon: icon,
    indicatorValue: indicatorValue,
    isLargeFromViewport: isLargeFromViewport,
    key: label,
    label: label,
    onClick: onClick
  }))));
};

const CommonPlatformHeaderMenuSignUpLoginLinks = ({
  loginLink,
  signUpLink,
  isLargeFromViewport
}) => {
  if (!signUpLink || !loginLink) {
    return null;
  }
  const {
    href: signUpHref,
    label: signUpLabel,
    onClick: handleSignUpClick
  } = signUpLink;
  const {
    href: loginHref,
    label: loginLabel,
    onClick: handleLoginClick
  } = loginLink;
  return /*#__PURE__*/React.createElement(React.Fragment, null, signUpLink && ( /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinkItem, {
    href: signUpHref,
    icon: /*#__PURE__*/React.createElement(IconAccount, null),
    isLargeFromViewport: isLargeFromViewport,
    label: signUpLabel ? signUpLabel : "Sign up" /* SignUp */,
    onClick: handleSignUpClick
  })), loginLink && ( /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinkItem, {
    hasDivider: true,
    href: loginHref,
    icon: /*#__PURE__*/React.createElement(IconLogin, null),
    isLargeFromViewport: isLargeFromViewport,
    label: loginLabel ? loginLabel : "Log in" /* LogIn */,
    onClick: handleLoginClick
  })));
};

var styles$n = {"root":"CommonPlatformHeaderMenuUserCard-module_root__FBJUj","title":"CommonPlatformHeaderMenuUserCard-module_title__IxH-p","subtitle":"CommonPlatformHeaderMenuUserCard-module_subtitle__dPM45","link":"CommonPlatformHeaderMenuUserCard-module_link__m6A3D"};

const CommonPlatformHeaderMenuUserCard = ({
  userDetails
}) => {
  if (!userDetails) {
    return null;
  }
  const {
    avatar,
    name,
    sitterUrl,
    onClick
  } = userDetails;
  return /*#__PURE__*/React.createElement("li", {
    className: classnames(styles$n.root, {
      [styles$n.link]: sitterUrl
    }),
    key: name
  }, /*#__PURE__*/React.createElement("a", {
    href: sitterUrl,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Avatar, {
    alt: `Image of ${name}`,
    size: "medium",
    src: avatar
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles$n.title
  }, name), sitterUrl && /*#__PURE__*/React.createElement("div", {
    className: styles$n.subtitle
  }, "View public profile"))));
};

const CommonPlatformHeaderListContainer = ({
  children,
  className,
  label = 'Primary'
}) => ( /*#__PURE__*/React.createElement("nav", {
  "aria-label": label,
  className: className
}, /*#__PURE__*/React.createElement("ul", {
  role: "list"
}, children)));

const PlatformHeaderMenu = ({
  domContainerId,
  handleMenuClose,
  loginLink,
  signUpLink,
  isMenuOpen = false,
  onLogOutClick,
  primaryMenuLinks,
  secondaryMenuLinks,
  userDetails
}) => {
  const isLargeFromViewport = useLargeFromMediaQuery();
  const renderLogout = () => {
    if (!userDetails) {
      return null;
    }
    return /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinkItem, {
      isLargeFromViewport: isLargeFromViewport,
      key: "Log out" /* LogOut */,
      label: "Log out" /* LogOut */,
      onClick: onLogOutClick
    });
  };
  return /*#__PURE__*/React.createElement(Dialog, {
    domContainerId: domContainerId,
    hasNoSidePadding: true,
    isDrawer: true,
    isOpen: isMenuOpen,
    onRequestClose: handleMenuClose
  }, /*#__PURE__*/React.createElement(CommonPlatformHeaderListContainer, {
    className: styles$j.root,
    label: "Menu"
  }, /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuUserCard, {
    userDetails: userDetails
  }), /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuSignUpLoginLinks, {
    isLargeFromViewport: isLargeFromViewport,
    loginLink: loginLink,
    signUpLink: signUpLink
  }), /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinks, {
    hasDivider: true,
    isLargeFromViewport: isLargeFromViewport,
    menuLinks: primaryMenuLinks
  }), secondaryMenuLinks === null || secondaryMenuLinks === void 0 ? void 0 : secondaryMenuLinks.map((menuLinks, index) => ( /*#__PURE__*/React.createElement(CommonPlatformHeaderMenuLinks, {
    hasDivider: !!userDetails || index < secondaryMenuLinks.length - 1,
    isLargeFromViewport: isLargeFromViewport,
    key: index,
    menuLinks: menuLinks
  }))), renderLogout()));
};

var styles$o = {"root":"CommonPlatformHeaderMenuButton-module_root__0VwFT"};

const PlatformHeaderMenuButton = ({
  handleMenuOpen,
  userDetails,
  isLargeFromViewport
}) => {
  if (isLargeFromViewport && !userDetails) {
    return null;
  }
  // display the user's avatar if the viewport is web view
  // otherwise display the menu icon
  if (isLargeFromViewport && userDetails) {
    return /*#__PURE__*/React.createElement("div", {
      className: styles$o.root
    }, /*#__PURE__*/React.createElement(Avatar, {
      alt: `Image of ${userDetails.name}`,
      onClick: handleMenuOpen,
      size: "medium",
      src: userDetails.avatar
    }));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: styles$o.root
  }, /*#__PURE__*/React.createElement(IconButton, {
    label: "Open menu",
    onClick: handleMenuOpen
  }, /*#__PURE__*/React.createElement(IconMenu, null)));
};

var styles$p = {"root":"CommonPlatformHeaderLeftNavigationLinks-module_root__txV-k"};

var styles$q = {"root":"CommonPlatformHeaderNavigationLinkItem-module_root__YZGZJ"};

const CommonPlatformHeaderLinkItem = ({
  label,
  icon,
  isLabelHidden = false,
  href,
  onClick,
  target = '_self'
}) => ( /*#__PURE__*/React.createElement(TappableLinkContainer, null, /*#__PURE__*/React.createElement("a", {
  "aria-label": label,
  className: styles$q.root,
  href: href,
  onClick: onClick,
  target: target
}, icon, !isLabelHidden ? label : undefined)));

const CommonPlatformHeaderLeftNavigationLinks = ({
  navigationLinks
}) => {
  if (!navigationLinks || navigationLinks.length === 0) {
    return /*#__PURE__*/React.createElement("div", null);
  }
  return /*#__PURE__*/React.createElement("ul", {
    className: styles$p.root
  }, navigationLinks.map(({
    label,
    href,
    icon,
    onClick,
    target
  }) => ( /*#__PURE__*/React.createElement("li", {
    key: label
  }, /*#__PURE__*/React.createElement(CommonPlatformHeaderLinkItem, {
    href: href,
    icon: icon,
    label: label,
    onClick: onClick,
    target: target
  })))));
};

var styles$r = {"root":"CommonPlatformHeaderNavigation-module_root__eE7PR"};

var styles$s = {"root":"CommonPlatformHeaderNavigationIconLinks-module_root__cgr7q","indicatorNotifier":"CommonPlatformHeaderNavigationIconLinks-module_indicatorNotifier__2SRbW"};

const CommonPlatformHeaderNavigationIconLinks = ({
  iconLinks,
  isLargeFromViewport
}) => {
  if (!iconLinks) {
    return null;
  }
  const iconLinksToDisplay = iconLinks.filter(({
    viewport
  }) => isDisplayed(isLargeFromViewport, viewport));
  return /*#__PURE__*/React.createElement(React.Fragment, null, iconLinksToDisplay.map(({
    href,
    label,
    icon,
    onClick,
    indicatorValue
  }) => ( /*#__PURE__*/React.createElement("li", {
    className: styles$s.root,
    key: label
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames({
      [styles$s.indicatorNotifier]: !!indicatorValue
    })
  }, /*#__PURE__*/React.createElement(CommonPlatformHeaderLinkItem, {
    href: href,
    icon: icon,
    isLabelHidden: true,
    label: label,
    onClick: onClick
  }))))));
};

const CommonPlatformHeaderNavigationSignUpLoginLinks = ({
  logInLink,
  signUpLink
}) => {
  const handleLoginClick = event => {
    if (logInLink !== null && logInLink !== void 0 && logInLink.onClick) {
      logInLink.onClick(event);
    }
  };
  const handleSignUpClick = event => {
    if (signUpLink !== null && signUpLink !== void 0 && signUpLink.onClick) {
      signUpLink.onClick(event);
    }
  };
  if (!logInLink && !signUpLink) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, logInLink && ( /*#__PURE__*/React.createElement("li", {
    key: logInLink.label ? logInLink.label : "Log in" /* LogIn */
  }, /*#__PURE__*/React.createElement(Button, {
    label: logInLink.label ? logInLink.label : "Log in" /* LogIn */,
    onClick: handleLoginClick,
    variant: "secondary"
  }))), signUpLink && ( /*#__PURE__*/React.createElement("li", {
    key: signUpLink.label ? signUpLink.label : "Sign up" /* SignUp */
  }, /*#__PURE__*/React.createElement(Button, {
    label: signUpLink.label ? signUpLink.label : "Sign up" /* SignUp */,
    onClick: handleSignUpClick,
    variant: "primary"
  }))));
};

var styles$t = {"root":"CommonPlatformHeaderRightNavigationLinks-module_root__N1FIR"};

const CommonPlatformHeaderRightNavigationLinks = ({
  navigationLinks
}) => ( /*#__PURE__*/React.createElement("ul", null, navigationLinks.map(({
  label,
  href,
  onClick
}) => ( /*#__PURE__*/React.createElement("li", {
  className: styles$t.root,
  key: label
}, /*#__PURE__*/React.createElement(CommonPlatformHeaderLinkItem, {
  href: href,
  label: label,
  onClick: onClick
}))))));

const PlatformHeaderNavigation = ({
  leftNavigationLinks,
  iconLinks,
  logInLink,
  rightNavigationLinks,
  signUpLink,
  isLargeFromViewport
}) => ( /*#__PURE__*/React.createElement(CommonPlatformHeaderListContainer, {
  className: styles$r.root
}, isLargeFromViewport && ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CommonPlatformHeaderLeftNavigationLinks, {
  navigationLinks: leftNavigationLinks
}), /*#__PURE__*/React.createElement(CommonPlatformHeaderRightNavigationLinks, {
  navigationLinks: rightNavigationLinks
}))), /*#__PURE__*/React.createElement(CommonPlatformHeaderNavigationIconLinks, {
  iconLinks: iconLinks,
  isLargeFromViewport: isLargeFromViewport
}), isLargeFromViewport && ( /*#__PURE__*/React.createElement(CommonPlatformHeaderNavigationSignUpLoginLinks, {
  logInLink: logInLink,
  signUpLink: signUpLink
}))));

var styles$u = {"root":"CommonPlatformHeaderSubNavigation-module_root__Mp37p"};

var styles$v = {"root":"CommonPlatformHeaderSubNavigationLink-module_root__oYuJJ","anchor":"CommonPlatformHeaderSubNavigationLink-module_anchor__cm3nB","isActive":"CommonPlatformHeaderSubNavigationLink-module_isActive__zjudR"};

/**
 * The CommonPlatformHeaderSubNavigationLink component is used
 * to display a secondary navigation links on the PlatformHeader.
 */
const CommonPlatformHeaderSubNavigationLink = ({
  isActive = false,
  link,
  onClick,
  title
}) => ( /*#__PURE__*/React.createElement("li", {
  className: classnames(styles$v.root, {
    [styles$v.isActive]: isActive
  })
}, /*#__PURE__*/React.createElement("a", {
  className: styles$v.anchor,
  href: link,
  onClick: onClick
}, title)));

const PlatformHeaderSubNavigation = ({
  secondaryNavigationLinks,
  isLargeFromViewport
}) => {
  if (!isLargeFromViewport || !secondaryNavigationLinks || secondaryNavigationLinks.length === 0) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(CommonPlatformHeaderListContainer, {
    className: styles$u.root,
    label: "Secondary"
  }, secondaryNavigationLinks.map(({
    href,
    label,
    isActive,
    onClick
  }, index) => ( /*#__PURE__*/React.createElement(CommonPlatformHeaderSubNavigationLink, {
    isActive: isActive,
    key: index,
    link: href,
    onClick: onClick,
    title: label
  })))), /*#__PURE__*/React.createElement(Divider, null));
};

/**
 * The PlatformHeader component gets used for the platform's site header, which
 * gets consumed in the LayoutPlatform component.
 *
 * ## Usage Guidelines
 *
 * - The display order of both the navigation and menu links, see the
 *   `navigationLinks` and `menuLinks` props, respectively, is not controlled by
 *   the PlatformHeader component. The order gets determined by the order
 *   they're passed in, which should comply with the header's hi-fi design.
 * - If one of the navigation links contains a link with the label: "Become a
 *   Sitter", and the user is of type "Sitter", it should not get passed to the
 *   `navigationLinks` prop.
 */
const CommonPlatformHeader = ({
  domContainerId,
  iconLinks,
  leftNavigationLinks,
  logInLink,
  logoFolderPath,
  onLogOutClick,
  onMenuCloseClick,
  onMenuOpenClick,
  primaryMenuLinks,
  rightNavigationLinks,
  secondaryMenuLinks,
  secondaryNavigationLinks,
  signUpLink,
  userDetails
}) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  // NOTE: due to problems on react-v2 when using related components
  // we decided to pass the isLargeFromViewport as a prop to sub-components
  const isLargeFromViewport = useLargeFromMediaQuery();
  const handleMenuOpen = () => {
    if (onMenuOpenClick) {
      onMenuOpenClick();
    }
    setMenuOpen(true);
  };
  const handleMenuClose = () => {
    if (onMenuCloseClick) {
      onMenuCloseClick();
    }
    setMenuOpen(false);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("header", null, /*#__PURE__*/React.createElement("div", {
    className: styles$e.root
  }, /*#__PURE__*/React.createElement(PlatformHeaderLogo, {
    folderPath: logoFolderPath
  }), /*#__PURE__*/React.createElement(PlatformHeaderNavigation, {
    iconLinks: iconLinks,
    isLargeFromViewport: isLargeFromViewport,
    leftNavigationLinks: leftNavigationLinks,
    logInLink: logInLink,
    rightNavigationLinks: rightNavigationLinks,
    signUpLink: signUpLink
  }), /*#__PURE__*/React.createElement(PlatformHeaderMenuButton, {
    handleMenuOpen: handleMenuOpen,
    isLargeFromViewport: isLargeFromViewport,
    userDetails: userDetails
  })), /*#__PURE__*/React.createElement(PlatformHeaderSubNavigation, {
    isLargeFromViewport: isLargeFromViewport,
    secondaryNavigationLinks: secondaryNavigationLinks
  })), /*#__PURE__*/React.createElement(PlatformHeaderMenu, {
    domContainerId: domContainerId,
    handleMenuClose: handleMenuClose,
    isMenuOpen: isMenuOpen,
    loginLink: logInLink,
    navigationLinks: rightNavigationLinks,
    onLogOutClick: onLogOutClick,
    primaryMenuLinks: primaryMenuLinks,
    secondaryMenuLinks: secondaryMenuLinks,
    signUpLink: signUpLink,
    userDetails: userDetails
  }));
};

var styles$w = {"counterGroupContainer":"CounterGroup-module_counterGroupContainer__M8IXY","hasOptionDescription":"CounterGroup-module_hasOptionDescription__cxif-","optionDescription":"CounterGroup-module_optionDescription__ZpEIC","optionLabel":"CounterGroup-module_optionLabel__-YFcs","counterValue":"CounterGroup-module_counterValue__dkjJF","isDisabled":"CounterGroup-module_isDisabled__myk5b"};

/**
 * The CounterGroup component is a form field component that provides a list of
 * countable options.
 *
 * ## Usage Guidelines
 *
 * - A counter group gets compromised of at least one counter item where a user
 *   can interact with at a time and the onChange event will return an object of
 *   the name and value of the counter item.
 * - The CounterGroup component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning a counter's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For initializing the value
 *   of a counter, the default value of `useState` needs to match corresponding
 *   counter's `value` prop: `options[x].value`. E.g., if the object item of the
 *   initial option:

 *  ```js
 *  {
 *    label: 'Dog Walking',
 *    name: 'dogWalking',
 *    value: 10,
 *  }
 *  ```
 *
 *   Then the default value of `useState` would be:
 *  `useState([{
 *    label: 'Dog Walking',
 *    name: 'dogWalking',
 *    value: 10,
 *  }])`.
 */
const CounterGroup = ({
  isDisabled = false,
  isLegendVisuallyHidden = false,
  legend,
  onChange,
  options
}) => ( /*#__PURE__*/React.createElement(Fieldset, {
  isGroupField: true,
  isLegendVisuallyHidden: isLegendVisuallyHidden,
  legend: legend
}, options.map(({
  value: optionValue = 0,
  label: optionLabel,
  isDisabled: optionIsDisabled = false,
  maxValue: optionMaxValue = 20,
  minValue: optionMinValue = 0,
  description: optionDescription,
  name: optionName
}) => {
  const inputId = createCounterId(optionName);
  const isMinusButtonDisabled = optionValue <= optionMinValue;
  const isPlusButtonDisabled = optionValue >= optionMaxValue;
  const onMinusButtonClicked = () => {
    if (isMinusButtonDisabled) {
      return;
    }
    onChange({
      target: {
        value: {
          name: optionName,
          value: optionValue - 1
        }
      }
    });
  };
  const onPlusButtonClicked = () => {
    if (isPlusButtonDisabled) {
      return;
    }
    onChange({
      target: {
        value: {
          name: optionName,
          value: optionValue + 1
        }
      }
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$w.counterGroupContainer, {
      [styles$w.hasOptionDescription]: optionDescription
    }),
    key: optionName
  }, /*#__PURE__*/React.createElement("span", {
    className: styles$w.optionLabel
  }, optionLabel), /*#__PURE__*/React.createElement(IconButton, {
    isDisabled: isDisabled || optionIsDisabled || isMinusButtonDisabled,
    label: `Minus for ${optionLabel}`,
    onClick: onMinusButtonClicked,
    size: "small2x",
    variant: "secondary"
  }, /*#__PURE__*/React.createElement(IconMinus, {
    size: "small2x"
  })), /*#__PURE__*/React.createElement("label", {
    "aria-live": "polite",
    className: classnames(styles$w.counterValue, {
      [styles$w.isDisabled]: isDisabled || optionIsDisabled
    }),
    htmlFor: inputId
  }, optionValue), /*#__PURE__*/React.createElement(IconButton, {
    isDisabled: isDisabled || optionIsDisabled || isPlusButtonDisabled,
    label: `Plus for ${optionLabel}`,
    onClick: onPlusButtonClicked,
    size: "small2x",
    variant: "secondary"
  }, /*#__PURE__*/React.createElement(IconPlus, {
    size: "small2x"
  })), optionDescription && ( /*#__PURE__*/React.createElement("span", {
    className: styles$w.optionDescription
  }, optionDescription)));
})));

var styles$x = {"root":"DatePickerField-module_root__fuDPW","mergedFirst":"DatePickerField-module_mergedFirst__dTF49","mergedSecond":"DatePickerField-module_mergedSecond__fW5cw"};

var styles$y = {"inputContainer":"TextField-module_inputContainer__azWVB","withIcon":"TextField-module_withIcon__Yal2M","isBorderHidden":"TextField-module_isBorderHidden__t--OL","isActive":"TextField-module_isActive__ivNvz","prefix":"TextField-module_prefix__NM8Vr","currencyWithIcon":"TextField-module_currencyWithIcon__nM33s","prefixGap":"TextField-module_prefixGap__EUGfN","prefixGapWithIcon":"TextField-module_prefixGapWithIcon__X9uVx","currencyWithPrefix":"TextField-module_currencyWithPrefix__xAZkP","currencyWithPrefixAndIcon":"TextField-module_currencyWithPrefixAndIcon__tXPXn","icon":"TextField-module_icon__ZSvhD","clearButton":"TextField-module_clearButton__kCKV-","isDisabled":"TextField-module_isDisabled__9Sc1q"};

/**
 * The TextField component is a form field component that accepts text input no
 * longer than a single line, such as a user's name or phone number.
 *
 * ## Usage Guidelines
 *
 * - A text field is a mandatory form field by default. For optional text
 *   fields, apply the `isOptional` prop.
 * - Setting focus to a text field automatically via the `autoFocus` prop is
 *   appropiate when the whole purpose of the page is to fill out a form. E.g.,
 *   a login page where the main content consists only of a login form.
 *   Conversely, setting autofocus to a text field when the form is not the
 *   primary focus of a page should always be avoided.
 * - Unlike changing a text field's input "type" via the `type` prop, a text
 *   field's inputmode applied via the `inputMode` prop doesn't change how the
 *   browser interprets the input—it instructs the browser which virtual
 *   keyboard to display. In other words, a text field's inputmode is a
 *   **display** concern, whereas a text field's "type" is a **semantic**
 *   concern. Read more about inputmode
 *   [here](https://funwithforms.com/posts/inputmode/), including the
 *   differences between the `inputmode` and `type` attributes.
 * - By default, a text field gets autocomplete capabilities applied via the
 *   `autoComplete` prop. The default value for this component's `autocomplete`
 *   attribute is "on" (see
 *   [here](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#values)
 *   for all possible values). Unless the "off" value gets set, the "tel" and
 *   "url" input types (see the `type` prop) get automatically assigned the
 *   "tel" and "url" autocomplete values, respectively.
 * - There are edge cases when a text field's label should not be visible on the
 *   screen; that is, it's hidden visually, applied via the
 *   `isLabelVisuallyHidden` prop. A primary use case for this treatment is when
 *   form fields do not follow the standard structure. E.g., a form field may
 *   exist within a table where its visible label gets placed in a different
 *   position to the default, which is directly above the input. Therefore, to
 *   make this custom form field layout work, the default label has to disappear
 *   from the screen to make way for the "fake" label. It's a fake label as it's
 *   not connected to the field's input in any way—it's purely visual while the
 *   actual connected label gets hidden visually. Fake labels should get wrapped
 *   in a `div` or `span` element with the `aria-hidden="true"` attribute set;
 *   this ensures they don't get exposed to the [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 * - A text field's placeholder, applied via the `placeholder` prop, gets used
 *   to provide a brief hint as to what kind of information gets expected in the
 *   field. It should be a word or short phrase that hints about the expected
 *   type of data rather than an explanation or prompt. However, placeholders
 *   are best avoided due to their [numerous accessibility and usability
 *   issues](https://www.smashingmagazine.com/2018/06/placeholder-attribute/).
 *   Therefore, the most desirable location for any instructional type text is
 *   within a text field's label applied via the `label` prop; second to that is
 *   a text field's description applied via the `description` prop. The last
 *   resort would be a placeholder, but only if the placeholder text is not
 *   fundamental to completing the field—one of the annoying issues with
 *   placeholders is that they disappear when the focus gets set to the input
 *   resulting in recall issues for many users.
 * - The TextField component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the input's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting a value,
 *   the default value of `useState` needs to match the corresponding `value`
 *   prop. E.g., if a text field should have a default value of "Bunny rabbit",
 *   the default value of `useState` should be: `useState('Bunny rabbit')`.
 * - Text fields support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `type`
 *   - `pattern`
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *   - `minLength`
 *   - `maxLength`
 *
 *  As text fields are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  text fields getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - If additional context is required for a text field, then a description can
 *   get used via the `description` prop. The description provides context for
 *   the entire field and gets displayed underneath the input.
 */
// eslint-disable-next-line react/display-name
const TextField = /*#__PURE__*/forwardRef(({
  autoComplete = 'on',
  description,
  hasAutoFocus = false,
  hasSpellCheck = false,
  hasTighterSpacing,
  icon,
  inputMode = 'text',
  isBorderHidden = false,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isOptional = false,
  isReadOnly = false,
  label,
  maxLength,
  minLength,
  asInteger = false,
  prefix,
  currencySymbol,
  name,
  onBlur,
  maxValue,
  minValue,
  onChange,
  onClear,
  onClick,
  onFocus,
  pattern,
  placeholder,
  type = 'text',
  value,
  isTopDescription = false,
  mask
}, forwardedRef) => {
  const createId = id => createTextFieldId(`${name}${id ? ` ${id}` : ''}`);
  const inputId = createId();
  const descriptionId = createId("description" /* FORM_FIELD_DESCRIPTION */);
  const ariaDescribedBy = description ? formatMultipleAriaDescribedBy([descriptionId]) : undefined;
  const isTypeCurrency = type === 'currency';
  const typeCurrencyFieldHasValue = (value === null || value === void 0 ? void 0 : value.length) <= 0 && isTypeCurrency;
  const shouldMask = !!mask && type === 'tel';
  const [showPassword, setShowPassword] = React.useState(false);
  const renderLabel = () => ( /*#__PURE__*/React.createElement(FieldLabel, {
    hasTighterSpacing: hasTighterSpacing,
    id: inputId,
    isDisabled: isDisabled,
    isLabelVisuallyHidden: isLabelVisuallyHidden,
    isOptional: isOptional,
    label: label
  }));
  const typeToAutoCompleteMap = {
    email: autoComplete,
    number: autoComplete,
    password: autoComplete,
    search: autoComplete,
    tel: 'tel',
    text: autoComplete,
    url: 'url',
    currency: 'off'
  };
  const formatCurrencyOnBlur = event => {
    var _event$target;
    if (!((_event$target = event.target) !== null && _event$target !== void 0 && _event$target.value)) {
      return;
    }
    const formattedValue = asInteger ? Math.floor(parseInt(event.target.value, 10)).toString() : parseFloat(event.target.value).toFixed(2);
    // eslint-disable-next-line no-param-reassign
    event.target.value = formattedValue;
    onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
  };
  const handleKeyPress = event => {
    if (!isTypeCurrency) {
      return;
    }
    if (isTypeCurrency && asInteger && event.key === '.' || event.key === ',') {
      event.preventDefault();
    }
  };
  const renderDefaultDescription = () => {
    if (!description || !isTopDescription) {
      return null;
    }
    return /*#__PURE__*/React.createElement(FieldDescription, {
      id: descriptionId,
      isTextField: true,
      isTopDescription: true
    }, description);
  };
  const renderBottomDescription = () => {
    if (description && !isTopDescription) {
      return /*#__PURE__*/React.createElement(FieldDescription, {
        id: descriptionId,
        isTextField: true
      }, description);
    }
    return null;
  };
  const handleOnChange = event => {
    let finalEvent = event;
    // in case of masked input we need to tweak event a bit
    if (shouldMask) {
      finalEvent = processMaskedInputEvent(value, event, mask);
    }
    // This check required to keep backward compatibility
    // with scenarios when native HTML validation and
    // form submission are used
    if (onChange) {
      onChange(finalEvent);
    }
  };
  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };
  return /*#__PURE__*/React.createElement("div", {
    "data-faux-role": "formField" /* FORM_FIELD */
  }, isLabelVisuallyHidden ? /*#__PURE__*/React.createElement(VisuallyHidden, null, renderLabel()) : renderLabel(), renderDefaultDescription(), /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$y.inputContainer, {
      [styles$y.withIcon]: isTypeCurrency || icon || type === 'password'
    })
  }, icon && ( /*#__PURE__*/React.createElement("span", {
    className: classnames(styles$y.icon, {
      [styles$y.isDisabled]: isDisabled
    })
  }, /*#__PURE__*/cloneElement(icon, {
    size: 'medium'
  }))), (isTypeCurrency || prefix) && ( /*#__PURE__*/React.createElement("span", {
    className: classnames(styles$y.prefix, {
      [styles$y.isActive]: typeCurrencyFieldHasValue,
      [styles$y.currencyWithIcon]: isTypeCurrency && currencySymbol && icon && !prefix,
      [styles$y.currencyWithPrefix]: isTypeCurrency && prefix && !icon,
      [styles$y.currencyWithPrefixAndIcon]: isTypeCurrency && prefix && icon
    })
  }, /*#__PURE__*/React.createElement("span", {
    className: classnames({
      [styles$y.prefixGap]: currencySymbol && prefix,
      [styles$y.prefixGapWithIcon]: currencySymbol && prefix && icon
    })
  }, prefix), /*#__PURE__*/React.createElement("span", null, currencySymbol))), /*#__PURE__*/React.createElement("input", {
    "aria-describedby": ariaDescribedBy,
    autoComplete: autoComplete === 'off' ? 'off' : typeToAutoCompleteMap[type],
    autoFocus: hasAutoFocus,
    className: classnames({
      [styles$y.isBorderHidden]: isBorderHidden
    }),
    disabled: isDisabled,
    id: inputId,
    inputMode: inputMode,
    max: maxValue,
    maxLength: maxLength,
    min: minValue,
    minLength: minLength,
    name: name,
    onBlur: isTypeCurrency ? formatCurrencyOnBlur : onBlur,
    onChange: handleOnChange,
    onClick: onClick,
    onFocus: onFocus,
    onKeyPress: handleKeyPress,
    pattern: pattern,
    placeholder: placeholder,
    readOnly: isReadOnly,
    ref: forwardedRef,
    required: !isOptional,
    spellCheck: hasSpellCheck,
    type: isTypeCurrency ? 'number' : type === 'password' && showPassword ? 'text' : type,
    value: value
  }), onClear && !isDisabled && typeof value === 'string' && (value === null || value === void 0 ? void 0 : value.length) > 0 && ( /*#__PURE__*/React.createElement("div", {
    className: styles$y.clearButton
  }, /*#__PURE__*/React.createElement(IconButton, {
    label: "Clear",
    onClick: onClear
  }, /*#__PURE__*/React.createElement(IconClose, null)))), type === 'password' && ( /*#__PURE__*/React.createElement("div", {
    className: styles$y.clearButton
  }, /*#__PURE__*/React.createElement(IconButton, {
    label: "Clear",
    onClick: togglePasswordVisibility,
    size: "small1x"
  }, showPassword ? ( /*#__PURE__*/React.createElement(IconVisibilityHide, {
    size: "medium"
  })) : ( /*#__PURE__*/React.createElement(IconVisibilityShow, {
    size: "medium"
  })))))), renderBottomDescription());
});

/* eslint-disable react/display-name */
const DatePickerField = /*#__PURE__*/React.forwardRef(({
  description,
  hasTighterSpacing,
  isDisabled,
  isLabelVisuallyHidden,
  isOptional,
  isReadOnly,
  label,
  name,
  value,
  onBlur,
  onChange,
  onClick,
  placeholder,
  onFocus
}, ref) => ( /*#__PURE__*/React.createElement(TextField, {
  description: description,
  hasTighterSpacing: hasTighterSpacing,
  icon: /*#__PURE__*/React.createElement(IconCalendar, null),
  isDisabled: isDisabled,
  isLabelVisuallyHidden: isLabelVisuallyHidden,
  isOptional: isOptional,
  isReadOnly: isReadOnly,
  label: label,
  name: name,
  onBlur: onBlur,
  onChange: onChange,
  onClick: onClick,
  onFocus: onFocus,
  placeholder: placeholder,
  ref: ref,
  value: value
})));
const DatePickerFieldWithoutIcon = /*#__PURE__*/React.forwardRef(({
  description,
  hasTighterSpacing,
  isDisabled,
  isLabelVisuallyHidden,
  isOptional,
  isReadOnly,
  label,
  name,
  value,
  onBlur,
  onChange,
  onClick,
  placeholder,
  onFocus
}, ref) => ( /*#__PURE__*/React.createElement(TextField, {
  description: description,
  hasTighterSpacing: hasTighterSpacing,
  isDisabled: isDisabled,
  isLabelVisuallyHidden: isLabelVisuallyHidden,
  isOptional: isOptional,
  isReadOnly: isReadOnly,
  label: label,
  name: name,
  onBlur: onBlur,
  onChange: onChange,
  onClick: onClick,
  onFocus: onFocus,
  placeholder: placeholder,
  ref: ref,
  value: value
})));
const DatePickerFieldMerged = /*#__PURE__*/React.forwardRef(({
  description,
  hasTighterSpacing,
  isDisabled,
  isLabelVisuallyHidden,
  isOptional,
  isReadOnly,
  label,
  name,
  value,
  onBlur,
  onChange,
  onClick,
  placeholder,
  onFocus,
  isSecond = false
}, ref) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$x.root, isSecond ? styles$x.mergedSecond : styles$x.mergedFirst)
}, /*#__PURE__*/React.createElement(TextField, {
  description: description,
  hasTighterSpacing: hasTighterSpacing,
  icon: /*#__PURE__*/React.createElement(IconCalendar, null),
  isBorderHidden: true,
  isDisabled: isDisabled,
  isLabelVisuallyHidden: isLabelVisuallyHidden,
  isOptional: isOptional,
  isReadOnly: isReadOnly,
  label: label,
  name: name,
  onBlur: onBlur,
  onChange: onChange,
  onClick: onClick,
  onFocus: onFocus,
  placeholder: placeholder,
  ref: ref,
  value: value
}))));

var styles$z = {"root":"DatePickerMonthNavigation-module_root__IEUQr"};

const DatePickerMonthNavigation = ({
  onPreviousClick,
  onNextClick,
  showPreviousButton,
  showNextButton,
  labels
}) => ( /*#__PURE__*/React.createElement("div", {
  className: styles$z.root
}, /*#__PURE__*/React.createElement(IconButton, {
  isDisabled: !showPreviousButton,
  label: labels ? labels.previousMonth : '',
  onClick: () => onPreviousClick ? onPreviousClick() : undefined
}, /*#__PURE__*/React.createElement(IconChevronLeft, null)), /*#__PURE__*/React.createElement(IconButton, {
  isDisabled: !showNextButton,
  label: labels ? labels.nextMonth : '',
  onClick: () => onNextClick ? onNextClick() : undefined
}, /*#__PURE__*/React.createElement(IconChevronRight, null))));

const ROOT_CLASS_NAME = 'reactDayPicker';
const RANGE_CLASS_NAME = `${ROOT_CLASS_NAME}--range`;

// NOTE: Exported for re-use across all public date picker components
const inputClasses = {
  container: '',
  overlayWrapper: ROOT_CLASS_NAME,
  overlay: 'overlay',
  isRightAligned: 'isRightAligned'
};
// NOTE: Exported for re-use across all public date picker components
const overlayClasses = {
  container: '',
  wrapper: 'calendar',
  interactionDisabled: '',
  months: '',
  month: 'month',
  navBar: '',
  navButtonPrev: '',
  navButtonNext: '',
  navButtonInteractionDisabled: '',
  caption: 'caption',
  weekdays: '',
  weekdaysRow: 'weekdays',
  weekday: 'weekday',
  body: 'days',
  week: 'week',
  weekNumber: '',
  day: 'day',
  footer: '',
  todayButton: '',
  today: 'today',
  // NOTE: For certain functionality to work, i.e., "deselection of dates", the
  // 'selected' class name should not get changed to something else. There is
  // probably a better way to handle this, e.g., via the "Modifiers" API.
  // However, it can get looked at at a later date.
  selected: 'selected',
  disabled: 'disabled',
  outside: 'outside'
};
const DatePickerOverlay = ({
  classNames,
  children,
  dayPickerRef,
  input,
  month,
  selectedDay,
  isRightAligned = false,
  hasCta = false,
  ...props
}) => {
  const ref = useRef(null);
  const handleKeyDown = useCallback(event => {
    if (event.key === 'Escape') {
      var _dayPickerRef$current;
      (_dayPickerRef$current = dayPickerRef.current) === null || _dayPickerRef$current === void 0 ? void 0 : _dayPickerRef$current.hideDayPicker();
    }
  }, [dayPickerRef]);
  useEffect(() => {
    const checkIfClickedOutside = clicked => {
      if (ref.current && !ref.current.contains(clicked.target)) {
        var _dayPickerRef$current2;
        (_dayPickerRef$current2 = dayPickerRef.current) === null || _dayPickerRef$current2 === void 0 ? void 0 : _dayPickerRef$current2.hideDayPicker();
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  });
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement("div", Object.assign({
      className: classNames.overlayWrapper
    }, props, {
      ref: ref,
      tabIndex: undefined
    }), /*#__PURE__*/React.createElement("div", {
      className: classnames(classNames.overlay, {
        [classNames.isRightAligned]: isRightAligned
      })
    }, children, hasCta && ( /*#__PURE__*/React.createElement("div", {
      className: "cta"
    }, /*#__PURE__*/React.createElement(Button, {
      label: "Done",
      onClick: () => {
        var _dayPickerRef$current3;
        return (_dayPickerRef$current3 = dayPickerRef.current) === null || _dayPickerRef$current3 === void 0 ? void 0 : _dayPickerRef$current3.hideDayPicker();
      }
    })))))
  );
};

const parseDate = date => {
  const parsedDate = dayjs(date, DATE_DATA_FORMAT);
  return parsedDate.isValid() ? parsedDate.toDate() : undefined;
};
const parseValidDates = dates => {
  const parsedDates = [];
  dates.forEach(date => {
    const parsedDate = parseDate(date);
    if (parsedDate) {
      parsedDates.push(parsedDate);
    }
  });
  return parsedDates;
};
const setDisabledOptions = (disabledDates, daysOfWeek) => {
  const disabledOptions = [{
    before: new Date()
  }];
  if (disabledDates) {
    disabledOptions.push(...parseValidDates(disabledDates));
  }
  if (daysOfWeek) {
    const disabledDaysOfWeek = [...Array(7).keys()].filter(x => !daysOfWeek.includes(x));
    disabledOptions.push({
      daysOfWeek: disabledDaysOfWeek
    });
  }
  return disabledOptions;
};

/**
 * The DatePickerSingle component is a form field component that provides a date
 * picker for selecting a single date.
 *
 * ## Usage Guidelines
 *
 * - The DatePickerSingle component should only get used for selecting a single
 *   date. For multiple and range date selections, see the DatePickerMulti and
 *   DatePickerRange components, respectively.
 * - The DatePickerSingle component is a mandatory field by default. For
 *   optional DatePickerSingle fields, apply the `isOptional` prop.
 * - Selecting dates by manual input via the text input is disabled for this
 *   date picker component until validation gets added to ensure the required
 *   date format gets entered. Specifically, the text input gets set to
 *   "read-only", meaning the input's text cannot get changed, but the input can
 *   be tabbed into, highlighted, and its text copied.
 * - To disable dates in the date picker, pass an array of dates to the
 *   `disabledDates` prop. The dates must use the "YYYY-MM-DD" format.
 * - The DatePickerSingle component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the date picker's value gets driven by the React state
 *   (`useState`), indicating a consumer has to set up the component's state.
 *   For preselecting a date, the default value of `useState` needs to match the
 *   corresponding `value` prop: E.g., if this were the value of the preselected
 *   date: `'2021-09-05'`, then the default value of `useState` would be:
 *   `useState('2021-09-05')`
 *
 *   **⚠️ NOTE:** The string (the date) must use the "YYYY-MM-DD" format.
 * - Date picker fields support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *
 *  As date fields are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  date fields getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - If additional context is required for a DatePickerSingle component, then a
 *   description can get used via the description prop. The description provides
 *   context for the entire field and gets displayed underneath the input.
 */
const DatePickerSingle = ({
  daysOfWeek,
  description,
  disabledDates,
  hasTighterSpacing,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isOptional = false,
  isRightAligned = false,
  dateFormat = 'default',
  label,
  name,
  onDateSelection,
  placeholder = '',
  value,
  isCalendarIconHidden = false,
  onDayPickerShow
}) => {
  const inputRef = useRef(null);
  const handleDayChange = day => {
    onDateSelection(dayjs(day).format(DATE_DATA_FORMAT));
  };
  const parsedDate = value ? parseDate(value) : undefined;
  return /*#__PURE__*/React.createElement("div", {
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: isCalendarIconHidden ? DatePickerFieldWithoutIcon : DatePickerField,
    dayPickerProps: {
      locale: 'en',
      disabledDays: setDisabledOptions(disabledDates, daysOfWeek),
      firstDayOfWeek: 1,
      fromMonth: new Date(),
      selectedDays: parsedDate,
      classNames: overlayClasses,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null)
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      description,
      hasTighterSpacing,
      isDisabled,
      isLabelVisuallyHidden,
      isOptional,
      isReadOnly: true,
      label,
      name
    },
    keepFocus: false,
    onDayChange: handleDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow();
      if (parsedDate) {
        var _inputRef$current;
        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.getDayPicker().showMonth(parsedDate);
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputRef,
      isRightAligned: isRightAligned
    }))),
    parseDate: parseDate,
    placeholder: placeholder,
    ref: inputRef,
    value: parsedDate
  }));
};

var styles$A = {"root":"DatePickerMulti-module_root__UYxyB"};

/**
 * The DatePickerMulti component is a form field component that provides a date
 * picker for selecting multiple dates.
 *
 * ## Usage Guidelines
 *
 * - The DatePickerMulti component should only get used for selecting multiple
 *   dates. Once all dates are selected, clicking the "Done" button will close
 *   the date picker and set focus back to the input. For single and range date
 *   selections, see the DatePickerSingle and DatePickerRange components,
 *   respectively.
 * - The DatePickerMulti component is a mandatory field by default. For optional
 *   DatePickerMulti fields, apply the `isOptional` prop.
 * - Selecting dates by manual input via the text input is disabled for this
 *   date picker component due to the complexities of manually inputting
 *   multiple dates into a single input. However, in the future, this UI pattern
 *   will get rethought where manual input is possible. Specifically, the text
 *   input gets set to "read-only", meaning the input's text cannot get changed,
 *   but the input can be tabbed into, highlighted, and its text copied.
 * - To disable dates in the date picker, pass an array of dates to the
 *   `disabledDates` prop. The dates must use the "YYYY-MM-DD" format.
 * - The DatePickerMulti component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the date picker's value gets driven by the React state
 *   (`useState`), indicating a consumer has to set up the component's state.
 *   For preselecting a date, the default value of `useState` needs to match the
 *   `value` prop: E.g., if this were the value of the preselected dates:
 *
      ```js

      ['2021-09-05','2021-09-06','2021-09-07']

      ```
 *
 *   Then the default value of `useState` would be:
 *   `useState(['2021-09-05','2021-09-06','2021-09-07'])`
 *
 *   **⚠️ NOTE:** The `value` prop uses a type that is an array of strings
 *   (`string[]`) and the strings (the dates) must use the "YYYY-MM-DD" format.
 * - Date picker fields support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *
 *  As date fields are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  date fields getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - If additional context is required for the DatePickerMulti component, then a
 *   description can get used via the `description` prop. The description
 *   provides context for the entire field and gets displayed underneath the
 *   input.
 */
const DatePickerMulti = ({
  description,
  disabledDates,
  label,
  hasTighterSpacing,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isOptional = false,
  dateFormat = 'default',
  name,
  onDateSelection,
  placeholder,
  value,
  isCalendarIconHidden = false,
  onDayPickerShow
}) => {
  const inputRef = useRef(null);
  const handleDayChange = (day, {
    selected
  }) => {
    const updatedValue = [...value];
    if (selected) {
      const selectedIndex = updatedValue.findIndex(updatedValueDate => dayjs(updatedValueDate).isSame(day, 'day'));
      updatedValue.splice(selectedIndex, 1);
    } else {
      updatedValue.push(dayjs(day).format(DATE_DATA_FORMAT));
    }
    onDateSelection(updatedValue);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles$A.root,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: isCalendarIconHidden ? DatePickerFieldWithoutIcon : DatePickerField,
    dayPickerProps: {
      classNames: overlayClasses,
      firstDayOfWeek: 1,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      disabledDays: setDisabledOptions(disabledDates),
      fromMonth: new Date(),
      selectedDays: parseValidDates(value)
    },
    hideOnDayClick: false,
    inputProps: {
      description,
      hasTighterSpacing,
      isDisabled,
      isLabelVisuallyHidden,
      isOptional,
      isReadOnly: true,
      name,
      label
    },
    keepFocus: false,
    onDayChange: handleDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow();
      if (value.length > 0) {
        var _inputRef$current;
        (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.getDayPicker().showMonth(dayjs(parseValidDates(value)[parseValidDates(value).length - 1]).toDate());
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputRef,
      hasCta: true
    }))),
    placeholder: placeholder,
    ref: inputRef,
    value: value.map(date => formatDate(date, dateFormat)).join(', ')
  }));
};

/**
 * The DatePickerRange component is a form field component that provides two
 * date pickers for selecting a “from” and “to” date.
 *
 * ## Usage Guidelines
 *
 * - The DatePickerRange component should only get used for selecting a from and
 *   to date. For single and multiple date selections, see the DatePickerSingle
 *   and DatePickerMulti components, respectively.
 * - The DatePickerRange component is a mandatory field by default. For optional
 *   DatePickerRange fields, apply the `isOptional` prop.
 * - Selecting dates by manual input via the text inputs is disabled for this
 *   date picker component until validation gets added to ensure the required
 *   date format gets entered. Specifically, the text input gets set to
 *   "read-only", meaning the input's text cannot get changed, but the input can
 *   be tabbed into, highlighted, and its text copied.
 * - To disable dates in the date picker, pass an array of dates to the
 *   `disabledDates` prop. The dates must use the "YYYY-MM-DD" format.
 * - The “end” date picker field is disabled until the “start” date gets
 *   selected.
 * - The DatePickerRange component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the date picker's value gets driven by the React state
 *   (`useState`), indicating a consumer has to set up the component's state.
 *   For preselecting a range, the default value of `useState` needs to match
 *   the corresponding `value` prop: E.g., if this were the value of the
 *   preselected range:
 *
    ```js
    {
      from: '2021-09-02',
      to: '2021-09-09',
    }
    ```
 *
 *   Then the default value of useState would be: `useState({from:'2021-09-02',
 *   to:'2021-09-08'})`
 *
 *   **⚠️ NOTE:** The `value` prop uses a type that is an object with keys
 *   `from, to` both values of which are strings. The strings (the dates) must
 *   use the "YYYY-MM-DD" format.
 * - Date picker fields support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *
 *  As date fields are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  date fields getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - The DatePickerRange component is wrapped in a fieldset (see the Fieldset
 *   component) as it consists of two fields. Fieldsets, by default, come with a
 *   legend. There are edge cases when a fieldset's legend should not be visible
 *   on the screen; that is, it's hidden visually, applied via the
 *   `isFieldsetLegendVisuallyHidden` prop. A primary use case for this
 *   treatment is for "compact" forms, that is, forms intended to take up as
 *   little space on the screen as possible. Typically compact forms get
 *   associated with the Form component where its `hasTighterSpacing` prop gets
 *   set to "true".
 * - If additional context is required for a DatePickerRange component, then a
 *   description can get used via the description prop. The description provides
 *   context for the entire field and gets displayed underneath the input.
 */
const DatePickerRange = ({
  dateFormat = 'default',
  descriptionTo,
  descriptionFrom,
  disabledDates,
  hasTighterSpacing,
  isDisabled = false,
  isFieldsetLegendVisuallyHidden = false,
  isLabelVisuallyHidden = false,
  isOptional = false,
  isSideBySide = false,
  labelFrom = 'From',
  labelTo = 'To',
  legend,
  nameFrom,
  nameTo,
  onDateSelection,
  placeholderFrom = '',
  placeholderTo = '',
  value,
  isCalendarIconHidden = false,
  onDayPickerShow
}) => {
  const [fromTo, setFromTo] = useState(false);
  const inputToRef = useRef(null);
  const inputFromRef = useRef(null);
  useEffect(() => {
    if (fromTo) {
      var _inputToRef$current;
      (_inputToRef$current = inputToRef.current) === null || _inputToRef$current === void 0 ? void 0 : _inputToRef$current.getInput().click();
    }
  }, [fromTo]);
  const DatePickerFieldElement = isCalendarIconHidden ? DatePickerFieldWithoutIcon : DatePickerField;
  const handleFromDayChange = from => {
    if (value.to && from >= new Date(value.to)) {
      onDateSelection({
        from: dayjs(from).format(DATE_DATA_FORMAT),
        to: dayjs(from).add(1, 'day').format(DATE_DATA_FORMAT)
      });
    } else {
      onDateSelection({
        ...value,
        from: dayjs(from).format(DATE_DATA_FORMAT)
      });
    }
    setFromTo(true);
  };
  const handleToDayChange = to => {
    if (value.from && to && to < new Date(value.from) || value.from && to && to.setHours(0, 0, 0, 0) === new Date(value.from).setHours(0, 0, 0, 0)) {
      onDateSelection({
        from: dayjs(to).format(DATE_DATA_FORMAT),
        to: dayjs(to).add(1, 'day').format(DATE_DATA_FORMAT)
      });
    } else {
      onDateSelection({
        ...value,
        to: dayjs(to).format(DATE_DATA_FORMAT)
      });
    }
    setFromTo(false);
  };
  const setDisabledFromOptions = () => {
    const beforeAfterModifier = {
      after: undefined,
      before: new Date()
    };
    const disabledFromOptions = [beforeAfterModifier];
    if (disabledDates) {
      disabledFromOptions.push(...parseValidDates(disabledDates));
    }
    return disabledFromOptions;
  };
  const setDisabledToOptions = () => {
    const disabledToOptions = [];
    if (value.from) {
      disabledToOptions.push({
        before: new Date()
      });
    }
    if (disabledDates) {
      disabledToOptions.push(...parseValidDates(disabledDates));
    }
    return disabledToOptions;
  };
  const selectedDays = {
    from: value.from ? parseDate(value.from) : undefined,
    to: value.to ? parseDate(value.to) : undefined
  };
  const modifiers = {
    start: value.from ? parseDate(value.from) : undefined,
    end: value.to ? parseDate(value.to) : undefined
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, isSideBySide ? ( /*#__PURE__*/React.createElement(Fieldset, {
    isGroupField: true,
    isLegendVisuallyHidden: isFieldsetLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend
  }, /*#__PURE__*/React.createElement(LayoutGridContainer, {
    columnGap: {
      small: 'default',
      medium: 'default',
      large: 'small2x'
    }
  }, /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: [1, 7]
  }, /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldElement,
    dayPickerProps: {
      classNames: overlayClasses,
      disabledDays: setDisabledFromOptions(),
      firstDayOfWeek: 1,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      description: descriptionFrom,
      hasTighterSpacing,
      isDisabled,
      isReadOnly: true,
      label: labelFrom,
      name: nameFrom,
      isLabelVisuallyHidden
    },
    keepFocus: false,
    onDayChange: handleFromDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameFrom);
      if (value.from) {
        var _inputFromRef$current;
        (_inputFromRef$current = inputFromRef.current) === null || _inputFromRef$current === void 0 ? void 0 : _inputFromRef$current.getDayPicker().showMonth(dayjs(value.from).toDate());
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputFromRef
    }))),
    parseDate: parseDate,
    placeholder: placeholderFrom,
    ref: inputFromRef,
    value: selectedDays.from
  }))), /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: [7, 13]
  }, /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldElement,
    dayPickerProps: {
      firstDayOfWeek: 1,
      classNames: overlayClasses,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      disabledDays: setDisabledToOptions(),
      month: selectedDays.from,
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      description: descriptionTo,
      hasTighterSpacing,
      isDisabled: isDisabled || value.from === null,
      isReadOnly: true,
      label: labelTo,
      name: nameTo,
      isLabelVisuallyHidden
    },
    keepFocus: false,
    onDayChange: handleToDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameTo);
      if (value.to) {
        var _inputToRef$current2, _inputFromRef$current2;
        (_inputToRef$current2 = inputToRef.current) === null || _inputToRef$current2 === void 0 ? void 0 : _inputToRef$current2.getDayPicker().showMonth(dayjs(value.to).toDate());
        (_inputFromRef$current2 = inputFromRef.current) === null || _inputFromRef$current2 === void 0 ? void 0 : _inputFromRef$current2.hideDayPicker();
      }
    },
    overlayComponent: props => ( /*#__PURE__*/React.createElement(DatePickerOverlay
    // eslint-disable-next-line react/jsx-props-no-spreading
    , Object.assign({}, props, {
      dayPickerRef: inputToRef,
      isRightAligned: true
    }))),
    parseDate: parseDate,
    placeholder: placeholderTo,
    ref: inputToRef,
    value: selectedDays.to
  })))))) : ( /*#__PURE__*/React.createElement(Fieldset, {
    isGroupField: true,
    isLegendVisuallyHidden: isFieldsetLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend
  }, /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldElement,
    dayPickerProps: {
      classNames: overlayClasses,
      disabledDays: setDisabledFromOptions(),
      firstDayOfWeek: 1,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      description: descriptionFrom,
      hasTighterSpacing,
      isDisabled,
      isReadOnly: true,
      label: labelFrom,
      name: nameFrom,
      isLabelVisuallyHidden
    },
    keepFocus: false,
    onDayChange: handleFromDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameFrom);
      if (value.from) {
        var _inputFromRef$current3;
        (_inputFromRef$current3 = inputFromRef.current) === null || _inputFromRef$current3 === void 0 ? void 0 : _inputFromRef$current3.getDayPicker().showMonth(dayjs(value.from).toDate());
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputFromRef
    }))),
    parseDate: parseDate,
    placeholder: placeholderFrom,
    ref: inputFromRef,
    value: selectedDays.from
  })), /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldElement,
    dayPickerProps: {
      firstDayOfWeek: 1,
      classNames: overlayClasses,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      disabledDays: setDisabledToOptions(),
      month: selectedDays.from,
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      description: descriptionTo,
      hasTighterSpacing,
      isDisabled: isDisabled || value.from === null,
      isReadOnly: true,
      label: labelTo,
      name: nameTo,
      isLabelVisuallyHidden
    },
    keepFocus: false,
    onDayChange: handleToDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameTo);
      if (value.to) {
        var _inputToRef$current3, _inputFromRef$current4;
        (_inputToRef$current3 = inputToRef.current) === null || _inputToRef$current3 === void 0 ? void 0 : _inputToRef$current3.getDayPicker().showMonth(dayjs(value.to).toDate());
        (_inputFromRef$current4 = inputFromRef.current) === null || _inputFromRef$current4 === void 0 ? void 0 : _inputFromRef$current4.hideDayPicker();
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputToRef
    }))),
    parseDate: parseDate,
    placeholder: placeholderTo,
    ref: inputToRef,
    value: selectedDays.to
  })))));
};

var styles$B = {"root":"DatePickerRangeMerged-module_root__fXXTi","isDisabled":"DatePickerRangeMerged-module_isDisabled__e-7YE"};

/**
 * The DatePickerRangeMerged component is a form field component that provides
 * two date pickers for selecting a “from” and “to” date.
 *
 * ## Usage Guidelines
 *
 * - The DatePickerRangeMerged component should only get used for selecting
 *   a from and to date. For single and multiple date selections, see the
 *   DatePickerSingle and DatePickerMulti components, respectively.
 * - The DatePickerRangeMerged component is a mandatory field by default. For
 *   optional DatePickerRangeMerged fields, apply the `isOptional` prop.
 * - Selecting dates by manual input via the text inputs is disabled for this
 *   date picker component until validation gets added to ensure the required
 *   date format gets entered. Specifically, the text input gets set to
 *   "read-only", meaning the input's text cannot get changed, but the input can
 *   be tabbed into, highlighted, and its text copied.
 * - To disable dates in the date picker, pass an array of dates to the
 *   `disabledDates` prop. The dates must use the "YYYY-MM-DD" format.
 * - The “end” date picker field is disabled until the “start” date gets
 *   selected.
 * - The DatePickerRangeMerged component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the date picker's value gets driven by the React state
 *   (`useState`), indicating a consumer has to set up the component's state.
 *   For preselecting a range, the default value of `useState` needs to match
 *   the corresponding `value` prop: E.g., if this were the value of the
 *   preselected range:
 *
    ```js
    {
      from: '2021-09-02',
      to: '2021-09-09',
    }
    ```
 *
 *   Then the default value of useState would be: `useState({from:'2021-09-02',
 *   to:'2021-09-08'})`
 *
 *   **⚠️ NOTE:** The `value` prop uses a type that is an object with keys
 *   `from, to` both values of which are strings. The strings (the dates) must
 *   use the "YYYY-MM-DD" format.
 * - Date picker fields support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *
 *  As date fields are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  date fields getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - The DatePickerRangeMerged component is wrapped in a fieldset (see the
 *   Fieldset component) as it consists of two fields. Fieldsets, by default,
 *   come with a legend. There are edge cases when a fieldset's legend shouldn't
 *   be visible on the screen; that is, it's hidden visually, applied via the
 *   `isFieldsetLegendVisuallyHidden` prop. A primary use case for this
 *   treatment is for "compact" forms, that is, forms intended to take up as
 *   little space on the screen as possible. Typically compact forms get
 *   associated with the Form component where its `hasTighterSpacing` prop gets
 *   set to "true".
 */
const DatePickerRangeMerged = ({
  dateFormat = 'default',
  disabledDates,
  isDisabled = false,
  isFieldsetLegendVisuallyHidden = false,
  isOptional = false,
  hasTighterSpacing = false,
  legend,
  nameFrom,
  nameTo,
  onDateSelection,
  placeholderFrom = '',
  placeholderTo = '',
  value,
  onDayPickerShow
}) => {
  const [fromTo, setFromTo] = useState(false);
  const inputToRef = useRef(null);
  const inputFromRef = useRef(null);
  useEffect(() => {
    if (fromTo) {
      var _inputToRef$current, _inputToRef$current2;
      (_inputToRef$current = inputToRef.current) === null || _inputToRef$current === void 0 ? void 0 : _inputToRef$current.getInput().focus();
      (_inputToRef$current2 = inputToRef.current) === null || _inputToRef$current2 === void 0 ? void 0 : _inputToRef$current2.getInput().click();
    }
  }, [fromTo]);
  const handleFromDayChange = from => {
    if (value.to && from >= new Date(value.to)) {
      onDateSelection({
        from: dayjs(from).format(DATE_DATA_FORMAT),
        to: dayjs(from).add(1, 'day').format(DATE_DATA_FORMAT)
      });
    } else {
      onDateSelection({
        ...value,
        from: dayjs(from).format(DATE_DATA_FORMAT)
      });
    }
    setFromTo(true);
  };
  const handleToDayChange = to => {
    if (value.from && to && to < new Date(value.from) || value.from && to && to.setHours(0, 0, 0, 0) === new Date(value.from).setHours(0, 0, 0, 0)) {
      onDateSelection({
        from: dayjs(to).format(DATE_DATA_FORMAT),
        to: dayjs(to).add(1, 'day').format(DATE_DATA_FORMAT)
      });
    } else {
      onDateSelection({
        ...value,
        to: dayjs(to).format(DATE_DATA_FORMAT)
      });
    }
    setFromTo(false);
  };
  const setDisabledFromOptions = () => {
    const beforeAfterModifier = {
      after: undefined,
      before: new Date()
    };
    const disabledFromOptions = [beforeAfterModifier];
    if (disabledDates) {
      disabledFromOptions.push(...parseValidDates(disabledDates));
    }
    return disabledFromOptions;
  };
  const setDisabledToOptions = () => {
    const disabledToOptions = [];
    if (value.from) {
      disabledToOptions.push({
        before: new Date()
      });
    }
    if (disabledDates) {
      disabledToOptions.push(...parseValidDates(disabledDates));
    }
    return disabledToOptions;
  };
  const selectedDays = {
    from: value.from ? parseDate(value.from) : undefined,
    to: value.to ? parseDate(value.to) : undefined
  };
  const modifiers = {
    start: value.from ? parseDate(value.from) : undefined,
    end: value.to ? parseDate(value.to) : undefined
  };
  return /*#__PURE__*/React.createElement(Fieldset, {
    hasTighterSpacing: hasTighterSpacing,
    isGroupField: true,
    isLegendVisuallyHidden: isFieldsetLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$B.root, {
      [styles$B.isDisabled]: isDisabled
    })
  }, /*#__PURE__*/React.createElement(LayoutGridContainer, {
    columnGap: "none"
  }, /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: [1, 7]
  }, /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldMerged,
    dayPickerProps: {
      classNames: overlayClasses,
      disabledDays: setDisabledFromOptions(),
      firstDayOfWeek: 1,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      isDisabled,
      isReadOnly: true,
      name: nameFrom,
      isLabelVisuallyHidden: true
    },
    keepFocus: false,
    onDayChange: handleFromDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameFrom);
      if (value.from) {
        var _inputFromRef$current;
        (_inputFromRef$current = inputFromRef.current) === null || _inputFromRef$current === void 0 ? void 0 : _inputFromRef$current.getDayPicker().showMonth(dayjs(value.from).toDate());
      }
    },
    overlayComponent: props => (
    /*#__PURE__*/
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement(DatePickerOverlay, Object.assign({}, props, {
      dayPickerRef: inputFromRef
    }))),
    parseDate: parseDate,
    placeholder: placeholderFrom,
    ref: inputFromRef,
    value: selectedDays.from
  }))), /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: [7, 13]
  }, /*#__PURE__*/React.createElement("div", {
    className: RANGE_CLASS_NAME,
    "data-faux-role": "datePicker" /* DATE_PICKER */
  }, /*#__PURE__*/React.createElement(DayPickerInput, {
    classNames: inputClasses,
    component: DatePickerFieldMerged,
    dayPickerProps: {
      firstDayOfWeek: 1,
      classNames: overlayClasses,
      modifiers,
      navbarElement: /*#__PURE__*/React.createElement(DatePickerMonthNavigation, null),
      disabledDays: setDisabledToOptions(),
      month: selectedDays.from,
      fromMonth: new Date(),
      selectedDays: [selectedDays.from, selectedDays]
    },
    formatDate: date => formatDate(date, dateFormat),
    hideOnDayClick: true,
    inputProps: {
      hasTighterSpacing,
      isDisabled: isDisabled,
      isReadOnly: true,
      name: nameTo,
      isLabelVisuallyHidden: true,
      isSecond: true
    },
    keepFocus: false,
    onDayChange: handleToDayChange,
    onDayPickerShow: () => {
      onDayPickerShow === null || onDayPickerShow === void 0 ? void 0 : onDayPickerShow(nameTo);
      if (value.to) {
        var _inputToRef$current3, _inputFromRef$current2;
        (_inputToRef$current3 = inputToRef.current) === null || _inputToRef$current3 === void 0 ? void 0 : _inputToRef$current3.getDayPicker().showMonth(dayjs(value.to).toDate());
        (_inputFromRef$current2 = inputFromRef.current) === null || _inputFromRef$current2 === void 0 ? void 0 : _inputFromRef$current2.hideDayPicker();
      }
    },
    overlayComponent: props => ( /*#__PURE__*/React.createElement(DatePickerOverlay
    // eslint-disable-next-line react/jsx-props-no-spreading
    , Object.assign({}, props, {
      dayPickerRef: inputToRef,
      isRightAligned: true
    }))),
    parseDate: parseDate,
    placeholder: placeholderTo,
    ref: inputToRef,
    value: selectedDays.to
  }))))));
};

var styles$C = {"root":"LayoutVerticalSpacer-module_root__CIjU5","large1x":"LayoutVerticalSpacer-module_large1x__BsRlV","large2x":"LayoutVerticalSpacer-module_large2x__CcwEu","large3x":"LayoutVerticalSpacer-module_large3x__t3VpX","large4x":"LayoutVerticalSpacer-module_large4x__jqbFB","large5x":"LayoutVerticalSpacer-module_large5x__t4TcN","large6x":"LayoutVerticalSpacer-module_large6x__YP6Om","large7x":"LayoutVerticalSpacer-module_large7x__ehaGR","large8x":"LayoutVerticalSpacer-module_large8x__LfKUS","large9x":"LayoutVerticalSpacer-module_large9x__9ZFGe","default":"LayoutVerticalSpacer-module_default__ep6Y-","small1x":"LayoutVerticalSpacer-module_small1x__ZQBDG","small2x":"LayoutVerticalSpacer-module_small2x__ojfZh","small3x":"LayoutVerticalSpacer-module_small3x__yczlM","small4x":"LayoutVerticalSpacer-module_small4x__F-YqV","none":"LayoutVerticalSpacer-module_none__pncd2"};

/**
 * The LayoutVerticalSpacer component gets used to create vertical white space
 * between components and elements (from here on in, "components" also covers
 * "elements").
 *
 * This component is necessary as the design system keeps non-layout components
 * free from generating white space outside of themselves, ensuring components
 * remain highly composable while keeping white space entirely predictable.
 *
 * ## Usage Guidelines
 *
 * - Multiple components can get wrapped by the LayoutVerticalSpacer component,
 *   which results in the same white space getting applied between each
 *   component. However, when used like this, nested LayoutVerticalSpacer
 *   components are not allowed.
 * - The white space generated from the LayoutVerticalSpacer component gets
 *   applied in a **downward** direction (specifically, via the `margin-bottom`
 *   CSS property). Therefore, to avoid unwanted white space from getting
 *   applied to the User Interface, ensure components get wrapped correctly by
 *   the LayoutVerticalSpacer component, which also applies when multiple
 *   components (see guideline no.1) get wrapped. For example:
 *
 *   <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1rem;">
 *     <div>
 *      **✓ Correct**

 *      ```jsx
 *      <LayoutVerticalSpacer>
 *        <SomeComponent />
 *      </LayoutVerticalSpacer>
 *      <LastComponent />
 *      // ↑↑↑ flush to the bounding box
 *      ```
 *     </div>
 *     <div>
 *      **✗ Incorrect**
 *
 *      ```jsx
 *      <LayoutVerticalSpacer>
 *        <SomeComponent />
 *        <LastComponent />
 *      </LayoutVerticalSpacer>
 *      // ↓↓↓ unwanted white space
 *      ```
 *     </div>
 *   </div>
 * - The `space` property's "none" value gets reserved for when white space gets
 *   applied to a conditionally rendered component. For example:
 *
 *  ```jsx
 *  <LayoutVerticalSpacer space={hasConditionalComponent ? 'default' : 'none'}>
 *    <SomeComponent />
 *  </LayoutVerticalSpacer>
 *
 *  {hasConditionalComponent && <ConditionalComponent />}
 *  ```
 */
const LayoutVerticalSpacer = ({
  children,
  space = 'default'
}) => ( /*#__PURE__*/React.createElement(React.Fragment, null, flattenChildren(children).map((child, index) => (
/*#__PURE__*/
// eslint-disable-next-line react/no-array-index-key
React.createElement("div", {
  className: classnames(styles$C.root, {
    [styles$C[space]]: space
  }),
  key: index
}, child)))));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationGeneric = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M254.128 171.56c-2.11 1.089-4.321 2.03-6.499 2.953-8.057 3.405-16.239 6.845-24.892 8.015-16.26 2.182-33.035-3.755-49.008-.05-13.379 3.093-24.444 12.571-37.516 16.792-6.078 1.962-12.401 2.731-18.782 2.731h-.823c-.786 0-1.568-.03-2.353-.06-6.469-.243-12.965-1.187-19.306-2.425-20.024-3.859-40.403-10.932-55.712-23.955 44.575-8.16 136.395-20.681 214.891-4.001z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.3,
  d: "M269.665 73.913V54.916h-11.261v18.997h-13.335V44.526h-9.779V22.56h-8.001V0h-16.595v22.56h-8.001v21.966h-9.483V18.107h-5.334V4.75h-3.259v13.358h-18.966v84.303h-14.52v-8.905h-8.89v8.905H127.72V73.913h-26.077V54.916h-11.26v18.997h-.593v36.512H77.936V89.052H63.712v21.373H51.859V44.526h-9.187V22.56H8.89v21.966H0v116.065h283V73.913h-13.335z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.4,
  d: "M18.076 51.353H5.63v7.124h12.446v-7.124zM33.486 58.478h12.446v-7.124H33.486v7.124zM18.076 65.305H5.63v7.124h12.446v-7.124zM33.486 72.428h12.446v-7.124H33.486v7.124zM18.076 79.257H5.63v7.124h12.446v-7.124zM33.486 86.38h12.446v-7.124H33.486v7.124zM18.076 93.207H5.63v7.124h12.446v-7.124zM33.486 100.333h12.446v-7.124H33.486v7.124zM18.076 107.16H5.63v7.124h12.446v-7.124zM33.486 114.283h12.446v-7.124H33.486v7.124zM18.076 121.112H5.63v7.124h12.446v-7.124zM33.486 128.235h12.446v-7.124H33.486v7.124zM18.076 135.062H5.63v7.124h12.446v-7.124zM33.486 142.187h12.446v-7.124H33.486v7.124zM85.345 117.549H56.304v3.562h29.04v-3.562zM85.345 122.892H56.304v3.562h29.04v-3.562zM85.345 128.235H56.304v3.562h29.04v-3.562zM161.206 119.329h-29.041v3.562h29.041v-3.562zM161.206 124.672h-29.041v3.562h29.041v-3.562zM161.206 130.015h-29.041v3.562h29.041v-3.562zM85.345 133.578H56.304v3.562h29.04v-3.562zM85.345 138.921H56.304v3.562h29.04v-3.562zM85.345 144.264H56.304v3.562h29.04v-3.562zM104.014 80.741h-8.001v10.39h8.001V80.74zM113.496 91.13h8.001V80.742h-8.001v10.39zM104.014 97.363h-8.001v10.389h8.001v-10.39zM113.496 107.752h8.001v-10.39h-8.001v10.39zM104.014 113.986h-8.001v10.39h8.001v-10.39zM113.496 124.376h8.001v-10.389h-8.001v10.389zM104.014 130.61h-8.001v10.389h8.001V130.61zM113.496 141h8.001v-10.39h-8.001V141zM176.319 24.934h-10.668V38.59h10.668V24.934zM176.319 44.526h-10.668V58.18h10.668V44.526zM176.319 64.117h-10.668v13.655h10.668V64.117zM176.319 83.709h-10.668v13.655h10.668V83.709zM176.319 103.301h-10.668v13.654h10.668v-13.654zM176.319 122.892h-10.668v13.655h10.668v-13.655zM238.994 52.837h-39.709v11.577h39.709V52.837zM239.142 77.771h-39.709v11.577h39.709V77.771zM239.291 102.708h-39.709v11.577h39.709v-11.577zM239.439 127.642H199.73v11.577h39.709v-11.577zM14.224 28.794H8.89v11.28h5.334v-11.28zM283 80.741h-13.335v17.217H283V80.741zM283 105.676h-13.335v17.216H283v-17.216zM283 130.61h-13.335v17.217H283V130.61z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M35.48 178.96s-7.31-24.863 1.372-43.163c3.642-7.679 4.851-16.303 3.168-24.638a65.943 65.943 0 00-3.482-11.577",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M41.931 88.03c0 3.046-5.506 11.874-5.506 11.874s-5.509-8.837-5.509-11.873a5.526 5.526 0 011.567-3.985 5.51 5.51 0 013.94-1.662 5.5 5.5 0 013.94 1.662 5.519 5.519 0 011.568 3.985zM51.047 104.91c-1.654 2.559-11.071 6.976-11.071 6.976s.169-10.416 1.822-12.972a5.5 5.5 0 017.578-1.572 5.525 5.525 0 011.671 7.568zM50.647 133.754c-2.715 1.372-13.072.422-13.072.422s5.397-8.906 8.114-10.277a5.501 5.501 0 017.337 2.476 5.525 5.525 0 01-2.38 7.379zM45.44 155.509c-2.445 1.811-12.81 2.63-12.81 2.63s3.813-9.692 6.261-11.503a5.502 5.502 0 017.617 1.22 5.526 5.526 0 01-1.068 7.653zM28.41 112.476c2.181 2.123 12.348 4.322 12.348 4.322s-2.489-10.113-4.673-12.235a5.496 5.496 0 00-6.018-1.109 5.516 5.516 0 00-1.657 9.022zM22.643 137.995c2.715 1.374 13.072.424 13.072.424s-5.397-8.905-8.111-10.276a5.502 5.502 0 00-7.497 2.391 5.524 5.524 0 002.536 7.461zM20.436 163.572c2.444 1.81 12.813 2.63 12.813 2.63s-3.817-9.692-6.265-11.503a5.505 5.505 0 00-7.617 1.22 5.526 5.526 0 001.069 7.653z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.25,
  d: "M41.931 88.03c0 3.046-5.506 11.874-5.506 11.874s-5.509-8.837-5.509-11.873a5.526 5.526 0 011.567-3.985 5.51 5.51 0 013.94-1.662 5.5 5.5 0 013.94 1.662 5.519 5.519 0 011.568 3.985zM51.047 104.91c-1.654 2.559-11.071 6.976-11.071 6.976s.169-10.416 1.822-12.972a5.5 5.5 0 017.578-1.572 5.525 5.525 0 011.671 7.568zM50.647 133.754c-2.715 1.372-13.072.422-13.072.422s5.397-8.906 8.114-10.277a5.501 5.501 0 017.337 2.476 5.525 5.525 0 01-2.38 7.379zM45.44 155.509c-2.445 1.811-12.81 2.63-12.81 2.63s3.813-9.692 6.261-11.503a5.502 5.502 0 017.617 1.22 5.526 5.526 0 01-1.068 7.653zM28.41 112.476c2.181 2.123 12.348 4.322 12.348 4.322s-2.489-10.113-4.673-12.235a5.496 5.496 0 00-6.018-1.109 5.516 5.516 0 00-1.657 9.022zM22.643 137.995c2.715 1.374 13.072.424 13.072.424s-5.397-8.905-8.111-10.276a5.502 5.502 0 00-7.497 2.391 5.524 5.524 0 002.536 7.461zM20.436 163.572c2.444 1.81 12.813 2.63 12.813 2.63s-3.817-9.692-6.265-11.503a5.505 5.505 0 00-7.617 1.22 5.526 5.526 0 001.069 7.653z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M263.673 172.539s-7.31-24.863 1.372-43.163c3.642-7.679 4.854-16.303 3.171-24.638a66.474 66.474 0 00-3.482-11.577",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M270.136 81.61c0 3.046-5.508 11.874-5.508 11.874s-5.509-8.837-5.509-11.874c0-1.463.58-2.867 1.613-3.902a5.504 5.504 0 017.791 0 5.523 5.523 0 011.613 3.902zM279.239 98.488c-1.653 2.559-11.071 6.976-11.071 6.976s.172-10.416 1.826-12.972a5.505 5.505 0 017.615-1.633 5.516 5.516 0 012.393 3.477 5.527 5.527 0 01-.763 4.152zM278.843 127.334c-2.718 1.371-13.075.421-13.075.421s5.4-8.905 8.114-10.276a5.501 5.501 0 017.399 2.443 5.52 5.52 0 01-2.438 7.412zM273.636 149.088c-2.448 1.811-12.814 2.63-12.814 2.63s3.817-9.692 6.262-11.503a5.506 5.506 0 017.669 1.181 5.525 5.525 0 01-1.117 7.692zM256.602 106.046c2.184 2.123 12.348 4.322 12.348 4.322s-2.489-10.113-4.67-12.236a5.508 5.508 0 00-6.057-1.171 5.502 5.502 0 00-2.963 3.054 5.533 5.533 0 001.342 6.031zM250.847 131.574c2.718 1.374 13.075.424 13.075.424s-5.396-8.905-8.114-10.277a5.5 5.5 0 00-6.152.678 5.513 5.513 0 00-1.922 3.816 5.53 5.53 0 001.376 4.046 5.519 5.519 0 001.737 1.313zM248.628 157.15c2.448 1.811 12.814 2.63 12.814 2.63s-3.817-9.688-6.262-11.502a5.5 5.5 0 00-7.669 1.181 5.522 5.522 0 001.117 7.691z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M230.697 170.118s-3.93-13.358.738-23.195a21.163 21.163 0 001.704-13.239 35.657 35.657 0 00-1.873-6.216",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M234.17 121.253c0 1.638-2.963 6.388-2.963 6.388s-2.964-4.75-2.964-6.388a2.967 2.967 0 012.964-2.969 2.967 2.967 0 012.963 2.969zM239.062 130.325c-.889 1.375-5.95 3.75-5.95 3.75s.092-5.599.981-6.973a2.96 2.96 0 015.294 1.038 2.97 2.97 0 01-.325 2.185zM238.849 145.823c-1.461.736-7.026.225-7.026.225s2.901-4.785 4.359-5.521a2.958 2.958 0 013.866 1.368 2.975 2.975 0 01-1.199 3.928zM236.049 157.515c-1.313.974-6.884 1.413-6.884 1.413s2.051-5.207 3.366-6.18a2.96 2.96 0 014.272.523 2.98 2.98 0 01.534 2.284 2.966 2.966 0 01-1.288 1.96zM226.898 134.392c1.185 1.14 6.635 2.321 6.635 2.321s-1.336-5.435-2.51-6.575a2.958 2.958 0 00-3.304-.709 2.975 2.975 0 00-1.82 2.852 2.981 2.981 0 00.999 2.111zM223.798 148.105c1.461.736 7.026.229 7.026.229s-2.901-4.785-4.362-5.524a2.961 2.961 0 00-3.866 1.368 2.975 2.975 0 00-.188 2.205c.223.731.72 1.349 1.387 1.722h.003zM222.613 161.853c1.312.974 6.884 1.413 6.884 1.413s-2.06-5.21-3.364-6.183a2.963 2.963 0 00-4.049.69 2.97 2.97 0 00.529 4.08z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M78.434 14.152h-2.039a1.427 1.427 0 001.494-1.429 1.433 1.433 0 00-.927-1.34 1.426 1.426 0 00-.567-.09H59.881a1.426 1.426 0 00-1.379.867 1.433 1.433 0 001.379 1.992h2.038a1.427 1.427 0 00-1.428 1.431 1.432 1.432 0 001.428 1.43h-2.853a1.427 1.427 0 00-1.494 1.43 1.433 1.433 0 001.494 1.43H75.58a1.427 1.427 0 001.494-1.43 1.433 1.433 0 00-.927-1.34 1.427 1.427 0 00-.567-.09h2.854a1.427 1.427 0 001.428-1.43 1.432 1.432 0 00-1.428-1.43zM151.333 50.071h-2.039a1.424 1.424 0 001.378-.867 1.431 1.431 0 00-1.378-1.992h-16.515a1.426 1.426 0 00-1.493 1.43 1.431 1.431 0 001.493 1.429h2.039a1.427 1.427 0 00-1.429 1.43 1.432 1.432 0 001.429 1.431h-2.854a1.426 1.426 0 00-1.493 1.43 1.431 1.431 0 001.493 1.43h16.515a1.424 1.424 0 001.378-.868 1.431 1.431 0 00-1.378-1.992h2.854a1.424 1.424 0 001.319-.883 1.429 1.429 0 00-1.319-1.978zM275.497 3.17h-2.039a1.422 1.422 0 001.378-.867 1.431 1.431 0 00-1.378-1.992h-16.515a1.425 1.425 0 00-1.378.867 1.431 1.431 0 001.378 1.992h2.039a1.427 1.427 0 00-1.428 1.43 1.431 1.431 0 001.428 1.431h-2.854a1.428 1.428 0 00-1.378 1.992 1.427 1.427 0 001.378.867h16.515a1.422 1.422 0 001.378-.867 1.432 1.432 0 00-.326-1.597 1.432 1.432 0 00-1.052-.395h2.854a1.427 1.427 0 001.428-1.43 1.431 1.431 0 00-1.428-1.431z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.064 156.982a.445.445 0 100-.89.445.445 0 000 .89z",
  fill: "#535461"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.5,
  d: "M154.696 131.503L150.39 111.2h.441l-.068-.345h.125l-2.229-10.983s.113-11.473.655-15.91c.397-3.239 1.026-7.365.566-10.838a3.142 3.142 0 00.513-1.484 4.905 4.905 0 00-.373-2.865l4.477-2.802c.101 0 .205.02.296.027 0-.042-.041-.107-.074-.193a9.944 9.944 0 005.99-1.624 9.972 9.972 0 003.867-4.862 6.594 6.594 0 001.443 1.555c1.206.906 3.105 1.108 4.11-.017a3.47 3.47 0 00.72-1.553c.059-.255.101-.517.136-.775.569-3.033-.678-6.162-2.287-8.822-2.013-3.322-4.683-6.329-8.076-8.216-3.393-1.888-7.556-2.565-11.228-1.306-3.671 1.258-7.37 4.675-7.704 8.549-.166 1.98.071 2.897-.338 4.841-.205.968.237 2.853.231 3.921a4.74 4.74 0 00-.071.594c-.06 1.187.412 2.31.755 3.45-4.842.243-17.575 1.225-21.253 4.971-4.575 4.658-15.794 6.323-15.794 6.323l-18.684 3.767-6.52 2.772.074.297-.13.057s.231.863.563 2.003a8.485 8.485 0 00-2.694 5.047c-.361 2.015-.169 4.61 1.106 7.92 0 0 6.433 11.834 6.878 16.607l8.12 16.235c-.697 1.392-7.655 15.462-8.852 23.337a16.143 16.143 0 00-.193 1.713s-.044.416-.154 1.107c-.477 2.918-1.985 10.093-5.669 13.447l.047.042-.106.104.083.071c-1.106 2.116.492 4.984.492 4.984l1.816 8.127 2.033.33 2.14-.873 1.185 2.283s9.23 1.484 11.853 1.398c2.623-.086-2.222-4.539-3.92-5.86-.942-.73-1.68-3.731-2.149-6.257.248-.201.47-.431.661-.686 0 0-1.129-5.937 2.27-14.082 1.45-3.467 3.008-7.454 4.327-10.888.708 4.613 1.452 9.365 2 12.61.797 4.699.693 7.566.447 9.184a5.432 5.432 0 01-.394 1.641.48.48 0 01.03.048.897.897 0 01-.056.122c.017.029.035.056.05.086-1.855 2.125.077 5.592.077 5.592l1.817 8.104 2.032.329 2.14-.872 1.185 2.282s9.231 1.485 11.854 1.399c2.622-.087-2.223-4.539-3.924-5.86-1.324-1.03-2.243-6.56-2.584-8.932.246-.024.382-.042.382-.042l-.026-.163h.056l-1.162-6.803c-1.985-1.713-1.709-26.045-1.709-26.045s-1.035 2.544-1.067 2.339c.101-.41.474-1.567 1.339-4.034a24.194 24.194 0 001.123-4.358c.94-5.791-.812-9.332-.812-9.332.116-1.164-1.108-5.685-2.554-10.5l4.543-8.869a3.62 3.62 0 001.979.163l3.441-6.234s7.112-4.244 9.148-3.915c1.893.297 7.826-1.003 10.119-3.479.165-.18.316-.374.451-.579 1.846-2.835 1.087 14.801 1.087 14.801l-.986 6.109-.054.338 2.943.059 6.934 23.825s.436 8.08 8.315 9.876c7.88 1.796-3.265-13.225-3.265-13.225zm-47.364-27.534c-.426-1.318-.8-2.455-1.081-3.307l1.467-1.146s.183.154.495.4l-.881 4.053z",
  fill: "url(#illustrationGeneric_svg__paint0_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M114.483 168.994s1.005 8.478 2.667 9.766c1.663 1.289 6.38 5.614 3.823 5.709-2.558.095-11.557-1.36-11.557-1.36l-1.165-2.226-2.074.849-1.983-.321-1.778-7.923s-2.495-4.476 1.017-6.284c3.511-1.807 11.05 1.79 11.05 1.79z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M106.04 99.49s6.979 20.805 6.715 23.477c0 0 2.557 5.165-.264 13.194-2.821 8.03-.296 1.82-.296 1.82s-.267 23.714 1.665 25.386l1.132 6.631s-10.881 1.294-12.712-2.057c0 0 1.416-2.485.029-10.686-1.387-8.202-4.06-26.274-4.06-26.274l-7.242-41.222 15.033 9.73z",
  fill: "#C3C3C3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M106.067 99.322s6.979 20.818 6.715 23.489c0 0 2.557 5.165-.264 13.195-2.821 8.029-.296 1.819-.296 1.819s-.267 23.718 1.665 25.386l1.132 6.635s-10.881 1.291-12.712-2.06c0 0 1.416-2.485.029-10.687-1.387-8.201-4.063-26.282-4.063-26.282l-7.24-41.225 15.034 9.73z",
  fill: "#E1E1E1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M92.83 90.672l7.097 40.412s2.667 18.08 4.063 26.282c1.396 8.202-.03 10.686-.03 10.686.534.977 1.829 1.556 3.399 1.891-2.205-.258-4.193-.861-4.886-2.131 0 0 1.419-2.485.032-10.686-1.387-8.202-4.063-26.283-4.063-26.283L91.2 89.618l1.63 1.054z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M92.72 166.319s1.002 8.478 2.668 9.766c1.665 1.288 6.38 5.613 3.822 5.708-2.557.095-11.557-1.359-11.557-1.359l-1.164-2.226-2.075.851-1.982-.323-1.778-7.923s-2.495-4.476 1.013-6.284c3.509-1.808 11.054 1.79 11.054 1.79z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.925 81.144s-7.767 2.986-3.589 13.844c0 0 6.274 11.536 6.71 16.19l7.941 15.881s-8.469 16.789-8.845 24.365c0 0-1.132 10.169-5.702 14.349 0 0 9.83 8.38 13.49 3.541 0 0-1.1-5.776 2.216-13.726 3.316-7.949 7.248-18.671 7.248-18.671s9.51-6.438 3.174-19.683l-7.112-29.491-.116-6.638-5.455-1.9-9.96 1.939z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.57 81.254S75.803 84.24 79.98 95.1c0 0 6.27 11.538 6.706 16.193l7.942 15.881s-8.47 16.786-8.84 24.364c0 0-1.132 10.167-5.704 14.35 0 0 9.83 8.379 13.492 3.541 0 0-1.102-5.78 2.213-13.726 3.316-7.947 7.249-18.683 7.249-18.683s9.512-6.439 3.176-19.684l-7.112-29.49-.115-6.638-5.453-1.9-9.965 1.947z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M83.596 81.09s-7.767 2.99-3.588 13.845c0 0 4.608 8.475 6.196 13.827a41.283 41.283 0 002.667 6.676l5.785 11.577s-8.47 16.789-8.843 24.365c0 0-1.132 10.169-5.704 14.349 0 0 9.829 8.38 13.492 3.541 0 0-1.103-5.776 2.213-13.726 3.316-7.949 7.249-18.683 7.249-18.683s9.512-6.438 3.176-19.683l-7.112-29.492-.115-6.637-5.453-1.9-9.963 1.942z",
  fill: "#E1E1E1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.596 81.091l9.972-1.95 1.301.454-9.29 1.814s-7.767 2.986-3.589 13.845c0 0 4.605 8.474 6.197 13.826a41.274 41.274 0 002.667 6.676l5.787 11.577s-8.481 16.784-8.846 24.359c0 0-1.135 10.17-5.704 14.349 0 0 4.235 3.61 8.179 4.723-4.35-.089-10.161-5.046-10.161-5.046 4.572-4.18 5.704-14.35 5.704-14.35.373-7.575 8.843-24.364 8.843-24.364l-5.785-11.577a41.283 41.283 0 01-2.667-6.676c-1.588-5.343-6.196-13.827-6.196-13.827-4.178-10.844 3.588-13.833 3.588-13.833z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M115.597 102.222l-6.184 12.067s1.813 16.077-1.651 16.534c-3.464.457-2.074-17.644-2.074-17.644l3.334-15.415 6.575 4.458zM149.4 110.072l4.422 20.909s10.851 14.655 3.17 12.904c-7.681-1.751-8.104-9.638-8.104-9.638l-7.409-25.469 7.921 1.294zM153.807 66.996l-10.935 6.886-3.903-10.475s9.05.617 10.105-2.775c1.055-3.393 4.733 6.364 4.733 6.364z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M148.191 71.352l-.699-3-10.778.631-4.341 17.457 8.087.291 4.528-7.073 2.468-6.899.735-1.407z",
  fill: "#BE7C5E"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M144.463 68.203l.889-5.031s-19.946.25-24.406 4.788c-4.46 4.539-15.395 6.165-15.395 6.165l-18.212 3.67-6.368 2.704s1.795 6.738 2.566 7.204c.77.466 9.37-6.632 9.37-6.632l3.26.867-10.615 11.18 19.591 19.294.361-10.633 2.51-1.965s5.965 5.046 8.626 4.28l3.355-6.076s6.934-4.138 8.917-3.817c1.846.297 7.63-.977 9.865-3.39.156-.17.299-.351.426-.544 1.802-2.763 1.061 14.427 1.061 14.427l-1.013 6.284 10.807.223-2.169-10.704s.109-11.182.637-15.51c.527-4.328 1.482-10.28-.453-13.99l-3.805 3.627-6.62 10.645L140.16 76l4.303-7.798z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.52 67.87l-.946-5.162s-18.103.38-22.563 4.919c-4.459 4.539-15.409 6.162-15.409 6.162l-18.213 3.672-6.341 2.707s1.795 6.739 2.566 7.202c.77.463 9.373-6.629 9.373-6.629l3.26.867-10.615 11.18 19.591 19.294.361-10.636 2.519-1.968s5.965 5.046 8.626 4.283l3.355-6.076s6.896-4.138 8.89-3.809c1.846.297 7.631-.98 9.865-3.392.157-.17.299-.351.427-.544 1.801-2.763 1.06 14.43 1.06 14.43l-1.013 6.284 10.807.223-2.169-10.705s.11-11.185.637-15.51c.528-4.325 1.482-10.282-.453-13.99l-3.805 3.613-6.629 10.636 2.513-9.268 4.306-7.783z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M121.687 74.44s-10.79 9.117-11.919 14.026l11.919-14.025zM133.466 82.3s-8.297 6.293-9.779 10.294l9.779-10.294zM109.235 91.773s-.779 3.776-.933 5.789l.933-5.789zM105.291 100.47S92.863 89.294 91.123 89.522l14.168 10.948zM149.205 60.823c1.011-3.248 4.424 5.557 4.712 6.314a9.653 9.653 0 01-7.417-4.2c1.33-.42 2.391-1.081 2.705-2.114z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M154.364 66.964c5.358 0 9.702-4.351 9.702-9.718 0-5.368-4.344-9.719-9.702-9.719s-9.702 4.351-9.702 9.718c0 5.368 4.344 9.72 9.702 9.72z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M162.244 58.647c.761 1.256 1.455 2.618 2.631 3.497 1.177.879 3.029 1.077 4.007-.018.355-.438.596-.959.699-1.514.717-3.06-.536-6.233-2.163-8.932-1.962-3.238-4.567-6.168-7.871-8.014-3.304-1.847-7.367-2.503-10.947-1.274-3.579 1.229-6.519 4.512-6.815 8.288-.163 1.93.323 3.897-.077 5.794-.208.989-.655 1.93-.706 2.939-.094 1.873 1.186 3.616 1.07 5.489-.071 1.154-.661 2.202-1.022 3.303-.889 2.654-.409 5.542-.593 8.333-.184 2.79-1.402 5.919-4.069 6.744 2.949 1.484 7.015-.077 8.215-3.158.364-.941.498-1.986 1.052-2.83.936-1.424 2.883-1.988 3.668-3.502.786-1.514.104-3.357-.755-4.827-.86-1.47-1.932-2.947-1.95-4.648a4.096 4.096 0 012.667-3.806c.809-.282 1.683-.297 2.534-.392a13.399 13.399 0 005.879-2.247c2.279-1.493 2.889-1.956 4.546.775z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M162.306 58.222c.761 1.258 1.455 2.618 2.631 3.5 1.177.881 3.029 1.077 4.007-.018.355-.439.595-.96.699-1.514.717-3.06-.536-6.234-2.163-8.932-1.962-3.242-4.567-6.169-7.871-8.015-3.304-1.846-7.367-2.5-10.947-1.273-3.579 1.226-7.183 4.56-7.5 8.335-.163 1.93.071 2.823-.329 4.717-.207.988.276 2.968.226 3.971-.095 1.873 1.185 3.616 1.069 5.489-.071 1.155-.661 2.203-1.022 3.3-.889 2.658-.409 5.546-.593 8.336-.183 2.79-1.401 5.916-4.068 6.741 2.948 1.485 7.014-.077 8.214-3.158.364-.938.498-1.983 1.052-2.826.936-1.425 2.883-1.989 3.669-3.503.785-1.513.103-3.36-.753-4.83-.857-1.469-1.935-2.944-1.953-4.648a4.084 4.084 0 012.667-3.803c.809-.281 1.683-.296 2.534-.391a13.439 13.439 0 005.879-2.247c2.291-1.5 2.901-1.96 4.552.769z",
  fill: "#723647"
}), /*#__PURE__*/React.createElement("path", {
  d: "M142.171 63.85l4.116.666a.587.587 0 10.187-1.158l-4.116-.666a.587.587 0 10-.187 1.157z",
  fill: "#252A62"
}), /*#__PURE__*/React.createElement("path", {
  d: "M133.688 145.881c.069 4.649 3.983 8.312 8.624 8.312h.646c.57-.002 1.119.22 1.529.617.311.292.527.671.619 1.087.044.167.066.34.065.513v8.208a8.237 8.237 0 001.298 4.453 11.753 11.753 0 011.932 6.334 3.97 3.97 0 001.183 2.826 3.952 3.952 0 002.842 1.134c2.169-.038 3.87-1.909 3.87-4.084v-2.052l1.078.125v2.057a3.975 3.975 0 001.184 2.822 3.957 3.957 0 002.838 1.132c2.172-.038 3.873-1.909 3.873-4.084v-1.039c1.076.451 7.177.451 8.869 0l.459-.036v1.187a3.968 3.968 0 002.487 3.69 3.933 3.933 0 001.538.282c2.169-.038 3.87-1.909 3.87-4.084v-1.704l.358-.033v1.87c.001.525.107 1.045.311 1.529a3.952 3.952 0 003.714 2.422 4.026 4.026 0 003.852-3.529 5.17 5.17 0 00.036-.555v-8.585a13.17 13.17 0 00-2.801-8.133 2.441 2.441 0 01-.068-2.88c2.009-2.947 4.981-7.922 4.884-11.449a3.867 3.867 0 00-.94-2.609c-.038-.045-.08-.086-.118-.128-2.893-3.034-7.984-.647-7.542 3.524a11.81 11.81 0 01-.296 4.17 12.994 12.994 0 01-.809 2.235 3.792 3.792 0 01-3.873 2.159 13.073 13.073 0 00-1.618-.098h-16.595a4.66 4.66 0 01-4.416-3.168 4.528 4.528 0 01-.251-1.505v-12.942a2.522 2.522 0 00-1.605-2.348 2.45 2.45 0 00-2.719.641 2.699 2.699 0 00-.699 1.811 1.33 1.33 0 01-1.331 1.333h-7.844a8.42 8.42 0 00-6.008 2.518 8.461 8.461 0 00-2.426 6.054z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M182.987 176.398a3.958 3.958 0 003.9 2.969 4.018 4.018 0 003.852-3.53c-2.418-.644-5.731-1.089-7.752.561zM174.719 176.398a3.959 3.959 0 003.903 2.969 4.014 4.014 0 003.834-3.53c-2.388-.644-5.692-1.089-7.737.561zM157.502 176.398a3.954 3.954 0 003.9 2.969 4.018 4.018 0 003.852-3.53c-2.418-.644-5.725-1.089-7.752.561zM148.523 176.398a3.963 3.963 0 001.437 2.163 3.947 3.947 0 002.466.806 4.014 4.014 0 003.834-3.53c-2.388-.644-5.701-1.089-7.737.561zM183.858 149.192c2.789-1.122 6.191-3.176 8.89-4.957a3.86 3.86 0 00-.939-2.609c-.039-.045-.08-.086-.119-.128-2.892-3.033-7.983-.647-7.541 3.524a11.807 11.807 0 01-.291 4.17zM180.483 153.511s-7.678 9.683-20.24-.023l20.24.023zM156.296 135.514s-4.664 6.112 0 13.655v-13.655zM145.172 156.363s4.11 5.111.053 9.178l-.053-9.178z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M144.813 155.284c.296.297.201.564.296.985l13.335-3.499c-.717-.556-1.609-1.212-1.902-2.078-1.594 1.232-4.756 3.315-11.729 4.592z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.487 154.824c.311.291.527.67.62 1.086l13.006-3.396a4.655 4.655 0 01-1.565-2.193c-1.6 1.244-5.088 3.22-12.061 4.503z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M156.059 151.038s-4.068 3.066-10.887 4.016l10.887-4.016z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M156.059 150.68s-4.068 3.067-10.887 4.017l10.887-4.017z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M145.889 150.97s-6.816 2.158-8.612-4.672l8.612 4.672z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M145.889 150.611s-6.816 2.158-8.612-4.673l8.612 4.673z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M133.689 146.298c.396 0 .717-.564.717-1.259s-.321-1.259-.717-1.259c-.396 0-.717.564-.717 1.259s.321 1.259.717 1.259z",
  fill: "#383F4D"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M188.054 170.921l-1.434-11.502s4.306 6.827 1.434 11.502z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M186.261 159.419s4.306 6.827 1.434 11.502l-1.434-11.502z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationGeneric_svg__paint0_linear",
  x1: 75.132,
  y1: 103.531,
  x2: 164.876,
  y2: 118.01,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})))));

var styles$D = {"message":"EmptyState-module_message__zR2kD","cta":"EmptyState-module_cta__qiM0I","divider":"EmptyState-module_divider__yE0qF"};

const LayoutGridItem$1 = ({
  children,
  isIndented = false
}) => {
  const createPlacement = placement => {
    const [start, end] = placement;
    return isIndented ? [start + 2, end - 2] : placement;
  };
  return /*#__PURE__*/React.createElement(LayoutGridItem, {
    placement: createPlacement([1, 13])
  }, children);
};
/**
 * The EmptyState component gets used to provide guidance on what steps to take
 * when content is either unavailable or can't get displayed.
 *
 * ## Usage Guidelines
 *
 * - Some common use cases for empty states include:
 *   - When content is missing from a page, and you need to communicate why.
 *   - When a user is starting a new workflow and hasn't created any content
 *     yet.
 *   - When a user has nothing more to do, for example, when they have completed
 *     all the required tasks.
 * - Empty states ordinarily come built into the component they serve. For
 *   example, a search result's empty state gets handled internally in the
 *   SearchResultsList component. Therefore, in most cases, empty states get
 *   handled by the design system.
 * - An empty state's heading should summarise the situation clearly and
 *   concisely.
 * - An empty state's message should include a more detailed explanation of the
 *   situation and next steps, when applicable, and don't repeat the heading
 *   copy in the message if it doesn't add additional clarity. Additionally, to
 *   ensure a positive experience, avoid blaming or making a user feel guilty
 *   when they see an empty state. Instead, the message should be friendly and
 *   encouraging.
 * - Illustrations create imagery for an empty state where the illustration is
 *   relevant to the message. When an appropriate illustration isn't available,
 *   the EmptyState component serves a generic illustration. For available
 *   illustrations, see: Foundations → Illustrations.
 * - Include a Call to Action (CTA) to provide users with a follow-up action to
 *   resolve an empty state. Avoid showing the same CTA twice. If the same CTA
 *   exists on the page, use the CTA in the empty state where it’s more
 *   attention-grabbing for the user.
 */
const EmptyState = ({
  cta,
  heading,
  illustration = /*#__PURE__*/React.createElement(IllustrationGeneric, null),
  message
}) => {
  const isMediumFromViewport = useMediumFromMediaQuery();
  return /*#__PURE__*/React.createElement(LayoutGridContainer, null, /*#__PURE__*/React.createElement(LayoutGridItem$1, {
    isIndented: true
  }, /*#__PURE__*/React.createElement(LayoutVerticalSpacer, {
    space: isMediumFromViewport ? 'large7x' : 'large3x'
  }, illustration)), /*#__PURE__*/React.createElement(LayoutGridItem$1, null, /*#__PURE__*/React.createElement(LayoutVerticalSpacer, {
    space: isMediumFromViewport ? 'large4x' : 'large2x'
  }, /*#__PURE__*/React.createElement(Heading, {
    level: 1,
    size: isMediumFromViewport ? 'medium' : 'small1x'
  }, heading), /*#__PURE__*/React.createElement("p", {
    className: styles$D.message
  }, message)), /*#__PURE__*/React.createElement(LayoutVerticalSpacer, {
    space: isMediumFromViewport ? 'large6x' : 'large3x'
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$D.cta
  }, cta))), /*#__PURE__*/React.createElement(LayoutGridItem$1, {
    isIndented: true
  }, /*#__PURE__*/React.createElement(LayoutVerticalSpacer, {
    space: isMediumFromViewport ? 'large6x' : 'large1x'
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$D.divider
  }))), /*#__PURE__*/React.createElement(LayoutGridItem$1, null, /*#__PURE__*/React.createElement(LayoutVerticalSpacer, null, /*#__PURE__*/React.createElement("p", null, "Want to help us expand our Pet Sitting community in your area?")), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("a", {
    href: "https://www.madpaws.com.au/about/become-a-sitter/" /* BECOME_A_PET_SITTER */,
    rel: "noopener noreferrer",
    target: "_blank"
  }, "Become a Pet Sitter"), ' ', "today!")));
};

var styles$E = {"root":"Footnote-module_root__90aUj"};

/**
 * The Footnote component gets used to create styled blobs
 * of Footnote text
 *
 */
const Footnote = ({
  children
}) => {
  const Element = 'div';
  return /*#__PURE__*/React.createElement(Element, {
    className: styles$E.root
  }, children);
};

var styles$F = {"root":"Form-module_root__98av8","hasTighterSpacing":"Form-module_hasTighterSpacing__-57lP","isGridRowGapDisabled":"Form-module_isGridRowGapDisabled__E6tN-"};

/**
 * The Form component gets used to contain a collection of form field components
 * to submit data or update the User Interface in some way.
 *
 * ## Usage Guidelines
 *
 * - The Form component uses the HTML `form` element, which identifies as a
 *   [`form` landmark
 *   role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Form_Role).
 *   Landmark roles programmatically identify sections of a page and thus must
 *   be labelled, i.e., have an accessible name, visually or non-visually. The
 *   preference is to apply a visual label via the Heading component that
 *   precedes the form. A visual label gets connected to the form via the
 *   `label.id` prop, which takes its id and sets it to the form's
 *   `aria-labelledby` attribute. Be sure to understand why it's the case, but
 *   if it's impossible to use a visual label, provide the label to the
 *   `label.name` prop, which sets it to the form's `aria-label` attribute.
 *   Here's an example of assigning a visual label to the Form component:
 *
 *  ```jsx
 *  <Heading id="refineSearchFormLabel">Refine search</Heading>
 *
 *  <Form label={{ id: 'refineSearchFormLabel' }}>
 *    […]
 *  </Form>
 *  ```
 *
 *   And the less optimal approach of assigning a visually hidden label:
 *
 *  ```jsx
 *  <Form label={{ name: 'Refine search' }}>
 *    […]
 *  </Form>
 *  ```
 * - Following the above guideline, the Form component also supports the
 *   [`search` landmark
 *   role](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Search_role)
 *   via the `hasSearchRole` prop. Setting `hasSearchRole` to `true` identifies
 *   a form as being specific to a search function.
 * - The Form component controls the spacing between form fields and fieldsets,
 *   including form fields within LayoutGrid components. Conversely, spacing
 *   does not apply to elements the Form component's contents do not support.
 *   Currently, the Form component's contents support the following components:
 *    - CheckboxGroup
 *    - Fieldset
 *    - RadioGroup
 *    - TextField
 *    - LayoutGridContainer and LayoutGridItem
 *
 *   Tighter spacing can get applied via the `hasTighterSpacing` prop.
 *
 *   **⚠️ NOTE:** For the Form component to control the spacing of its contents,
 *   the `rowGap` prop from the LayoutGridContainer component gets overridden to
 *   zero, i.e., it will not apply.
 * - Until custom client-side form validation gets implemented, the [HTML5
 *   Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   gets leveraged. By default, HTML5 validation gets turned on via the Form
 *   component. To turn it off, set the `hasHtml5Validation` prop to `false`.
 * - Currently, the Form component doesn't come with any actions, mainly a
 *   submit button; until it does, use the `isSubmit` variant of the Button
 *   component and nest it as the last component within the Form component. If
 *   the Button component can't get nested, as per forms within the Dialog
 *   component, then associate the submit button with the form via the Form
 *   component's `id` prop and the Button component's `associatedForm` prop,
 *   like this:
 *
 *  ```jsx
 *  <Form id="refineFilters">
 *    […]
 *  </Form>
 *
 *  <Button associatedForm="refineFilters" isSubmit […] />
 *  ```
 */
const Form = ({
  children,
  hasHtml5Validation = true,
  hasSearchRole,
  hasTighterSpacing = false,
  isGridRowGapDisabled = true,
  id,
  label,
  onSubmit
}) => {
  const handleSubmit = event => {
    event.preventDefault();
    if (onSubmit) {
      onSubmit(event);
    }
  };
  return /*#__PURE__*/React.createElement("form", {
    "aria-label": label.name,
    "aria-labelledby": label.id ? formatMultipleAriaDescribedBy([label.id]) : undefined,
    className: classnames(styles$F.root, {
      [styles$F.hasTighterSpacing]: hasTighterSpacing,
      [styles$F.isGridRowGapDisabled]: isGridRowGapDisabled
    }),
    id: id,
    noValidate: !hasHtml5Validation,
    onSubmit: handleSubmit,
    role: hasSearchRole ? 'search' : undefined
  }, children);
};

var styles$G = {"root":"Pagination-module_root__ITRjt","pagination":"Pagination-module_pagination__icb-v","summary":"Pagination-module_summary__ZTggV"};

const formatPageHref = (template, pageNumber) => template.replace(':page', pageNumber);

const PAGE_SEPARATOR = '…';
// NOTE: Exported for testing purposes
const NUMBER_OF_PAGES_TO_SHOW = 4;
const generatePageNumbers = (currentPage, totalPages) => {
  let pagesToShow = [];
  if (totalPages <= NUMBER_OF_PAGES_TO_SHOW) {
    // eslint-disable-next-line no-plusplus
    for (let i = 1; i <= totalPages; i++) {
      pagesToShow.push(i);
    }
  } else {
    const isFirstOrSecond = currentPage <= 2;
    const isLastOrPenultimate = currentPage >= totalPages - 1;
    pagesToShow = [1, isFirstOrSecond ? 2 : PAGE_SEPARATOR,
    // eslint-disable-next-line no-nested-ternary
    isFirstOrSecond ? 3 : isLastOrPenultimate ? totalPages - 2 : currentPage, isLastOrPenultimate ? totalPages - 1 : PAGE_SEPARATOR, totalPages];
  }
  return pagesToShow.map(p => p.toString());
};
const formatPrevNextLinkHref = (template, currentPage) => formatPageHref(template, currentPage.toString());
const Pagination = ({
  currentOffset,
  isClientSide = false,
  label,
  linkComponent: Link,
  onPageChange,
  pageHrefTemplate,
  pageSize,
  totalItems
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  const currentPage = currentOffset / pageSize + 1;
  const currentItemsRangeStart = currentOffset === 0 ? 1 : currentOffset + 1;
  const currentItemsRangeEnd = currentPage === totalPages ? totalItems : currentOffset + pageSize;
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;
  const renderPreviousActionTrigger = () => {
    if (isClientSide && onPageChange) {
      return /*#__PURE__*/React.createElement(IconButton, {
        isUsedForPagination: true,
        label: "Previous",
        onClick: () => onPageChange(currentPage - 1)
      }, /*#__PURE__*/React.createElement(IconChevronLeft, null));
    }
    return /*#__PURE__*/React.createElement(Link, {
      href: formatPrevNextLinkHref(pageHrefTemplate, currentPage - 1),
      passHref: true
    }, /*#__PURE__*/React.createElement("a", {
      "aria-label": "Previous",
      href: "https://tinyurl.com/n5byh3ym"
    }, /*#__PURE__*/React.createElement(IconChevronLeft, null)));
  };
  const renderNextActionTrigger = () => {
    if (isClientSide && onPageChange) {
      return /*#__PURE__*/React.createElement(IconButton, {
        isUsedForPagination: true,
        label: "Next",
        onClick: () => onPageChange(currentPage + 1)
      }, /*#__PURE__*/React.createElement(IconChevronRight, null));
    }
    return /*#__PURE__*/React.createElement(Link, {
      href: formatPrevNextLinkHref(pageHrefTemplate, currentPage + 1),
      passHref: true
    }, /*#__PURE__*/React.createElement("a", {
      "aria-label": "Next",
      href: "https://tinyurl.com/n5byh3ym"
    }, /*#__PURE__*/React.createElement(IconChevronRight, null)));
  };
  const renderActionTrigger = (isCurrentPage, item) => {
    if (isClientSide && onPageChange) {
      return /*#__PURE__*/React.createElement(Button
      // We don't need aria-current and aria-label on the button
      // element but it's fine to use them
      , {
        "aria-current": isCurrentPage ? true : undefined,
        "aria-label": isCurrentPage ? `Current page, page ${item}` : `Go to page ${item}`,
        isUsedForPagination: true,
        label: item,
        onClick: () => onPageChange(parseInt(item, 10)),
        variant: isCurrentPage ? 'primary' : 'tertiary'
      });
    }
    return /*#__PURE__*/React.createElement(Link, {
      href: formatPageHref(pageHrefTemplate, item),
      passHref: true
    }, /*#__PURE__*/React.createElement("a", {
      "aria-current": isCurrentPage ? true : undefined,
      "aria-label": isCurrentPage ? `Current page, page ${item}` : `Go to page ${item}`,
      href: "https://tinyurl.com/n5byh3ym"
    }, item));
  };
  return /*#__PURE__*/React.createElement("nav", {
    "aria-label": `${label} pagination`,
    className: styles$G.root
  }, /*#__PURE__*/React.createElement("ul", {
    className: styles$G.pagination,
    role: "list"
  }, hasPrevious && /*#__PURE__*/React.createElement("li", {
    className: styles$G.previous
  }, renderPreviousActionTrigger()), generatePageNumbers(currentPage, totalPages).map((item, index) => {
    const isPageSeparator = item === PAGE_SEPARATOR;
    const isCurrentPage = currentPage.toString() === item;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      React.createElement("li", {
        "aria-hidden": isPageSeparator ? true : undefined,
        key: index
      }, isPageSeparator ? ( /*#__PURE__*/React.createElement("div", {
        "data-testid": "pageSeparator"
      }, /*#__PURE__*/React.createElement(IconEllipses, null))) : renderActionTrigger(isCurrentPage, item))
    );
  }), hasNext && /*#__PURE__*/React.createElement("li", {
    className: styles$G.next
  }, renderNextActionTrigger())), /*#__PURE__*/React.createElement("p", {
    className: styles$G.summary
  }, `${currentItemsRangeStart}–${currentItemsRangeEnd} of ${totalItems} ${label}`));
};

var styles$H = {"description":"PriceBreakdown-module_description__bsJR8","name":"PriceBreakdown-module_name__F9it7","total":"PriceBreakdown-module_total__dy0e9","title":"PriceBreakdown-module_title__nsdLV","value":"PriceBreakdown-module_value__lOv5d","subtext":"PriceBreakdown-module_subtext__4uo9l"};

var styles$I = {"container":"PriceBreakdownItem-module_container__QKw8t","isBold":"PriceBreakdownItem-module_isBold__oQgzs","title":"PriceBreakdownItem-module_title__TbfBY","value":"PriceBreakdownItem-module_value__LpK-E","description":"PriceBreakdownItem-module_description__spOo5"};

const PriceBreakdownItem = ({
  isBold = false,
  title,
  tooltip,
  value,
  description
}) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$I.container, {
    [styles$I.isBold]: isBold
  })
}, /*#__PURE__*/React.createElement("div", {
  className: styles$I.title
}, /*#__PURE__*/React.createElement("span", null, title), tooltip && /*#__PURE__*/React.createElement(ToolTip, {
  tip: tooltip
})), /*#__PURE__*/React.createElement("span", {
  className: styles$I.value
}, value), description && /*#__PURE__*/React.createElement("span", {
  className: styles$I.description
}, description)));

/**
 * The price breakdown component provides services price details of a booking.
 *
 * The structure of the price breakdown is
 *
 * ```js
 * {
  entities: [{
    title: string,
    value: string,
    description: string, // optional
    details: [{ // optional, e.g. for discount, offered price and booking fee
      name: string,
      value: string, // optional
      items: [{
        title: string,
        value: string,
        description: string, // optional
        tooltip: string, // i.e. tooltip, optional
      }]
    }]
  }],
  total: string
  }
 * ```
 */
const PriceBreakdown = ({
  entities,
  total,
  isSimpleVersion = false
}) => {
  const renderItem = ({
    title,
    description,
    tooltip,
    value
  }) => ( /*#__PURE__*/React.createElement(PriceBreakdownItem, {
    description: description,
    key: `price-breakdown-item-${title}`,
    title: title,
    tooltip: tooltip,
    value: value
  }));
  const renderDetail = ({
    items,
    name,
    value
  }) => ( /*#__PURE__*/React.createElement(React.Fragment, {
    key: `price-breakdown-item-${name}`
  }, /*#__PURE__*/React.createElement("span", {
    className: styles$H.name
  }, name), items.map(item => renderItem(item)), value && /*#__PURE__*/React.createElement(PriceBreakdownItem, {
    isBold: true,
    title: "Subtotal",
    value: value
  })));
  const renderEntity = ({
    description,
    details,
    title,
    value
  }) => {
    if (isSimpleVersion || !(details !== null && details !== void 0 && details.length)) {
      return /*#__PURE__*/React.createElement(PriceBreakdownItem, {
        isBold: details ? details.length > 0 : false,
        title: title,
        value: value
      });
    }
    return /*#__PURE__*/React.createElement(Accordion, {
      headerSize: "medium",
      legend: title,
      secondaryLegend: value
    }, description && /*#__PURE__*/React.createElement("span", {
      className: styles$H.description
    }, description), details.map(detail => renderDetail(detail)));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, entities.map(entity => ( /*#__PURE__*/React.createElement(React.Fragment, {
    key: `price-breakdown-item-${entity.title}`
  }, renderEntity(entity), /*#__PURE__*/React.createElement(Divider, null)))), /*#__PURE__*/React.createElement("div", {
    className: styles$H.total
  }, /*#__PURE__*/React.createElement("span", {
    className: styles$H.title
  }, "Total"), /*#__PURE__*/React.createElement("span", {
    className: styles$H.value
  }, total), /*#__PURE__*/React.createElement("span", {
    className: styles$H.subtext
  }, "Includes fees & taxes")));
};

var styles$J = {"root":"Pills-module_root__Gjp94"};

/**
 * The Pills component is used to display single points of information, such as
 * the commands a dog knows. This may be 'Sit', 'Down' and 'Come'.
 */
const Pills = ({
  values
}) => ( /*#__PURE__*/React.createElement("div", {
  className: styles$J.root
}, values.map(value => ( /*#__PURE__*/React.createElement("span", {
  key: value
}, value)))));

/**
 * The RadioGroup component is a form field component that provides a list of
 * mutually exclusive options.
 *
 * ## Usage Guidelines
 *
 * - A radio group is a mandatory field by default. For optional radio group
 *   fields, apply the `isOptional` prop. For mandatory fields, it's possible to
 *   have one option preselected by default, however, do not do this for
 *   optional fields as a user cannot deselect an option once one of them gets
 *   selected.
 * - A radio group gets comprised of at least two options where a user can only
 *   select one at a time. If a user needs to select more than one option, the
 *   CheckboxGroup is the component to use as checkboxes allow multiple options
 *   to be selected.
 * - It's best to avoid too many options in a radio group, with six being a
 *   reasonable limit. However, use your discretion here. Any more than six
 *   typically means an alternative, more optimal UI pattern can get used, e.g.,
 *   a select list.
 * - The RadioGroup component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning a radio button's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting an option,
 *   the default value of `useState` needs to match the corresponding option's
 *   `value` prop: `options[x].value`. E.g., if this were the object item of the
 *   preselected option:
 *
    ```js
    {
      label: 'Pet Sitters',
      value: 'petSitters'
    }
    ```
 *
 *   Then the default value of `useState` would be:
 *   `useState({ value:'petSitters' })`.
 * - Radio groups support [HTML5 Constraint
 *   validation](https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Constraint_validation)
 *   via the following props:
 *
 *   - `isOptional` _(when `false`, sets the `required` attribute to `true`)_
 *
 *  As radio groups are mandatory by default, they get assigned the `required`
 *  attribute meaning HTML5 validation gets applied automatically, resulting in
 *  radio groups getting completed before submitting a form. To turn off HTML5
 *  validation, set the `hasHtml5Validation` prop to `false` on the containing
 *  Form component. Until custom form validation gets implemented, HTML5
 *  validation can get used in the meantime.
 * - If additional context is required for a radio group, then a description can
 *   get used via the `description` prop. The description provides context for
 *   the entire field and gets displayed underneath all of the options.
 *   Conversely, suppose additional context gets required for an individual
 *   option. In that case, there needs to be a description provided to the
 *   corresponding `options[x].description` prop and gets displayed underneath
 *   the option's label.
 */
const RadioGroup = ({
  description,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  isLegendVisuallyHidden = false,
  isOptional = false,
  leftLabel,
  legend,
  name,
  onChange,
  options,
  rightLabel,
  subtitle,
  value,
  variant = 'default',
  isLegendCentered = false,
  isTileWithImageCompressed = false
}) => {
  const handleChange = ({
    target: {
      value: inputValue
    }
  }) => {
    onChange({
      target: {
        value: {
          value: inputValue,
          subValue: inputValue === 'other' ? value.subValue : undefined
        }
      }
    });
  };
  const handleTextChange = ({
    target: {
      value: inputValue
    }
  }) => {
    onChange({
      target: {
        value: {
          value: value === null || value === void 0 ? void 0 : value.value,
          subValue: inputValue
        }
      }
    });
  };
  return /*#__PURE__*/React.createElement(CheckboxRadioGroup, {
    description: description,
    isDisabled: isDisabled,
    isLabelVisuallyHidden: isLabelVisuallyHidden,
    isLegendCentered: isLegendCentered,
    isLegendVisuallyHidden: isLegendVisuallyHidden,
    isOptional: isOptional,
    isTileWithImageCompressed: isTileWithImageCompressed,
    leftLabel: leftLabel,
    legend: legend,
    name: name,
    onChange: handleChange,
    onChangeText: handleTextChange,
    options: options,
    rightLabel: rightLabel,
    subtitle: subtitle,
    value: value,
    variant: variant
  });
};

/**
 * The SearchField component is a specialised text field component for entering
 * search queries.
 *
 * ## Usage Guidelines
 *
 * - A search field must only get used for entering search queries, e.g. a
 *   site-wide search, an address lookup used in an autocomplete pattern, etc.
 * - The TextField component is the base text field component that all
 *   specialised text fields build upon, meaning specialised text fields get the
 *   same API as the TextField component except for the API props specific to
 *   the specialised text field and thus get hardcoded. Given that, the
 *   SearchField component hardcodes the following props and values:
 *
 *   - `autoComplete="off"`
 *   - `hasSpellCheck={false}`
 *   - `icon={<IconSearch />}`
 *   - `type="search"`
 */
const simulateChangeEvent = (element, event) => ({
  ...event,
  target: element,
  currentTarget: element
});
const SearchField = ({
  description,
  hasAutoFocus = false,
  isDisabled = false,
  isLabelVisuallyHidden = false,
  label,
  name,
  onBlur,
  onChange,
  onClear,
  onFocus,
  placeholder,
  value
}) => {
  const inputRef = useRef(null);
  const [selfValue, setselfValue] = useState(value || '');
  const handleChange = event => {
    setselfValue(event.target.value);
    onChange(event);
  };
  const handleClear = event => {
    setselfValue('');
    if (onClear) {
      onClear(event);
    }
    if (inputRef.current) {
      const changeEvent = simulateChangeEvent(inputRef.current, event);
      changeEvent.target.value = '';
      onChange(changeEvent);
      inputRef.current.focus();
    }
  };
  return /*#__PURE__*/React.createElement(TextField, {
    autoComplete: "off",
    description: description,
    hasAutoFocus: hasAutoFocus,
    icon: /*#__PURE__*/React.createElement(IconSearch, null),
    isDisabled: isDisabled,
    isLabelVisuallyHidden: isLabelVisuallyHidden,
    label: label,
    name: name,
    onBlur: onBlur,
    onChange: handleChange,
    onClear: handleClear,
    onFocus: onFocus,
    placeholder: placeholder,
    ref: inputRef,
    type: "search",
    value: selfValue
  });
};

var styles$K = {"list":"SearchResultsList-module_list__BEmKW","hasPagination":"SearchResultsList-module_hasPagination__sUNC5"};

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationDogGrooming = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#illustrationDogGrooming_svg__clip0)"
}, /*#__PURE__*/React.createElement("path", {
  d: "M139.089 92.228c.034-.121.063-.243.086-.366 0-.045 0-.09.019-.133.02-.043.026-.17.032-.255.005-.086 0-.12 0-.18v-.22-.176c0-.057 0-.159-.017-.238-.017-.08-.015-.1-.02-.148 0-.09-.029-.181-.046-.283v-.043l-6.339-28.501c-4.111.567-1.925.068-4.111.567-2.186.499 0 0-3.833 1.242l1.595 7.168-5.708-8.55a4.034 4.034 0 00-2.9-1.8 26.594 26.594 0 01-4.193-.79 10.765 10.765 0 00-2.991-.423h-9.666c-1.013-.001-2.02.141-2.993.422-1.375.376-2.777.64-4.194.791a4.044 4.044 0 00-2.9 1.8L76.25 84.088 62.092 65.43c-3.386 2.215-1.618.817-3.386 2.215s0 0-3.048 2.86l17.567 23.14a4.029 4.029 0 002.843 1.577h.696c.108 0 .22-.023.33-.04h.014a4.048 4.048 0 002.758-1.78l9.91-14.461 1.493 23.846c0 3.592 3.172 6.521 6.7 6.521h15.725c3.528 0 6.416-2.937 6.416-6.521l1.222-23.846 10.465 14.46c0 .018.026.035.037.055.011.02.065.087.1.133.034.045.105.142.165.21 0 .017.028.03.039.048a4.068 4.068 0 001.908 1.222c.052.016.105.029.159.04l.213.05c.069.015.14.023.211.035l.176.022h.56c.088 0 .173 0 .262-.017l.096-.014c.108 0 .219-.034.327-.057h.031c.124-.028.245-.063.364-.105h.015c.343-.115.669-.276.969-.479a4.11 4.11 0 001.646-2.319l.014.003z",
  fill: "url(#illustrationDogGrooming_svg__paint0_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M99.934 182.335h-7.878v9.79h7.878v-9.79z",
  fill: "url(#illustrationDogGrooming_svg__paint1_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M119.496 182.335h-7.878v9.79h7.878v-9.79z",
  fill: "url(#illustrationDogGrooming_svg__paint2_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M112.534 97.664H99.635c-8.29 0-11.087 6.694-11.087 14.599l2.502 73.381h11.323v-57.689a3.67 3.67 0 013.261-3.613c2.243-.255 4.168 1.418 4.168 3.516v57.786h11.32l2.948-73.381c-.003-7.905-3.249-14.6-11.536-14.6z",
  fill: "url(#illustrationDogGrooming_svg__paint3_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M99.934 192.128v9.876H88.736a1.172 1.172 0 01-1.2-1.412c.05-.234.171-.447.347-.61 2.248-2.064 5.117-5.316 4.173-7.854",
  fill: "url(#illustrationDogGrooming_svg__paint4_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M111.743 192.128v9.876h11.201a1.173 1.173 0 001.134-.714 1.167 1.167 0 00-.281-1.308c-2.246-2.064-5.117-5.316-4.173-7.854",
  fill: "url(#illustrationDogGrooming_svg__paint5_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M100.062 181.447h-7.454v9.689h7.454v-9.689zM118.572 181.447h-7.454v9.689h7.454v-9.689z",
  fill: "#FFD9CB"
}), /*#__PURE__*/React.createElement("path", {
  d: "M111.982 97.664H99.777c-7.843 0-10.493 6.623-10.493 14.446l2.368 72.613h10.715v-57.077a3.565 3.565 0 013.085-3.57 3.528 3.528 0 013.63 2.041c.205.452.312.942.313 1.438v57.168h10.715l2.786-72.613c0-7.823-3.07-14.446-10.914-14.446z",
  fill: "#6B6B6B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M100.062 191.135v9.774H89.475a1.165 1.165 0 01-1.137-1.408c.049-.226.165-.433.332-.594 2.127-2.041 4.833-5.259 3.95-7.772M111.237 191.135v9.774h10.598a1.163 1.163 0 00.805-2.002c-2.127-2.041-4.833-5.259-3.949-7.772",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.964 62.592c15.396 0 27.877-13.116 27.877-29.295C133.841 17.117 121.36 4 105.964 4c-15.397 0-27.878 13.116-27.878 29.296 0 16.179 12.481 29.295 27.878 29.295z",
  fill: "url(#illustrationDogGrooming_svg__paint6_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.964 61.131c14.628 0 26.487-12.462 26.487-27.835 0-15.373-11.859-27.835-26.487-27.835-14.629 0-26.488 12.462-26.488 27.835 0 15.373 11.86 27.835 26.488 27.835z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.574 57.642c12.066 0 21.848-9.983 21.848-22.297 0-12.315-9.782-22.298-21.848-22.298-12.066 0-21.847 9.983-21.847 22.298 0 12.314 9.781 22.297 21.847 22.297z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.3,
  d: "M105.858 56.792c12.067 0 21.848-9.983 21.848-22.298 0-12.314-9.781-22.298-21.848-22.298-12.066 0-21.847 9.984-21.847 22.298 0 12.315 9.781 22.298 21.847 22.298z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M125.366 47.301l-3.605 12.614a1.267 1.267 0 00.874 1.567l5.022 1.428a1.272 1.272 0 001.571-.872l3.605-12.614a1.267 1.267 0 00-.874-1.566l-5.022-1.428a1.272 1.272 0 00-1.571.871z",
  fill: "#BDBDBD"
}), /*#__PURE__*/React.createElement("path", {
  d: "M57.13 57.384l-.433.22a.564.564 0 00-.248.758l2.641 5.176a.566.566 0 00.76.247l.434-.22a.564.564 0 00.248-.759l-2.64-5.175a.566.566 0 00-.762-.247z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M52.818 59.93l-.243.42a.563.563 0 00.207.77l4.948 2.85c.27.155.617.063.773-.207l.243-.42a.563.563 0 00-.207-.77l-4.949-2.85a.567.567 0 00-.772.206z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M56.136 63.539c-.637.36-3.96 2.523.313 6.482a1.535 1.535 0 001.95.105l3.014-2.226a1.528 1.528 0 00.537-1.724l-.853-2.507a2.386 2.386 0 00-1.62-1.526 2.401 2.401 0 00-2.178.466l-.935.78a1.3 1.3 0 01-.228.15zM131.564 53.92h-.486a.565.565 0 00-.566.565v3.332c0 .311.254.564.566.564h.486a.565.565 0 00.566-.564v-3.332a.565.565 0 00-.566-.564z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M127.698 57.395s-5.624.096-2.044 7.17l5.97-1.213.634-5.954a.853.853 0 00-1.081-.925l-3.479.922z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M127.261 51.451l-.44.206a.563.563 0 00-.271.751l2.57 5.45c.133.282.47.403.753.27l.44-.206a.564.564 0 00.271-.75l-2.57-5.45a.567.567 0 00-.753-.27z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M126.211 52.329l-.439.206a.564.564 0 00-.272.751l2.158 4.575c.133.282.47.403.753.27l.439-.206a.564.564 0 00.272-.75l-2.158-4.576a.567.567 0 00-.753-.27z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M125.111 53.776l-.44.207a.563.563 0 00-.271.75l2.02 4.285c.133.282.47.403.753.27l.44-.206a.563.563 0 00.271-.75l-2.021-4.285a.566.566 0 00-.752-.27z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M124.21 55.629l-.44.206a.563.563 0 00-.271.751l1.718 3.643c.133.282.47.404.753.27l.44-.206a.563.563 0 00.271-.75l-1.718-3.644a.567.567 0 00-.753-.27z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M138.538 91.589c.031-.117.059-.236.082-.355 0-.042 0-.085.02-.127.02-.043.026-.165.031-.247.006-.082 0-.116 0-.176v-.212-.17c0-.057 0-.157-.014-.233-.014-.077 0-.094-.02-.142-.02-.048-.028-.176-.045-.264v-.04l-6.272-27.65c-4.065.55-1.904.068-4.065.55-2.161.482 0 0-3.793 1.205l1.578 6.95-5.643-8.288a4.01 4.01 0 00-2.868-1.747 26.684 26.684 0 01-4.148-.766 10.811 10.811 0 00-2.957-.41h-9.563c-1 0-1.995.138-2.957.41-1.36.364-2.747.62-4.148.766a4.013 4.013 0 00-2.86 1.746L76.397 83.692l-14-18.087c-3.35 2.146-1.601.794-3.35 2.146-1.748 1.353 0 0-3.013 2.776l17.37 22.437a3.977 3.977 0 002.812 1.528H76.903c.109 0 .217-.02.328-.037h.02a4.025 4.025 0 002.729-1.724l10.348-15.234 1.185 24.33a6.341 6.341 0 001.865 4.471 6.375 6.375 0 004.483 1.858h15.548a6.374 6.374 0 004.483-1.858 6.337 6.337 0 001.865-4.471l1.183-24.33 10.382 15.24.04.051.097.128c.051.07.105.141.162.207l.043.045a4.037 4.037 0 001.577 1.083c.103.037.205.074.307.102l.157.04.21.048c.068.014.139.023.208.031l.176.023h.554c.086 0 .174 0 .259-.017l.094-.014a4.29 4.29 0 00.324-.054h.031a3.92 3.92 0 001.319-.564 3.97 3.97 0 001.629-2.246l.029-.011z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.645 54.24c10.587 0 19.17-8.56 19.17-19.12 0-10.559-8.583-19.119-19.17-19.119-10.587 0-19.17 8.56-19.17 19.12 0 10.558 8.583 19.118 19.17 19.118z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  d: "M109.873 53.5a3.906 3.906 0 00-3.911-3.902 3.905 3.905 0 00-3.91 3.902v6.654a3.905 3.905 0 003.91 3.902 3.906 3.906 0 003.911-3.902V53.5z",
  fill: "#FFE0B2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.645 53.22c8.188 0 14.826-6.62 14.826-14.786s-6.638-14.786-14.826-14.786-14.826 6.62-14.826 14.786c0 8.167 6.638 14.787 14.826 14.787z",
  fill: "#FFE0B2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M100.702 35.247a.894.894 0 100-1.786c-.495 0-.896.4-.896.893s.401.893.896.893zM110.586 35.247c.495 0 .896-.4.896-.893a.895.895 0 00-1.791 0c0 .493.401.893.895.893z",
  fill: "#DB7657"
}), /*#__PURE__*/React.createElement("path", {
  d: "M109.307 43.41c0 1.195-1.64 2.164-3.664 2.164-2.025 0-3.659-.98-3.659-2.163 0-1.183 7.323-1.194 7.323 0z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M98.316 22.374s-2.385 11.908 23.175 9.518l-6.305-10.366-16.87.848z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  d: "M91.706 40.028c.797-.283 1.046-1.624.556-2.994s-1.533-2.252-2.33-1.968c-.797.283-1.046 1.624-.556 2.994s1.533 2.251 2.33 1.968z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M107.817 33.463h-3.707v1.784h3.707v-1.784z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M128.488 51.086a.798.798 0 00.799-.797.798.798 0 00-1.598 0c0 .44.358.797.799.797z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M105.02 79.378a5.76 5.76 0 00-8.134.02 5.728 5.728 0 00.02 8.112l8.154 8.09 8.11-8.13a5.728 5.728 0 001.315-6.301 5.745 5.745 0 00-5.382-3.552 5.763 5.763 0 00-4.083 1.761z",
  fill: "#EAF6F6"
}), /*#__PURE__*/React.createElement("path", {
  d: "M122.414 38.61c.496-1.367.253-2.708-.542-2.995-.796-.287-1.843.589-2.339 1.956-.495 1.368-.253 2.709.543 2.996.795.287 1.842-.59 2.338-1.957z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M100.107 59.465c.6 0 1.086-.485 1.086-1.083 0-.599-.486-1.084-1.086-1.084a1.085 1.085 0 100 2.166z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M100.207 58.381h-.285a.327.327 0 00-.327.326v10.437c0 .18.147.326.327.326h.285c.18 0 .327-.146.327-.326V58.707a.327.327 0 00-.327-.326zM112.36 59.465c.6 0 1.086-.485 1.086-1.083 0-.599-.486-1.084-1.086-1.084a1.084 1.084 0 100 2.166z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M112.502 58.381h-.284a.326.326 0 00-.327.326v8.589c0 .18.146.326.327.326h.284a.326.326 0 00.327-.326v-8.589a.326.326 0 00-.327-.326z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M97.569 41.553c3.986 0 7.218-3.223 7.218-7.199s-3.232-7.2-7.218-7.2c-3.987 0-7.218 3.224-7.218 7.2s3.231 7.2 7.218 7.2z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.3,
  d: "M97.568 39.398a5.05 5.05 0 005.058-5.044 5.05 5.05 0 00-5.058-5.044 5.05 5.05 0 00-5.057 5.044 5.05 5.05 0 005.057 5.044z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M114.273 41.553c3.987 0 7.219-3.223 7.219-7.199s-3.232-7.2-7.219-7.2c-3.986 0-7.218 3.224-7.218 7.2s3.232 7.2 7.218 7.2z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.3,
  d: "M114.274 39.398a5.05 5.05 0 005.057-5.044 5.05 5.05 0 00-5.057-5.044 5.05 5.05 0 00-5.058 5.044 5.05 5.05 0 005.058 5.044z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M229.468 148.023c-.094 6.649-5.72 11.909-12.389 11.909h-.927a3.133 3.133 0 00-2.198.885 3.05 3.05 0 00-.892 1.554 2.837 2.837 0 00-.094.734v11.741a11.753 11.753 0 01-1.865 6.369 16.791 16.791 0 00-2.775 9.059 5.668 5.668 0 01-3.575 5.266c-.7.28-1.45.417-2.204.405-3.128-.057-5.564-2.731-5.564-5.841v-2.952l-1.547.182v2.94a5.658 5.658 0 01-1.699 4.044 5.677 5.677 0 01-4.083 1.627c-3.127-.057-5.564-2.731-5.564-5.841v-1.5c-1.546.641-10.311.641-12.744.02l-.66-.051v1.701a5.66 5.66 0 01-1.699 4.044 5.681 5.681 0 01-4.083 1.627c-3.128-.057-5.564-2.731-5.564-5.841v-2.444l-.514-.046v2.674a5.66 5.66 0 01-3.573 5.271 5.666 5.666 0 01-2.207.4c-2.843-.051-5.137-2.288-5.513-5.05a6.956 6.956 0 01-.051-.791v-12.314a18.784 18.784 0 014.023-11.625 3.488 3.488 0 00.724-2.045 3.489 3.489 0 00-.627-2.075c-2.889-4.217-7.156-11.342-7.019-16.378.037-1.46.44-2.741 1.353-3.734.057-.062.114-.125.17-.181 4.154-4.339 11.471-.925 10.838 5.041a16.682 16.682 0 00.437 5.954c.292 1.1.685 2.169 1.174 3.196a5.432 5.432 0 005.564 3.085 19.648 19.648 0 012.326-.139h23.849c1.4 0 2.764-.438 3.902-1.252a6.676 6.676 0 002.44-3.285 6.504 6.504 0 00.361-2.155v-18.506a3.6 3.6 0 012.306-3.36 3.62 3.62 0 011.416-.241 3.517 3.517 0 012.49 1.156 3.866 3.866 0 011.006 2.595 1.91 1.91 0 001.914 1.905h11.235a12.14 12.14 0 018.633 3.601 12.068 12.068 0 013.483 8.662h-.014z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M158.651 191.677a5.664 5.664 0 01-2.062 3.096 5.693 5.693 0 01-3.541 1.157c-2.843-.051-5.137-2.288-5.513-5.049 3.435-.925 8.194-1.563 11.116.796zM170.512 191.677a5.673 5.673 0 01-2.064 3.097 5.69 5.69 0 01-3.543 1.156c-2.842-.051-5.134-2.288-5.509-5.049 3.426-.925 8.193-1.563 11.116.796zM195.262 191.677a5.671 5.671 0 01-2.063 3.097 5.69 5.69 0 01-3.543 1.156c-2.843-.051-5.134-2.288-5.509-5.049 3.434-.925 8.193-1.563 11.115.796zM208.152 191.677a5.664 5.664 0 01-2.062 3.096 5.693 5.693 0 01-3.541 1.157c-2.843-.051-5.137-2.288-5.513-5.049 3.435-.925 8.194-1.563 11.116.796zM157.383 152.758c-4.005-1.605-8.895-4.536-12.793-7.088.037-1.46.441-2.742 1.354-3.734.056-.063.113-.125.17-.182 4.154-4.338 11.471-.924 10.837 5.041a16.707 16.707 0 00.432 5.963zM162.233 158.936s11.034 13.851 29.08-.034l-29.08.034zM196.985 133.191s6.704 8.741 0 19.542v-19.542zM212.968 163.017s-5.902 7.319-.074 13.131l.074-13.131z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M213.485 161.474c-.438.411-.284.808-.423 1.418l-19.17-5.008c1.029-.793 2.308-1.735 2.732-2.988 2.303 1.769 6.851 4.752 16.861 6.578z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M213.954 160.816a3.067 3.067 0 00-.893 1.554l-18.683-4.86a6.647 6.647 0 002.249-3.139c2.3 1.781 7.309 4.619 17.327 6.445z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M197.326 155.401s5.845 4.386 15.636 5.745l-15.636-5.745z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M197.326 154.891s5.845 4.384 15.636 5.742l-15.636-5.742z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M211.939 155.304s9.796 3.085 12.375-6.686l-12.375 6.686z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M211.939 154.789s9.796 3.088 12.375-6.686l-12.375 6.686z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M229.471 148.618c.57 0 1.032-.806 1.032-1.801 0-.994-.462-1.8-1.032-1.8-.57 0-1.032.806-1.032 1.8 0 .995.462 1.801 1.032 1.801z",
  fill: "#383F4D"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M151.351 183.846l2.064-16.445s-6.189 9.771-2.064 16.445z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M153.929 167.389s-6.186 9.771-2.061 16.446l2.061-16.446z",
  fill: "#FBDCA3"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint0_linear",
  x1: 97.452,
  y1: 109.323,
  x2: 97.452,
  y2: 59.099,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint1_linear",
  x1: 1188.16,
  y1: 6509.27,
  x2: 1406.53,
  y2: 6509.27,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint2_linear",
  x1: 1749.78,
  y1: 6509.27,
  x2: 1968.07,
  y2: 6509.27,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint3_linear",
  x1: 14785.1,
  y1: 51682.2,
  x2: 19223.6,
  y2: 51682.2,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint4_linear",
  x1: 5182.07,
  y1: 7913.39,
  x2: 5725.37,
  y2: 7913.39,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint5_linear",
  x1: 6267.1,
  y1: 7913.39,
  x2: 6810.53,
  y2: 7913.39,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationDogGrooming_svg__paint6_linear",
  x1: 10563.4,
  y1: 12111.8,
  x2: 10563.4,
  y2: 4.001,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "illustrationDogGrooming_svg__clip0"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  transform: "translate(0 .001)",
  d: "M0 0h283v202H0z"
})))));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationDogTraining = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M0 86.96C0 62.662 19.7 5.001 44.003 5.001c24.302 0 44.003 57.66 44.003 81.96a43.993 43.993 0 01-12.888 31.11 44.007 44.007 0 01-62.23 0A43.993 43.993 0 010 86.961z",
  fill: "#ABD9D9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M44.003 194.001a.361.361 0 01-.362-.362V41.951a.361.361 0 11.724 0v151.688a.363.363 0 01-.362.362z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M44.003 71.236a.36.36 0 01-.256-.106L26.331 53.716a.361.361 0 11.512-.512l17.416 17.414a.362.362 0 01-.256.618zM44.003 101.382a.36.36 0 01-.334-.223.362.362 0 01.078-.394l29.167-29.163a.365.365 0 01.395-.08.362.362 0 01.117.591l-29.167 29.163a.353.353 0 01-.256.106z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M83.845 116.534c0-17.46 14.166-58.895 31.641-58.895 17.475 0 31.641 41.434 31.641 58.895a31.63 31.63 0 01-9.275 22.357 31.645 31.645 0 01-44.732 0 31.636 31.636 0 01-9.275-22.357z",
  fill: "#EAF6F6"
}), /*#__PURE__*/React.createElement("path", {
  d: "M115.486 193.452a.257.257 0 01-.099-.02.257.257 0 01-.161-.24v-109c0-.069.028-.135.077-.183a.261.261 0 01.443.183v109a.24.24 0 01-.02.099.257.257 0 01-.24.161z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M115.486 105.235a.257.257 0 01-.184-.077l-12.523-12.513a.26.26 0 010-.368.26.26 0 01.368 0l12.523 12.514a.257.257 0 01.056.283.26.26 0 01-.24.161zM115.486 126.897a.259.259 0 01-.184-.444l20.973-20.956a.263.263 0 01.283-.056.257.257 0 01.141.34.263.263 0 01-.056.084l-20.973 20.956a.263.263 0 01-.184.076z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M116.879 192.083a2.563 2.563 0 01-2.207-3.858l3.587-6.124 4.424 2.591-3.588 6.124a2.566 2.566 0 01-2.216 1.267zM131.453 177.37a3.154 3.154 0 100-6.308 3.154 3.154 0 000 6.308zM103.495 191.367a2.563 2.563 0 01-2.208-3.858l3.588-6.124 4.423 2.59-3.587 6.125a2.571 2.571 0 01-2.216 1.267z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M124.149 187.274h-15.895a8.166 8.166 0 01-8.159-8.159v-2.414a2.832 2.832 0 012.833-2.833h21.221a6.705 6.705 0 016.704 6.703 6.705 6.705 0 01-6.704 6.703z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M123.396 193.452a2.566 2.566 0 01-2.563-2.563v-7.097h5.126v7.097a2.566 2.566 0 01-2.563 2.563zM108.521 193.452a2.565 2.565 0 01-2.564-2.563v-7.097h5.127v7.097a2.564 2.564 0 01-2.563 2.563z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M95.412 169.266l-4.92 3.95-5.185-6.455a3.157 3.157 0 01.49-4.429 3.152 3.152 0 012.313-.676 3.15 3.15 0 012.116 1.154l5.186 6.456z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M102.368 178.725l-8.136 6.533a2.275 2.275 0 01-3.198-.349l-3.423-4.261a7.25 7.25 0 011.112-10.183l1.298-1.042a6.083 6.083 0 018.542.932l4.154 5.172a2.274 2.274 0 01-.349 3.198z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M95.412 169.266l-3.382 2.716-5.803-7.224a2.17 2.17 0 01.333-3.049 2.172 2.172 0 013.049.333l5.803 7.224zM101.873 179.035l-8.916 7.16a3.156 3.156 0 01-4.43-.489 3.153 3.153 0 01.479-4.429l8.916-7.16 3.951 4.918z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M89.55 186.52c.318-.256.203-.927-.257-1.5s-1.092-.831-1.41-.575c-.318.256-.203.928.257 1.501.46.573 1.091.83 1.41.574z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M64.248 189.27a3.8 3.8 0 01-3.799 3.798c-2.098 0-2.17-1.899-.542-3.798-1.629-1.086-1.556-3.799.542-3.799a3.797 3.797 0 013.8 3.799zM75.103 189.27a3.8 3.8 0 003.8 3.798c2.097 0 2.17-1.899.542-3.798 1.628-1.086 1.556-3.799-.543-3.799a3.797 3.797 0 00-3.799 3.799z",
  fill: "#C3C3C3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M77.274 187.37H62.078v3.799h15.196v-3.799z",
  fill: "#C3C3C3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M282.638 193.792H12.716a.361.361 0 110-.724h269.922a.362.362 0 110 .724z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M218.713 122.03a5.354 5.354 0 100-10.708 5.355 5.355 0 100 10.708zM218.367 146.692a2.41 2.41 0 01.216-1.949 2.395 2.395 0 011.917-1.175l5.641-9.052-5.153-4.898a2.055 2.055 0 012.186-3.397c.252.101.481.252.675.442l7.114 7.014.014.018a1.864 1.864 0 01-.129 2.22l-7.904 9.346a2.405 2.405 0 01-.377 2.14 2.4 2.4 0 01-4.2-.709zM209.893 191.194h-2.673l-1.271-10.309h3.945l-.001 10.309z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M210.684 193.893l-8.837-.001v-.218a3.461 3.461 0 013.464-3.463h5.373v3.682z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M236.461 187.498l-2.297 1.368-6.367-8.207 3.39-2.018 5.274 8.857z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M230.929 193.934l-.112-.187a3.462 3.462 0 011.204-4.748l4.617-2.748 1.884 3.163-7.593 4.52z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M205.612 186.653c-1.791-21.082-2.856-40.667 3.69-48.656l.05-.062 11.016 4.406.018.039c.037.081 3.701 8.139 2.846 13.559l2.718 12.497 8.859 14.833a.978.978 0 01-.447 1.401l-3.85 1.694a.98.98 0 01-1.231-.385l-9.638-15.713-5.444-12.052a.324.324 0 00-.35-.189.329.329 0 00-.273.289l-3.018 28.361a.978.978 0 01-.975.877h-2.993a.988.988 0 01-.978-.899z",
  fill: "#6B6B6B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.339 138.165l-.053-.025-.008-.058c-.412-2.879.075-6.079 1.448-9.51a7.548 7.548 0 018.765-4.522 7.554 7.554 0 014.81 3.698 7.47 7.47 0 01.517 5.979c-1.73 5.058-3.974 9.79-3.996 9.837l-.047.099-11.436-5.498z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M201.412 150.108a2.454 2.454 0 011.756-2.671l10.301-19.774a1.986 1.986 0 011.226-.892 1.987 1.987 0 011.496.24 1.976 1.976 0 01.911 2.003 1.976 1.976 0 01-.274.722l-10.544 19.637a2.429 2.429 0 01-.671 2.16 2.47 2.47 0 01-2.838.482 2.467 2.467 0 01-1.363-1.907z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M212.22 113.65h8.503v-3.706c-1.866-.741-3.692-1.372-4.796 0a3.707 3.707 0 00-3.707 3.706z",
  fill: "#4A3620"
}), /*#__PURE__*/React.createElement("path", {
  d: "M221.194 109.29c5.084 0 6.506 6.371 6.506 9.965 0 2.005-.906 2.721-2.331 2.964l-.503-2.683-1.178 2.799c-.4.002-.821-.006-1.257-.014l-.399-.823-.891.808c-3.569.005-6.453.525-6.453-3.051 0-3.594 1.248-9.965 6.506-9.965z",
  fill: "#4A3620"
})));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationDogWalking = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  d: "M0 81.817C0 58.45 18.979 3.001 42.39 3.001c23.412 0 42.391 55.45 42.391 78.816a42.27 42.27 0 01-12.42 29.91 42.432 42.432 0 01-29.97 12.388 42.431 42.431 0 01-29.97-12.388A42.269 42.269 0 010 81.817z",
  fill: "#D5ECEC"
}), /*#__PURE__*/React.createElement("path", {
  d: "M42.39 184.753a.346.346 0 01-.348-.348V38.535a.348.348 0 01.697 0v145.87a.346.346 0 01-.349.348z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M42.39 66.695a.35.35 0 01-.246-.102L25.366 49.846a.348.348 0 11.493-.491L42.637 66.1a.347.347 0 01-.247.594zM42.39 95.687a.35.35 0 01-.341-.415.347.347 0 01.095-.178l28.098-28.045a.35.35 0 01.57.111.348.348 0 01-.077.38L42.637 95.586a.345.345 0 01-.247.102z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M17.173 193.867c.06.046.124.088.186.134h51.969c1.018-4.118 1.377-8.241-.144-11.82-2.167-5.102-8.58-8.286-13.68-6.095a7.782 7.782 0 00-3.058 2.415c-1.993-3.561-5.567-6.245-9.693-6.242-5.17.004-9.467 4.223-10.87 9.097-2.258-3.805-6.72-6.465-11.08-5.321-7.293 1.914-9.597 13.229-3.63 17.832z",
  fill: "#F2F2F2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M222.037 113.388c0-16.791 13.647-56.636 30.481-56.636 16.835 0 30.482 39.845 30.482 56.636a30.393 30.393 0 01-8.935 21.5 30.512 30.512 0 01-21.547 8.903c-8.08 0-15.83-3.202-21.546-8.903a30.393 30.393 0 01-8.935-21.5z",
  fill: "#ABD9D9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M252.518 187.356a.254.254 0 01-.177-.073.245.245 0 01-.073-.177V82.286c0-.066.027-.13.073-.176a.252.252 0 01.428.176v104.82a.245.245 0 01-.073.177.266.266 0 01-.082.054.242.242 0 01-.096.019z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M252.519 102.522a.252.252 0 01-.178-.074l-12.064-12.033a.25.25 0 01.177-.427.25.25 0 01.177.073l12.065 12.034a.243.243 0 01.068.128.254.254 0 01-.106.257.255.255 0 01-.139.042zM252.518 123.354a.248.248 0 01-.139-.043.246.246 0 01-.106-.256.243.243 0 01.068-.128l20.204-20.153a.25.25 0 01.355.354l-20.204 20.152a.252.252 0 01-.178.074z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M234.385 193.904c.044.034.09.064.134.097h37.369c.732-2.959.99-5.922-.103-8.494-1.559-3.666-6.171-5.954-9.837-4.38a5.584 5.584 0 00-2.199 1.736c-1.433-2.559-4.003-4.488-6.97-4.486-3.717.003-6.808 3.035-7.816 6.537-1.624-2.734-4.832-4.645-7.967-3.823-5.245 1.375-6.901 9.506-2.611 12.813z",
  fill: "#F2F2F2"
}), /*#__PURE__*/React.createElement("path", {
  d: "M147.271 123.268c2.572 5.079-.115 12.235-5.732 13.842-1.58.452-3.37.404-4.762-.548-1.148-.787-1.977-2.177-1.868-3.592.21-2.724 3.685-3.736 5.898-2.857 2.996 1.19 4.338 4.698 4.757 7.643.512 3.601-.192 7.321-1.601 10.648-2.822 6.659-8.408 11.848-13.944 16.302a88.275 88.275 0 01-21.052 12.544.524.524 0 00-.188.714.537.537 0 00.716.187 89.425 89.425 0 0020.367-11.996c6.027-4.721 12.109-10.293 15.116-17.489 1.498-3.587 2.173-7.549 1.56-11.41-.516-3.25-2.135-6.846-5.403-8.13-2.577-1.013-6.17.054-7.086 2.849-.503 1.536.018 3.198 1.015 4.413a5.807 5.807 0 004.427 2.043c3.361.07 6.437-2.083 8.126-4.877a11.575 11.575 0 001.346-8.748 10.21 10.21 0 00-.789-2.064c-.303-.6-1.206-.072-.903.526z",
  fill: "#CCC"
}), /*#__PURE__*/React.createElement("path", {
  d: "M205.174 186.453l-3.758 1.323-6.904-13.836 5.546-1.954 5.116 14.467z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M198.354 187.652l7.247-2.554 1.61 4.555-11.81 4.16a4.829 4.829 0 01.198-3.691 4.826 4.826 0 012.755-2.47z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M164.371 190.116h-3.985l-1.895-15.341h5.881l-.001 15.341z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M157.539 188.979h7.686v4.83h-12.524a4.825 4.825 0 012.987-4.462 4.84 4.84 0 011.851-.368z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M164.002 185.612a1.647 1.647 0 01-.39-.049l-5.877-1.487a1.565 1.565 0 01-1.179-1.638l5.616-73.147.174.013-.174-.013a6.275 6.275 0 011.879-4.003 6.295 6.295 0 014.055-1.776l17.761-.92 2.987 45.905 17.712 31.84a1.56 1.56 0 01-.841 2.233l-8.159 2.923a1.566 1.566 0 01-1.954-.818l-20.83-45.024a.518.518 0 00-.833-.161.519.519 0 00-.155.285l-8.254 44.555a1.577 1.577 0 01-1.538 1.282z",
  fill: "#3B3B3B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.513 120.661a3.26 3.26 0 012.066-1.835 3.277 3.277 0 012.736.403l6.189-4.173 3.458 3.135-8.822 5.771a3.29 3.29 0 01-5.744-.973 3.275 3.275 0 01.117-2.328zM178.158 74.725c4.728 0 8.561-3.826 8.561-8.545 0-4.719-3.833-8.545-8.561-8.545s-8.561 3.826-8.561 8.545c0 4.72 3.833 8.545 8.561 8.545z",
  fill: "#A0616A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M166.509 105.441a3.24 3.24 0 01-2.496-1.174 3.231 3.231 0 01-.692-2.667l1.494-7.972c-1.595-5.78 6.53-10.877 6.877-11.091l5.069-1.94.062.162-.062-.162a6.19 6.19 0 015.799.74 6.17 6.17 0 012.584 5.234l-.615 17.531-17.77 1.33a3.501 3.501 0 01-.25.009z",
  fill: "#ABD9D9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M173.926 160.107a86.205 86.205 0 01-11.66-.931l-.163-.025.017-.164c.307-2.995 7.545-73.394 9.112-78.356.404-1.283 1.663-2.29 3.741-2.996 6.532-2.216 13.869 1.07 16.703 7.482a17.783 17.783 0 011.503 7.513l-1.317 61.975-.139.026c-.037.007-3.685.719-8.502 3.826-1.961 1.264-5.596 1.65-9.295 1.65z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.051,
  d: "M183.698 92.663c4.529 7.673 2.244 11.76-2.04 19.59-2.376 4.344-9.708 9.716-13.142 6.263-3.434-3.453-.72-8.148.771-12.915 1.492-4.768 4.699-8.694 8.048-12.24 2.387-2.529 7.317-.453 6.363-.698z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M154.251 122.687a1.57 1.57 0 01-1.515-1.161l-1.087-4.057a1.572 1.572 0 01.603-1.677l17.598-12.568 2.371-13.444a6.641 6.641 0 012.983-4.454 6.668 6.668 0 017.75.432 6.647 6.647 0 012.468 4.758 6.63 6.63 0 01-.396 2.709l-5.012 13.611a11.021 11.021 0 01-5.52 6.114l-19.552 9.577c-.215.105-.451.16-.691.16z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M174.543 62.936a3.83 3.83 0 01-1.98 1.853 3.842 3.842 0 01-2.714.08 3.83 3.83 0 01-2.497-4.413 8.53 8.53 0 012.513-4.449 8.564 8.564 0 019.662-1.417 8.544 8.544 0 013.689 3.54c2.71-1.89 6.963-.936 8.599 1.931 1.637 2.867.292 7.006-2.719 8.368-1.499.678-1.387 3.295.163 3.844a2.641 2.641 0 01-.027 4.987c-1.951.667-3.939 1.34-6.001 1.392-2.062.052-4.246-.613-5.547-2.21-1.301-1.597-1.391-4.218.143-5.593a4.895 4.895 0 00.997-5.828c-1.024-1.865-3.764-3.138-4.281-2.085z",
  fill: "#2F2E41"
}), /*#__PURE__*/React.createElement("path", {
  d: "M119.251 192.147a2.27 2.27 0 01-1.963-1.138 2.258 2.258 0 01.012-2.266l3.171-5.404 3.91 2.286-3.171 5.404a2.266 2.266 0 01-1.959 1.118zM131.695 179.797a2.785 2.785 0 002.788-2.783 2.786 2.786 0 00-2.788-2.784 2.786 2.786 0 00-2.789 2.784 2.786 2.786 0 002.789 2.783zM106.983 192.147a2.26 2.26 0 01-1.964-1.138 2.258 2.258 0 01.012-2.266l3.171-5.404 3.91 2.286-3.171 5.404c-.2.341-.486.623-.83.819-.343.197-.733.3-1.128.299z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M125.239 188.536h-14.05a7.232 7.232 0 01-5.097-2.111 7.196 7.196 0 01-2.115-5.088v-2.13a2.504 2.504 0 012.504-2.5h18.758a5.93 5.93 0 014.19 1.732 5.907 5.907 0 010 8.364 5.927 5.927 0 01-4.19 1.733z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M124.574 193.987a2.262 2.262 0 01-2.266-2.261v-6.263h4.531v6.263c0 .599-.239 1.174-.664 1.598a2.272 2.272 0 01-1.601.663zM111.425 193.987a2.269 2.269 0 01-2.266-2.261v-6.263h4.532v6.263a2.263 2.263 0 01-2.266 2.261z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M108.288 168.228h-5.577v-7.306c0-.738.293-1.446.816-1.968a2.792 2.792 0 013.944 0 2.78 2.78 0 01.817 1.968v7.306z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M107.846 178.579h-9.224a2.013 2.013 0 01-2.011-2.007v-4.822a6.393 6.393 0 011.877-4.517 6.418 6.418 0 014.526-1.874h1.472a5.384 5.384 0 013.796 1.572 5.36 5.36 0 011.574 3.788v5.853a2.013 2.013 0 01-2.01 2.007z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M108.288 168.228h-3.834v-8.176a1.914 1.914 0 011.917-1.913 1.916 1.916 0 011.917 1.913v8.176zM107.332 178.519H97.224a2.79 2.79 0 01-1.972-.815 2.78 2.78 0 010-3.937 2.795 2.795 0 011.972-.815h10.108v5.567z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M94.696 176.86c.361 0 .654-.526.654-1.174 0-.649-.293-1.174-.654-1.174-.36 0-.653.525-.653 1.174 0 .648.292 1.174.653 1.174z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M219.735 194.001H86.931a.346.346 0 01-.348-.348.35.35 0 01.348-.347h132.804a.35.35 0 01.247.101.347.347 0 01-.247.594z",
  fill: "#3F3D56"
})));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationHouseVisting = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("path", {
  opacity: 0.7,
  d: "M141.5 194.001c78.148 0 141.5-31.09 141.5-69.442 0-38.352-63.352-69.442-141.5-69.442S0 86.207 0 124.56c0 38.352 63.352 69.442 141.5 69.442z",
  fill: "url(#illustrationHouseVisting_svg__paint0_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M141.5 190.995c74.766 0 135.376-29.745 135.376-66.437 0-36.692-60.61-66.436-135.376-66.436S6.124 87.866 6.124 124.558c0 36.692 60.61 66.437 135.376 66.437z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.5,
  d: "M246.434 165.323l-2.418-3.634-4.803-15.912-1.975-7.033h-2.01a3.208 3.208 0 00-.382-2.034 3.207 3.207 0 00-1.139-1.166c.012-.102.018-.205.019-.308a3.217 3.217 0 00-1.199-2.5h-.021a3.413 3.413 0 00-.246-.179l-.071-.043a2.874 2.874 0 00-.218-.12l-.098-.049-.209-.09-.111-.04c-.071-.024-.144-.043-.215-.064l-.12-.028a2.52 2.52 0 00-.233-.043h-.108a3.17 3.17 0 00-2.268.622 3.185 3.185 0 00-1.004-1.417.041.041 0 01-.022 0 3.695 3.695 0 00-.249-.175l-.07-.046a2.688 2.688 0 00-.216-.12l-.098-.047c-.067-.034-.138-.061-.209-.089l-.113-.043-.216-.062-.116-.03a1.845 1.845 0 00-.234-.04l-.107-.019a3.228 3.228 0 00-.354-.018c-.126 0-.252 0-.375.021a3.196 3.196 0 00-.332-3.51 3.21 3.21 0 00-.43-.509 3.198 3.198 0 00-.295-4.727l-.012-.015a3.413 3.413 0 00-.246-.179l-.07-.043a3.102 3.102 0 00-.219-.12l-.095-.049-.212-.089-.111-.04c-.07-.025-.144-.044-.215-.065-.07-.022-.076-.022-.116-.028-.04-.006-.157-.031-.237-.043l-.108-.015a3.2 3.2 0 00-.353-.022h-.252c-.064-.101-.132-.197-.206-.308a3.365 3.365 0 00-.491-.493 2.657 2.657 0 00-.271-.19l-.07-.047a3.038 3.038 0 00-.216-.12l-.098-.046a2.15 2.15 0 00-.209-.089l-.113-.043-.216-.062-.116-.031a3.198 3.198 0 00-.234-.043l-.135.009a3.162 3.162 0 00-3.073 1.54 3.352 3.352 0 00-.236-.043l-.108-.015a3.2 3.2 0 00-.353-.022h-.157a3.223 3.223 0 00-1.039-3.495l-.021-.016a2.89 2.89 0 00-.249-.178l-.071-.043a2.812 2.812 0 00-.215-.12l-.098-.05-.209-.089-.114-.04c-.07-.025-.141-.043-.215-.065l-.117-.027c-.076-.019-.156-.031-.233-.043l-.111-.016a3.146 3.146 0 00-.35-.021 3.185 3.185 0 00-1.6.438 3.214 3.214 0 00-1.166 1.181c-.055-.04-.107-.08-.165-.117l-.071-.043a2.76 2.76 0 00-.218-.123l-.096-.046a2.15 2.15 0 00-.209-.089l-.076-.031c.364-.249.674-.571.909-.945a3.22 3.22 0 00.377-2.542 3.2 3.2 0 00-1.087-1.662h-.021a2.857 2.857 0 00-.246-.179l-.071-.043c-.07-.043-.144-.083-.218-.123l-.095-.046-.212-.089-.111-.041c-.07-.024-.141-.043-.215-.064l-.117-.031-.236-.04h-.108a3.181 3.181 0 00-1.578.223 3.194 3.194 0 00-1.731 1.737 3.208 3.208 0 00-.243 1.227v.086c-.07.019-.144.034-.215.056a3.213 3.213 0 00-2.231 3.418l.019.108v.074l-.037-.022-.071-.043-.215-.123-.098-.046-.209-.09-.114-.04a2.219 2.219 0 00-.215-.064l-.117-.031-.233-.04-.108-.015a3.195 3.195 0 00-3.311 1.959 3.21 3.21 0 00-.244 1.228c.001.117.008.234.022.351v.108c0 .08.024.16.043.237.018.077.018.077.03.117.013.04.04.144.062.215l.043.111c.027.074.055.142.089.213l.046.095c.037.074.077.148.12.219l.046.071c.054.085.112.167.175.246l.015.022.047.058h-.056a2.98 2.98 0 00-.236-.043l-.108-.015a3.2 3.2 0 00-.353-.022 3.198 3.198 0 00-3.055 2.254 3.344 3.344 0 00-.107.496h-.025l-.073-.046a3.038 3.038 0 00-.216-.12l-.098-.047a2.79 2.79 0 00-.209-.089l-.11-.043-.216-.062-.116-.03a2.81 2.81 0 00-.237-.043l-.107-.016a3.198 3.198 0 00-2.616.919 3.213 3.213 0 00-.918 2.623l.018.107c0 .077.025.157.04.234.016.077.022.08.031.117.009.037.04.148.065.219.024.071.024.074.039.111.016.037.056.142.09.209l.046.099c.037.074.08.145.123.215a3.385 3.385 0 00.218.321l.018.021v.019a3.2 3.2 0 00-.455 2.026l.019.108c0 .08.024.16.04.237.015.077.021.077.031.117.009.04.039.145.064.215.025.071.025.074.04.111.015.037.058.145.089.213l.046.095c.037.075.079.148.123.219 0 .025.028.049.043.071a3.191 3.191 0 00-2.974 3.196c0 .118.006.237.018.354l.019.108c.008.079.022.157.04.234l.027.114h-4.79l-6.905 22.85-2.854 3.68h74.667zm-42.981-36.243l-.062-.095.148-.04-.086.135zm14.998-2.974l.052-.09.117.025c-.055.022-.114.04-.169.065z",
  fill: "url(#illustrationHouseVisting_svg__paint1_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M186.442 150.359a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M183.883 147.79a3.032 3.032 0 011.715-2.734 3.02 3.02 0 013.201.369 3.03 3.03 0 00-2.196-1.134 3.012 3.012 0 00-2.307.883 3.031 3.031 0 00-.552 3.533c.195.379.467.713.799.98a3.007 3.007 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M196.926 151.062a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M194.351 148.489a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.025 3.025 0 00-5.38 2.066 3.036 3.036 0 001.124 2.199 3.031 3.031 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M202.983 151.526a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M200.42 148.955a3.032 3.032 0 011.715-2.734 3.015 3.015 0 013.202.369 3.025 3.025 0 00-5.38 2.066 3.036 3.036 0 001.124 2.199 3.047 3.047 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.009 150.359a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M203.447 147.79a3.028 3.028 0 014.916-2.365 3.03 3.03 0 00-2.196-1.134 3.012 3.012 0 00-2.307.883 3.031 3.031 0 00-.552 3.533c.195.379.467.713.799.98a3.013 3.013 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M229.537 150.359a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M226.975 147.79a3.032 3.032 0 011.715-2.734 3.02 3.02 0 013.201.369 3.03 3.03 0 00-2.196-1.134 3.012 3.012 0 00-2.307.883 3.031 3.031 0 00-.552 3.533c.195.379.467.713.799.98a3.032 3.032 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M217.658 150.128a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M215.095 147.557a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.025 3.025 0 00-5.38 2.066 3.034 3.034 0 001.124 2.199 3.02 3.02 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M190.87 147.56a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M188.307 144.989a3.043 3.043 0 011.717-2.731 3.026 3.026 0 013.2.366 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.611 4.215c.193.379.464.713.795.98a3.036 3.036 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M215.329 141.257a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M212.766 138.686a3.031 3.031 0 013.388-3.016 3.025 3.025 0 011.55.66 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.939 2.995c.022.425.134.841.328 1.22.193.379.464.713.795.98a3.025 3.025 0 01-.682-1.909z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.009 134.253a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.027 129.581a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M203.447 131.682c.002-.57.164-1.128.468-1.611a3.026 3.026 0 014.448-.754 3.031 3.031 0 00-2.195-1.126 3.022 3.022 0 00-2.988 1.944 3.035 3.035 0 00.927 3.447 3.025 3.025 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M196.225 136.587c1.674 0 3.03-1.36 3.03-3.036a3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.033 3.033 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M193.666 134.016a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.015 3.015 0 00-2.194-1.126 3.026 3.026 0 00-3.185 3.192 3.036 3.036 0 001.124 2.199 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M190.169 141.021c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M187.606 138.453a3.053 3.053 0 01.467-1.612 3.027 3.027 0 014.45-.756 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.023 3.023 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M208.338 142.657c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M205.779 140.086a3.037 3.037 0 011.717-2.73 3.022 3.022 0 013.199.365 3.02 3.02 0 00-5.183.817 3.038 3.038 0 00.927 3.448 3.02 3.02 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M215.095 128.183a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M212.533 125.612c0-.57.162-1.129.466-1.612a3.027 3.027 0 012.903-1.397 3.029 3.029 0 011.547.644 3.008 3.008 0 00-2.195-1.125 3.023 3.023 0 00-2.988 1.943 3.038 3.038 0 00.927 3.448 3.041 3.041 0 01-.66-1.901z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M207.641 125.148c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M205.078 122.578a3.03 3.03 0 013.369-3.012 3.015 3.015 0 011.548.644 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.04 3.04 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M199.953 125.85a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M197.39 123.279a3.039 3.039 0 011.719-2.727 3.02 3.02 0 013.197.362 3.015 3.015 0 00-2.194-1.126 3.02 3.02 0 00-2.989 1.943 3.038 3.038 0 00.928 3.448 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M199.953 130.752a3.033 3.033 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M197.39 128.181a3.041 3.041 0 011.719-2.727 3.029 3.029 0 013.197.362 3.015 3.015 0 00-2.194-1.126 3.02 3.02 0 00-2.989 1.944 3.032 3.032 0 00.928 3.447 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M212.533 138.454a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M209.97 135.885a3.022 3.022 0 01.464-1.615 3.019 3.019 0 014.452-.75 3.033 3.033 0 00-2.198-1.147 3.023 3.023 0 00-3.005 1.95 3.04 3.04 0 00.954 3.459 3.029 3.029 0 01-.667-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M216.026 136.822c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M213.464 134.25a3.028 3.028 0 014.916-2.365 3.02 3.02 0 00-3.444-.937 3.024 3.024 0 00-1.94 3.001 3.034 3.034 0 001.131 2.201 3.022 3.022 0 01-.663-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M196.225 147.325c1.674 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M196.225 147.325c1.674 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M193.666 144.754a3.037 3.037 0 011.717-2.73 3.022 3.022 0 013.199.365 3.015 3.015 0 00-2.194-1.126 3.018 3.018 0 00-2.303.884 3.036 3.036 0 00.242 4.507 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M205.779 145.691a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M205.779 145.691a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M203.216 143.122a3.028 3.028 0 013.369-3.013c.567.066 1.103.29 1.548.648a3.03 3.03 0 00-2.196-1.135 3.032 3.032 0 00-2.859 4.417c.194.379.467.713.799.98a3.025 3.025 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M202.282 148.494c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M202.282 148.494c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M199.722 145.921a3.033 3.033 0 013.37-3.01 3.025 3.025 0 011.547.645 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.939 2.995c.022.425.134.841.328 1.22.193.379.464.713.795.98a3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M190.87 150.826a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M190.87 150.826a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M188.307 148.258a3.033 3.033 0 011.716-2.732 3.027 3.027 0 013.201.364 3.029 3.029 0 00-3.449-.944 3.037 3.037 0 00-.801 5.209 3.033 3.033 0 01-.667-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M193.896 148.494c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M193.896 148.494c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M191.334 145.92a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.027 3.027 0 00-3.444-.937 3.042 3.042 0 00-1.94 3.002 3.043 3.043 0 001.132 2.2 3.02 3.02 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M193.896 143.357c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M193.896 143.357c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M191.334 140.787a3.027 3.027 0 013.369-3.012c.566.065 1.103.29 1.547.647a3.034 3.034 0 00-2.197-1.141 3.01 3.01 0 00-1.25.195 3.015 3.015 0 00-1.75 1.753 3.043 3.043 0 00.141 2.476c.196.38.47.713.804.979a3.012 3.012 0 01-.664-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M199.723 138.688a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M199.723 138.688a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M197.16 136.119a3.03 3.03 0 013.368-3.013 3.022 3.022 0 011.548.645 3.015 3.015 0 00-2.194-1.126 3.018 3.018 0 00-2.303.884 3.041 3.041 0 00-.554 3.527c.194.379.465.713.796.98a3.014 3.014 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.97 150.128a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M209.97 150.128a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M207.407 147.556a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.03 3.03 0 00-2.197-1.133 3.021 3.021 0 00-2.994 1.947 3.05 3.05 0 00.138 2.472c.195.379.467.713.8.979a3.02 3.02 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M218.356 140.788a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M218.356 140.788a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M215.793 138.22a3.04 3.04 0 011.715-2.734 3.014 3.014 0 013.201.369 3.025 3.025 0 00-3.47-.986 3.022 3.022 0 00-1.763 1.774 3.039 3.039 0 00.999 3.474 3.018 3.018 0 01-.682-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M218.356 126.784a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M218.356 126.784a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M215.793 124.21a3.036 3.036 0 011.717-2.73 3.016 3.016 0 013.199.365 3.03 3.03 0 00-2.205-1.169 3.032 3.032 0 00-2.332.888 3.035 3.035 0 00.303 4.547 3.027 3.027 0 01-.682-1.901z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M221.385 132.617a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M221.385 132.617a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M218.822 130.049a3.03 3.03 0 013.369-3.012 3.024 3.024 0 011.548.644 3.025 3.025 0 00-3.44-.929 3.027 3.027 0 00-1.939 2.995 3.021 3.021 0 001.123 2.199 3.023 3.023 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M224.412 137.053c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M224.412 137.053c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M221.849 134.481a3.03 3.03 0 013.369-3.012c.567.065 1.103.29 1.548.647a3.039 3.039 0 00-2.198-1.14 3.033 3.033 0 00-2.055 5.402 3.012 3.012 0 01-.664-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.97 131.919c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M209.97 131.919c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M207.407 129.347a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.03 3.03 0 00-2.197-1.132 3.02 3.02 0 00-2.994 1.946 3.05 3.05 0 00.138 2.472c.195.379.467.713.8.979a3.02 3.02 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M200.887 134.253a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M200.887 134.253a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M198.325 131.682a3.028 3.028 0 014.916-2.365 3.025 3.025 0 00-4.497-.242 3.036 3.036 0 00.241 4.507 3.013 3.013 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.71 130.752c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M206.71 130.752c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M204.147 128.181a3.038 3.038 0 011.717-2.73 3.02 3.02 0 013.2.365 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.939 2.996c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M212.066 128.183a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M212.066 128.183a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M209.503 125.612a3.038 3.038 0 011.718-2.726 3.022 3.022 0 013.198.361 3.031 3.031 0 00-2.195-1.125 3.023 3.023 0 00-2.988 1.943 3.038 3.038 0 00.927 3.448 3.022 3.022 0 01-.66-1.901z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M196.926 130.752c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M196.926 130.752c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M194.351 128.181a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.744 1.748 3.049 3.049 0 00.132 2.467c.194.379.465.713.796.98a3.036 3.036 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M207.641 140.554c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M207.641 140.554c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M205.078 137.986a3.034 3.034 0 011.716-2.734 3.02 3.02 0 013.201.369 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.047 3.047 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M194.831 140.323a3.033 3.033 0 003.029-3.037 3.033 3.033 0 00-3.029-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M194.831 140.323a3.033 3.033 0 003.029-3.037 3.033 3.033 0 00-3.029-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M192.268 137.752a3.037 3.037 0 011.717-2.73 3.017 3.017 0 013.2.365 3.025 3.025 0 00-5.38 2.066 3.036 3.036 0 001.124 2.199 3.02 3.02 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M214.395 144.758a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M214.395 144.758a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M211.835 142.18a3.04 3.04 0 011.716-2.733 3.027 3.027 0 013.201.365 3.016 3.016 0 00-2.195-1.126 3.02 3.02 0 00-2.989 1.943 3.039 3.039 0 00.928 3.448 3.025 3.025 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M219.056 146.392a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M219.056 146.392a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M216.494 143.821a3.028 3.028 0 014.916-2.365 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.013 3.013 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M223.014 149.426a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M223.014 149.426a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M220.451 146.857a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.022 3.022 0 00-5.19.81 3.041 3.041 0 00.935 3.452 3.014 3.014 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M229.537 145.456a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M229.537 145.456a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M226.974 142.887a3.034 3.034 0 011.716-2.734 3.02 3.02 0 013.201.369 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.047 3.047 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M226.043 141.955c1.674 0 3.03-1.36 3.03-3.036a3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.033 3.033 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M226.043 141.955c1.674 0 3.03-1.36 3.03-3.036a3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.033 3.033 0 003.029 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M223.481 139.387a3.022 3.022 0 01.464-1.615 3.019 3.019 0 014.452-.75 3.018 3.018 0 00-4.532-.28 3.036 3.036 0 00.292 4.542 3.028 3.028 0 01-.676-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M204.611 122.812a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M204.611 122.812a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M202.052 120.244a3.03 3.03 0 013.368-3.012 3.024 3.024 0 011.548.644 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.016 3.016 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M192.498 136.353c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M192.498 136.353c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M189.939 133.782a3.037 3.037 0 011.717-2.73 3.022 3.022 0 013.199.365 3.015 3.015 0 00-2.194-1.126 3.018 3.018 0 00-2.303.884 3.036 3.036 0 00.242 4.507 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M187.84 144.991a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M187.84 144.991a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M185.277 142.419a3.043 3.043 0 011.717-2.73 3.02 3.02 0 013.2.365 3.023 3.023 0 00-5.166.845 3.042 3.042 0 00.919 3.436 3.026 3.026 0 01-.67-1.916z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M202.282 141.955c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M202.282 141.955c1.673 0 3.03-1.36 3.03-3.036a3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.034 3.034 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M199.722 139.386c0-.571.161-1.131.464-1.615a3.02 3.02 0 014.453-.75 3.043 3.043 0 00-.977-.802 3.038 3.038 0 00-2.467-.137 3.032 3.032 0 00-1.611 4.221c.194.38.467.714.799.98a3.01 3.01 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M202.749 129.585a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M202.749 129.585a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M200.19 127.014a3.028 3.028 0 014.916-2.365 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.039 3.039 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M203.914 126.315a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M203.914 126.315a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M201.351 123.746a3.036 3.036 0 011.715-2.734 3.022 3.022 0 013.201.369 3.026 3.026 0 00-3.47-.986 3.025 3.025 0 00-1.763 1.775 3.043 3.043 0 00.999 3.473 3.035 3.035 0 01-.682-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M199.953 145.456a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M197.39 142.887a3.037 3.037 0 011.717-2.73 3.017 3.017 0 013.199.365 3.028 3.028 0 00-2.194-1.126 3.026 3.026 0 00-3.185 3.192 3.036 3.036 0 001.124 2.199 3.026 3.026 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M192.268 132.851a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M189.705 130.281a3.038 3.038 0 011.717-2.731 3.022 3.022 0 013.2.366 3.016 3.016 0 00-2.195-1.126 3.023 3.023 0 00-2.988 1.943 3.038 3.038 0 00.927 3.448 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M195.995 141.724a3.032 3.032 0 003.03-3.036 3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.032 3.032 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M193.429 139.153a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.025 3.025 0 00-5.38 2.066 3.036 3.036 0 001.124 2.199 3.02 3.02 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.944 119.78a3.033 3.033 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M204.381 117.209a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.015 3.015 0 00-2.194-1.126 3.018 3.018 0 00-2.303.884 3.036 3.036 0 00.242 4.507 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.71 126.548c1.673 0 3.03-1.36 3.03-3.037a3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.034 3.034 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M204.147 123.98a3.032 3.032 0 013.369-3.013 3.025 3.025 0 011.548.645 3.03 3.03 0 00-3.44-.93 3.027 3.027 0 00-1.939 2.995c.022.425.134.841.328 1.22.193.379.464.713.795.98a3.025 3.025 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M212.066 124.215a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M209.503 121.642a3.033 3.033 0 013.369-3.006 3.025 3.025 0 011.547.641 3.031 3.031 0 00-2.195-1.126 3.024 3.024 0 00-2.988 1.944 3.038 3.038 0 00.927 3.447 3.019 3.019 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.736 137.286c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  d: "M211.381 135.913a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M207.174 134.717a3.027 3.027 0 013.369-3.012c.566.065 1.102.29 1.547.647a3.018 3.018 0 00-2.197-1.14 3.015 3.015 0 00-3 1.947 3.046 3.046 0 00.14 2.476c.197.38.471.713.804.979a3.017 3.017 0 01-.663-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M208.108 145.691a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M205.546 143.122a3.022 3.022 0 01.464-1.615 3.019 3.019 0 014.452-.75 3.03 3.03 0 00-2.196-1.135 3.03 3.03 0 00-2.859 4.417c.195.379.467.713.799.98a3.018 3.018 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.272 139.621a3.032 3.032 0 003.03-3.036 3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.032 3.032 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M206.71 137.052a3.027 3.027 0 013.369-3.013c.566.066 1.102.29 1.547.648a3.017 3.017 0 00-3.443-.94 3.03 3.03 0 00-1.748 1.75 3.05 3.05 0 00.136 2.472c.195.379.467.713.799.98a3.018 3.018 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M214.628 134.484a3.032 3.032 0 003.03-3.036 3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.032 3.032 0 003.029 3.036zM230.527 141.355a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M212.065 131.916a3.028 3.028 0 013.369-3.013c.567.066 1.103.29 1.548.648a3.02 3.02 0 00-3.444-.937 3.016 3.016 0 00-1.746 1.751 3.048 3.048 0 00.137 2.472c.195.379.468.713.8.979a3.034 3.034 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M211.601 142.657c1.674 0 3.03-1.36 3.03-3.037a3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.033 3.033 0 003.029 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M209.039 140.086a3.037 3.037 0 011.717-2.73 3.017 3.017 0 013.199.365 3.03 3.03 0 00-2.202-1.158 3.026 3.026 0 00-3.013 1.959 3.043 3.043 0 00.975 3.464 3.02 3.02 0 01-.676-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M221.152 128.183a3.032 3.032 0 003.029-3.036 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M218.589 125.612a3.036 3.036 0 011.717-2.73 3.016 3.016 0 013.199.365 3.028 3.028 0 00-2.194-1.125 3.018 3.018 0 00-2.303.884 3.041 3.041 0 00-.554 3.527c.194.379.465.712.796.98a3.036 3.036 0 01-.661-1.901z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M218.822 132.617a3.032 3.032 0 003.03-3.036 3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.032 3.032 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M216.26 130.049a3.033 3.033 0 011.715-2.732 3.024 3.024 0 013.201.364 3.02 3.02 0 00-4.497-.241 3.041 3.041 0 00-.554 3.527c.194.378.465.712.795.979a3.028 3.028 0 01-.66-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M220.685 136.822a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M218.122 134.25a3.037 3.037 0 011.717-2.731 3.025 3.025 0 013.199.366 3.027 3.027 0 00-3.444-.937 3.021 3.021 0 00-1.746 1.751 3.035 3.035 0 00.938 3.451 3.023 3.023 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M225.343 145.456a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M222.784 142.887a3.035 3.035 0 011.715-2.734 3.02 3.02 0 013.201.369 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.025 3.025 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M229.07 138.454a3.032 3.032 0 003.03-3.036 3.033 3.033 0 00-3.03-3.037 3.033 3.033 0 00-3.029 3.037 3.032 3.032 0 003.029 3.036zM196.727 134.334a3.033 3.033 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M226.507 135.885a3.033 3.033 0 011.717-2.73 3.019 3.019 0 013.2.365 3.022 3.022 0 00-4.504-.251 3.029 3.029 0 00-.882 2.312 3.025 3.025 0 001.13 2.201 3.025 3.025 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M221.385 141.724a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M218.822 139.153a3.038 3.038 0 011.717-2.73 3.02 3.02 0 013.2.365 3.025 3.025 0 00-5.379 2.066c.022.425.134.84.328 1.219.193.379.464.713.795.98a3.02 3.02 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M221.849 145.456a3.032 3.032 0 003.03-3.036 3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.032 3.032 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M219.286 142.888a3.028 3.028 0 013.369-3.012 3.016 3.016 0 011.548.647 3.02 3.02 0 00-3.444-.937 3.016 3.016 0 00-1.746 1.751 3.043 3.043 0 00.937 3.451 3.034 3.034 0 01-.664-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M213 148.025a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M210.437 145.456a3.036 3.036 0 011.715-2.734 3.013 3.013 0 013.201.369 3.022 3.022 0 00-5.19.81 3.041 3.041 0 00.935 3.452 3.006 3.006 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M202.983 138.22a3.033 3.033 0 003.029-3.037 3.032 3.032 0 00-3.029-3.036 3.032 3.032 0 00-3.03 3.036 3.033 3.033 0 003.03 3.037z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M200.42 135.652a3.027 3.027 0 013.369-3.012 3.013 3.013 0 011.547.647 3.028 3.028 0 00-2.195-1.114 3.024 3.024 0 00-2.979 1.947 3.04 3.04 0 00.925 3.441 3.025 3.025 0 01-.667-1.909z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M206.009 137.988a3.033 3.033 0 003.03-3.036 3.033 3.033 0 00-3.03-3.036 3.033 3.033 0 00-3.03 3.036 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M203.447 135.406a3.039 3.039 0 011.718-2.727 3.024 3.024 0 013.198.362 3.019 3.019 0 00-2.195-1.126 3.023 3.023 0 00-2.988 1.944 3.03 3.03 0 00.132 2.467c.194.379.465.713.795.98a3.027 3.027 0 01-.66-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M199.723 141.724a3.032 3.032 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.032 3.032 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M197.16 139.153a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.028 3.028 0 00-2.194-1.126 3.026 3.026 0 00-3.185 3.192 3.036 3.036 0 001.124 2.199 3.02 3.02 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M226.043 149.426a3.032 3.032 0 003.03-3.036 3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.032 3.032 0 003.029 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M226.043 149.426a3.032 3.032 0 003.03-3.036 3.032 3.032 0 00-3.03-3.036 3.032 3.032 0 00-3.029 3.036 3.032 3.032 0 003.029 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M223.481 146.858a3.027 3.027 0 013.369-3.012 3.013 3.013 0 011.547.647 3.015 3.015 0 00-2.203-1.165 3.024 3.024 0 00-3.206 3.222c.027.43.146.849.348 1.229.201.38.481.713.821.976a3.034 3.034 0 01-.676-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M223.014 138.454a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M223.014 138.454a3.033 3.033 0 003.03-3.036 3.034 3.034 0 00-3.03-3.037 3.034 3.034 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M220.451 135.885a3.037 3.037 0 011.717-2.73 3.016 3.016 0 013.199.365 3.022 3.022 0 00-5.19.81 3.041 3.041 0 00.935 3.452 3.014 3.014 0 01-.661-1.897z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M214.395 130.752a3.033 3.033 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#795548"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M214.395 130.752a3.033 3.033 0 003.029-3.036 3.033 3.033 0 00-3.029-3.037 3.033 3.033 0 00-3.03 3.037 3.033 3.033 0 003.03 3.036z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M211.835 128.181a3.033 3.033 0 011.717-2.73 3.017 3.017 0 013.2.365 3.03 3.03 0 00-3.44-.93 3.03 3.03 0 00-1.744 1.748 3.034 3.034 0 00.928 3.447 3.022 3.022 0 01-.661-1.9z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M242.722 162.387h-66.841l7.126-23.635h52.59l7.125 23.635z",
  fill: "#E1E1E1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M185.013 140.763l48.675.102-.399-2.011-50.282-.102-7.126 23.635h2.612l6.52-21.624z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M244.428 163.897h-70.655l2.808-3.619h65.438l2.409 3.619z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M244.428 163.897h-70.655l2.808-3.619h65.438l2.409 3.619z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M244.428 163.897h-70.655l2.808-3.619h65.438l2.409 3.619z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.05,
  d: "M233.375 138.558l6.044 21.719 2.867.145-6.76-21.864h-2.151z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M187.524 57.963c-.148 10.377-8.884 18.584-19.239 18.584h-1.441a4.867 4.867 0 00-3.411 1.38 4.757 4.757 0 00-1.385 2.423 4.453 4.453 0 00-.145 1.149v18.32a18.379 18.379 0 01-2.888 9.937c-2.713 4.244-4.302 9.1-4.302 14.138a8.843 8.843 0 01-5.554 8.203 8.809 8.809 0 01-3.422.626c-4.842-.09-8.64-4.262-8.64-9.119v-4.582l-2.4.284v4.588a8.852 8.852 0 01-2.642 6.301 8.815 8.815 0 01-6.336 2.528c-4.843-.09-8.638-4.262-8.638-9.119v-2.318c-2.403 1.004-16.015 1.004-19.795.034l-1.026-.081v2.655a8.848 8.848 0 01-2.64 6.301 8.823 8.823 0 01-6.335 2.528c-4.846-.09-8.641-4.262-8.641-9.119v-3.809l-.802-.074v4.173c0 .739-.09 1.476-.27 2.193a8.848 8.848 0 01-3.204 4.831 8.818 8.818 0 01-5.501 1.805c-4.428-.08-7.974-3.572-8.561-7.881-.05-.408-.077-.82-.08-1.231v-19.176a29.407 29.407 0 016.247-18.17 5.452 5.452 0 00.15-6.43c-4.483-6.58-11.114-17.684-10.896-25.558.056-2.28.68-4.278 2.1-5.827.085-.095.174-.19.263-.28 6.453-6.775 17.822-1.444 16.83 7.865a26.184 26.184 0 00.682 9.31 28.585 28.585 0 001.8 4.99 8.455 8.455 0 003.545 3.807 8.429 8.429 0 005.096 1.007 29.72 29.72 0 013.61-.216h37.018a10.384 10.384 0 006.051-1.96 10.424 10.424 0 003.782-5.122c.375-1.081.564-2.219.559-3.363V35.63a5.63 5.63 0 011.704-4.036 5.594 5.594 0 014.079-1.58 5.433 5.433 0 013.878 1.795 6.064 6.064 0 011.561 4.046 2.987 2.987 0 00.87 2.105 2.972 2.972 0 002.101.87h17.447a18.778 18.778 0 0113.405 5.618 18.87 18.87 0 015.41 13.515h.006z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M77.553 126.086a8.851 8.851 0 01-3.203 4.832 8.817 8.817 0 01-5.502 1.804c-4.428-.08-7.974-3.572-8.56-7.881 5.334-1.447 12.724-2.438 17.265 1.245zM95.968 126.086a8.844 8.844 0 01-3.201 4.832 8.808 8.808 0 01-5.501 1.804c-4.428-.08-7.99-3.572-8.56-7.881 5.334-1.447 12.724-2.438 17.262 1.245zM134.405 126.086a8.845 8.845 0 01-3.203 4.832 8.814 8.814 0 01-5.502 1.804c-4.428-.08-7.99-3.572-8.558-7.881 5.331-1.447 12.721-2.438 17.263 1.245zM154.424 126.086a8.85 8.85 0 01-3.204 4.832 8.815 8.815 0 01-5.501 1.804c-4.428-.08-7.989-3.572-8.561-7.881 5.335-1.447 12.724-2.438 17.266 1.245zM75.583 65.353c-6.222-2.504-13.827-7.083-19.874-11.068.055-2.279.679-4.277 2.099-5.826.086-.096.175-.191.264-.28 6.453-6.775 17.822-1.445 16.83 7.864-.333 3.12-.102 6.273.681 9.31zM83.118 74.999s17.13 21.612 45.169-.056L83.117 75zM137.078 34.818s10.411 13.642 0 30.486V34.818zM161.903 81.364s-9.169 11.416-.123 20.476l.123-20.476z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M162.702 78.954c-.679.641-.449 1.26-.655 2.2l-29.765-7.816c1.601-1.232 3.586-2.704 4.243-4.663 3.565 2.775 10.62 7.43 26.177 10.28z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M163.433 77.927a4.756 4.756 0 00-1.385 2.423l-29.019-7.582a10.384 10.384 0 003.49-4.896c3.571 2.778 11.354 7.206 26.914 10.055z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M137.61 69.477s9.08 6.843 24.293 8.958l-24.293-8.958z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M137.61 68.675s9.08 6.843 24.293 8.961l-24.293-8.96z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M160.302 69.326s15.213 4.813 19.217-10.433l-19.217 10.433z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M160.302 68.523s15.213 4.816 19.217-10.43l-19.217 10.43z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M187.527 58.893c.884 0 1.601-1.257 1.601-2.809 0-1.55-.717-2.808-1.601-2.808s-1.601 1.257-1.601 2.808c0 1.552.717 2.809 1.601 2.809z",
  fill: "#383F4D"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M66.218 113.864l3.201-25.68s-9.611 15.247-3.201 25.68z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M70.212 88.184s-9.609 15.247-3.205 25.68l3.205-25.68z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationHouseVisting_svg__paint0_linear",
  x1: 141.5,
  y1: 194.001,
  x2: 141.5,
  y2: 55.117,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationHouseVisting_svg__paint1_linear",
  x1: 52327.2,
  y1: 28586.8,
  x2: 70471.4,
  y2: 28586.8,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})))));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationPetDayCare = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#illustrationPetDayCare_svg__clip0)"
}, /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M254.128 171.561c-2.11 1.089-4.321 2.03-6.499 2.953-8.057 3.405-16.239 6.845-24.892 8.015-16.26 2.182-33.035-3.755-49.008-.05-13.379 3.093-24.444 12.571-37.516 16.792-6.078 1.962-12.401 2.731-18.782 2.731h-.823c-.786 0-1.568-.03-2.353-.06-6.469-.243-12.965-1.187-19.306-2.425-20.024-3.859-40.403-10.932-55.712-23.955 44.575-8.16 136.395-20.681 214.891-4.001z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.3,
  d: "M269.665 73.914V54.916h-11.261v18.998h-13.335V44.527h-9.779V22.561h-8.001V0h-16.595v22.56h-8.001v21.966h-9.483V18.108h-5.334V4.75h-3.259v13.358h-18.966v84.303h-14.52v-8.905h-8.89v8.905H127.72V73.914h-26.077V54.916h-11.26v18.998h-.593v36.512H77.936V89.053H63.712v21.373H51.859V44.527h-9.187V22.561H8.89v21.966H0v116.065h283V73.914h-13.335z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.4,
  d: "M18.076 51.354H5.63v7.124h12.446v-7.124zM33.486 58.48h12.446v-7.125H33.486v7.124zM18.076 65.306H5.63v7.124h12.446v-7.124zM33.486 72.43h12.446v-7.125H33.486v7.124zM18.076 79.258H5.63v7.124h12.446v-7.124zM33.486 86.381h12.446v-7.124H33.486v7.124zM18.076 93.208H5.63v7.124h12.446v-7.124zM33.486 100.334h12.446V93.21H33.486v7.124zM18.076 107.161H5.63v7.124h12.446v-7.124zM33.486 114.284h12.446v-7.124H33.486v7.124zM18.076 121.113H5.63v7.124h12.446v-7.124zM33.486 128.236h12.446v-7.124H33.486v7.124zM18.076 135.063H5.63v7.124h12.446v-7.124zM33.486 142.188h12.446v-7.124H33.486v7.124zM85.344 117.55h-29.04v3.562h29.04v-3.562zM85.344 122.893h-29.04v3.562h29.04v-3.562zM85.344 128.236h-29.04v3.562h29.04v-3.562zM161.206 119.33h-29.041v3.562h29.041v-3.562zM161.206 124.673h-29.041v3.562h29.041v-3.562zM161.206 130.016h-29.041v3.562h29.041v-3.562zM85.344 133.579h-29.04v3.562h29.04v-3.562zM85.344 138.922h-29.04v3.562h29.04v-3.562zM85.344 144.265h-29.04v3.562h29.04v-3.562zM104.014 80.742h-8.001v10.39h8.001v-10.39zM113.496 91.132h8.001v-10.39h-8.001v10.39zM104.014 97.364h-8.001v10.389h8.001v-10.39zM113.496 107.753h8.001v-10.39h-8.001v10.39zM104.014 113.987h-8.001v10.39h8.001v-10.39zM113.496 124.377h8.001v-10.389h-8.001v10.389zM104.014 130.611h-8.001V141h8.001v-10.389zM113.496 141.001h8.001v-10.39h-8.001v10.39zM176.319 24.935h-10.668V38.59h10.668V24.935zM176.319 44.527h-10.668v13.655h10.668V44.527zM176.319 64.118h-10.668v13.655h10.668V64.118zM176.319 83.71h-10.668v13.655h10.668V83.71zM176.319 103.302h-10.668v13.654h10.668v-13.654zM176.319 122.893h-10.668v13.655h10.668v-13.655zM238.994 52.838h-39.709v11.577h39.709V52.838zM239.142 77.772h-39.709V89.35h39.709V77.772zM239.291 102.709h-39.709v11.577h39.709v-11.577zM239.439 127.643H199.73v11.577h39.709v-11.577zM14.224 28.794H8.89v11.28h5.334v-11.28zM283 80.742h-13.335V97.96H283V80.742zM283 105.677h-13.335v17.216H283v-17.216zM283 130.611h-13.335v17.217H283v-17.217z",
  fill: "#fff"
}), /*#__PURE__*/React.createElement("path", {
  d: "M35.48 178.961s-7.31-24.863 1.372-43.163c3.642-7.679 4.851-16.303 3.168-24.638a65.943 65.943 0 00-3.482-11.577",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M41.931 88.032c0 3.045-5.506 11.873-5.506 11.873s-5.509-8.837-5.509-11.873a5.526 5.526 0 011.567-3.985 5.51 5.51 0 013.94-1.662 5.5 5.5 0 013.941 1.662 5.519 5.519 0 011.567 3.985zM51.047 104.911c-1.654 2.559-11.072 6.976-11.072 6.976s.17-10.416 1.823-12.972a5.5 5.5 0 017.578-1.572 5.524 5.524 0 011.671 7.568zM50.647 133.755c-2.715 1.372-13.071.422-13.071.422s5.396-8.906 8.113-10.277a5.501 5.501 0 017.337 2.476 5.524 5.524 0 01-2.38 7.379zM45.44 155.51c-2.445 1.811-12.81 2.63-12.81 2.63s3.813-9.692 6.261-11.503a5.502 5.502 0 017.617 1.22 5.525 5.525 0 01-1.068 7.653zM28.41 112.477c2.18 2.123 12.348 4.322 12.348 4.322s-2.49-10.113-4.673-12.235a5.496 5.496 0 00-6.018-1.109 5.516 5.516 0 00-1.657 9.022zM22.643 137.996c2.715 1.374 13.072.424 13.072.424s-5.397-8.905-8.111-10.276a5.502 5.502 0 00-7.497 2.391 5.524 5.524 0 002.536 7.461zM20.435 163.573c2.445 1.81 12.814 2.63 12.814 2.63s-3.817-9.692-6.265-11.503a5.506 5.506 0 00-7.617 1.22 5.526 5.526 0 001.068 7.653z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.25,
  d: "M41.931 88.032c0 3.045-5.506 11.873-5.506 11.873s-5.509-8.837-5.509-11.873a5.526 5.526 0 011.567-3.985 5.51 5.51 0 013.94-1.662 5.5 5.5 0 013.941 1.662 5.519 5.519 0 011.567 3.985zM51.047 104.911c-1.654 2.559-11.072 6.976-11.072 6.976s.17-10.416 1.823-12.972a5.5 5.5 0 017.578-1.572 5.524 5.524 0 011.671 7.568zM50.647 133.755c-2.715 1.372-13.071.422-13.071.422s5.396-8.906 8.113-10.277a5.501 5.501 0 017.337 2.476 5.524 5.524 0 01-2.38 7.379zM45.44 155.51c-2.445 1.811-12.81 2.63-12.81 2.63s3.813-9.692 6.261-11.503a5.502 5.502 0 017.617 1.22 5.525 5.525 0 01-1.068 7.653zM28.41 112.477c2.18 2.123 12.348 4.322 12.348 4.322s-2.49-10.113-4.673-12.235a5.496 5.496 0 00-6.018-1.109 5.516 5.516 0 00-1.657 9.022zM22.643 137.996c2.715 1.374 13.072.424 13.072.424s-5.397-8.905-8.111-10.276a5.502 5.502 0 00-7.497 2.391 5.524 5.524 0 002.536 7.461zM20.435 163.573c2.445 1.81 12.814 2.63 12.814 2.63s-3.817-9.692-6.265-11.503a5.506 5.506 0 00-7.617 1.22 5.526 5.526 0 001.068 7.653z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M263.673 172.54s-7.31-24.863 1.372-43.163c3.642-7.679 4.854-16.303 3.171-24.638a66.475 66.475 0 00-3.482-11.577",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M270.136 81.611c0 3.046-5.509 11.874-5.509 11.874s-5.508-8.837-5.508-11.874c0-1.463.58-2.867 1.613-3.902a5.503 5.503 0 017.791 0 5.523 5.523 0 011.613 3.902zM279.239 98.489c-1.653 2.559-11.071 6.976-11.071 6.976s.172-10.416 1.826-12.972a5.505 5.505 0 017.615-1.633 5.516 5.516 0 012.393 3.477 5.526 5.526 0 01-.763 4.152zM278.843 127.335c-2.718 1.371-13.075.421-13.075.421s5.4-8.905 8.114-10.276a5.501 5.501 0 017.399 2.443 5.52 5.52 0 01-2.438 7.412zM273.636 149.089c-2.448 1.811-12.814 2.63-12.814 2.63s3.817-9.692 6.262-11.503a5.506 5.506 0 017.669 1.181 5.525 5.525 0 01-1.117 7.692zM256.602 106.047c2.184 2.123 12.348 4.322 12.348 4.322s-2.489-10.113-4.67-12.236a5.508 5.508 0 00-6.057-1.171 5.502 5.502 0 00-2.963 3.054 5.533 5.533 0 001.342 6.031zM250.847 131.575c2.718 1.374 13.075.424 13.075.424s-5.396-8.905-8.114-10.277a5.5 5.5 0 00-6.152.678 5.513 5.513 0 00-1.922 3.816 5.53 5.53 0 001.376 4.046 5.519 5.519 0 001.737 1.313zM248.628 157.151c2.448 1.811 12.814 2.63 12.814 2.63s-3.817-9.688-6.262-11.502a5.5 5.5 0 00-7.669 1.181 5.522 5.522 0 001.117 7.691z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M230.697 170.119s-3.93-13.358.738-23.195a21.163 21.163 0 001.704-13.239 35.657 35.657 0 00-1.873-6.216",
  stroke: "#535461",
  strokeWidth: 2,
  strokeMiterlimit: 10
}), /*#__PURE__*/React.createElement("path", {
  d: "M234.17 121.254c0 1.638-2.963 6.388-2.963 6.388s-2.964-4.75-2.964-6.388a2.967 2.967 0 012.964-2.969 2.967 2.967 0 012.963 2.969zM239.062 130.326c-.889 1.375-5.95 3.75-5.95 3.75s.092-5.599.981-6.973a2.96 2.96 0 015.294 1.038 2.97 2.97 0 01-.325 2.185zM238.849 145.824c-1.461.736-7.026.225-7.026.225s2.901-4.785 4.359-5.521a2.958 2.958 0 013.866 1.368 2.975 2.975 0 01-1.199 3.928zM236.049 157.516c-1.313.974-6.884 1.413-6.884 1.413s2.051-5.207 3.366-6.18a2.96 2.96 0 014.272.523 2.98 2.98 0 01.534 2.284 2.964 2.964 0 01-1.288 1.96zM226.898 134.393c1.185 1.14 6.635 2.321 6.635 2.321s-1.337-5.435-2.51-6.575a2.958 2.958 0 00-3.304-.709 2.96 2.96 0 00-1.82 2.852 2.964 2.964 0 00.999 2.111zM223.798 148.106c1.461.736 7.026.229 7.026.229s-2.901-4.785-4.362-5.524a2.961 2.961 0 00-3.866 1.368 2.975 2.975 0 00-.188 2.205c.223.731.72 1.349 1.387 1.722h.003zM222.613 161.854c1.312.974 6.884 1.413 6.884 1.413s-2.06-5.21-3.364-6.183a2.963 2.963 0 00-4.049.69 2.97 2.97 0 00.529 4.08z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M78.434 14.153h-2.039a1.427 1.427 0 001.494-1.43 1.433 1.433 0 00-.927-1.339 1.426 1.426 0 00-.567-.09H59.881a1.426 1.426 0 00-1.379.867 1.433 1.433 0 001.379 1.992h2.038a1.427 1.427 0 00-1.428 1.43 1.432 1.432 0 001.428 1.432h-2.853a1.427 1.427 0 00-1.494 1.429 1.433 1.433 0 001.494 1.43H75.58a1.427 1.427 0 001.494-1.43 1.433 1.433 0 00-.927-1.34 1.427 1.427 0 00-.567-.09h2.854a1.427 1.427 0 001.428-1.43 1.432 1.432 0 00-1.428-1.43zM151.333 50.072h-2.039a1.424 1.424 0 001.378-.867 1.431 1.431 0 00-1.378-1.992h-16.515a1.426 1.426 0 00-1.493 1.43 1.431 1.431 0 001.493 1.429h2.039a1.427 1.427 0 00-1.429 1.43 1.432 1.432 0 001.429 1.431h-2.854a1.426 1.426 0 00-1.493 1.43 1.431 1.431 0 001.493 1.43h16.515a1.424 1.424 0 001.378-.868 1.431 1.431 0 00-1.378-1.991h2.854a1.424 1.424 0 001.319-.884 1.429 1.429 0 00-1.319-1.978zM275.497 3.17h-2.039a1.422 1.422 0 001.378-.866 1.431 1.431 0 00-1.378-1.992h-16.515a1.425 1.425 0 00-1.378.867 1.431 1.431 0 001.378 1.992h2.039a1.427 1.427 0 00-1.428 1.43 1.431 1.431 0 001.428 1.431h-2.854a1.428 1.428 0 00-1.378 1.992 1.427 1.427 0 001.378.867h16.515a1.422 1.422 0 001.378-.867 1.432 1.432 0 00-.326-1.597 1.432 1.432 0 00-1.052-.395h2.854a1.427 1.427 0 001.428-1.43 1.431 1.431 0 00-1.428-1.431z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  d: "M209.064 156.983a.445.445 0 100-.89.445.445 0 000 .89z",
  fill: "#535461"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.5,
  d: "M154.696 131.504l-4.306-20.303h.441l-.068-.345h.125l-2.229-10.983s.113-11.473.655-15.91c.397-3.24 1.025-7.365.566-10.838a3.142 3.142 0 00.513-1.484 4.898 4.898 0 00-.374-2.865l4.478-2.802c.101 0 .205.02.296.027 0-.042-.041-.107-.074-.193a9.944 9.944 0 005.99-1.624 9.965 9.965 0 003.866-4.862 6.62 6.62 0 001.444 1.555c1.206.905 3.105 1.107 4.11-.018a3.47 3.47 0 00.72-1.552c.059-.255.101-.517.136-.775.569-3.034-.678-6.162-2.288-8.822-2.012-3.322-4.682-6.329-8.075-8.217-3.393-1.887-7.556-2.564-11.228-1.306-3.671 1.259-7.37 4.676-7.705 8.55-.166 1.98.072 2.897-.337 4.84-.205.969.237 2.854.231 3.922a4.74 4.74 0 00-.071.594c-.06 1.187.412 2.31.755 3.45-4.842.242-17.575 1.225-21.253 4.971-4.575 4.658-15.795 6.323-15.795 6.323l-18.683 3.767-6.52 2.772.074.297-.13.056s.231.864.563 2.004a8.486 8.486 0 00-2.694 5.046c-.361 2.016-.169 4.61 1.106 7.92 0 0 6.433 11.835 6.877 16.608l8.12 16.235c-.696 1.392-7.654 15.462-8.852 23.337a16.117 16.117 0 00-.192 1.713s-.044.416-.154 1.107c-.477 2.918-1.986 10.093-5.67 13.447l.048.042-.106.104.083.071c-1.106 2.116.492 4.984.492 4.984l1.816 8.127 2.033.33 2.14-.873 1.185 2.283s9.23 1.484 11.853 1.398c2.623-.086-2.222-4.539-3.92-5.86-.943-.73-1.68-3.731-2.149-6.257a3.92 3.92 0 00.661-.686s-1.129-5.937 2.27-14.082c1.45-3.467 3.008-7.454 4.326-10.888.709 4.613 1.453 9.365 2.001 12.61.797 4.699.693 7.566.447 9.184a5.432 5.432 0 01-.394 1.641.48.48 0 01.03.048.903.903 0 01-.057.122c.018.029.036.056.051.086-1.855 2.125.077 5.592.077 5.592l1.816 8.104 2.033.329 2.14-.872 1.185 2.282s9.231 1.485 11.854 1.399c2.622-.087-2.223-4.539-3.924-5.86-1.325-1.03-2.243-6.56-2.584-8.932.246-.024.382-.042.382-.042l-.026-.163h.056l-1.162-6.803c-1.985-1.713-1.71-26.045-1.71-26.045s-1.034 2.544-1.066 2.339c.1-.41.474-1.567 1.339-4.034a24.362 24.362 0 001.123-4.358c.94-5.791-.812-9.332-.812-9.332.116-1.164-1.108-5.685-2.554-10.5l4.543-8.869a3.619 3.619 0 001.979.163l3.441-6.234s7.112-4.245 9.148-3.915c1.893.297 7.826-1.003 10.119-3.479.165-.18.316-.374.451-.579 1.846-2.835 1.087 14.801 1.087 14.801l-.986 6.109-.054.338 2.943.059 6.934 23.825s.436 8.08 8.315 9.876c7.88 1.796-3.265-13.225-3.265-13.225zm-47.364-27.534c-.426-1.318-.8-2.455-1.081-3.307l1.467-1.146s.183.154.494.4l-.88 4.053z",
  fill: "url(#illustrationPetDayCare_svg__paint0_linear)"
}), /*#__PURE__*/React.createElement("path", {
  d: "M114.483 168.995s1.005 8.478 2.667 9.766c1.663 1.289 6.38 5.614 3.823 5.709-2.558.095-11.557-1.36-11.557-1.36l-1.165-2.226-2.074.849-1.983-.321-1.778-7.923s-2.495-4.476 1.017-6.284c3.511-1.807 11.05 1.79 11.05 1.79z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M106.04 99.49s6.979 20.806 6.715 23.478c0 0 2.557 5.165-.264 13.194-2.821 8.03-.296 1.82-.296 1.82s-.267 23.714 1.665 25.386l1.132 6.631s-10.881 1.294-12.712-2.057c0 0 1.416-2.485.029-10.686-1.387-8.202-4.06-26.274-4.06-26.274L91.007 89.76l15.033 9.73z",
  fill: "#C3C3C3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M106.067 99.323s6.979 20.818 6.715 23.489c0 0 2.557 5.165-.264 13.195-2.821 8.029-.296 1.819-.296 1.819s-.267 23.718 1.665 25.386l1.132 6.635s-10.881 1.291-12.712-2.06c0 0 1.416-2.485.029-10.687-1.387-8.201-4.063-26.282-4.063-26.282l-7.239-41.225 15.033 9.73z",
  fill: "#E1E1E1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M92.83 90.673l7.097 40.412s2.667 18.08 4.063 26.282c1.396 8.202-.03 10.686-.03 10.686.534.977 1.829 1.556 3.399 1.891-2.205-.258-4.193-.861-4.886-2.131 0 0 1.419-2.485.032-10.686-1.387-8.202-4.063-26.283-4.063-26.283L91.2 89.619l1.63 1.054z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M92.72 166.32s1.002 8.478 2.668 9.766c1.665 1.288 6.38 5.613 3.822 5.708-2.557.095-11.557-1.359-11.557-1.359l-1.164-2.226-2.075.851-1.982-.323-1.778-7.923s-2.495-4.476 1.013-6.284c3.509-1.808 11.054 1.79 11.054 1.79z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.925 81.145s-7.767 2.986-3.588 13.844c0 0 6.273 11.536 6.709 16.19l7.941 15.881s-8.469 16.789-8.845 24.365c0 0-1.132 10.169-5.702 14.349 0 0 9.83 8.38 13.49 3.541 0 0-1.1-5.776 2.216-13.726 3.316-7.949 7.248-18.671 7.248-18.671s9.51-6.438 3.174-19.683l-7.112-29.492-.115-6.637-5.456-1.9-9.96 1.939z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.57 81.255S75.803 84.242 79.98 95.1c0 0 6.27 11.538 6.706 16.193l7.942 15.881s-8.47 16.786-8.84 24.364c0 0-1.132 10.167-5.704 14.35 0 0 9.83 8.379 13.492 3.541 0 0-1.102-5.78 2.213-13.726 3.316-7.947 7.249-18.683 7.249-18.683s9.512-6.439 3.176-19.684l-7.112-29.49-.115-6.638-5.453-1.9-9.965 1.947z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M83.596 81.092s-7.767 2.989-3.588 13.844c0 0 4.608 8.475 6.196 13.827a41.283 41.283 0 002.667 6.676l5.785 11.577s-8.47 16.789-8.843 24.365c0 0-1.132 10.169-5.704 14.349 0 0 9.829 8.38 13.492 3.541 0 0-1.103-5.776 2.213-13.726 3.316-7.949 7.249-18.683 7.249-18.683s9.512-6.438 3.176-19.683l-7.112-29.492-.115-6.637-5.453-1.9-9.963 1.942z",
  fill: "#E1E1E1"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M83.596 81.092l9.972-1.95 1.301.454-9.29 1.814s-7.767 2.986-3.589 13.845c0 0 4.605 8.474 6.197 13.826a41.274 41.274 0 002.667 6.676l5.787 11.577s-8.481 16.784-8.846 24.359c0 0-1.135 10.17-5.704 14.349 0 0 4.235 3.61 8.179 4.723-4.35-.089-10.161-5.046-10.161-5.046 4.572-4.18 5.704-14.35 5.704-14.35.373-7.575 8.843-24.364 8.843-24.364l-5.785-11.577a41.283 41.283 0 01-2.667-6.676c-1.588-5.343-6.196-13.827-6.196-13.827-4.178-10.843 3.588-13.833 3.588-13.833z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M115.597 102.223l-6.184 12.067s1.813 16.077-1.651 16.534c-3.464.457-2.074-17.644-2.074-17.644l3.334-15.415 6.575 4.458zM149.4 110.073l4.422 20.909s10.852 14.655 3.171 12.904c-7.681-1.751-8.105-9.638-8.105-9.638l-7.409-25.469 7.921 1.294zM153.807 66.997l-10.935 6.886-3.903-10.475s9.05.617 10.105-2.776c1.055-3.392 4.733 6.365 4.733 6.365z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M148.191 71.353l-.699-3-10.778.631-4.341 17.458 8.087.29 4.528-7.073 2.468-6.899.735-1.407z",
  fill: "#BE7C5E"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M144.463 68.204l.889-5.031s-19.946.25-24.406 4.788c-4.46 4.538-15.395 6.165-15.395 6.165l-18.212 3.67-6.368 2.703s1.795 6.739 2.566 7.205c.77.466 9.37-6.632 9.37-6.632l3.26.867-10.615 11.18 19.591 19.294.361-10.633 2.51-1.965s5.965 5.046 8.626 4.28l3.355-6.076s6.934-4.138 8.917-3.818c1.846.297 7.63-.976 9.865-3.39.156-.169.299-.35.426-.543 1.802-2.763 1.061 14.427 1.061 14.427l-1.013 6.284 10.807.223-2.169-10.704s.109-11.182.637-15.51c.527-4.328 1.482-10.28-.453-13.99l-3.805 3.627-6.62 10.645 2.512-9.268 4.303-7.798z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.52 67.871l-.946-5.162s-18.103.38-22.563 4.919c-4.459 4.538-15.409 6.162-15.409 6.162l-18.213 3.672-6.341 2.707s1.796 6.739 2.566 7.202c.77.463 9.373-6.629 9.373-6.629l3.26.867-10.615 11.179 19.591 19.295.361-10.636 2.519-1.968s5.965 5.046 8.626 4.283l3.355-6.076s6.896-4.138 8.89-3.809c1.846.297 7.631-.98 9.865-3.392.157-.17.3-.351.427-.544 1.801-2.763 1.061 14.43 1.061 14.43l-1.014 6.284 10.807.223-2.169-10.705s.11-11.185.637-15.51c.528-4.325 1.482-10.282-.453-13.99l-3.805 3.613-6.629 10.636 2.513-9.268 4.306-7.783z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M121.687 74.442s-10.79 9.116-11.919 14.025l11.919-14.025zM133.466 82.3s-8.297 6.294-9.779 10.295l9.779-10.294zM109.235 91.774s-.779 3.776-.933 5.789l.933-5.789zM105.291 100.471S92.863 89.295 91.123 89.523l14.168 10.948zM149.205 60.824c1.01-3.247 4.424 5.557 4.712 6.314a9.646 9.646 0 01-4.614-1.489 9.668 9.668 0 01-2.804-2.712c1.331-.418 2.392-1.08 2.706-2.113z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M154.364 66.965c5.358 0 9.702-4.351 9.702-9.719 0-5.367-4.344-9.718-9.702-9.718-5.359 0-9.702 4.351-9.702 9.718 0 5.368 4.343 9.72 9.702 9.72z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M162.244 58.648c.761 1.256 1.455 2.618 2.631 3.497 1.176.878 3.029 1.077 4.006-.018a3.39 3.39 0 00.7-1.514c.717-3.06-.537-6.233-2.163-8.932-1.962-3.238-4.567-6.168-7.871-8.014-3.304-1.847-7.367-2.503-10.947-1.274-3.579 1.229-6.519 4.512-6.815 8.288-.163 1.93.323 3.897-.077 5.794-.208.989-.655 1.93-.706 2.939-.095 1.873 1.186 3.615 1.07 5.489-.071 1.154-.661 2.202-1.022 3.303-.889 2.654-.409 5.542-.593 8.333-.184 2.79-1.402 5.919-4.069 6.744 2.949 1.484 7.015-.077 8.215-3.159.364-.94.498-1.985 1.052-2.828.936-1.425 2.883-1.99 3.668-3.503.786-1.514.104-3.357-.755-4.827-.86-1.47-1.932-2.947-1.95-4.648a4.096 4.096 0 012.667-3.806c.809-.282 1.683-.297 2.533-.392a13.403 13.403 0 005.88-2.247c2.279-1.493 2.889-1.956 4.546.775z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M162.306 58.222c.761 1.26 1.455 2.619 2.631 3.5 1.177.882 3.029 1.078 4.007-.017.355-.44.595-.96.699-1.514.717-3.06-.536-6.234-2.163-8.932-1.962-3.242-4.567-6.169-7.871-8.015-3.304-1.846-7.367-2.5-10.947-1.273-3.579 1.226-7.183 4.559-7.5 8.335-.163 1.93.071 2.823-.329 4.717-.207.988.276 2.968.226 3.971-.095 1.873 1.185 3.616 1.069 5.489-.071 1.155-.661 2.203-1.022 3.3-.889 2.657-.409 5.546-.593 8.336-.183 2.79-1.401 5.916-4.068 6.741 2.948 1.484 7.014-.077 8.214-3.158.364-.938.498-1.983 1.052-2.826.936-1.425 2.883-1.989 3.669-3.503.785-1.514.103-3.36-.753-4.83-.857-1.469-1.935-2.944-1.953-4.648a4.084 4.084 0 012.667-3.803c.809-.281 1.683-.296 2.534-.391a13.439 13.439 0 005.879-2.247c2.291-1.5 2.901-1.96 4.552.768z",
  fill: "#723647"
}), /*#__PURE__*/React.createElement("path", {
  d: "M142.171 63.85l4.116.666a.587.587 0 10.187-1.157l-4.116-.666a.587.587 0 10-.187 1.157z",
  fill: "#252A62"
}), /*#__PURE__*/React.createElement("path", {
  d: "M133.688 145.882c.069 4.649 3.983 8.312 8.624 8.312h.646c.57-.002 1.119.22 1.529.617.311.292.527.671.619 1.087.044.167.066.34.065.513v8.208a8.237 8.237 0 001.298 4.453 11.753 11.753 0 011.932 6.334 3.97 3.97 0 001.183 2.826 3.952 3.952 0 002.842 1.134c2.169-.038 3.87-1.909 3.87-4.084v-2.052l1.078.125v2.057a3.975 3.975 0 001.184 2.822 3.957 3.957 0 002.838 1.132c2.172-.038 3.873-1.909 3.873-4.084v-1.039c1.076.451 7.177.451 8.869 0l.459-.036v1.187a3.968 3.968 0 002.487 3.69 3.933 3.933 0 001.538.282c2.169-.038 3.87-1.909 3.87-4.084v-1.704l.358-.033v1.87c.001.525.107 1.045.311 1.529a3.952 3.952 0 003.714 2.422 4.026 4.026 0 003.852-3.529 5.17 5.17 0 00.036-.555v-8.585a13.17 13.17 0 00-2.801-8.133 2.441 2.441 0 01-.068-2.88c2.009-2.947 4.981-7.922 4.884-11.449a3.867 3.867 0 00-.94-2.609c-.038-.045-.08-.086-.118-.128-2.893-3.034-7.984-.647-7.542 3.524a11.81 11.81 0 01-.296 4.17 12.994 12.994 0 01-.809 2.235 3.792 3.792 0 01-3.873 2.159 13.073 13.073 0 00-1.618-.098h-16.595a4.66 4.66 0 01-4.416-3.168 4.528 4.528 0 01-.251-1.505v-12.942a2.522 2.522 0 00-1.605-2.348 2.45 2.45 0 00-2.719.641 2.699 2.699 0 00-.699 1.811 1.33 1.33 0 01-1.331 1.333h-7.844a8.42 8.42 0 00-6.008 2.518 8.461 8.461 0 00-2.426 6.054z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M182.987 176.399a3.958 3.958 0 003.899 2.969 4.019 4.019 0 003.853-3.53c-2.418-.644-5.731-1.089-7.752.561zM174.719 176.399a3.959 3.959 0 003.903 2.969 4.014 4.014 0 003.834-3.53c-2.388-.644-5.692-1.089-7.737.561zM157.502 176.399a3.958 3.958 0 003.9 2.969 4.018 4.018 0 003.852-3.53c-2.418-.644-5.725-1.089-7.752.561zM148.523 176.399a3.963 3.963 0 001.437 2.163 3.947 3.947 0 002.466.806 4.014 4.014 0 003.834-3.53c-2.388-.644-5.701-1.089-7.737.561zM183.858 149.193c2.789-1.122 6.191-3.176 8.89-4.957a3.86 3.86 0 00-.939-2.609c-.039-.045-.08-.086-.119-.128-2.892-3.033-7.983-.647-7.541 3.524a11.807 11.807 0 01-.291 4.17zM180.483 153.512s-7.678 9.683-20.24-.023l20.24.023zM156.296 135.515s-4.664 6.112 0 13.655v-13.655zM145.172 156.364s4.11 5.111.053 9.178l-.053-9.178z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M144.813 155.285c.296.297.201.564.296.985l13.335-3.499c-.717-.556-1.609-1.212-1.902-2.078-1.594 1.232-4.756 3.315-11.729 4.592z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.487 154.825c.311.291.527.67.62 1.086l13.006-3.396a4.655 4.655 0 01-1.565-2.193c-1.6 1.244-5.088 3.22-12.061 4.503z",
  fill: "#4A4A4A"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M156.059 151.039s-4.068 3.066-10.887 4.016l10.887-4.016z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M156.059 150.681s-4.068 3.067-10.887 4.017l10.887-4.017z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M145.889 150.971s-6.816 2.158-8.612-4.672l8.612 4.672z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M145.889 150.612s-6.816 2.158-8.612-4.673l8.612 4.673z",
  fill: "#FBDCA3"
}), /*#__PURE__*/React.createElement("path", {
  d: "M133.689 146.299c.396 0 .717-.564.717-1.259s-.321-1.259-.717-1.259c-.396 0-.717.564-.717 1.259s.321 1.259.717 1.259z",
  fill: "#383F4D"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M188.054 170.922l-1.434-11.502s4.306 6.827 1.434 11.502z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M186.261 159.42s4.306 6.827 1.434 11.502l-1.434-11.502z",
  fill: "#FBDCA3"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("linearGradient", {
  id: "illustrationPetDayCare_svg__paint0_linear",
  x1: 75.131,
  y1: 103.532,
  x2: 164.876,
  y2: 118.011,
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React.createElement("stop", {
  stopColor: "gray",
  stopOpacity: 0.25
}), /*#__PURE__*/React.createElement("stop", {
  offset: 0.54,
  stopColor: "gray",
  stopOpacity: 0.12
}), /*#__PURE__*/React.createElement("stop", {
  offset: 1,
  stopColor: "gray",
  stopOpacity: 0.1
})), /*#__PURE__*/React.createElement("clipPath", {
  id: "illustrationPetDayCare_svg__clip0"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  transform: "translate(0 .001)",
  d: "M0 0h283v202H0z"
})))));

/**
 * The Illustration[Name] component gets used to display an illustration.
 *
 * ## Usage Guidelines
 *
 * - For available illustrations, see Foundations → Illustrations. The
 *   [Name] placeholder represents an illustration name. For example, the
 *   "Pet Sitting" illustration gets written like this: `<IllustrationPetSitting />`.
 * - Illustrations get left unsized, i.e., no width or height is applied, as
 *   their usage is too custom for sizes to be defined. Therefore, an
 *   illustration's size gets defined in the component it's consumed in.
 * - All illustrations are decorative, meaning they don't get exposed to the
 *   [Accessibility
 *   Tree](https://developer.mozilla.org/en-US/docs/Glossary/Accessibility_tree).
 */
const IllustrationPetSitting = () => ( /*#__PURE__*/React.createElement("svg", {
  viewBox: "0 0 283 202",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false"
}, /*#__PURE__*/React.createElement("g", {
  clipPath: "url(#illustrationPetSitting_svg__clip0)"
}, /*#__PURE__*/React.createElement("path", {
  d: "M150.977 191.672c53.308 0 96.523-4.828 96.523-10.785 0-5.956-43.215-10.784-96.523-10.784-53.309 0-96.524 4.828-96.524 10.784 0 5.957 43.215 10.785 96.524 10.785z",
  fill: "#D5ECEC"
}), /*#__PURE__*/React.createElement("path", {
  d: "M198.846 168.955l1.665 5.129s8.017 8.283 5.118 10.528c-2.9 2.245-9.406-9.59-9.406-9.59l-1.199-5.791 3.822-.276z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M183.465 129.255l2.427-.809s3.775 1.078 4.314 4.853c.539 3.775 4.583 22.918 4.583 22.918s1.618.809.54 2.157c-1.079 1.348 5.392 13.481 5.392 13.481l-5.123 2.427s-10.245-10.785-10.245-11.864c0-1.078.269-2.157.269-3.235 0-1.079-.269-1.079 0-1.888.18-.704.271-1.429.27-2.157l-1.887-4.314-.54-21.569z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M59.878 147.751c13.464 0 24.379-22.271 24.379-49.742 0-27.472-10.915-49.743-24.379-49.743-13.463 0-24.378 22.27-24.378 49.743 0 27.471 10.915 49.742 24.378 49.742z",
  fill: "#ABD9D9"
}), /*#__PURE__*/React.createElement("path", {
  d: "M59.654 98.524c1.217-21.653 3.667-36.278 3.691-36.423l-.531-.09c-.025.145-2.48 14.794-3.697 36.474-1.124 20.01-1.396 50.276 3.697 80.829l.532-.088c-5.084-30.502-4.813-60.722-3.691-80.702z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M81.667 87.92L59.625 99.717l.255.476L81.92 88.395l-.254-.475zM36.61 93.833l-.254.475L58.401 106.1l.255-.475L36.61 93.833z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M117.617 136.138c18.424 0 33.359-30.476 33.359-68.069S136.041.001 117.617.001c-18.425 0-33.36 30.475-33.36 68.068 0 37.593 14.935 68.069 33.36 68.069z",
  fill: "#EAF6F6"
}), /*#__PURE__*/React.createElement("path", {
  d: "M117.211 68.767c1.665-29.634 5.018-49.651 5.052-49.85l-.532-.09c-.033.199-3.391 20.24-5.058 49.902-1.538 27.376-1.909 68.785 5.058 110.586l.532-.089c-6.958-41.749-6.588-83.111-5.052-110.459z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M147.479 54.352l-30.162 16.145.254.475 30.163-16.144-.255-.476zM85.73 62.444l-.255.476 30.167 16.136.255-.476L85.73 62.444z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M89.826 162.662c6.884 0 12.464-11.386 12.464-25.432s-5.58-25.432-12.464-25.432-12.464 11.386-12.464 25.432 5.58 25.432 12.464 25.432z",
  fill: "#EBEBEB"
}), /*#__PURE__*/React.createElement("path", {
  d: "M89.815 138.326l.073.137 11.27-6.032-.254-.475-11.066 5.923c.613-11.271 1.865-18.837 1.89-18.986l-.531-.09c-.028.163-1.516 9.148-2.044 22.148l-11.16-5.973-.255.475 11.27 6.032.134-.251c-.422 10.73-.186 24.147 2.055 37.588l.531-.089c-2.442-14.654-2.496-29.281-1.913-40.407z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M186.765 103.661c-4.334-7.386-12.909-7.73-12.909-7.73s-8.355-1.078-13.714 10.171c-4.996 10.486-11.891 20.61-1.11 23.065l1.947-6.114 1.206 6.569c1.535.111 3.075.138 4.612.079 11.545-.376 22.54.11 22.186-4.069-.47-5.555 1.952-14.864-2.218-21.971z",
  fill: "#2F2E41"
}), /*#__PURE__*/React.createElement("path", {
  d: "M156.556 162.826a16.508 16.508 0 01-.525 2.282c-.258.731-.656 1.407-.892 2.146-.751 2.355.29 4.981 1.954 6.798a10.977 10.977 0 005.362 3.212c1.501.38 3.061.435 4.608.488 4.283.147 8.676.278 12.734-1.111a21.953 21.953 0 003.699-1.695c.373-.179.701-.44.96-.762.31-.445.35-1.021.347-1.564-.009-1.832-.377-3.654-.29-5.483.048-1.013.236-2.026.144-3.036a5.588 5.588 0 00-4.243-4.824c-1.151-.249-2.346-.113-3.518.001a89.093 89.093 0 01-9.154.412c-3.122-.02-6.226-.501-9.333-.551-.919-.015-.9.271-1.193 1.168-.269.826-.49 1.667-.66 2.519z",
  fill: "#6B6B6B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M144.345 161.337a9.158 9.158 0 00-2.57-.275 6.475 6.475 0 00-4.686 2.457 6.603 6.603 0 00-1.27 5.17c.21.978.552 1.923 1.019 2.808.681 1.395 1.499 2.787 2.761 3.681.759.492 1.578.885 2.436 1.17l6.647 2.526c.977.371 1.955.743 2.943 1.087a57.159 57.159 0 0015.371 3.083c1.596.095 3.216.122 4.773-.244.381-.051.729-.241.978-.533.126-.223.201-.472.218-.728l.409-2.887c.092-.403.08-.822-.035-1.218-.187-.485-.657-.791-1.104-1.05-4.37-2.531-9.472-3.928-13.149-7.402-.866-.818-1.591-2.114-2.643-2.67-1.193-.63-2.598-.989-3.831-1.57-2.695-1.269-5.345-2.727-8.267-3.405z",
  fill: "#6B6B6B"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M150.98 165.23c2.349.742 12.267 5.886 13.967 7.682-.227.083-7.959-4.287-8.191-4.357-2.108-.639-4.229-1.281-6.232-2.215-.402-.188-2.617-1.072-2.517-1.585.114-.578 2.556.343 2.973.475z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M180.287 184.541a.549.549 0 00.373-.11.587.587 0 00.116-.433l-.011-3.934c-1.122-.539-2.376-.712-3.608-.878l-5.746-.774c.132.017-.898 2.496-.74 2.827.279.583 2.407 1.1 3.007 1.375 2.088.957 4.258 2.01 6.609 1.927z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M162.26 180.052c-1.085.174-2.296.328-2.999 1.18-.891 1.08-.511 2.69-.086 4.028.063.271.189.522.369.734.29.288.735.33 1.141.356.99.064 2.043.113 2.907-.376.392-.261.769-.544 1.129-.847a7.214 7.214 0 012.076-.957 20.812 20.812 0 014.168-.867 6.405 6.405 0 001.641-.291 1.834 1.834 0 001.157-1.133c.2-.715-.212-1.444-.607-2.071-.492-.781-1.014-1.603-1.748-2.178-1.07-.839-1.6-.195-2.678.342a22.615 22.615 0 01-6.47 2.08z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M159.426 181.185l-6.889-2.054a9.04 9.04 0 00-2.563-.501c-.874.001-1.788.289-2.354.96-.535.633-.687 1.497-.821 2.317l-.284 1.753a8.892 8.892 0 00-.16 2.693c.13.894.583 1.787 1.369 2.219.953.525 2.121.269 3.185.049a34.415 34.415 0 016.528-.706c.824-.01 1.689 0 2.418-.389.866-.463 1.384-1.442 1.473-2.427.136-1.498-.359-3.454-1.902-3.914zM182.994 179.394c.893-.157 1.681-.669 2.537-.972 1.204-.425 2.509-.425 3.785-.42.469.002.968.012 1.358.274.562.377.706 1.133.801 1.807l.577 4.068c.109.765.216 1.551.047 2.305-.169.754-.678 1.481-1.42 1.675-.901.236-1.784-.344-2.634-.725-2.126-.953-4.597-.71-6.814-1.422-.411-.132-.852-.334-1.019-.735a1.696 1.696 0 01-.08-.703c.038-1.308-.235-3.094.155-4.343.351-1.124 1.71-.809 2.707-.809z",
  fill: "#9B9B9B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M197.573 159.7a11.867 11.867 0 014.214-.29c1.126.125 2.287.441 3.11 1.227 1.001.958 1.322 2.438 1.349 3.829a12.032 12.032 0 01-2.577 7.64 6.922 6.922 0 01-1.474 1.431 9.686 9.686 0 01-1.918.932l-14.883 5.921c-3.916 1.558-7.845 3.121-11.93 4.148a.924.924 0 01-.722.036.93.93 0 01-.529-.494 31.157 31.157 0 01-3.262-6.019.54.54 0 01-.042-.451.534.534 0 01.22-.188l10.527-6.102a10.571 10.571 0 002.143-1.489 5.337 5.337 0 001.1-1.545c.218-.459.239-1.544.517-1.884.273-.333 1.207-.359 1.638-.49a14.768 14.768 0 001.783-.68c2.355-1.063 4.452-2.606 6.709-3.851a17.193 17.193 0 014.027-1.681z",
  fill: "#6B6B6B"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.1,
  d: "M191.605 164.256c-1.809 1.37-4.151 2.63-5.943 4.036-.612.48-10.811 6.264-10.955 7.032 1.673.284 12.326-7.233 13.85-7.983 1.524-.751 2.875-1.808 4.214-2.858.341-.268 1.787-1.072.808-1.371-.552-.169-1.567.87-1.974 1.144z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M173.49 122.245c5.658 0 10.245-4.588 10.245-10.246 0-5.659-4.587-10.246-10.245-10.246-5.659 0-10.246 4.587-10.246 10.246 0 5.658 4.587 10.246 10.246 10.246z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M177.803 120.627s-1.617 4.044 1.348 5.662c2.966 1.618-9.167 14.56-9.167 14.56l-5.392-15.638s4.584-1.618 3.775-5.932l9.436 1.348z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M171.063 131.682l6.53-7.209 8.838 4.512-1.348 31.816-2.157 2.426s-9.167 8.089-23.187 1.349c0 0-4.853-4.045-4.583-10.246.269-6.201-.54-7.01-.54-7.01l-.269-21.84 13.311-2.612 3.405 8.814z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M141.123 165.989l-1.665 5.129s-8.017 8.283-5.118 10.528c2.9 2.245 9.406-9.59 9.406-9.59l1.199-5.791-3.822-.276z",
  fill: "#FFCFBE"
}), /*#__PURE__*/React.createElement("path", {
  d: "M156.504 126.289l-2.427-.809s-3.775 1.078-4.314 4.853c-.539 3.775-4.583 22.918-4.583 22.918s-1.618.809-.54 2.157c1.079 1.348-5.392 13.481-5.392 13.481l5.123 2.427s10.245-10.785 10.245-11.864c0-1.078-.269-2.157-.269-3.235 0-1.079.269-1.079 0-1.887a8.673 8.673 0 01-.27-2.157l1.887-4.314.54-21.57z",
  fill: "#2EA1A1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M184.849 102.949l-8.471-4.475-11.698 1.831-2.421 10.782 6.025-.234 1.683-3.961v3.896l2.78-.108 1.614-6.306 1.008 6.713 9.883-.203-.403-7.935z",
  fill: "#2F2E41"
}), /*#__PURE__*/React.createElement("path", {
  d: "M124.799 172.311v15.552H77.298a15.554 15.554 0 0115.551-15.552c4.454-1.389 9.604-1.831 15.191-1.685 5.271.135 10.93.795 16.759 1.685z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M118.014 184.47h22.337c.375 0 .734.149.999.415a1.41 1.41 0 010 1.999 1.412 1.412 0 01-.999.414h-22.337v-2.828z",
  fill: "#3F3D56"
}), /*#__PURE__*/React.createElement("path", {
  d: "M121.882 159.002l5.987-7.258a1.576 1.576 0 012.418-.533c.246.203.425.477.512.784l2.361 8.336-11.278-1.329z",
  fill: "#E1983B"
}), /*#__PURE__*/React.createElement("path", {
  d: "M114.62 162.699l2.262-9.133a1.573 1.573 0 012.751-1.04l5.732 6.497-10.745 3.676zM77.297 187.863h4.524v9.614a4.524 4.524 0 01-4.524 4.524v-14.138zM117.165 185.037h22.337a1.415 1.415 0 110 2.827h-22.337v-2.827z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  opacity: 0.2,
  d: "M124.799 172.311v15.465a15.262 15.262 0 01-12.536-4.513 15.276 15.276 0 01-4.223-12.637c5.271.134 10.93.795 16.759 1.685z",
  fill: "#000"
}), /*#__PURE__*/React.createElement("path", {
  d: "M124.799 187.863c8.433 0 15.269-6.836 15.269-15.269 0-8.432-6.836-15.268-15.269-15.268-8.432 0-15.268 6.836-15.268 15.268 0 8.433 6.836 15.269 15.268 15.269z",
  fill: "#F3AD55"
}), /*#__PURE__*/React.createElement("path", {
  d: "M139.502 177.685c.937 0 1.697-1.772 1.697-3.959 0-2.186-.76-3.958-1.697-3.958s-1.696 1.772-1.696 3.958c0 2.187.759 3.959 1.696 3.959zM135.403 170.616c.234 0 .424-.444.424-.99 0-.547-.19-.99-.424-.99-.235 0-.425.443-.425.99 0 .546.19.99.425.99zM138.513 168.636c.234 0 .424-.443.424-.989 0-.547-.19-.99-.424-.99-.234 0-.424.443-.424.99 0 .546.19.989.424.989zM123.518 157.156s-.848-1.476-.848-2.377l.848 2.377zM123.892 156.201s.462-1.639 1.106-2.268l-1.106 2.268z",
  fill: "#3F3D56"
})), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
  id: "illustrationPetSitting_svg__clip0"
}, /*#__PURE__*/React.createElement("path", {
  fill: "#fff",
  transform: "translate(0 .001)",
  d: "M0 0h283v202H0z"
})))));

const SERVICE_TYPES_AS_PET_SITTING_TYPE = ['House Visiting', 'Pet Day Care', 'Pet Hosting'];
const MESSAGE_START = 'To see more';
const MESSAGE_END = 'adjusting your search filters or changing your dates.';
const formatServiceTypeProviders$1 = serviceType => {
  const isServiceTypesAsPetSittingType = SERVICE_TYPES_AS_PET_SITTING_TYPE.includes(serviceType);
  if (isServiceTypesAsPetSittingType) {
    return formatServiceTypeProviders('Pet Sitting');
  }
  return formatServiceTypeProviders(serviceType);
};
// NOTE: Exported for testing purposes
const createHeading = (serviceType, suburb) => `Sorry, there are currently no available ${formatServiceTypeProviders$1(serviceType)} in ${suburb}`;
const messageMiddleMap = {
  'Dog Grooming': '',
  'Dog Training': '',
  'Dog Walking': '',
  'House Visiting': 'choosing a different service type like Pet Sitting, ',
  'Pet Day Care': 'choosing a different service type, ',
  'Pet Hosting': 'changing your service type to Pet Sitting, ',
  'Pet Sitting': 'changing your service type to Pet Hosting or House Visiting, '
};
// NOTE: Exported for testing purposes
const createMessage = serviceType => `${MESSAGE_START} ${formatServiceTypeProviders$1(serviceType)}, try ${messageMiddleMap[serviceType]}${MESSAGE_END}`;
const illustrationMap = {
  'Dog Grooming': /*#__PURE__*/React.createElement(IllustrationDogGrooming, null),
  'Dog Training': /*#__PURE__*/React.createElement(IllustrationDogTraining, null),
  'Dog Walking': /*#__PURE__*/React.createElement(IllustrationDogWalking, null),
  'Pet Hosting': /*#__PURE__*/React.createElement(IllustrationPetSitting, null),
  'Pet Sitting': /*#__PURE__*/React.createElement(IllustrationPetSitting, null),
  'Pet Day Care': /*#__PURE__*/React.createElement(IllustrationPetDayCare, null),
  'House Visiting': /*#__PURE__*/React.createElement(IllustrationHouseVisting, null)
};
const SearchResultsListEmptyState = ({
  ctaOnClick,
  serviceType,
  suburb
}) => ( /*#__PURE__*/React.createElement(EmptyState, {
  cta: /*#__PURE__*/React.createElement(Button, {
    iconLeading: /*#__PURE__*/React.createElement(IconFilter, null),
    label: "Refine search",
    onClick: ctaOnClick
  }),
  heading: createHeading(serviceType, suburb),
  illustration: illustrationMap[serviceType],
  message: createMessage(serviceType)
}));

/**
 * The SkeletonContainer and SkeletonBlock components get used to reduce
 * load-time frustration by providing an accessible placeholder preview of a
 * component while its data is getting loaded.
 *
 * ## Usage Guidelines
 *
 * - A skeleton gets comprised of two components: SkeletonContainer and
 *   SkeletonBlock. As the component names imply, the SkeletonContainer
 *   component must always wrap the skeleton blocks, which are the SkeletonBlock
 *   components.
 * - When it can, the design system provides skeletons for components
 *   out-of-the-box, meaning it's rare that a skeleton has to get set up outside
 *   of the design system.
 * - The SkeletonContainer component informs a screen reader user what is
 *   getting loaded via its `label` prop. The `label` must explain clearly and
 *   concisely what exactly is getting loaded. E.g., "Loading the search
 *   results".
 * - A SkeletonBlock component comes in two distinct variants: "text" and
 *   "shape". Text variants represent typography elements such as paragraphs
 *   (`p`), headings (`h1`–`h6`), and any other text elements defined by the
 *   "typography" tokens (see Foundations → Tokens → Typography). Shape variants
 *   represent non-typography elements such as images (`img`), buttons
 *   (`button`), etc.
 * - If a skeleton requires layout, e.g., to resemble a "card" like component
 *   such as a sitter profile card. Then reuse the same layout from the
 *   component the skeleton is being a placeholder for. This ensures an exact
 *   representation of the component's layout, which is essential for preventing
 *   layout shifts.
 */
const SkeletonContainer = ({
  children,
  label
}) => ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("div", {
  "aria-live": "polite",
  role: "status"
}, label)), /*#__PURE__*/React.createElement("div", {
  "aria-hidden": "true"
}, children)));

var styles$L = {"root":"SkeletonBlock-module_root__-SoE-","textDefault":"SkeletonBlock-module_textDefault__YcmRK","textSupplementary":"SkeletonBlock-module_textSupplementary__fI4Ab","shapeCircle":"SkeletonBlock-module_shapeCircle__cicVp","shimmer":"SkeletonBlock-module_shimmer__U8uXW"};

const createBlockInlineStyle = (shapeHeight, textWidth) => {
  if (shapeHeight) {
    return {
      height: `${shapeHeight}px`
    };
  }
  if (textWidth) {
    return {
      width: `${textWidth}%`
    };
  }
  return undefined;
};
const Block = ({
  className,
  headingSize,
  shapeHeight,
  textWidth
}) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$L.root, {
    [styles$L[className || '']]: className
  }),
  "data-nosnippet": "true",
  style: createBlockInlineStyle(shapeHeight, textWidth)
}, headingSize ? /*#__PURE__*/React.createElement(Heading, {
  size: headingSize
}, "\u00A0") : '*'));
const SkeletonBlock = ({
  shapeHeight,
  textWidth,
  variant = 'textDefault'
}) => {
  const map = {
    textDefault: /*#__PURE__*/React.createElement(Block, {
      className: "textDefault",
      textWidth: textWidth
    }),
    textSupplementary: /*#__PURE__*/React.createElement(Block, {
      className: "textSupplementary",
      textWidth: textWidth
    }),
    textHeadingLarge1x: /*#__PURE__*/React.createElement(Block, {
      headingSize: "large1x",
      textWidth: textWidth
    }),
    textHeadingLarge2x: /*#__PURE__*/React.createElement(Block, {
      headingSize: "large2x",
      textWidth: textWidth
    }),
    textHeadingMedium: /*#__PURE__*/React.createElement(Block, {
      headingSize: "medium",
      textWidth: textWidth
    }),
    textHeadingSmall1x: /*#__PURE__*/React.createElement(Block, {
      headingSize: "small1x",
      textWidth: textWidth
    }),
    textHeadingSmall2x: /*#__PURE__*/React.createElement(Block, {
      headingSize: "small2x",
      textWidth: textWidth
    }),
    textHeadingSmall3x: /*#__PURE__*/React.createElement(Block, {
      headingSize: "small3x",
      textWidth: textWidth
    }),
    shapeRectangle: /*#__PURE__*/React.createElement(Block, {
      className: "shapeRectangle",
      shapeHeight: shapeHeight
    }),
    shapeCircle: /*#__PURE__*/React.createElement(Block, {
      className: "shapeCircle",
      shapeHeight: shapeHeight
    })
  };
  return map[variant];
};

var styles$M = {"root":"SitterCard-module_root__HUNdR","isSkeleton":"SitterCard-module_isSkeleton__whLnI","hasBadges":"SitterCard-module_hasBadges__L3BIZ","link":"SitterCard-module_link__xU8LI","location":"SitterCard-module_location__rBgfI","hasSeasonalUpsellBanner":"SitterCard-module_hasSeasonalUpsellBanner__O2d--","headerContainer":"SitterCard-module_headerContainer__7EWVl","tagline":"SitterCard-module_tagline__WaWbp","seasonalUpsellBanner":"SitterCard-module_seasonalUpsellBanner__7UxCj","christmasUpsellBanner":"SitterCard-module_christmasUpsellBanner__sP37v","easterUpsellBanner":"SitterCard-module_easterUpsellBanner__JMiRZ","winterHolidaysUpsellBanner":"SitterCard-module_winterHolidaysUpsellBanner__ulk9q","springHolidaysUpsellBanner":"SitterCard-module_springHolidaysUpsellBanner__Em1ox","priceAndRating":"SitterCard-module_priceAndRating__oOpR5","rating":"SitterCard-module_rating__5ejfY","withOutPrice":"SitterCard-module_withOutPrice__QCYVe","price":"SitterCard-module_price__hzEPA","mobileReviewAndBadges":"SitterCard-module_mobileReviewAndBadges__obFee","badges":"SitterCard-module_badges__Oe8Eb","greenBadge":"SitterCard-module_greenBadge__4t6zn","photo":"SitterCard-module_photo__Zo594","photoRounded":"SitterCard-module_photoRounded__JeZue","latestReview":"SitterCard-module_latestReview__5j1nn","reviewText":"SitterCard-module_reviewText__YLcps","review":"SitterCard-module_review__Fr570","readMore":"SitterCard-module_readMore__k6Q6m","likeButton":"SitterCard-module_likeButton__FG5ym","isLiked":"SitterCard-module_isLiked__QaT0Q","isLikedAnimation":"SitterCard-module_isLikedAnimation__-KIxH","activeAnimation":"SitterCard-module_activeAnimation__5cnZv"};

const useProfilePhotoSize = () => {
  const isLargeFromViewport = useLargeFromMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  let finalSize;
  if (isLargeFromViewport) {
    finalSize = 185;
  } else if (isMediumFromViewport) {
    finalSize = 162;
  } else {
    finalSize = 80;
  }
  return finalSize;
};

const SearchResultsListCardSkeleton = ({
  isRounded
}) => {
  // NOTE: These media queries must match the media queries in the
  // SitterCard component.
  const isSmallToViewport = useSmallToMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  const profilePhotoSize = useProfilePhotoSize();
  return (
    /*#__PURE__*/
    // NOTE: The following HTML must match the structure of the HTML in the
    // SitterCard component.
    React.createElement("div", {
      className: classnames(styles$M.root, styles$M.hasBadges, styles$M.isSkeleton)
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: styles$M.heading
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: isMediumFromViewport ? 50 : 80,
      // NOTE: These heading sizes must match the sizes in the
      // SitterCard component.
      variant: isMediumFromViewport ? 'textHeadingSmall1x' : 'textHeadingSmall3x'
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$M.tagline
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: isMediumFromViewport ? 70 : 100
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$M.location
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: isMediumFromViewport ? 30 : 50
    })), /*#__PURE__*/React.createElement("div", {
      className: styles$M.price
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: isMediumFromViewport ? 30 : 50,
      variant: "textSupplementary"
    })), isMediumFromViewport && ( /*#__PURE__*/React.createElement("div", {
      className: styles$M.badges
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: 80,
      variant: "textSupplementary"
    })))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      style: {
        width: `${profilePhotoSize}px`
      }
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      shapeHeight: profilePhotoSize,
      variant: isRounded ? 'shapeCircle' : 'shapeRectangle'
    })), isSmallToViewport && ( /*#__PURE__*/React.createElement("div", {
      className: styles$M.rating
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: 70,
      variant: "textSupplementary"
    })))), isSmallToViewport && ( /*#__PURE__*/React.createElement("div", {
      className: styles$M.badges
    }, /*#__PURE__*/React.createElement(SkeletonBlock, {
      textWidth: 100,
      variant: "textSupplementary"
    }))))
  );
};

const SearchResultsListSkeleton = ({
  pageSize,
  isRounded = false
}) => ( /*#__PURE__*/React.createElement(SkeletonContainer, {
  label: "Loading the search results"
}, /*#__PURE__*/React.createElement("ul", {
  className: styles$K.list
}, [...Array(pageSize)].map((_, index) => (
/*#__PURE__*/
// eslint-disable-next-line react/no-array-index-key
React.createElement("li", {
  key: index
}, /*#__PURE__*/React.createElement(SearchResultsListCardSkeleton, {
  isRounded: isRounded
})))))));

var styles$N = {"root":"Spinner-module_root__trcUV"};

const Spinner = ({
  label
}) => ( /*#__PURE__*/React.createElement("p", {
  "aria-live": "polite",
  className: styles$N.root,
  role: "status"
}, "Loading ", label));

// NOTE: Exported for testing purposes
const BADGE_ORDER = ['repeatClient', 'shieldStar', 'fence', 'shieldCheck', 'repeat', 'calendarCheck'];
const EASTER_UPSELL_BADGE = 'easter';
const CHRISTMAS_UPSELL_BADGE = 'christmas';
const WINTER_HOLIDAYS_UPSELL_BADGE = 'winter';
const SPRING_HOLIDAYS_UPSELL_BADGE = 'spring';
const badgeIconMap = {
  fence: IconFence,
  shieldStar: IconShieldStar,
  tick: IconCheck,
  repeat: IconRepeat,
  repeatClient: IconRepeatClient,
  calendarCheck: IconCalendarCheck,
  shieldCheck: IconShieldCheckOutlined
};
const seasonalUpsellBannerGraphicMap = {
  christmas: IconSantasHat,
  easter: IconEasterEgg,
  winter: IconSnowFlake,
  spring: IconGreenSproute
};
const observerOptions = {
  rootMargin: '0px',
  threshold: 0.1
};
// NOTE: Exported for testing purposes
const sortBadges = (badges, orderList) => badges.sort((a, b) => orderList.indexOf(a.icon) - orderList.indexOf(b.icon));
const getIconSize = (isMedium = false) => isMedium ? 'medium' : 'small';
const LATEST_REVIEW_SHORTEN_THRESHOLD = 25;
const LATEST_REVIEW_SHORTEN_THRESHOLD_MOBILE = 12;
const SitterCard = ({
  data: {
    badges,
    id,
    location: {
      distanceFromSearchAddress,
      suburbName
    },
    name,
    price,
    profilePhoto,
    profileUrl,
    rating: {
      numberOfReviews,
      score
    },
    seasonalUpsellBanner,
    tagline,
    latestReview,
    isSitterLiked
  },
  hasTotalPrice = false,
  isTaglineHidden,
  index,
  onClickSearchResultCard,
  onLoadPrice,
  serviceTypeTimeUnit,
  hasLikedButton = false,
  onClickLike,
  isPriceHidden = false,
  isRounded = false
}) => {
  const elementRef = useRef(null);
  const observerRef = useRef(null);
  const hasLoadedPrice = useRef(!!price);
  const hasBadges = badges.length > 0;
  const hasRating = score !== 0;
  const isSmallToViewport = useSmallToMediaQuery();
  const isMediumFromViewport = useMediumFromMediaQuery();
  const profilePhotoSize = useProfilePhotoSize();
  const ref = React.useRef(null);
  const [isShortenReview, setIsShortenReview] = useState(true);
  const [isAnimating, setIsAnimating] = React.useState(false);
  // Sitter Profile should be opened in a new tab for Desktop view only
  const anchorTarget = isMediumFromViewport ? '_blank' : '_self';
  const renderSeasonalUpsellBanner = () => {
    if (!seasonalUpsellBanner) {
      return null;
    }
    const renderSeasonalUpsellGraphic = () => {
      if (!seasonalUpsellBanner.graphic) {
        return null;
      }
      const Graphic = seasonalUpsellBannerGraphicMap[seasonalUpsellBanner.graphic];
      return /*#__PURE__*/React.createElement(Graphic, {
        size: "medium"
      });
    };
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(styles$M.seasonalUpsellBanner, {
        [styles$M.easterUpsellBanner]: seasonalUpsellBanner.graphic === EASTER_UPSELL_BADGE,
        [styles$M.christmasUpsellBanner]: seasonalUpsellBanner.graphic === CHRISTMAS_UPSELL_BADGE,
        [styles$M.winterHolidaysUpsellBanner]: seasonalUpsellBanner.graphic === WINTER_HOLIDAYS_UPSELL_BADGE,
        [styles$M.springHolidaysUpsellBanner]: seasonalUpsellBanner.graphic === SPRING_HOLIDAYS_UPSELL_BADGE
      })
    }, /*#__PURE__*/React.createElement("em", null, seasonalUpsellBanner.label), renderSeasonalUpsellGraphic());
  };
  const renderRating = (ratingScore, ratingNumberOfReviews) => hasRating && ( /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$M.rating, {
      [styles$M.withOutPrice]: isPriceHidden
    }),
    "data-testid": "rating"
  }, /*#__PURE__*/React.createElement(IconStar, {
    size: getIconSize(isMediumFromViewport)
  }), /*#__PURE__*/React.createElement(VisuallyHidden, {
    isInline: true
  }, "Rating score: "), formatRatingScore(ratingScore), /*#__PURE__*/React.createElement(VisuallyHidden, {
    isInline: true
  }, "Total reviews: "), /*#__PURE__*/React.createElement("span", null, "(", ratingNumberOfReviews, ")")));
  const renderTotalPriceContent = () => ( /*#__PURE__*/React.createElement("p", null, formatCurrency(price), /*#__PURE__*/React.createElement("span", null, "\u00A0/ total")));
  const getLatestReviewDescription = () => {
    if (!latestReview) {
      return '';
    }
    if (isShortenReview) {
      const wordArray = latestReview.description.split(' ');
      const threshold = isSmallToViewport ? LATEST_REVIEW_SHORTEN_THRESHOLD_MOBILE : LATEST_REVIEW_SHORTEN_THRESHOLD;
      return wordArray.length > threshold ? `${wordArray.slice(0, threshold).join(' ')}...` : latestReview.description;
    }
    return latestReview.description;
  };
  const renderLatestReview = () => {
    if (!latestReview || !latestReview.description) {
      return null;
    }
    const onClickReadMore = () => {
      setIsShortenReview(!isShortenReview);
    };
    const threshold = isSmallToViewport ? LATEST_REVIEW_SHORTEN_THRESHOLD_MOBILE : LATEST_REVIEW_SHORTEN_THRESHOLD;
    const showReadMore = latestReview.description.split(' ').length > threshold;
    return /*#__PURE__*/React.createElement("div", {
      className: styles$M.latestReview
    }, /*#__PURE__*/React.createElement(Avatar, {
      alt: 'review',
      size: "medium",
      src: latestReview.avatar
    }), /*#__PURE__*/React.createElement("div", {
      className: styles$M.reviewText
    }, /*#__PURE__*/React.createElement("p", {
      className: styles$M.review,
      ref: ref
    }, getLatestReviewDescription(), showReadMore && ( /*#__PURE__*/React.createElement("span", {
      className: styles$M.readMore,
      onClick: onClickReadMore
    }, isShortenReview ? 'Read more' : ' Read less')))));
  };
  const renderPricePerTimeUnitContent = () => ( /*#__PURE__*/React.createElement("p", null, formatCurrency(price), /*#__PURE__*/React.createElement("span", null, "\u00A0/ ", serviceTypeTimeUnit)));
  const renderBasePriceContent = () => ( /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("span", null, "from "), formatCurrency(price), /*#__PURE__*/React.createElement("span", null, "\u00A0/ ", serviceTypeTimeUnit)));
  const renderPriceContent = () => {
    if (!price) {
      return /*#__PURE__*/React.createElement(Spinner, {
        label: "price"
      });
    }
    if (hasTotalPrice === null) {
      return renderBasePriceContent();
    }
    const priceContent = hasTotalPrice ? renderTotalPriceContent() : renderPricePerTimeUnitContent();
    return priceContent;
  };
  const renderBadges = () => {
    const badgesSorted = sortBadges(badges, BADGE_ORDER);
    return hasBadges && ( /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(VisuallyHidden, null, "Has the following badges: "), /*#__PURE__*/React.createElement("ul", {
      className: classnames(styles$M.badges, {
        [styles$M.greenBadge]: isRounded
      }),
      role: "list"
    }, badgesSorted.map(({
      icon,
      id: badgeId,
      name: badgeName
    }) => {
      const IconComponent = badgeIconMap[icon];
      return /*#__PURE__*/React.createElement("li", {
        key: badgeId
      }, /*#__PURE__*/React.createElement(IconComponent, {
        size: getIconSize(isMediumFromViewport)
      }), /*#__PURE__*/React.createElement("span", null, badgeName));
    }))));
  };
  useEffect(() => {
    if (hasLoadedPrice.current || !onLoadPrice) {
      return () => {};
    }
    observerRef.current = new IntersectionObserver(([entry]) => {
      var _observerRef$current;
      if (!entry.isIntersecting) {
        return;
      }
      onLoadPrice(id);
      hasLoadedPrice.current = true;
      (_observerRef$current = observerRef.current) === null || _observerRef$current === void 0 ? void 0 : _observerRef$current.disconnect();
    }, observerOptions);
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }
    return () => {
      var _observerRef$current2;
      (_observerRef$current2 = observerRef.current) === null || _observerRef$current2 === void 0 ? void 0 : _observerRef$current2.disconnect();
    };
  }, [id, onLoadPrice]);
  const handleOnClickSearchResultCard = () => onClickSearchResultCard === null || onClickSearchResultCard === void 0 ? void 0 : onClickSearchResultCard(index);
  const renderTagLine = () => {
    if (isTaglineHidden) {
      return null;
    }
    return /*#__PURE__*/React.createElement("p", {
      className: styles$M.tagline
    }, tagline);
  };
  const handleSitterLiked = () => {
    if (!isSitterLiked) {
      setIsAnimating(true);
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
    onClickLike === null || onClickLike === void 0 ? void 0 : onClickLike(id, !isSitterLiked, index);
  };
  return /*#__PURE__*/React.createElement("article", {
    className: classnames(styles$M.root, {
      [styles$M.hasBadges]: hasBadges,
      [styles$M.hasSeasonalUpsellBanner]: seasonalUpsellBanner
    }),
    ref: elementRef
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles$M.headerContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$M.heading
  }, /*#__PURE__*/React.createElement(Heading, {
    fontFamily: isRounded ? 'heading' : 'default',
    level: 3,
    size: isMediumFromViewport ? 'small1x' : 'small3x'
  }, /*#__PURE__*/React.createElement("a", {
    className: styles$M.link,
    href: profileUrl,
    onClick: handleOnClickSearchResultCard,
    target: anchorTarget
  }, name))), hasLikedButton && ( /*#__PURE__*/React.createElement("div", {
    className: classnames({
      [styles$M.likeButton]: true,
      [styles$M.isLikedAnimation]: isAnimating && isSitterLiked,
      [styles$M.isLiked]: isSitterLiked
    })
  }, /*#__PURE__*/React.createElement(IconButton, {
    label: "Favorite",
    onClick: handleSitterLiked,
    variant: "secondary"
  }, isSitterLiked ? /*#__PURE__*/React.createElement(IconHeartFilled, null) : /*#__PURE__*/React.createElement(IconHeartOutlined, null))))), renderSeasonalUpsellBanner(), renderTagLine(), /*#__PURE__*/React.createElement("p", {
    className: styles$M.location
  }, suburbName, !!distanceFromSearchAddress && ` (${formatDistanceFromLocation(distanceFromSearchAddress)})`), /*#__PURE__*/React.createElement("div", {
    className: styles$M.priceAndRating
  }, renderRating(score, numberOfReviews), !isPriceHidden && /*#__PURE__*/React.createElement("div", {
    className: styles$M.price
  }, renderPriceContent())), isMediumFromViewport && renderLatestReview(), isMediumFromViewport && renderBadges()), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("img", {
    alt: `Thumbnail of ${name}`,
    className: classnames(styles$M.photo, {
      [styles$M.photoRounded]: isRounded
    }),
    "data-testid": "profilePhoto",
    decoding: "async",
    height: profilePhotoSize,
    loading: index >= 5 ? 'lazy' : 'eager',
    src: profilePhoto,
    width: profilePhotoSize
  })), isSmallToViewport && ( /*#__PURE__*/React.createElement("div", {
    className: styles$M.mobileReviewAndBadges
  }, renderLatestReview(), renderBadges())));
};

/**
 * The SearchResultsList component gets used to display the results of a pet
 * sitter search.
 *
 * <p class="sbdocs-consumer-message sbdocs-consumer-message--single-row">
 *  <em>A Next.js component</em>
 *  <span>This component is only compatible with the Next.js framework.</span>
 * </p>
 *
 * ## Usage Guidelines
 *
 * - A good standard to follow for the number of results to display is "20",
 *   driven by `pageInfo.size`. When the number of results exceeds
 *   `pageInfo.size`, the list gets paginated.
 * - When search dates get provided, driven by the
 *   `searchParameters.hasAccuratePrice` prop, an accurate service price (rate)
 *   gets displayed for each result. Conversely, when search dates don't get
 *   provided, generic rates are shown.
 * - When there are no search results available, an empty state gets displayed
 *   to help the user get back on track (see the EmptyState component).
 * - While the search results are loading, driven by the `isLoadingItems` prop,
 *   a placeholder preview or a skeleton of the search results is displayed to
 *   reduce load-time frustration.
 */
const SearchResultsList = ({
  emptyStateCtaOnClick,
  hasTotalPrice = false,
  hasLikedButton = false,
  isTaglineHidden = false,
  isLoadingItems,
  items,
  linkComponent,
  onClickSearchResultCard,
  onLoadPrice,
  pageHrefTemplate,
  onClickLike,
  isPriceHidden,
  isRounded,
  pageInfo: {
    offset,
    size,
    total
  },
  searchParameters: {
    serviceType,
    serviceTypeTimeUnit,
    suburb
  }
}) => {
  const hasPagination = total > size;
  const isEmptyState = items.length === 0 && !isLoadingItems;
  if (isLoadingItems) {
    return /*#__PURE__*/React.createElement(SearchResultsListSkeleton, {
      isRounded: isRounded,
      pageSize: size
    });
  }
  if (isEmptyState) {
    return /*#__PURE__*/React.createElement(SearchResultsListEmptyState, {
      ctaOnClick: emptyStateCtaOnClick,
      serviceType: serviceType,
      suburb: suburb
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("ul", {
    className: classnames(styles$K.list, {
      [styles$K.hasPagination]: hasPagination
    }),
    role: "list"
  }, items.map((item, index) => ( /*#__PURE__*/React.createElement("li", {
    key: item.id
  }, /*#__PURE__*/React.createElement(SitterCard, {
    data: item,
    hasLikedButton: hasLikedButton,
    hasTotalPrice: hasTotalPrice,
    index: index,
    isPriceHidden: isPriceHidden,
    isRounded: isRounded,
    isTaglineHidden: isTaglineHidden,
    onClickLike: onClickLike,
    onClickSearchResultCard: onClickSearchResultCard,
    onLoadPrice: onLoadPrice,
    serviceTypeTimeUnit: serviceTypeTimeUnit
  }))))), hasPagination && ( /*#__PURE__*/React.createElement(Pagination, {
    currentOffset: offset,
    label: serviceType,
    linkComponent: linkComponent,
    pageHrefTemplate: pageHrefTemplate,
    pageSize: size,
    totalItems: total
  })));
};

var styles$O = {"root":"Status-module_root__6ORAz","isActive":"Status-module_isActive__RSXnN","dot":"Status-module_dot__2IrkL","inActiveDot":"Status-module_inActiveDot__ld0lY","activeDot":"Status-module_activeDot__V3iwQ"};

/**
 * The Status component is used to display the active or
 * inactive status of a particular component.
 */
const Status = ({
  activeTitle = 'Active',
  inActiveTitle = 'Inactive',
  isActive
}) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$O.root, {
    [styles$O.isActive]: isActive
  })
}, /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$O.dot, {
    [styles$O.activeDot]: isActive,
    [styles$O.inActiveDot]: !isActive
  })
}), isActive ? /*#__PURE__*/React.createElement("div", {
  className: styles$O.isActive
}, activeTitle) : /*#__PURE__*/React.createElement("div", null, inActiveTitle)));

var styles$P = {"root":"Supplementary-module_root__kyUsn"};

/**
 * The Supplementary component gets used to create styled blobs
 * of supplementary text
 *
 */
const Supplementary = ({
  children
}) => {
  const Element = 'div';
  return /*#__PURE__*/React.createElement(Element, {
    className: styles$P.root
  }, children);
};

var styles$Q = {"root":"Tag-module_root__n61I6","greyScaleLight4x":"Tag-module_greyScaleLight4x__sPdCx","brandLight4x":"Tag-module_brandLight4x__nlGCW","large1x":"Tag-module_large1x__PjfHX","default":"Tag-module_default__AKh6v","small1x":"Tag-module_small1x__FDAN3","small2x":"Tag-module_small2x__--YVw","isTextWrapping":"Tag-module_isTextWrapping__kCBJW","tooltipContainer":"Tag-module_tooltipContainer__CO7jn"};

/**
 * The Tag component is used in service preferences data capture designs.
 */
const Tag = ({
  background = 'greyScaleLight4x',
  description,
  fontSize = 'default',
  iconLeading,
  tip,
  isTextWrapping = false
}) => ( /*#__PURE__*/React.createElement("div", {
  className: classnames(styles$Q.root, {
    [styles$Q[background]]: background,
    [styles$Q[fontSize]]: fontSize,
    [styles$Q.isTextWrapping]: isTextWrapping
  })
}, iconLeading, /*#__PURE__*/React.createElement("span", null, description), tip && ( /*#__PURE__*/React.createElement("div", {
  className: styles$Q.tooltipContainer
}, /*#__PURE__*/React.createElement(ToolTip, {
  tip: tip
})))));

var styles$R = {"switchContainer":"Switch-module_switchContainer__lhqzQ","hasDescription":"Switch-module_hasDescription__qNP29","optionDescription":"Switch-module_optionDescription__v2-EZ","label":"Switch-module_label__LrH7V","withToolTip":"Switch-module_withToolTip__fdvnW","slider":"Switch-module_slider__5suBx","sliderSwitch":"Switch-module_sliderSwitch__mgLPL","toggleSwitch":"Switch-module_toggleSwitch__qNcpA"};

const Switch = ({
  onChange,
  isDisabled = false,
  value,
  description,
  label,
  tip,
  name
}) => {
  const [ariaChecked, setAriaChecked] = useState(false);
  const inputId = createToggleId(name);
  const handleChange = () => {
    setAriaChecked(!ariaChecked);
    onChange({
      target: {
        value: !value,
        name
      }
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(styles$R.switchContainer, {
      [styles$R.hasDescription]: description
    }),
    key: name
  }, /*#__PURE__*/React.createElement("span", {
    className: classnames(styles$R.label, {
      [styles$R.withToolTip]: tip
    })
  }, /*#__PURE__*/React.createElement(FieldLabel, {
    label: label,
    tip: tip
  })), description && /*#__PURE__*/React.createElement("span", {
    className: styles$R.optionDescription
  }, description), /*#__PURE__*/React.createElement("button", {
    "aria-checked": ariaChecked,
    "aria-label": label,
    className: styles$R.toggleSwitch,
    "data-testid": "switch",
    disabled: isDisabled,
    id: inputId,
    onClick: handleChange,
    role: "switch",
    type: "button"
  }, /*#__PURE__*/React.createElement("span", {
    className: styles$R.slider
  }, /*#__PURE__*/React.createElement("span", {
    className: styles$R.sliderSwitch
  }, /*#__PURE__*/React.createElement(IconCheck, null)))));
};

var styles$S = {"toggleContainer":"ToggleGroup-module_toggleContainer__lWTeY","hasOptionDescription":"ToggleGroup-module_hasOptionDescription__g-08s","optionDescription":"ToggleGroup-module_optionDescription__wR-fX","optionLabel":"ToggleGroup-module_optionLabel__2Au66","singleOptionLabel":"ToggleGroup-module_singleOptionLabel__OudsQ","toggleInput":"ToggleGroup-module_toggleInput__j-e-3","slider":"ToggleGroup-module_slider__Yd5cg","sliderSwitch":"ToggleGroup-module_sliderSwitch__jXEro"};

/**
 * The ToggleGroup component is a form field component that provides a list of
 * multiple selectable options.
 *
 * ## Usage Guidelines
 *
 * - A toggle group is a mandatory field by default. For optional checkbox
 *   group fields, apply the `isOptional` prop.
 * - A toggle group gets compromised of at least one option where a user can
 *   select one option at a time and the onChange event will be return a array
 *   of string values.
 * - The ToggleGroup component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning a toggle's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting an option,
 *   the default value of `useState` needs to match the corresponding option's
 *   `value` prop: `options[x].value`. E.g., if this were the object item of the
 *   preselected option:

 *  ```js
 *  {
 *    label: 'Dog Walking',
 *    value: 'dogWalking',
 *  }
 *  ```
 *
 *   Then the default value of `useState`
 *   would be: `useState(['dogWalking'])`.
 */
const ToggleGroup = ({
  name,
  onChange,
  isDisabled = false,
  value,
  options,
  description,
  isOptional = false,
  legend,
  isLegendVisuallyHidden = false,
  subtitle,
  tip
}) => {
  const descriptionId = createToggleId(name);
  const handleChange = ({
    target: {
      value: inputValue
    }
  }) => {
    const updatedValue = [...value];
    const inputValueIndex = updatedValue.indexOf(inputValue);
    if (inputValueIndex !== -1) {
      updatedValue.splice(inputValueIndex, 1);
    } else {
      updatedValue.push(inputValue);
    }
    onChange({
      target: {
        value: updatedValue
      }
    });
  };
  return /*#__PURE__*/React.createElement(Fieldset, {
    describedBy: description ? [descriptionId] : undefined,
    isGroupField: true,
    isLegendVisuallyHidden: isLegendVisuallyHidden,
    isOptional: isOptional,
    legend: legend,
    subtitle: subtitle,
    tip: tip
  }, options.map(({
    value: optionValue,
    label: optionLabel,
    isDisabled: optionIsDisabled,
    description: optionDescription
  }) => {
    const inputId = createToggleId(optionValue);
    return /*#__PURE__*/React.createElement("div", {
      className: classnames(styles$S.toggleContainer, {
        [styles$S.hasOptionDescription]: optionDescription
      }),
      key: optionValue
    }, /*#__PURE__*/React.createElement("span", {
      className: classnames(styles$S.optionLabel, {
        [styles$S.singleOptionLabel]: options.length === 1 && isLegendVisuallyHidden
      })
    }, optionLabel), optionDescription && ( /*#__PURE__*/React.createElement("span", {
      className: styles$S.optionDescription
    }, optionDescription)), /*#__PURE__*/React.createElement("label", {
      className: styles$S.toggleInput,
      htmlFor: inputId
    }, /*#__PURE__*/React.createElement("input", {
      checked: value.includes(optionValue),
      disabled: isDisabled || optionIsDisabled,
      id: inputId,
      onChange: handleChange,
      type: "checkbox",
      value: optionValue
    }), /*#__PURE__*/React.createElement("span", {
      className: styles$S.slider
    }, /*#__PURE__*/React.createElement("span", {
      className: styles$S.sliderSwitch
    }, /*#__PURE__*/React.createElement(IconCheck, null)))));
  }), description && /*#__PURE__*/React.createElement(FieldDescription, {
    id: descriptionId
  }, description));
};

var styles$T = {"root":"Select-module_root__y0u6b","selectContainer":"Select-module_selectContainer__uzCk5","chevronIcon":"Select-module_chevronIcon__vZzSy"};

/**
 * The Select component is a form field component that accepts user selections
 * from a given list of values.
 *
 * ## Usage Guidelines
 *
 * - A select is a mandatory form field by default. For optional select fields
 *   , apply the `isOptional` prop.
 * - A select's placeholder, applied via the `placeholder` prop, gets used
 *   to provide a brief hint as to what kind of information gets expected in the
 *   field. It should be a word or short phrase that hints about the expected
 *   type of data rather than an explanation or prompt. If the `placeholder`
 *   prop is not available the first item from the `options` list will be
 *   selected automatically.
 * - The Select component is a [controlled
 *   component](https://reactjs.org/docs/forms.html#controlled-components)
 *   meaning the input's value gets driven by the [React
 *   state](https://reactjs.org/docs/hooks-state.html) (`useState`), indicating
 *   a consumer has to set up the component's state. For preselecting a value,
 *   the default value of `useState` needs to match the corresponding `value`
 *   prop. E.g., if this were the object item of the preselected option:
 *
 *  ```js
 *  {
 *    label: 'Dog Walking',
 *    value: 'dogWalking',
 *  }
 *  ```
 *
 *   Then the default value of `useState`
 *   would be: `useState('dogWalking')`.
 * - If additional context is required for a select, then a description can
 *   get used via the `description` prop. The description provides context for
 *   the entire field and gets displayed underneath the input.
 */
const Select = ({
  isLabelVisuallyHidden = false,
  label,
  isOptional = false,
  isDisabled,
  value,
  name,
  options,
  onChange,
  placeholder,
  description
}) => {
  const createId = id => createSelectId(`${name}${id ? ` ${id}` : ''}`);
  const descriptionId = createId("description" /* FORM_FIELD_DESCRIPTION */);
  const inputId = createId();
  const renderLabel = () => ( /*#__PURE__*/React.createElement(FieldLabel, {
    id: descriptionId,
    isDisabled: isDisabled,
    isOptional: isOptional,
    label: label
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: styles$T.root,
    "data-faux-role": "formField" /* FORM_FIELD */
  }, isLabelVisuallyHidden ? /*#__PURE__*/React.createElement(VisuallyHidden, null, renderLabel()) : renderLabel(), /*#__PURE__*/React.createElement("div", {
    className: styles$T.selectContainer
  }, /*#__PURE__*/React.createElement("select", {
    disabled: isDisabled,
    id: inputId,
    name: name,
    onChange: onChange,
    required: !isOptional,
    value: value || ''
  }, placeholder && ( /*#__PURE__*/React.createElement("option", {
    disabled: true,
    hidden: true,
    value: ""
  }, placeholder)), options.map(({
    value: optionValue,
    label: optionLabel,
    isDisabled: isOptionDisabled
  }) => ( /*#__PURE__*/React.createElement("option", {
    disabled: isOptionDisabled,
    key: optionValue,
    value: optionValue
  }, optionLabel)))), /*#__PURE__*/React.createElement("div", {
    className: styles$T.chevronIcon
  }, /*#__PURE__*/React.createElement(IconChevronDown, null))), description && /*#__PURE__*/React.createElement(FieldDescription, {
    id: descriptionId
  }, description));
};

var styles$U = {"mainContent":"LayoutPlatform-module_mainContent__Gu9Co","withOutPadding":"LayoutPlatform-module_withOutPadding__J7OwN"};

/**
 * The LayoutPlatform component gets used for the layout of the public pages of
 * the platform.
 *
 * ## Usage Guidelines
 *
 * - The layout gets comprised of the platform's header, footer and main content
 *   area. The header and footer get handled by the PlatformHeader and
 *   PlatformFooter components; currently, PlatformFooter resides in the
 *   application that handles the public pages of the platform.
 * - All of the vertical spacing between components within the main content area
 *   gets applied via the LayoutVerticalSpacer component.
 * - The LayoutPlatform component has to sit at the root of the application's
 *   component tree. I.e., its only parent element should be the application's
 *   [DOM
 *   container](https://reactjs.org/docs/add-react-to-a-website.html#step-1-add-a-dom-container-to-the-html).
 */
const LayoutPlatform = ({
  children,
  footer,
  header,
  isLayoutContainerOptional = false,
  isTopPaddingDisabled = false
}) => ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(VisuallyHidden, null, /*#__PURE__*/React.createElement("a", {
  href: `#${"mainContent" /* MAIN_CONTENT */}`
}, "Skip to main content")), header, /*#__PURE__*/React.createElement("main", {
  className: classnames(styles$U.mainContent, {
    [styles$U.withOutPadding]: isTopPaddingDisabled
  }),
  id: "mainContent" /* MAIN_CONTENT */
}, !isLayoutContainerOptional ? ( /*#__PURE__*/React.createElement(LayoutGridContainer, {
  isRootLayout: true
}, /*#__PURE__*/React.createElement(LayoutGridItem, {
  placement: {
    small: [1, 13],
    medium: [2, 12],
    large: [2, 12]
  }
}, children))) : children), footer));

const pxToEmForMediaQuery = px => `${px / tokenFontSizesUserAgent}em`;
const pxToRem = px => `${px / tokenFontSizesDefault[0]}rem`;

// NOTE: Implementation explained here: https://madpaws.atlassian.net/wiki/spaces/DEV/pages/1466105875/How+To+Performantly+Load+Multiple+Web+Fonts+From+The+Same+Family
const FONT_FAMILY = tokenFontFamiliesDefault;
const FONT_FAMILY_HEADING = tokenFontFamiliesHeading;
const FONT_FAMILY_OPTIMISED = `${tokenFontFamiliesDefault}Optimised`;
const FONT_FAMILY_FALLBACK = tokenFontFamiliesFallback;
const FONT_SIZE = tokenFontSizesDefault[1];
const FONT_FILENAME_PREFIX = 'sofia-pro-';
const FONT_HEADING_FILENAME_PREFIX = 'recoleta-';
const LOADED_CLASS_PREFIX = 'web-font-loaded-';
const LOADED_CLASS_STAGE_1 = `${LOADED_CLASS_PREFIX}1`;
const LOADED_CLASS_STAGE_2 = `${LOADED_CLASS_PREFIX}2`;
const WEB_FONT_UNAVAILABLE_CLASS = 'web-font-unavailable';
const SESSION_STORAGE_VALUE = 'webFontsLoaded';
const fonts = [{
  family: FONT_FAMILY_OPTIMISED,
  filename: `${FONT_FILENAME_PREFIX}regular-optimised`,
  /* Space, Uppercase A-Z, lowercase a-z */
  unicodeRange: 'U+32, U+65-U+90, U+97-122',
  weight: tokenFontWeightsRegular
}, {
  family: FONT_FAMILY,
  filename: `${FONT_FILENAME_PREFIX}regular`,
  weight: tokenFontWeightsRegular
}, {
  family: FONT_FAMILY,
  filename: `${FONT_FILENAME_PREFIX}regular-italic`,
  style: 'italic',
  weight: tokenFontWeightsRegular
}, {
  family: FONT_FAMILY,
  filename: `${FONT_FILENAME_PREFIX}medium`,
  weight: tokenFontWeightsMedium
}, {
  family: FONT_FAMILY,
  filename: `${FONT_FILENAME_PREFIX}semi-bold`,
  weight: tokenFontWeightsSemiBold
}, {
  family: FONT_FAMILY,
  filename: `${FONT_FILENAME_PREFIX}bold`,
  weight: tokenFontWeightsBold
}, {
  family: FONT_FAMILY_HEADING,
  filename: `${FONT_HEADING_FILENAME_PREFIX}semi-bold`,
  weight: tokenFontWeightsSemiBold
}];
const generateFontFaceAtRules = basePath => fonts.reduce((str, font) => `
    ${str}
    @font-face {
      font-display: swap;
      font-family: ${font.family};
      font-weight: ${font.weight};
      src: ${font.family === FONT_FAMILY_OPTIMISED ? `url('${basePath}${font.filename}.woff2') format('woff2')` : `url('${basePath}${font.filename}.woff2') format('woff2'),
             url('${basePath}${font.filename}.woff') format('woff')`};
      ${font.style ? `font-style: ${font.style};` : ''}
      ${font.unicodeRange ? `unicode-range: ${font.unicodeRange};` : ''}
    }
  `, '');
const setGlobalStyles = () => `
  html {
    font-family: ${FONT_FAMILY_FALLBACK};
  }

  html:not(.${LOADED_CLASS_STAGE_1}):not(.${LOADED_CLASS_STAGE_2}):not(.${WEB_FONT_UNAVAILABLE_CLASS}) {
    font-size: calc(${FONT_SIZE} - 1px);
    letter-spacing: 0.25px;
    line-height: 1.3;
  }

  .${LOADED_CLASS_STAGE_1} {
    font-family: ${FONT_FAMILY_OPTIMISED}, ${FONT_FAMILY_FALLBACK};
  }

  .${LOADED_CLASS_STAGE_2} {
    font-family: ${FONT_FAMILY}, ${FONT_FAMILY_FALLBACK};
  }
`;
const getLoadFontsScript = () => `
  (function () {
    const setWebFontUnavailableClass = () =>
      (document.documentElement.className += ' ${WEB_FONT_UNAVAILABLE_CLASS}');
    const setLoadedClassStage1 = () =>
      (document.documentElement.className += ' ${LOADED_CLASS_STAGE_1}');
    const setLoadedClassStage2 = () =>
      (document.documentElement.className += ' ${LOADED_CLASS_STAGE_2}');

    const load = () => {
      if (!('fonts' in document)) {
        setWebFontUnavailableClass();
        return;
      }

      if (sessionStorage['${SESSION_STORAGE_VALUE}']) {
        setLoadedClassStage2();
        return;
      }

      document.fonts.load('${FONT_SIZE} ${FONT_FAMILY_OPTIMISED}').then(() => {
        setLoadedClassStage1();

        Promise.all([
          document.fonts.load('${tokenFontWeightsRegular} ${FONT_SIZE} ${FONT_FAMILY}'),
          document.fonts.load('italic ${tokenFontWeightsRegular} ${FONT_SIZE} ${FONT_FAMILY}'),
          document.fonts.load('${tokenFontWeightsMedium} ${FONT_SIZE} ${FONT_FAMILY}'),
          document.fonts.load('${tokenFontWeightsSemiBold} ${FONT_SIZE} ${FONT_FAMILY}'),
          document.fonts.load('${tokenFontWeightsSemiBold} ${FONT_SIZE} ${FONT_FAMILY_HEADING}'),
          document.fonts.load('${tokenFontWeightsBold} ${FONT_SIZE} ${FONT_FAMILY}'),
        ]).then(() => {
          setLoadedClassStage2();
          sessionStorage['${SESSION_STORAGE_VALUE}'] = true;
        });
      });
    };

    if (
      navigator.connection &&
      (navigator.connection.effectiveType === 'slow-2g' ||
        navigator.connection.effectiveType === '2g' ||
        navigator.connection.saveData)
    ) {
      setWebFontUnavailableClass();
    } else {
      load();
    }
  })();
`;
const renderFonts = (basePath, nonce) => ( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("link", {
  as: "font",
  crossOrigin: "anonymous",
  href: `${basePath}${fonts[0].filename}.woff2`,
  rel: "preload",
  type: "font/woff2"
}), /*#__PURE__*/React.createElement("style", {
  // eslint-disable-next-line react/no-danger
  dangerouslySetInnerHTML: {
    __html: `
          ${generateFontFaceAtRules(basePath)}
          ${setGlobalStyles()}
        `
  },
  nonce: nonce
}), /*#__PURE__*/React.createElement("script", {
  dangerouslySetInnerHTML: {
    __html: getLoadFontsScript()
  },
  nonce: nonce
})));

export { Accordion, AnimatedSpinner, Avatar, Banner, Body, Button, ButtonLink, CheckboxGroup, CommonPlatformHeader, CommonPlatformHeaderListContainer, CommonPlatformHeaderMenuLinkItem, CommonPlatformHeaderMenuLinks, CommonPlatformHeaderMenuSignUpLoginLinks, CommonPlatformHeaderMenuUserCard, CounterGroup, DATE_DATA_FORMAT, DATE_DISPLAY_FANCY_FORMAT, DATE_DISPLAY_FORMAT, DATE_DISPLAY_SHORT_FORMAT, DATE_FORMATS, DatePickerMulti, DatePickerRange, DatePickerRangeMerged, DatePickerSingle, Dialog, Divider, EmptyState, Fieldset, Footnote, Form, Heading, IconAccount, IconAdjust, IconAlert, IconAvatar, IconBaby, IconBell, IconBookings, IconBuilding, IconButton, IconCalendar, IconCalendarBooking, IconCalendarCheck, IconCalendarDay, IconCalendarOutlined, IconChat, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight, IconChevronUp, IconClock, IconClose, IconCopy, IconCredit, IconCreditCard, IconCross, IconDashboard, IconDogGrooming, IconDogTraining, IconDogWalking, IconEasterEgg, IconEllipses, IconEnvelope, IconEnvelopeNav, IconExclamation, IconFaceHappy, IconFacebook, IconFacebookColoured, IconFacebookMessenger, IconFavourite, IconFence, IconFilter, IconFlag, IconGlobe, IconGreenSproute, IconHeartFilled, IconHeartOutlined, IconHelp, IconHouse, IconHouseVisiting, IconImage, IconInstagram, IconLightbulb, IconLocationFilled, IconLocationOutlined, IconLogin, IconLogout, IconMenu, IconMessage, IconMinus, IconMoney, IconMoon, IconNoHouse, IconNotifications, IconOpenInNew, IconPassword, IconPaw, IconPawPrint, IconPencil, IconPetDayCare, IconPetHosting, IconPetSitting, IconPlane, IconPlay, IconPlus, IconPresent, IconProfile, IconQuestion, IconQuestionMark, IconRepeat, IconRepeatClient, IconSantasHat, IconSavings, IconSearch, IconSearchRounded, IconSettings, IconShare, IconShieldCheckFilled, IconShieldCheckOutlined, IconShieldPaw, IconShieldStar, IconSleep, IconSnowFlake, IconSpinner, IconStar, IconStore, IconSuitcase, IconVisibilityHide, IconVisibilityShow, IconWhatsapp, IconX, IllustrationDogGrooming, IllustrationDogTraining, IllustrationDogWalking, IllustrationGeneric, IllustrationHouseVisting, IllustrationPetDayCare, IllustrationPetSitting, LayoutGridContainer, LayoutGridItem, LayoutPlatform, LayoutVerticalSpacer, Pagination, Pills, PlatformHeaderLogo, PlatformHeaderMenuButton, PlatformHeaderNavigation, PlatformHeaderSubNavigation, PriceBreakdown, RadioGroup, SearchField, SearchResultsList, SearchResultsListSkeleton, Select, SitterCard, Status, Supplementary, Switch, Tag, TappableLinkContainer, TextArea, TextField, ToggleGroup, ToolTip, VisuallyHidden, formatCurrency, formatDate, formatRatingScore, pxToEmForMediaQuery, pxToRem, renderFonts, useDialogHeadingId, useLargeFromMediaQuery, useLargeToMediaQuery, useMediumFromMediaQuery, useMediumToMediaQuery, useSmallToMediaQuery };
//# sourceMappingURL=design-system.esm.js.map
